// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { enDeadlineActivation, enFrameworkSuspension, enFrameworkBriefings, enRegulatoryUpdateComparison, enDeploymentPrivacy, enEscalationLadder, enModuleTips, enVehicleMonitoring, enRecordLinks, enOrgCalendar, enOrgIdentity, enFormPrefill, enCustomsTaxExports, enCrossBorderFilingFence, enSectorOverlaysModule, enProductIdentification, enServiceCertificates, enVendorServiceCertificate, } from './en-extras.js';
/**
 * English translations for the AesirX ComplianceOne.
 * Covers all modules through Phase 8 (Sprint 11.3.0 — full i18n coverage).
 */
export const en = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: {
                present: 'Present',
                absent: 'Absent',
                pending: 'Pending',
            },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. Real copy (R-57).
    controlled_testing: {
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    // ---------- Login & Authentication (Sprint 11.3.0) ----------
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Sign in to your account',
        email: 'Email',
        password: 'Password',
        sign_in: 'Sign In',
        signing_in: 'Signing in...',
        mfa_code: 'MFA Code',
        mfa_placeholder: 'Enter 6-digit code',
        mfa_instruction: 'Enter the 6-digit code from your authenticator app.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Verify',
        verifying: 'Verifying...',
        forgot_password: 'Forgot password?',
        account_locked: 'Account is locked. Please try again later.',
        account_deactivated: 'Account has been deactivated. Contact your administrator.',
        invalid_mfa: 'Invalid MFA code. Please try again.',
        invalid_credentials: 'Invalid email or password.',
        login_failed: 'Login failed',
        session_limit_reached: 'Your account has reached the active session limit. Sign out on another device, or ask your administrator to revoke an existing session, then try again.',
        session_expired: 'Your session has expired. Please reload the page and try again.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    // ---------- Auth pages (Sprint 11.3.0) ----------
    auth: {
        forgot_password: {
            title: 'Forgot Password',
            subtitle: 'Enter your email address and we will send you a link to reset your password.',
            send: 'Send Reset Link',
            sending: 'Sending...',
            success: 'If that email exists, a password reset link has been sent.',
            back_to_login: 'Back to Login',
        },
        reset_password: {
            title: 'Reset Password',
            subtitle: 'Enter your new password below.',
            new_password: 'New Password',
            confirm_password: 'Confirm Password',
            reset: 'Reset Password',
            resetting: 'Resetting...',
            success: 'Your password has been reset successfully.',
            go_to_login: 'Go to Login',
            password_mismatch: 'Passwords do not match.',
            invalid_token: 'Invalid or missing reset token. Please request a new password reset link.',
            requirements_title: 'Password requirements:',
            password_requirements: 'Password must meet the following requirements:',
            rules: {
                min_length: 'At least 12 characters',
                uppercase: 'At least one uppercase letter',
                lowercase: 'At least one lowercase letter',
                digit: 'At least one digit',
                special: 'At least one special character',
            },
        },
        verify_email: {
            title: 'Email Verification',
            verifying: 'Verifying your email address...',
            success: 'Your email has been verified successfully.',
            invalid_token: 'Invalid or missing verification token.',
            failed: 'Email verification failed. The token may be invalid or expired.',
            go_to_login: 'Go to Login',
        },
        offline: {
            title: 'You are offline',
            message: "It looks like you've lost your internet connection. Please check your network and try again.",
        },
    },
    // ---------- Public intake form (Sprint 1.0.0) ----------
    intake: {
        title: 'Data Rights Request',
        subtitle: 'Submit a data rights request to our organization',
        fields: {
            full_name: 'Full Name',
            email: 'Email Address',
            phone: 'Phone Number (optional)',
            request_type: 'Request Type',
            description: 'Describe Your Request',
            preferred_language: 'Preferred Language',
            consent: 'I consent to the processing of this request',
        },
        submit: 'Submit Request',
        validation: {
            name_required: 'Full name is required',
            email_required: 'A valid email address is required',
            type_required: 'Please select a request type',
            description_required: 'Please describe your request',
            consent_required: 'You must consent to processing to continue',
        },
        error_title: 'Error',
        config_load_error: 'Unable to load intake configuration. Please check the URL.',
        organization_label: 'Organization',
        customer_reference: 'Customer Reference Number',
        country: 'Country / Jurisdiction',
        preferred_channel: 'Preferred Response Channel',
        channels: {
            email: 'Email',
            postal: 'Postal',
            phone: 'Phone',
            portal: 'Portal',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Recording this request does not bring a lawsuit or a denunciation before a court or a competent authority, and the organisation does not decide either of them. Please keep your reference number.',
            title: 'Request Received',
            message: 'Your request has been successfully recorded. Your reference number is:',
            // fix-sprint #431 — said "The expected processing time is 30 working days".
            // The period is the one the organisation's installed framework states for
            // the request type, or its configured deadline, and is not derived here.
            next_steps: 'We will process your request as soon as possible in accordance with applicable law and contact you about the next steps. Please keep your reference number.',
        },
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: 'This organisation does not receive this kind of request under the data protection rules it follows. Please choose another request type.',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. If you know which of these you wish to raise, choose it; if you are not sure, leave it and the organisation will record it.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    // ---------- Request type labels ----------
    request_types: {
        access: 'Access (DSAR)',
        deletion: 'Deletion',
        correction: 'Correction / Update',
        objection: 'Objection',
        portability: 'Data Portability',
        restriction: 'Restriction of Processing',
        consent_withdrawal: 'Consent Withdrawal',
        protective_measures: 'Request personal data protection measures and solutions',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    // ---------- Case statuses ----------
    statuses: {
        new: 'New',
        in_review: 'In Review',
        waiting_on_requester: 'Waiting on Requester',
        waiting_on_internal_data: 'Waiting on Internal Data',
        approved: 'Approved',
        closed: 'Closed',
        rejected: 'Rejected',
    },
    // ---------- Admin UI ----------
    admin: {
        nav: {
            rights_requests: 'Rights Requests',
            templates: 'Templates',
            response_templates: 'Response Templates',
            data_mapping: 'Data Mapping',
            governance: 'Governance',
            consent: 'Consent',
            assessments: 'Assessments',
            classification: 'Classification',
            monitoring: 'Monitoring',
            incidents: 'Incidents',
            onboarding: 'Onboarding',
            connectors: 'Connectors',
            vendor_risk: 'Vendor Risk',
            deletion_orchestration: 'Deletion Orchestration',
            discovery: 'Data Discovery',
            incident_ops: 'Incident Operations',
            program_governance: 'Program Governance',
            access_accountability: 'Access Accountability',
            consent_properties: 'Consent Properties',
            localization: 'Localization',
            regulatory: 'Regulatory',
            partner_workspace: 'Partner Workspace',
            audit_trail: 'Audit Trail',
            // Sprint 47.5.2 — Forseti AI Legal Advisor menu label.
            forseti: 'Forseti',
            settings: 'Settings',
            help_center: 'Help Center',
            dashboard: 'Dashboard',
            platform: 'Platform',
            integrations: 'Integrations',
            cmp_dsar_sync: 'CMP DSAR Sync',
            cmp_integration: 'CMP Integration',
            purpose_mapping: 'Purpose Mapping',
            webhook_config: 'Webhook Config',
            bulk_sync: 'Re-map Records',
            privacy_scanner: 'Privacy Scanner',
            privacy_monitoring: 'Privacy Monitoring',
            real_time_scanner: 'Real-Time Scanner',
            compliance_forms: 'Compliance Forms',
            forms_dashboard: 'Forms Dashboard',
            form_templates: 'Templates',
            form_catalog: 'Form Catalog',
            pack_catalog: 'Pack Catalog',
            org_groups: 'Org Groups',
            consolidation: 'Consolidation',
            organizations: 'Organizations',
            org_list: 'All Organizations',
            intake_configs: 'Intake Configs',
            task_management: 'Task Management',
            unified_audit: 'Unified Audit',
            evidence_packs: 'Audit evidence packs',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Audit Periods',
            regulatory_updates: 'Regulatory Updates',
            departments: 'Departments',
        },
        sidebar_category_data_privacy_rights: 'Data Privacy & Rights',
        sidebar_category_vendor_third_party: 'Vendor & Third-Party Management',
        sidebar_category_risk_assessment: 'Risk & Assessment',
        sidebar_category_operations_automation: 'Operations & Automation',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Task & Workflow Management',
        // Sidebar sub-group labels (3rd-tier nav). Added in the sidebar-shortening
        // sprint; EN + VI only. Remaining 14 locales fall back to EN via deepMerge
        // until Sprint 51.0.0 adds proper translations.
        sidebar_groups: {
            // Consent
            consent_cmp_webhooks: 'CMP & Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            // Data Mapping
            data_mapping_ropa: 'RoPA Register',
            data_mapping_classification: 'Classification',
            data_mapping_governance: 'Governance',
            // Access & Accountability
            access_live_monitoring: 'Live Monitoring',
            access_reviews_investigations: 'Reviews & Investigations',
            access_evidence_policies: 'Evidence & Policies',
            // Classification
            classification_taxonomies: 'Taxonomies & Labels',
            classification_workflow: 'Workflow',
            classification_insights: 'Insights',
            // Connectors
            connectors_runtime: 'Runtime',
            connectors_due_diligence: 'Due Diligence',
            connectors_coverage: 'Coverage',
            // Consent Properties
            cp_definitions: 'Definitions',
            cp_lifecycle: 'Lifecycle',
            cp_quality: 'Quality & Evidence',
            // Deletion Orchestration
            deletion_execution: 'Execution',
            deletion_vendors: 'Vendors',
            deletion_evidence: 'Evidence & Integration',
            // Discovery
            discovery_rules: 'Rules & Templates',
            discovery_scanning: 'Scanning',
            discovery_review: 'Review Workflow',
            discovery_insights: 'Insights',
            // Governance
            governance_oversight: 'Oversight',
            governance_reporting: 'Reporting',
            governance_vendors: 'Vendors & Cross-Border',
            governance_lifecycle: 'Lifecycle',
            // Incident Ops
            incident_ops_dashboards: 'Dashboards',
            incident_ops_sla: 'SLA Config',
            incident_ops_playbooks: 'Playbooks',
            incident_ops_vendor_regulator: 'Vendor & Regulator',
            incident_ops_evidence: 'Evidence & Review',
            // Incidents
            incidents_workload: 'Workload',
            incidents_regulatory: 'Regulatory',
            incidents_reporting: 'Reporting',
            // Localization
            localization_content: 'Content',
            // Sprint 63.42.2 — the Regulatory section (`admin.nav.regulatory`): Frameworks
            // is the former Localization › Regulatory group, renamed so the menu never
            // reads Regulatory › Regulatory. Its other three groups keep their
            // `localization_*` keys, which the R-44 scope map names.
            regulatory_frameworks: 'Frameworks',
            // Sprint 63.43.0 — groups that now hold the Class B and C views (plan §3.2).
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            // Sprint 63.44.0 (F1) — `localization_sector_overlays`,
            // `localization_cybersecurity_assurance_index` and
            // `localization_food_traceability` are no longer rendered by the sidebar
            // (their groups moved to Sector Overlays in 63.43.0). Kept: the R-44
            // registry and scope map name them as the scope of governed terms, so
            // they leave only with a Compliance Manager batch; no unused-key check
            // reads them, and the strings-terminology tests pin their registered text.
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observability',
            // Monitoring
            monitoring_metrics: 'Metrics',
            monitoring_programs: 'Programs',
            monitoring_tasks: 'Tasks',
            monitoring_controls: 'Controls',
            // Organizations
            org_entities: 'Entities',
            org_config: 'Configuration',
            org_frameworks: 'Frameworks & Rollup',
            // Partner Workspace
            partner_overview: 'Overview',
            partner_partners: 'Partners',
            partner_delivery: 'Delivery',
            partner_support: 'Support',
            // Platform
            platform_identity: 'Identity & Roles',
            platform_access_control: 'Access Control',
            platform_auth_session: 'Authentication & Session',
            platform_access_reviews: 'Access Reviews',
            platform_files: 'Files',
            platform_notifications: 'Notifications',
            platform_module_config: 'Module Config',
            platform_ops: 'Ops & Logs',
            // Program Governance
            pg_health: 'Program Health',
            pg_maturity: 'Maturity',
            pg_audit: 'Audit & Evidence',
            pg_training: 'Training',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Obligations',
            pg_documents: 'Documents',
            // Vendor Risk
            vr_overview: 'Overview',
            vr_onboarding: 'Onboarding',
            vr_assessment: 'Assessment',
            vr_oversight: 'Oversight',
        },
        tickets: {
            title: 'Rights Requests',
            create: 'Create Request',
            filters: 'Filters',
            search_placeholder: 'Search by ticket ID or requester...',
            export_csv: 'Export CSV',
            exporting: 'Exporting...',
            bulk_export_failed: 'Bulk export failed',
            // Sprint 43.2.0 batch 4 (E3) — Bulk export menu on TicketListPage.
            bulk_export: {
                label: 'Export',
                format: {
                    pdf: 'PDF Summary',
                    zip: 'ZIP (per-case evidence packs)',
                    csv: 'CSV',
                },
                warning_over_50: 'This will generate approximately {count} case packs - may take several minutes. Continue?',
                blocked_over_500: 'Narrow filter to 500 or fewer cases before exporting ZIP (current: {count}).',
                success: 'Bulk export saved to Files Browser ({count} cases)',
                failed: 'Bulk export failed',
                continue: 'Continue',
                cancel: 'Cancel',
            },
            no_results: 'No requests found',
            ticket_id: 'Ticket ID',
            request_type: 'Request Type',
            requester: 'Requester',
            source: 'Source',
            all_sources: 'All Sources',
            assigned_to: 'Assigned To',
            created: 'Created',
            load_error: 'Failed to load cases. Please try again.',
            case_creation_failed: 'Case creation failed',
            business_unit: 'Business Unit',
            reference: 'Reference',
            sla: {
                title: 'SLA Status',
                on_track: '{days} days remaining',
                approaching: 'Approaching deadline',
                overdue: '{days} days overdue',
                ack_overdue: '{hours}h past acknowledgment deadline',
                ack_warning: 'Acknowledgment due soon',
                closed: 'Closed',
                completed_in: 'Completed in {days} days',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Response Templates',
            new: 'New Template',
            edit: 'Edit Template',
            all_types: 'All Types',
            all_locales: 'All Locales',
            type_access: 'Access/DSAR',
            type_deletion: 'Deletion',
            type_correction: 'Correction',
            type_objection: 'Objection',
            type_portability: 'Portability',
            type_other: 'Other',
            type_restriction: 'Restriction',
            type_all: 'All',
            locale_en: 'English',
            locale_vi: 'Vietnamese',
            locale_all: 'Both',
            name: 'Name',
            request_type: 'Request Type',
            locale: 'Locale',
            subject: 'Subject',
            body: 'Body',
            name_placeholder: 'Template name...',
            subject_placeholder: 'Email/letter subject...',
            body_placeholder: 'Template body. Use {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} as placeholders.',
            available_placeholders: 'Available placeholders:',
            create_template: 'Create Template',
            preview: 'Preview',
            close_preview: 'Close Preview',
            default_badge: 'Default',
            no_templates: 'No templates found',
            confirm_delete: 'Delete this template?',
            load_error: 'Failed to load templates. Please try again.',
            create_error: 'Failed to create template',
            update_error: 'Failed to update template',
            delete_error: 'Failed to delete template',
            preview_error: 'Failed to preview template',
            response_templates_badge: 'Response Templates',
            browse_form_templates: 'Browse Compliance Form Templates',
            templates_loaded: 'Templates loaded',
            sample_organization: 'Sample Organization',
        },
        detail: {
            requester_info: 'Requester Information',
            status: 'Status',
            assignment: 'Assignment',
            notes: 'Notes',
            audit_trail: 'Audit Trail',
            add_note: 'Add Note',
            case_not_found: 'Case not found',
            load_error: 'Failed to load case. Please try again.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Failed to add note. Please try again.',
            submitted_from: 'Submitted from',
            via_cmp: 'via AesirX CMP',
            cmp_ticket: 'CMP Ticket:',
            select_template: 'Select a Template',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Details',
                evidence: 'Evidence',
                communications: 'Communications',
                escalations: 'Escalations',
                audit_trail: 'Audit Trail',
                fulfillment: 'Fulfillment',
                response: 'Response',
                extension: 'Extension',
            },
            requester: {
                name: 'Name',
                email: 'Email',
                phone: 'Phone',
                language: 'Language',
                source: 'Source',
                customer_ref: 'Customer Ref',
                country: 'Country',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Preferred Channel',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'On Track',
                due_soon: 'Due Soon',
                overdue: 'Overdue',
                days_remaining: 'd remaining',
                due_date: 'Due Date',
                breached: 'Breached',
                paused: 'Paused',
                total_paused_days: 'Total Paused Days',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Days Elapsed',
                progress: 'Progress',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identity Verification',
                verified: 'Verified',
                not_verified: 'Not verified',
                method: 'Method',
                notes: 'Notes',
                verified_by: 'Verified By',
                verified_at: 'Verified At',
                warning: 'Identity not yet verified.',
                verify_button: 'Verify Identity',
                select_method: '-- Select --',
                methods: {
                    email_confirmation: 'Email Confirmation',
                    id_document: 'ID Document',
                    phone_call: 'Phone Call',
                    in_person: 'In Person',
                    other: 'Other',
                },
            },
            closure: {
                reason: 'Closure Reason',
                summary: 'Closure Summary',
                summary_placeholder: 'Summary of how the request was resolved...',
                reason_required: 'Closure summary and reason are required',
                response_not_sent: 'A formal response must be sent to the data subject before closing this case',
                reasons: {
                    fulfilled: 'Fulfilled',
                    partially_fulfilled: 'Partially Fulfilled',
                    unfounded: 'Unfounded',
                    excessive: 'Excessive',
                    withdrawn: 'Withdrawn',
                    duplicate: 'Duplicate',
                    identity_unverified: 'Identity Unverified',
                },
            },
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Export',
            export_pdf: 'Export PDF',
            export_zip: 'Export ZIP',
            // Sprint 43.1.0 batch 4 — Case File PDF / Evidence Pack ZIP labels
            case_file_pdf: 'Export PDF (Case File)',
            evidence_pack_zip: 'Export ZIP (Evidence Pack)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            // Sprint 43.2.0 batch 4 (E5) — Breach-trigger UI on TicketDetailPage.
            breach: {
                flag_btn: 'Flag as Breach-Causing',
                modal: {
                    title: 'Flag case as breach-causing',
                    type_label: 'Breach type',
                    type: {
                        misdirected_response: 'Misdirected response',
                        unauthorised_disclosure: 'Unauthorised disclosure',
                        identity_mismatch: 'Identity mismatch',
                        other: 'Other',
                    },
                    description: 'Description (optional)',
                    description_placeholder: 'What happened? (max 1000 chars)',
                    confirm: 'Flag and start the notification clock',
                    cancel: 'Cancel',
                    flagging: 'Flagging...',
                },
                banner: {
                    title: 'Breach flagged: {type}',
                    detected_at: 'Detected at {timestamp}',
                    filing_deadline: 'Notification deadline: in {hours} hours',
                    overdue: 'Notification deadline: OVERDUE',
                },
                toasts: {
                    flagged: 'Case flagged as breach-causing - notification clock started',
                    flag_failed: 'Failed to flag case as breach-causing',
                },
            },
            export: {
                generating: 'Generating... please wait',
                success: 'Export saved to Files Browser',
                failed: 'Export failed - check audit log',
            },
            no_notes: 'No notes yet',
            assign_placeholder: 'User ID to assign...',
            reassign: 'Reassign',
            evidence: {
                checklist_title: 'Evidence Checklist',
                complete_pct: '% complete',
                no_checklist: 'No checklist items',
                attachments: 'Attachments',
                upload_attachment: 'Upload Attachment',
                upload_button: 'Upload',
                uploading: 'Uploading...',
                attachment_uploaded: 'Attachment uploaded successfully',
                upload_error: 'Failed to upload attachment',
                category: 'Category',
                categories: {
                    identity_proof: 'Identity Proof',
                    correspondence: 'Correspondence',
                    internal_data: 'Internal Data',
                    legal: 'Legal',
                    other: 'Other',
                },
                no_attachments: 'No attachments',
                references_title: 'Evidence References',
                no_references: 'No references',
                add_reference: 'Add Reference',
                ref_type: 'Type',
                ref_value: 'Value',
                ref_desc: 'Description',
                ref_value_placeholder: 'URL, email ID, or reference...',
                ref_desc_placeholder: 'Description...',
                ref_types: {
                    url: 'URL',
                    email_id: 'Email ID',
                    crm_record: 'CRM Record',
                    ticket_ref: 'Ticket Reference',
                    other: 'Other',
                },
            },
            communications: {
                title: 'Communications',
                add: 'Add Message',
                no_records: 'No communications recorded',
                direction: 'Direction',
                channel: 'Channel',
                visibility: 'Visibility',
                subject: 'Subject',
                body: 'Body',
                send: 'Send',
                inbound: 'Inbound',
                outbound: 'Outbound',
                internal: 'Internal',
                external: 'External',
                tab_all: 'All',
                tab_internal: 'Internal',
                tab_external: 'External',
                subject_placeholder: 'Subject (optional)',
                body_placeholder: 'Message body...',
                channels: {
                    email: 'Email',
                    portal: 'Portal',
                    phone: 'Phone',
                    postal: 'Postal',
                    in_person: 'In Person',
                },
                apply_template: 'Apply Template',
                select_template: 'Select a template...',
                no_templates: 'No templates available',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Template applied to response.',
                template_apply_error: 'Failed to apply template.',
            },
            escalations: {
                title: 'Escalation History',
                no_events: 'No escalation events',
                tier: 'Tier',
                threshold: 'Threshold',
                action: 'Action',
                target: 'Target',
                triggered_at: 'Triggered At',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did.
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP Sync',
                sync_status: 'Sync Status',
                last_synced: 'Last Synced',
                not_synced: 'Not Synced',
                resync: 'Resync Now',
                syncing: 'Syncing...',
                sync_history: 'Sync History (Last 10)',
                direction: 'Direction',
                time: 'Time',
                retries: 'Retries',
                error_col: 'Error',
            },
            audit: {
                filter_event_placeholder: 'Filter by event type...',
                filter_actor_placeholder: 'Filter by actor...',
            },
            fulfillment: {
                title: 'Department Fulfillment',
                assign_departments: 'Assign Departments',
                department: 'Department',
                assignee: 'Assignee',
                deadline: 'Deadline',
                evidence_count: 'Evidence',
                signoff: 'Sign-off',
                signed_off: 'Signed Off',
                pending_signoff: 'Pending sign-off',
                start_button: 'Start',
                complete_button: 'Mark Complete',
                signoff_button: 'Sign Off',
                no_tasks: 'No fulfillment tasks assigned yet.',
                tasks_table: 'Fulfillment Tasks',
                progress_label: 'Fulfillment Progress',
                overdue: 'overdue',
                assigned_success: 'Department fulfillment task assigned successfully.',
                assign_error: 'Failed to assign fulfillment task.',
                signoff_success: 'Department sign-off recorded.',
                signoff_error: 'Failed to record sign-off.',
                identity_required: 'Identity verification is required before managing fulfillment tasks.',
                remove_task: 'Remove',
                remove_success: 'Task removed',
                add_evidence_btn: 'Add Evidence',
                evidence_uploaded: 'Evidence uploaded and linked',
                evidence_upload_error: 'Failed to upload evidence',
                suggest_data_stores: 'Suggest Data Stores',
                suggest_error: 'Failed to load data store suggestions.',
                suggest_stores_title: 'Suggested Data Stores',
                suggest_stores_none: 'No data stores match this case. Configure systems in Data Mapping to improve suggestions.',
                suggest_stores_add: 'Add to case',
                suggest_stores_added: 'Data store added to case',
                no_departments_title: 'No departments configured',
                no_departments_body: 'Departments let you route DSR fulfillment tasks to a function (Privacy, Legal, HR, IT Ops). Install sample departments or ask your admin to configure departments in the Organization settings.',
                no_departments_cta: 'Install sample data',
                department_placeholder: 'Department name...',
                assignee_placeholder: 'User ID to assign...',
                assign_button: 'Assign',
                status_pending: 'Pending',
                status_in_progress: 'In Progress',
                status_completed: 'Completed',
                status_verified: 'Verified',
                confirm_approve: 'Are you sure you want to approve this extension?',
                confirm_deny: 'Are you sure you want to deny this extension?',
            },
            response: {
                title: 'Response Generation',
                select_template: 'Response Template',
                choose_template: 'Select a template...',
                generate: 'Generate Response',
                preview: 'Response Preview',
                send: 'Send Response',
                // Sprint 43.1.0 batch 4 — delivery_method converted to object
                // (label kept for existing callers; sub-keys for picker options).
                delivery_method: {
                    label: 'Delivery Method',
                    email: 'Email',
                    portal: 'Portal',
                    postal: 'Postal mail',
                    in_person: 'In person',
                },
                recipient: {
                    email: 'Recipient email',
                    portal: 'Portal user ID',
                    address: 'Postal address',
                },
                auto_generate_btn: 'Generate and send response',
                auto_generate_success: 'Response generated and delivered',
                auto_generate_missing_recipient: 'Please provide a recipient before sending.',
                auto_fields: 'Auto-populated fields',
                case_id: 'Case ID',
                requester_name: 'Requester Name',
                rights_type: 'Rights Type',
                history: 'Sent Responses',
                generated_at: 'Generated',
                confirmation: 'Confirmation',
                sent_label: 'Sent',
                draft_label: 'Draft',
                generated_success: 'Response generated successfully.',
                generate_error: 'Failed to generate response.',
                sent_success: 'Response sent successfully.',
                send_error: 'Failed to send response.',
                load_error: 'Failed to load response templates.',
                pdf_title: 'Data Subject Response Letter',
                download_pdf: 'Download PDF',
                download_all: 'Download All',
                download_error: 'Failed to download response',
            },
            extension: {
                title: 'Deadline Extensions',
                acknowledgment_deadline: 'Acknowledgment Deadline',
                fulfillment_deadline: 'Fulfillment Deadline',
                request_extension: 'Request Extension',
                reason: 'Reason',
                reason_placeholder: 'Reason for requesting additional time...',
                additional_days: 'Additional Days',
                submit_request: 'Submit Extension Request',
                history: 'Extension History',
                requested_at: 'Requested',
                approve: 'Approve',
                deny: 'Deny',
                request_success: 'Extension request submitted.',
                request_error: 'Failed to submit extension request.',
                approved_success: 'Extension approved.',
                denied_success: 'Extension denied.',
                decision_error: 'Failed to process extension decision.',
                status_pending: 'Pending',
                status_approved: 'Approved',
                status_denied: 'Denied',
                confirm_approve: 'Are you sure you want to approve this extension request?',
                confirm_deny: 'Are you sure you want to deny this extension request?',
                decided_by: 'Decided By',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Export CSV',
            exporting: 'Exporting...',
            search_placeholder: 'Search audit details...',
            filter_by_actor: 'Filter by actor...',
            all_event_types: 'All Event Types',
            seq: '#',
            timestamp: 'Timestamp',
            event: 'Event',
            case_col: 'Case',
            actor: 'Actor',
            summary: 'Summary',
            no_events: 'No audit events found',
            export_success: 'Audit trail exported successfully.',
            export_error: 'Failed to export audit CSV',
            load_error: 'Failed to load audit trail. Please try again.',
            announce_loaded: 'Audit trail loaded',
            start_date: 'Start date',
            end_date: 'End date',
            per_page: '{count} / page',
            unknown: 'Unknown',
            // Sprint 47.9.0a — Source filter on the global audit feed.
            // "All" UNIONs Forseti AI events with the per-module trail; "Module"
            // shows only grc_case_audit_events; "Forseti" shows only
            // grc_ai_audit_events rows already returned by the UNION.
            source_filter: {
                label: 'Source',
                all: 'All',
                module: 'Module',
                forseti: 'Forseti',
                aria_label: 'Filter audit events by source',
            },
            event_types: {
                case_created: 'Case Created',
                status_changed: 'Status Changed',
                case_assigned: 'Case Assigned',
                note_added: 'Note Added',
                attachment_added: 'Attachment Added',
                attachment_deleted: 'Attachment Deleted',
                reference_added: 'Reference Added',
                checklist_updated: 'Checklist Updated',
                identity_verified: 'Identity Verified',
                closure_summary_added: 'Closure Summary',
                approval_granted: 'Approval Granted',
                template_applied: 'Template Applied',
                export_generated: 'Export Generated',
                sla_warning: 'SLA Warning',
                sla_overdue: 'SLA Overdue',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Unified Audit Trail',
                module_filter: 'Module',
                framework_filter: 'Framework',
                select_framework: 'Select a framework...',
                entity_type_filter: 'Entity Type',
                action_filter: 'Action',
                actor_filter: 'Actor',
                select_actor: 'Select an actor...',
                search_filter: 'Search',
                search_placeholder: 'Search audit events...',
                filters_heading: 'Filters',
                date_range: 'Date Range',
                no_events_found: 'No audit events found.',
                event_count: 'Event Count',
                action: 'Action',
                actor: 'Actor',
                date_range_start: 'Date Range Start',
                date_range_end: 'Date Range End',
                filtering_events: 'Filtering audit events',
                framework_codes: 'Framework Codes',
                entity_type_col: 'Entity Type',
                aria_table: 'Unified Audit Events',
                page_of: 'Page {page} of {total}',
                entity_types: {
                    user: 'User',
                    case: 'Case',
                    incident: 'Incident',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    consent: 'Consent',
                    policy: 'Policy',
                    data_map: 'Data Map',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Create',
                    update: 'Update',
                    delete: 'Delete',
                    view: 'View',
                    export: 'Export',
                    status_change: 'Status Change',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    // Fix sprint 20261021-2116 F2 — rows before the chain's cut-over: content recomputes, link/order does not.
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Audit evidence packs',
                assemble_pack: 'Assemble evidence pack',
                framework: 'Framework',
                select_framework: 'Select a framework...',
                framework_code_label: 'Framework Code',
                entity_types: 'Entity Types',
                select_entity_types: 'Select entity types...',
                period: 'Period',
                period_start: 'Period Start',
                period_end: 'Period End',
                completeness: 'Completeness',
                no_packs_found: 'No evidence packs found.',
                verify: 'Verify',
                export_pack: 'Export',
                exporting: 'Exporting...',
                pack_assembled: 'Evidence pack assembled',
                pack_exported: 'Evidence pack exported',
                aria_table: 'Audit evidence packs',
                aria_export_pack: 'Export pack',
                export_format: 'Export format',
                export_in_progress: 'Export in progress...',
                status_draft: 'Draft',
                status_assembled: 'Assembled',
                status_verified: 'Verified',
                status_submitted: 'Submitted',
                entity_type_labels: {
                    user: 'User',
                    case: 'Case',
                    incident: 'Incident',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    consent: 'Consent',
                    policy: 'Policy',
                    data_map: 'Data Map',
                },
                template_id: 'Template ID',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Module',
                    action: 'Action',
                    actor_id: 'Actor',
                    // fix-sprint-20260915-2128 F10 — the NINTH column. This map carried
                    // eight keys and the exported artefact emits nine; `actor_role` was
                    // the one with no key, so it was the one that could not be
                    // localised. Added here rather than dropping the column: the role a
                    // person held at the moment of the act is what an auditor reads the
                    // Actor column beside.
                    actor_role: 'Actor Role',
                    entity_type: 'Entity Type',
                    entity_id: 'Entity ID',
                    summary: 'Summary',
                    timestamp: 'Timestamp',
                    record_type: 'Record Type',
                    framework_code: 'Framework',
                },
                // fix-sprint-20260915-2128 F10 — the exported document's own title.
                // `title` above is the PAGE's ("Audit Evidence Packs", plural) and is
                // not this; borrowing a screen key for a document header is the
                // mistake `registry-export-renderer.ts` records at its top.
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Evidence Pack — Framework: {framework}',
                export_header_period: 'Period: {start} to {end}',
                export_header_completeness: 'Completeness: {score}%',
                export_header_status: 'Status: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Audit Periods',
                create_period: 'Create Audit Period',
                framework_code_label: 'Framework Code',
                select_framework: 'Select a framework...',
                period_label: 'Period Label',
                period_start: 'Period Start',
                period_end: 'Period End',
                start: 'Start',
                end: 'End',
                framework: 'Framework',
                retention_days: 'Retention Days',
                section_period_dates: 'Period Dates',
                no_periods_found: 'No audit periods found.',
                period_created: 'Audit period created',
                aria_table: 'Audit Periods',
                suggested_boundaries: 'Suggested Boundaries',
                suggested_boundaries_info: "Suggested period boundaries are derived from each framework's regulatory reporting cycle. Adjust as needed for your organization.",
                reporting_cycle: 'Reporting Cycle',
                suggested_start: 'Suggested Start',
                suggested_end: 'Suggested End',
                suggested_next: 'Suggested Next Period',
                cycle_monthly: 'Monthly',
                cycle_quarterly: 'Quarterly',
                cycle_semi_annual: 'Semi-Annual',
                cycle_annual: 'Annual',
                cycle_custom: 'Custom',
                period_deleted: 'Audit period deleted',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Customer Reference',
            country: 'Country / Jurisdiction',
            preferred_channel: 'Preferred Channel',
            channels: {
                email: 'Email',
                postal: 'Postal',
                phone: 'Phone',
                portal: 'Portal',
            },
            source: 'Source',
            sources: {
                admin_manual: 'Admin Manual',
                public_form: 'Public Form',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Phone Received',
                postal_received: 'Postal Received',
                email_received: 'Email Received',
                in_person: 'In-Person',
                legacy_import: 'Legacy Import',
            },
            required_fields: 'Request type, name, and email are required',
            create_error: 'Failed to create case',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Settings',
            request_types: 'Request Types',
            request_types_desc: 'Configure which request types are enabled for the public intake form.',
            notifications: 'Notifications',
            ticket_numbering: 'Ticket Numbering',
            ticket_format: 'Current format',
            ticket_prefix: 'Prefix',
            ticket_prefix_note: 'not configurable',
            sla_deadlines: 'SLA Deadlines',
            sla_desc: 'Set the maximum response time (in calendar days) for each request type. Cases approaching the threshold will show warnings.',
            days: 'days',
            warning_threshold: 'Warning threshold',
            escalation_label: 'Enable automatic escalation',
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Save SLA Configuration',
            saving: 'Saving...',
            saved: 'Saved successfully',
            announce_loaded: 'Settings loaded',
            // fix-sprint #431 (owner ruling 2026-10-03) — what the switches do now.
            email_on_creation: 'Notify the Compliance Owners when a rights request is received (in the platform and by email, under each person’s notification preferences)',
            send_confirmation: 'Email the requester an acknowledgement with the case reference',
            users: 'Users',
            roles: 'Roles',
            api_keys: 'API Keys',
            mfa: 'MFA',
            sessions: 'Sessions',
            webhooks: 'Webhooks',
            modules: 'Modules',
            // Sprint 62.45.0 (AC17) — the organisation calendar section. Declared in
            // `en-extras.ts` under a named interface: inlined here, it took `en`'s
            // inferred type past what the compiler will serialise (TS7056).
            calendar: enOrgCalendar,
            // fix-sprint #431 F1 — Settings › Escalation ladder.
            escalation_ladder: enEscalationLadder,
            deadline_labels: {
                access: 'Access (DSAR)',
                deletion: 'Deletion',
                correction: 'Correction',
                portability: 'Portability',
                objection: 'Objection',
                restriction: 'Restriction',
                consent_withdrawal: 'Consent Withdrawal',
                protective_measures: 'Protection Measures',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Section Assignments',
            assign_section: 'Assign Section',
            section_ref: 'Section Ref',
            section_label: 'Section Label',
            department_id: 'Department',
            assignee_id: 'Assignee',
            mandatory: 'Mandatory',
            assign: 'Assign',
            no_assignments: 'No section assignments',
            status: 'Status',
            completion: 'Completion',
            mark_complete: 'Complete',
        },
        consolidation: {
            title: 'Consolidation Progress',
            add_child: 'Add Child Task',
            child_task_id: 'Child Task ID',
            department_id: 'Department',
            link: 'Link',
            overall_completion: 'Overall Completion',
            departments_complete: 'Departments Complete',
            overdue: 'Overdue',
            ready: 'Ready',
            not_ready: 'Not Ready',
            master_status: 'Master Status',
            department_breakdown: 'Department Breakdown',
            department: 'Department',
            status: 'Status',
            completion: 'Completion',
            due_date: 'Due Date',
            overdue_flag: 'Overdue',
            child_tasks: 'Child Tasks',
            task: 'Task',
        },
        conflict_detection: {
            title: 'Conflict Detection',
            unresolved: 'unresolved',
            no_conflicts: 'No conflicts detected',
            section: 'Section',
            field: 'Field',
            dept_a: 'Department A',
            value_a: 'Value A',
            dept_b: 'Department B',
            value_b: 'Value B',
            severity: 'Severity',
            status: 'Status',
            resolved: 'Resolved',
            open: 'Open',
            resolve: 'Resolve',
        },
        org_maturity: {
            title: 'Organization Maturity',
            departments: 'Departments',
            department_hierarchy: 'Department Hierarchy',
            create_department: 'Create Department',
            department_name: 'Department Name',
            department_code: 'Code',
            level: 'Level',
            division: 'Division',
            department: 'Department',
            team: 'Team',
            parent_department: 'Parent Department',
            head_user: 'Department Head',
            no_departments: 'No departments configured.',
            user_mappings: 'User Mappings',
            map_user: 'Map User',
            user_id: 'User ID',
            role: 'Role',
            no_users: 'No users mapped to this department.',
            remove: 'Remove',
            framework_assignments: 'Framework Assignments',
            assign_framework: 'Assign Framework',
            entity_id: 'Entity ID',
            entity_type: 'Entity Type',
            framework_id: 'Framework ID',
            framework_code: 'Framework Code',
            no_assignments: 'No framework assignments.',
            compliance_rollup: 'Compliance Rollup',
            entity_name: 'Entity',
            total_assessments: 'Total Assessments',
            completed: 'Completed',
            completion_pct: 'Completion %',
            open_gaps: 'Open Gaps',
            critical_gaps: 'Critical',
            compliance_score: 'Score',
            no_rollup: 'No compliance rollup data available.',
            entity_summary: 'Entity Compliance Summary',
            frameworks_assigned: 'Frameworks',
            overall_score: 'Overall Score',
            load_departments_error: 'Failed to load departments',
            load_users_error: 'Failed to load users',
            department_created: 'Department created',
            department_created_announce: 'Department created',
            create_department_error: 'Failed to create department',
            user_mapped: 'User mapped',
            user_mapped_announce: 'User mapped to department',
            map_user_error: 'Failed to map user',
            user_removed: 'User removed',
            load_rollup_error: 'Failed to load rollup',
            load_assignments_error: 'Failed to load assignments',
            framework_assigned: 'Framework assigned',
            framework_assigned_announce: 'Framework assigned to entity',
            assign_framework_error: 'Failed to assign framework',
            assignment_removed: 'Assignment removed',
            enter_entity_id_placeholder: 'Enter entity ID to view assignments',
            entity_type_organization: 'Organization',
            entity_type_department: 'Department',
        },
        onboarding_maturity: {
            title: 'Onboarding Maturity',
            quick_starts: 'Quick Starts',
            progress: 'Onboarding Progress',
            personas: 'Persona Paths',
            persona_onboarding: 'Persona Onboarding',
            quick_start_scenarios: 'Quick-Start Scenarios',
            start: 'Start',
            restart: 'Restart',
            estimated_minutes: 'Estimated Minutes',
            steps: 'Steps',
            scenario: 'Scenario',
            no_scenarios: 'No quick-start scenarios available.',
            persona_id: 'Persona ID',
            persona_label: 'Persona',
            role_description: 'Role Description',
            primary_modules: 'Primary Modules',
            recommended_tours: 'Recommended Tours',
            sample_data_modules: 'Sample Data Modules',
            quick_start_scenario: 'Quick-Start Scenario',
            no_personas: 'No persona paths available.',
            framework_setup: 'Regulatory Frameworks',
            framework_code: 'Framework Code',
            framework_name: 'Framework Name',
            related_modules: 'Related Modules',
            persona_relevance: 'Relevant Personas',
            no_frameworks: 'No framework setup options available.',
            tier_config: 'Tier Module Configuration',
            tier: 'Tier',
            available_modules: 'Available Modules',
            module_count: 'Module Count',
            overall_completion: 'Overall Completion',
            wizard_completed: 'Wizard Completed',
            tours_completed: 'Tours Completed',
            quick_starts_completed: 'Quick Starts Completed',
            selected_frameworks: 'Selected Frameworks',
            no_progress: 'No onboarding progress data available.',
            detected_persona: 'Detected Persona',
            current_tier: 'Current Tier',
            completion_pct: 'Completion',
            progress_page: {
                loaded: 'Progress loaded',
                no_progress: 'No onboarding progress data available.',
                overall: 'Overall Progress',
                detected_persona: 'Detected Persona',
                tier: 'Tier',
                modules: 'modules',
                wizard: 'Wizard',
                completed: 'Completed',
                pending: 'Pending',
                tours: 'Tours',
                quick_starts: 'Quick Starts',
                frameworks: 'Selected Frameworks',
                no_frameworks: 'No frameworks selected.',
                go_to_wizard: 'Complete Setup Wizard',
                framework_selection: 'Framework Selection',
                framework_toggle: 'Enable or disable frameworks for your compliance program.',
                framework_updated: 'Framework selection updated.',
                view_tours: 'View Tours',
                view_quick_starts: 'View Quick Starts',
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard or save a persona in Persona Paths. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Select a persona to see recommended modules, tours, and quick-start scenarios.',
                no_personas: 'No persona paths available.',
                modules: 'Modules',
                tours: 'Tours',
                quick_start: 'Quick Start',
                details: 'Details',
                primary_modules: 'Primary Modules',
                recommended_tours: 'Recommended Tours',
                framework_options: 'Regulatory Frameworks',
                no_frameworks: 'No framework setup options available.',
                no_frameworks_explain: 'No regulatory frameworks are installed for your organisation yet, so there is nothing to set up here.',
                no_frameworks_action: 'Install a market pack to add the frameworks that apply to you',
                start_tour: 'Start',
                no_tours: 'No tours available',
                help_center: 'Help Center',
                no_articles: 'No articles found',
                primary: 'Primary',
                framework_code: 'Framework Code',
                framework_label: 'Label',
                region: 'Region',
                category: 'Category',
                enabled: 'Enabled',
                disabled: 'Disabled',
                tier_modules: 'Tier Module Configuration',
                tiers_loaded: 'Tier configurations loaded',
                no_tier_config: 'No tier configuration available.',
                view_tours: 'View Module Tours',
                view_quick_start: 'Start Quick Start',
                toggle_framework: 'Toggle framework',
                framework_updated: 'Framework selection updated.',
                tier_desc_light: 'Core privacy essentials — PDPL compliance basics',
                tier_desc_standard: 'Full compliance suite — all modules with workflows',
                tier_desc_enterprise: 'Advanced governance & automation — multi-entity, group reporting',
                persona_highlight: 'Your modules',
                // fix-sprint-20260411-0417 Findings 4 + 5 — replace hardcoded
                // English aria-label literals across PersonaOnboardingPage and
                // OnboardingProgressPage so screen-reader users in non-EN
                // locales hear the right text. All values use the {tpl}{vars}
                // pattern resolved by the page's `fmt` helper.
                aria_go_to_module: '{name} — go to module',
                aria_start_tour: 'Start {title}',
                aria_quick_start: 'Quick start: {title}',
                aria_help_article: 'Help article: {title}',
                aria_view_framework: '{label}{primary} — view framework details',
                aria_primary_for_persona: '{name} (primary for this persona)',
                primary_for_persona_suffix: ' (primary for this persona)',
                aria_view_in_persona_paths: '{name} — view in Persona Paths',
                progress_of_total: '{completed} of {total} completed',
            },
            // Sprint 63.28.0 — Persona Paths revised: the picker, the role detail and the saved persona.
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select.
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Practice common workflows with guided quick-start scenarios.',
                no_scenarios: 'No quick-start scenarios available.',
                started: 'Quick start started',
                status_completed: 'Completed',
                status_in_progress: 'In Progress',
                status_not_started: 'Not Started',
                minutes: 'min',
                steps: 'steps',
                step: 'Step',
                restart: 'Restart',
                start: 'Start',
                go_to_module: 'Go to Module',
                next_step: 'Next Step',
                complete: 'Complete',
                reset: 'Reset',
                step_of: 'of',
                action_navigate: 'Navigate',
                action_form_fill: 'Form Fill',
                action_review: 'Review',
                action_confirm: 'Confirm',
                step_changed: 'Step {current} of {total}: {title}',
                completed_message: 'Quick start completed successfully!',
                reset_message: 'Quick start has been reset.',
            },
            // Persona Path i18n — labels, descriptions, and quick start titles
            persona_p_vn_01_label: 'Enterprise DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Department Data Owner',
            persona_p_vn_02_description: 'Department lead responsible for sectional compliance reporting',
            persona_p_vn_03_label: 'E-Commerce Compliance Manager',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Data Governance Lead',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cybersecurity & Legal Operations Lead',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Governance Lead',
            persona_p_vn_07_description: 'Lead overseeing AI system governance, risk assessments, and classification',
            persona_p_vn_08_label: 'Cross-Border Transfer & Vendor Governance Lead',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Breach Response Lead',
            persona_p_vn_09_description: 'Lead coordinating breach notification and incident response',
            persona_p_vn_10_label: 'Internal Audit & Regulatory Inspection Lead',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'HR & Employee Data Steward',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Multinational Subsidiary Compliance Lead',
            persona_p_vn_12_description: 'DPO at foreign company Vietnam subsidiary needing local compliance layer',
            persona_p_vn_13_label: 'Education Compliance Lead',
            persona_p_vn_13_description: 'Compliance lead at foreign school managing student and parent data protection',
            qs_hr_consent_title: 'Register employee consent records',
            qs_hr_consent_description: 'Walk through registering employee consent and generating a coverage report.',
            qs_subsidiary_dpia_title: 'Create a subsidiary DPIA assessment',
            qs_subsidiary_dpia_description: 'Walk through creating a DPIA for a subsidiary processing activity.',
            qs_student_consent_title: 'Set up student consent collection',
            qs_student_consent_description: 'Walk through creating parent/guardian consent policies for student data.',
            persona_p_vn_14_label: 'External Legal Advisor',
            persona_p_vn_14_description: 'Senior law firm partner advising multiple client organizations on PDPL compliance',
            persona_p_vn_15_label: 'Law Firm Operations Manager',
            persona_p_vn_15_description: 'Client engagement coordinator managing deliverable tracking and deadline coordination',
            persona_p_vn_16_label: 'Business Owner / CEO',
            persona_p_vn_16_description: 'CEO or GM who is legally the data controller under PDPL, accountable for compliance investment',
            // Quick Start scenario titles (keyed by scenario_id with - replaced by _)
            qs_dpia_filing_title: 'Complete your first DPIA filing',
            qs_dept_compliance_title: 'Submit your first department compliance section',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Set up telecom subscriber data handling',
            qs_data_flow_title: 'Map your first data flow',
            qs_incident_response_title: 'Configure incident response workflow',
            qs_ai_risk_title: 'Run an AI system risk assessment',
            qs_cross_border_title: 'Assess a cross-border data transfer',
            qs_breach_notification_title: 'Simulate a breach notification workflow',
            qs_audit_trail_title: 'Review a compliance audit trail',
            qs_legal_advisor_portfolio_title: 'Review multi-client compliance portfolio',
            qs_legal_advisor_portfolio_description: 'Review compliance status across multiple client organizations.',
            qs_law_firm_ops_title: 'Track client deliverable status',
            qs_law_firm_ops_description: 'Monitor advisory deliverable progress and client engagement.',
            qs_ceo_dashboard_title: 'View executive compliance dashboard',
            qs_ceo_dashboard_description: 'Review high-level compliance status and investment metrics.',
        },
        // Sprint 43.2.0 batch 3 (E1) — Response Template admin page
        rights_requests: {
            response_templates: {
                title: 'Response Templates',
                subtitle: 'Manage the DSR response letter templates that ResponseGenerator uses to draft replies to data subjects.',
                create_btn: 'New Template',
                edit_btn: 'Edit Template',
                filter_right_type: 'Filter by Right Type',
                filter_locale: 'Filter by Locale',
                filter_status: 'Filter by Status',
                all_right_types: 'All Right Types',
                any_right_type: 'Any (applies to all types)',
                all_locales: 'All Locales',
                all_statuses: 'All',
                status_active: 'Active',
                status_inactive: 'Inactive',
                empty_state: 'No templates match the current filters.',
                columns: {
                    template_id: 'Template ID',
                    outcome: 'Outcome',
                    locale: 'Locale',
                    right_type: 'Right Type',
                    is_active: 'Status',
                    last_updated: 'Last Updated',
                    actions: 'Actions',
                },
                actions: {
                    edit: 'Edit',
                    clone: 'Clone',
                    deactivate: 'Deactivate',
                    reactivate: 'Reactivate',
                    preview: 'Preview',
                },
                form: {
                    template_id: 'Template ID',
                    template_id_help: 'Lowercase identifier, e.g. dsr_access_fulfilled_vi',
                    outcome: 'Outcome',
                    locale: 'Locale',
                    right_type: 'Right Type',
                    subject: 'Subject',
                    body: 'Body Template',
                    body_help: 'Use placeholders like {case_reference}, {data_subject_name}, {fulfilled_at}. Minimum 15 lines for multi-section letters.',
                    is_active: 'Active',
                    official_form_id: 'Official Form ID (optional)',
                },
                outcomes: {
                    fulfilled: 'Fulfilled',
                    partially_fulfilled: 'Partially Fulfilled',
                    denied: 'Denied',
                },
                clone_modal_title: 'Clone Template',
                clone_target_locale: 'Target Locale',
                clone_help: 'A new template will be created in the target locale using this template as a starting point.',
                preview_modal_title: 'Template Preview',
                preview_note: 'Preview uses demo case DSR-DEMO-0020 placeholders. Real delivery substitutes live case data.',
                preview_unavailable: 'Preview requires an existing case. Attach this template to a case and use the Draft Response action on the case detail page.',
                deactivate_confirm: 'Deactivate this template? It will continue to apply to existing cases that reference it.',
                toasts: {
                    created: 'Template created',
                    updated: 'Template updated',
                    deactivated: 'Template deactivated',
                    reactivated: 'Template reactivated',
                    cloned: 'Template cloned',
                    load_error: 'Failed to load templates',
                    save_error: 'Failed to save template',
                    clone_error: 'Failed to clone template',
                },
            },
        },
        // Sprint 43.2.0 batch 3 (E2 + E4) — Org admin pages
        organizations: {
            departments: {
                title: 'Departments',
                subtitle: 'Manage the departments that route DSR cases, DPIA tasks, and compliance workflows.',
                create_btn: 'New Department',
                edit_btn: 'Edit Department',
                columns: {
                    name: 'Name',
                    description: 'Description',
                    active_cases: 'Active Cases',
                    members: 'Members',
                    head_user: 'Head',
                    status: 'Status',
                    actions: 'Actions',
                },
                status_active: 'Active',
                status_inactive: 'Inactive',
                form: {
                    name: 'Name',
                    description: 'Description',
                    head_user: 'Department Head',
                    head_user_none: '— None —',
                },
                actions: {
                    edit: 'Edit',
                    deactivate: 'Deactivate',
                    reactivate: 'Reactivate',
                    delete: 'Delete',
                    manage_members: 'Manage members',
                },
                delete_modal_title: 'Delete Department',
                delete_warning: 'This department still has active cases. Choose a department to reassign them to before deletion.',
                delete_no_cases: 'This department has no active cases and can be deleted safely.',
                reassign_prompt: 'Reassign active cases to:',
                reassign_none_available: 'No other active departments available. Create another department first.',
                member_section_title: 'Members',
                add_member: 'Add Member',
                remove_member: 'Remove',
                member_role: 'Role',
                no_members: 'No members mapped to this department.',
                toasts: {
                    created: 'Department created',
                    updated: 'Department updated',
                    deactivated: 'Department deactivated',
                    reactivated: 'Department reactivated',
                    deleted: 'Department deleted',
                    reassigned_count: '{count} case(s) reassigned',
                    member_added: 'Member added',
                    member_removed: 'Member removed',
                    load_error: 'Failed to load departments',
                    save_error: 'Failed to save department',
                    delete_error: 'Failed to delete department',
                },
            },
            retention_policies: {
                title: 'Retention Policies',
                subtitle: 'Configure per-target-type retention windows. Identity-verification documents use this to stamp retention_expires_at on future uploads.',
                create_btn: 'New Policy',
                edit_btn: 'Edit Policy',
                columns: {
                    target_type: 'Target Type',
                    retention_days: 'Retention (days)',
                    owner: 'Policy Owner',
                    effective_from: 'Effective From',
                    last_reviewed: 'Last Reviewed',
                    status: 'Status',
                    actions: 'Actions',
                },
                status_active: 'Active',
                status_inactive: 'Inactive',
                form: {
                    target_type: 'Target Type',
                    target_type_help: 'What data category the retention window applies to. Currently only DSR identity-verification documents are supported.',
                    retention_days: 'Retention window (days)',
                    retention_days_help: 'Between 1 and 3650 days (10 years max).',
                    owner: 'Policy Owner',
                    owner_help: 'Usually the DPO. Accountable for the retention decision under DG-2.',
                    effective_from: 'Effective From',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR identity-verification documents',
                },
                deactivate_notice: 'Deactivating does not affect already-stamped attachments — only future uploads will no longer receive a retention window.',
                deactivate_confirm: 'Deactivate this retention policy?',
                toasts: {
                    created: 'Retention policy created',
                    updated: 'Retention policy updated',
                    deactivated: 'Retention policy deactivated',
                    load_error: 'Failed to load retention policies',
                    save_error: 'Failed to save retention policy',
                },
            },
        },
    },
    // ---------- Data Mapping module (Sprint 1.2.0) ----------
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — named type in `en-extras.ts` (TS7056).
        deployment_privacy: enDeploymentPrivacy,
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Systems',
            categories: 'Data Categories',
            // Sprint 45.4.0 — Data Registry nav entry.
            registry: 'Data Registry',
            purposes: 'Processing Purposes',
            retention: 'Retention',
            flows: 'Data Flows',
            activities: 'Processing Activities',
            evidence: 'Evidence',
            exports: 'Exports',
            attestation: 'Attestation Cycles',
            governance: 'Governance',
            review: 'Review Tasks',
            inventory: 'Inventory',
            exceptions: 'Exceptions',
            history: 'History',
            ropa_completeness: 'ROPA Completeness',
        },
        // Sprint 48.1.2 (finish) — Forseti autofix inline action on the
        // RoPA completeness page.
        ropa_completeness_autofix: {
            ask_button: 'Ask Forseti',
            drafting: 'Drafting…',
            accept: 'Accept',
            reject: 'Reject',
            accepted: 'Accepted — apply via the activity editor.',
            rejected: 'Rejected.',
            needs_human_input: 'Forseti needs more activity context (e.g. a documented purpose) before drafting this field.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        // Sprint 46.0.0 — ROPA Completeness Report (per-field hygiene)
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA Completeness Report',
            subtitle: 'Per-field hygiene audit of your Records of Processing Activities, scoped by regulatory framework.',
            framework_label: 'Framework',
            kpi_total: 'Activities scored',
            kpi_critical: 'Critical',
            kpi_high: 'High',
            kpi_conditional: 'Conditional',
            field_chart_heading: 'Missing fields by frequency',
            drilldown_heading: 'Activities with gaps',
            col_activity: 'Activity',
            col_field: 'Field',
            col_severity: 'Severity',
            col_condition: 'Condition',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Critical',
            severity_high: 'High',
            severity_conditional: 'Conditional',
            severity_informational: 'Informational',
            include_informational: 'Include informational',
            export_csv: 'Download CSV',
            exporting: 'Exporting...',
            loading: 'Loading…',
            empty_state: 'All active activities have complete ROPA records for this framework.',
            error_fetch: 'Failed to load the completeness report.',
            error_unsupported_framework: 'This framework is not supported.',
            error_framework_not_installed: 'This framework is not installed for your organization.',
            no_misses_for_field: 'No activities missing this field.',
            framework_PDPL: 'PDPL (Law 91/2025 + Decree 356)',
            framework_GDPR: 'GDPR Article 30',
            framework_DECREE_165: 'Decree 165 (Vietnam Data Law)',
            framework_ISO_27701: 'ISO 27701 (informational)',
        },
        // Sprint 45.4.0 — Data Registry (Light-tier sub-module)
        registry: {
            /**
             * Sprint 64.72.0 — the registry export's own column headers.
             *
             * Dedicated keys rather than reuse of the screen keys below, and the
             * reason is measured rather than stylistic. Gate E12.5 pass 1 on the
             * reverted first attempt found `modal_editor.label_volume_band` reading
             * `Lautstärkeband` in German (audio loudness) and `col.data_subjects`
             * reading `Supòsits de dades` in Catalan ("data assumptions") — both
             * survivable on a filter chip, neither survivable as a column header on
             * a document filed with a regulator. It also found the Art. 30 wording
             * diluted by reuse (`Third-country transfers` → `Cross-border`), and the
             * English CSV contract silently changed for customers keying scripts to
             * it.
             *
             * So: every English value here is byte-identical to the literal it
             * replaced in `registry-export-renderer.ts`, and the export no longer
             * depends on any key a screen sprint may reword.
             */
            export_col: {
                registry_item_id: 'Registry item ID',
                name: 'Name',
                description: 'Description',
                field_identifiers: 'Field identifiers',
                category_id: 'Category ID',
                category_name: 'Category name',
                data_type: 'Data type',
                effective_sensitivity: 'Effective sensitivity',
                sensitivity_override: 'Sensitivity override',
                data_subjects: 'Data subjects',
                volume_band: 'Volume band',
                source_system_ids: 'Source system IDs',
                processing_activity_ids: 'Processing activity IDs',
                retention_policy_id: 'Retention policy ID',
                effective_retention_policy_id: 'Effective retention policy ID',
                purposes: 'Purposes',
                collection_method: 'Collection method',
                legal_basis: 'Legal basis',
                data_tier: 'Data tier',
                data_tier_source: 'Data tier source',
                storage_locations: 'Storage locations',
                cross_border: 'Cross-border',
                status: 'Status',
                source_form_submission_id: 'Source form submission ID',
                source_form_template_id: 'Source form template ID',
                last_reviewed_at: 'Last reviewed at',
                next_review_at: 'Next review at',
                created_at: 'Created at',
                updated_at: 'Updated at',
                // PDPL register layout.
                data_element: 'Data element',
                data_category: 'Data category',
                systems: 'Systems',
                retention_policy: 'Retention policy',
                // GDPR Art. 30(1) controller record. The longer wording is Art. 30's
                // own and is deliberate — 30(1)(c) and 30(1)(e) are not satisfied by
                // "Data subjects" and "Retention".
                name_of_processing: 'Name of processing',
                categories_of_personal_data: 'Categories of personal data',
                categories_of_data_subjects: 'Categories of data subjects',
                recipients: 'Recipients',
                third_country_transfers: 'Third-country transfers',
                retention_period: 'Retention period',
                // PDF/DOCX document chrome.
                doc_title: 'Data Registry',
                doc_generated: 'Export generated {timestamp}',
                doc_item_count: '{count} registry item(s)',
                /**
                 * Gate E12.5 (Sprint 64.72.0), F4 — the designation of the *default*
                 * layout, which is the one most operators export.
                 *
                 * `PDPL 2025 Register` and `GDPR Art 30 Minimal` stay English literals
                 * in `registry-export-renderer.ts` because they name a regulator's own
                 * artefact and translating them makes the file harder to identify.
                 * "Full" names nothing of the sort — it is this product's word for its
                 * own widest column set — so a Vietnamese operator was reading
                 * `Đăng ký dữ liệu — Full`.
                 */
                doc_template_full: 'Full',
            },
            /**
             * Sprint 62.20.0, gate E12.5 pass 2 (P2-1) — the platform's own frame
             * around a tier that came from an instrument which does not bind.
             *
             * The register's tier column renders a level code, and a level code is
             * not self-describing: the same word can be a rung of a proposed ladder
             * and the binding level of an in-force list. `RegistryPage` was given a
             * draft notice in this sprint's pass 1; the regulator-facing export was
             * left rendering the two identically, which is the worse half — a
             * confusing screen is embarrassing, a filed document that presents a
             * proposal as an obligation is a false statement.
             *
             * Two keys only, and neither of them describes the instrument. The
             * sentence that says what is being revised is the PACK's own
             * `draft_warning`, authored bilingually beside the framework and
             * rendered verbatim; the platform authors no draft copy and names no
             * instrument, authority or level (R-46). EN and VI are byte-identical to
             * the reviewed strings the on-screen `DraftInstrumentNotice` already
             * uses (`components/regulatory/draft-mode-strings.ts`), so the document
             * and the page cannot drift apart in wording.
             *
             * fix-sprint-20260904-2120 F5 — "cannot" is now true. When this was
             * written the four strings were merely EQUAL: two files in different
             * parts of the tree, no file importing both, and no test naming
             * `export_draft` anywhere. The pin is
             * `src/frontend/src/components/regulatory/__tests__/draft-mode-strings-export-parity.test.ts`;
             * change the reviewed wording on both sides in one commit, or it fails.
             *
             * The other 14 locales receive the EN text through
             * `REGISTRY_EXPORT_DRAFT_FALLBACK_PATH` in `i18n/index.ts` (R-57), the
             * same named-contract shape as `localization.help_content_packs`.
             */
            export_draft: {
                /** Rendered as text, never as a colour or a symbol (WCAG 1.4.1). */
                marker: 'Draft — not an obligation',
                scheme_label: 'Classification scheme',
            },
            /**
             * Sprint 64.72.0 — the enumerated vocabularies the export cells read.
             *
             * The first attempt localised the headers and left these five columns
             * emitting their raw stored codes, so a Vietnamese register read
             * `Căn cứ pháp lý | consent` and `Chủ thể dữ liệu | customer;
             * employee; website_visitor`. That is what the revert was about, and the
             * residue assertion that should have caught it compared header rows
             * only.
             *
             * Each vocabulary is separate on purpose. A data type, a collection
             * method and a subject category are three different vocabularies owned
             * by three different things; one shared `humanise()` over all of them is
             * how a subject category ends up rendered as a collection method.
             *
             * `legal_basis` is deliberately absent — `data_mapping.purposes.basis_*`
             * already carries that vocabulary in all 16 locales, on the same module,
             * for the same six-value union (R-43).
             */
            export_vocab: {
                data_type: {
                    text: 'Text',
                    number: 'Number',
                    date: 'Date',
                    datetime: 'Date and time',
                    boolean: 'Yes/no',
                    binary: 'Binary',
                    structured: 'Structured',
                    file: 'File',
                    identifier: 'Identifier',
                    geolocation: 'Geolocation',
                },
                collection_method: {
                    direct_from_subject: 'Directly from the data subject',
                    third_party: 'From a third party',
                    derived: 'Derived from other data',
                    observed: 'Observed',
                    inferred: 'Inferred',
                    public_source: 'Public source',
                },
                data_subject_type: {
                    customer: 'Customer',
                    employee: 'Employee',
                    contractor: 'Contractor',
                    prospect: 'Prospect',
                    minor: 'Minor',
                    patient: 'Patient',
                    student: 'Student',
                    beneficiary: 'Beneficiary',
                    vendor_contact: 'Vendor contact',
                    website_visitor: 'Website visitor',
                    other: 'Other',
                },
                volume_band: {
                    lt_1k: 'Fewer than 1,000 records',
                    '1k_to_10k': '1,000 to 10,000 records',
                    '10k_to_100k': '10,000 to 100,000 records',
                    '100k_to_1m': '100,000 to 1 million records',
                    gt_1m: 'More than 1 million records',
                    unknown: 'Not known',
                },
                /**
                 * How the tier now in force was arrived at.
                 *
                 * `assessed_and_approved` reads "Assessed and approved", not
                 * "Screened and approved": pass 1 recorded that the first attempt's
                 * English said *screened* while twelve locales said *assessed* and the
                 * union member itself is spelled `assessed_and_approved`. The code is
                 * the tie-breaker.
                 */
                tier_source: {
                    assessed_and_approved: 'Assessed and approved',
                    manual_legacy: 'Adopted from a legacy value',
                    legacy_platform_field: 'Inherited platform value',
                    scheme_scoped_only: 'Assessed under scheme-specific levels — no single code covers several statutory ladders',
                },
            },
            page: {
                title: 'Data Registry',
                subtitle: 'Personal data elements your organization holds, organized under Data Categories.',
                empty_state: 'No registry items yet. Add an item manually or import from a regulatory register.',
                empty_filter_state: 'No items match your filters.',
            },
            filter: {
                category: 'Category',
                sensitivity: 'Sensitivity',
                data_subject: 'Data subject',
                status: 'Status',
                data_tier: 'Data tier',
                cross_border: 'Cross-border',
                has_owner: 'Has owner',
                has_form_source: 'Imported from form',
                search_placeholder: 'Search name, description, or field identifier…',
                clear: 'Clear filters',
                option_all: 'All',
            },
            col: {
                name: 'Name',
                category: 'Category',
                sensitivity: 'Sensitivity',
                data_subjects: 'Data subjects',
                systems: 'Systems',
                activities: 'Activities',
                owner: 'Owner',
                retention: 'Retention',
                data_tier: 'Data tier',
                status: 'Status',
                last_reviewed: 'Last reviewed',
                actions: 'Actions',
            },
            action: {
                add_manual: 'Add item',
                add_from_form: 'Add from regulatory register',
                import_history: 'Import past submissions',
                export: 'Export',
                archive: 'Archive',
                restore: 'Restore',
                edit: 'Edit',
                assign_owner: 'Assign owner',
                attach_evidence: 'Attach evidence',
                open_lineage: 'Open lineage',
            },
            modal_editor: {
                section_identity: 'Identity',
                section_classification: 'Classification',
                section_subjects: 'Subjects & Volume',
                section_location: 'Location & Transfer',
                section_systems: 'Systems & Activities',
                section_retention: 'Retention & Legal',
                section_governance: 'Governance',
                section_notes: 'Notes',
                hint_inherited: '(inherited from category)',
                hint_overridden: '(overridden)',
                placeholder_field_identifiers: 'e.g., customers.primary_email, hris.employee_id',
                validation_name_required: 'Name is required',
                validation_category_required: 'Category is required',
                validation_data_subjects_required: 'At least one data subject type is required',
                label_data_type: 'Data type',
                label_field_identifiers: 'Field identifiers (comma-separated)',
                label_data_subject_types: 'Data subject types',
                label_volume_band: 'Volume band',
                label_storage_locations: 'Storage locations (comma-separated)',
                placeholder_storage_locations: 'ISO-3166 codes or free text (e.g., VN, Hetzner EU)',
                label_cross_border: 'Cross-border transfer',
                label_source_systems: 'Source systems',
                label_processing_activities: 'Processing activities',
                label_collection_method: 'Collection method',
                label_purposes: 'Purposes',
                hint_multi_select: 'Hold Cmd/Ctrl to select multiple.',
                option_inherit_category: '— (inherit from category)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Import preview',
                label_to_create: 'To create',
                label_to_update: 'To update',
                label_skipped_archived: 'Skipped (archived)',
                label_conflicts: 'Conflicts',
                commit: 'Import',
                cancel: 'Cancel',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Category',
                section_systems: 'Source systems',
                section_activities: 'Processing activities',
                section_flows: 'Data flows',
                section_retention: 'Effective retention',
                section_governance: 'Governance',
                section_evidence: 'Evidence',
                section_audit: 'Recent audit trail',
                section_source_form: 'Source form submission',
                badge_inherited: 'Inherited',
                badge_overridden: 'Overridden',
            },
            toast: {
                created: 'Registry item created.',
                updated: 'Registry item updated.',
                archived: 'Registry item archived.',
                imported: '{count} items imported.',
                conflict_warning: '{count} field(s) kept your edits — see conflict list.',
            },
            // Sprint 62.35.0 (AC6) — provenance, notice and quality evidence.
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                // Gate E12.5 pass 1 (P2-1) — when and by whom the check was MADE (the
                // operator's entries), apart from when and by whom it was RECORDED
                // (the server's stamp).
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                // Gate E12.5 pass 2 (P2-1) — the server's two check refusals, stated by
                // their code. `{date}` is today in the organisation's calendar, as the
                // server judged it.
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                // P1-6 — a recorded answer: the check was made and could not settle it.
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Systems Inventory',
            new: 'New System',
            edit: 'Edit System',
            name: 'Name',
            owner: 'Owner',
            criticality: 'Criticality',
            dpa_status: 'DPA Status',
            location: 'Location',
            system_type: 'System Type',
            metadata: 'Metadata (JSON)',
            department: 'Department',
            hosting_location: 'Hosting Location',
            hosting_provider: 'Hosting Provider',
            data_residency_country: 'Data Residency Country',
            hosting: 'Hosting',
            no_systems: 'No systems found',
            confirm_archive: 'Archive system "{name}"?',
            count: 'systems',
            count_one: 'system',
            type_internal: 'Internal',
            type_external: 'External',
            type_cloud_service: 'Cloud Service',
            type_on_premise: 'On Premise',
            type_hybrid: 'Hybrid',
            status_active: 'Active',
            status_decommissioning: 'Decommissioning',
            status_decommissioned: 'Decommissioned',
            status_planned: 'Planned',
            dpa_not_required: 'Not Required',
            dpa_required_missing: 'Required Missing',
            dpa_in_progress: 'In Progress',
            dpa_signed: 'Signed',
            all_criticalities: 'All Criticalities',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Data Categories',
            new: 'New Category',
            edit: 'Edit Category',
            name: 'Name',
            sensitivity: 'Sensitivity',
            tags: 'Tags',
            used_in: 'Used In',
            examples: 'Examples',
            examples_hint: 'Examples (comma-separated)',
            default: 'Default',
            all_sensitivities: 'All Sensitivities',
            all_statuses: 'All Statuses',
            no_categories: 'No categories found',
            confirm_delete: 'Delete category "{name}"?',
            count: 'categories',
            count_one: 'category',
            status_draft: 'Draft',
            status_proposed: 'Proposed',
            status_under_review: 'Under Review',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            status_updated: 'Category status updated',
            propose: 'Propose',
            approve: 'Approve',
            deprecate: 'Deprecate',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Processing Purposes',
            new: 'New Purpose',
            edit: 'Edit Purpose',
            name: 'Name',
            legal_basis: 'Legal Basis',
            retention_period: 'Retention Period (days)',
            justification: 'Legitimate Interest Justification',
            legal_basis_detail: 'Legal Basis Detail',
            all_legal_bases: 'All Legal Bases',
            no_purposes: 'No purposes found',
            confirm_delete: 'Delete "{name}"?',
            default: 'Default',
            count: '{count} purposes',
            count_one: '{count} purpose',
        },
        retention: {
            title: 'Retention Policies',
            new: 'New Policy',
            edit: 'Edit Policy',
            name: 'Name',
            retention_days: 'Retention Days',
            disposal_method: 'Disposal Method',
            data_categories: 'Data Categories',
            activities: 'Activities',
            review_frequency: 'Review Frequency',
            review_frequency_days: 'Review Frequency (days)',
            no_policies: 'No retention policies found',
            confirm_delete: 'Delete "{name}"?',
            count: '{count} policies',
            count_one: '{count} policy',
            days: 'days',
            disposal_deletion: 'Deletion',
            disposal_anonymization: 'Anonymization',
            disposal_archival: 'Archival',
            review_overdue: 'Review Overdue',
            review_status: 'Review Status',
            review_ok: 'On Track',
            department: 'Department',
            all_departments: 'All Departments',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            // fix-sprint-20260415-0434 F14 — List/Map view tablist keys.
            view_tabs_aria: 'Flows view',
            view_list: 'List',
            view_map: 'Map',
            title: 'Data Flows',
            new: 'New Flow',
            // fix-sprint-20260717-2118-followup F16 — real section identities that
            // replace field-label pairs used as `title`+`description` on the
            // flow create modal.
            section_basic_info: 'Basic info',
            section_narrative: 'Narrative',
            edit: 'Edit Flow',
            name: 'Name',
            from_system: 'From System',
            to_system: 'To System',
            flow_type: 'Flow Type',
            cross_border: 'Cross-Border',
            risk_level: 'Risk Level',
            mechanism: 'Mechanism',
            transfer_mechanism: 'Transfer Mechanism',
            frequency: 'Frequency',
            source_country: 'Source Country',
            destination_country: 'Destination Country',
            transfer_safeguard: 'Transfer Safeguard',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'All Risk Levels',
            no_flows: 'No data flows found',
            confirm_archive: 'Archive "{name}"?',
            count: '{count} flows',
            count_one: '{count} flow',
            status_active: 'Active',
            status_inactive: 'Inactive',
            status_under_review: 'Under Review',
            mechanism_api: 'API',
            mechanism_file_transfer: 'File Transfer',
            mechanism_database_replication: 'Database Replication',
            mechanism_manual: 'Manual',
            mechanism_email: 'Email',
            mechanism_other: 'Other',
            frequency_real_time: 'Real Time',
            frequency_hourly: 'Hourly',
            frequency_daily: 'Daily',
            frequency_weekly: 'Weekly',
            frequency_monthly: 'Monthly',
            frequency_on_demand: 'On Demand',
            frequency_one_time: 'One Time',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdiction',
        },
        activities: {
            title: 'Processing Activities',
            new: 'New Activity',
            edit: 'Edit Activity',
            activity_title: 'Title',
            category: 'Category',
            legal_basis: 'Legal Basis',
            dpa_required: 'DPIA Required',
            ropa_generated: 'RoPA Generated',
            version: 'Version',
            generate_ropa: 'Generate RoPA',
            controller: 'Controller',
            controller_name: 'Controller Name',
            controller_contact: 'Controller Contact',
            legal_basis_detail: 'Legal Basis Detail',
            organization_info: 'Organization Information',
            org_address: 'Organization Address',
            tax_code: 'Tax Code',
            contact_person: 'Contact Person',
            contact_email: 'Contact Email',
            contact_phone: 'Contact Phone',
            select_entity: 'Select…',
            no_entities: 'No items found',
            dpo_name: 'DPO Name',
            dpo_contact: 'DPO Contact',
            processor_name: 'Processor Name',
            processor_contact: 'Processor Contact',
            data_subjects: 'Data Subjects',
            data_subjects_hint: 'Data Subjects (comma-separated)',
            recipients: 'Recipients',
            recipients_hint: 'Recipients (comma-separated)',
            security_measures: 'Security Measures',
            dpia_reference: 'DPIA Reference',
            no_activities: 'No processing activities found',
            confirm_archive: 'Archive "{name}"?',
            count: '{count} activities',
            count_one: '{count} activity',
            ropa_success: 'RoPA record generated. Go to Exports to download it.',
            ropa_error: 'Failed to generate RoPA',
            required_label: 'Required',
            status_draft: 'Draft',
            status_active: 'Active',
            status_under_review: 'Under Review',
            status_archived: 'Archived',
            // Sprint 46.1.0 — Decree 165 data-tier fields.
            decree_165_heading: 'Decree 165 data tier (Vietnam Data Law)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Data tier',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Data tier',
            data_tier_regular: 'Regular',
            data_tier_important: 'Important',
            data_tier_core: 'Core',
            transfer_mechanism_label: 'Cross-border transfer mechanism',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Decree 165 security-assessment reference (where applicable)',
            security_assessment_reference_placeholder: 'e.g. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Evidence Vault',
            new: 'New Evidence',
            edit: 'Edit Evidence',
            name: 'Name',
            evidence_type: 'Evidence Type',
            linked_entities: 'Linked Entities',
            uploaded: 'Uploaded',
            expiry: 'Expiry',
            file_url: 'File URL',
            // Sprint 41.3.0 F1 — source-mode toggle (upload file vs link URL)
            source_mode_label: 'Evidence source',
            source_mode_file: 'Upload file',
            source_mode_url: 'Link external URL',
            attachment_label: 'Attachment',
            link_to: 'Link Evidence To',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            no_evidence: 'No evidence items found',
            manage_links: 'Manage links',
            unlink: 'Unlink',
            confirm_delete: 'Delete evidence "{name}"?',
            count: '{count} items',
            count_one: '{count} item',
            type_document: 'Document',
            type_screenshot: 'Screenshot',
            type_policy: 'Policy',
            type_contract: 'Contract',
            type_assessment: 'Assessment',
            type_correspondence: 'Correspondence',
            type_other: 'Other',
            linked_type_system: 'System',
            linked_type_data_flow: 'Data Flow',
            linked_type_processing_activity: 'Processing Activity',
            linked_type_data_category: 'Data Category',
            linked_type_purpose: 'Purpose',
            linked_type_retention_policy: 'Retention Policy',
            link_entity: 'Link to Entity',
            link_success: 'Entity linked successfully',
            confirm_unlink: 'Are you sure you want to unlink this entity?',
            unlink_success: 'Entity unlinked successfully',
            no_linked_entities: 'No linked entities',
            select_entity: 'Select…',
            no_entities: 'No items',
        },
        exports: {
            title: 'Data Exports',
            recent: 'Recent Exports',
            export_type: 'Export Type',
            format: 'Format',
            requested_by: 'Requested By',
            requested_at: 'Requested At',
            download: 'Download',
            csv_export: 'CSV Export',
            csv_description: 'Download data in CSV format for spreadsheet analysis.',
            pdf_reports: 'PDF Reports',
            pdf_description: 'Generate formatted reports for compliance documentation.',
            csv_systems: 'Systems Inventory',
            csv_categories: 'Data Categories',
            csv_flows: 'Data Flows',
            csv_activities: 'Processing Activities',
            csv_cross_border: 'Cross-Border Flows',
            csv_retention_policies: 'Retention Policies',
            csv_evidence: 'Evidence Items',
            csv_governance_assignments: 'Governance Assignments',
            pdf_ropa: 'RoPA Register',
            pdf_systems: 'Systems Report',
            pdf_cross_border: 'Cross-Border Report',
            pdf_flow_map: 'Flow Map Data',
            export_failed: 'Export failed',
            pdf_generated: 'PDF generated successfully.',
            ropa: 'RoPA Register',
            retention_policies: 'Retention Policies',
            governance_assignments: 'Governance Assignments',
            col_entity: 'Export Entity',
            col_description: 'Description',
            desc_ropa: 'Full ROPA register of all processing activities with legal basis, DPIA status, and controls.',
            desc_systems: 'Systems inventory with owner, data category tags, and compliance status.',
            desc_activities: 'Individual processing activities with controller, legal basis, and DPIA classification.',
            desc_categories: 'Data categories and sensitivity tiers used across classification and audits.',
            desc_purposes: 'Processing purposes with the legal basis recorded for each.',
            desc_flows: 'Data flows between systems, including internal and cross-border routes.',
            desc_cross_border: 'Cross-border flows only, with transfer mechanism and risk assessment.',
            desc_retention_policies: 'Retention policies, disposal methods, and review cadence.',
            desc_evidence: 'Evidence attached to processing activities: certificates, DPA contracts, DPIA reports.',
            desc_governance_assignments: 'Governance assignments: owners, approvers, and ISO 27001 references.',
            unified_heading: 'Export Entities',
            unified_description: 'Export any data-mapping entity in CSV, XLSX, PDF, or DOCX format. All exports are saved to the File Browser.',
            export_button_label: 'Export',
            csv_download_success: 'CSV downloaded successfully.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Attestation Cycles',
            create: 'New Cycle',
            name: 'Cycle Name',
            year: 'Year',
            deadline: 'Deadline',
            scope: 'Scope',
            status: 'Status',
            tasks: 'Department Tasks',
            assign_tasks: 'Assign Tasks',
            department: 'Department',
            assignee: 'Assignee',
            progress: 'Progress',
            stale_entries: 'Stale Entries',
            detect_stale: 'Detect Stale',
            export_bundle: 'Export Bundle',
            no_cycles: 'No attestation cycles yet.',
            create_success: 'Attestation cycle created.',
            assign_success: 'Tasks assigned successfully.',
            complete_success: 'Task completed.',
            stale_detected: 'Stale entries detected.',
            stale_systems: 'Stale Systems',
            stale_flows: 'Stale Flows',
            no_stale: 'No stale entries detected.',
            stale_name: 'Name',
            stale_announcement: 'Found {0} stale systems and {1} stale flows',
            last_updated: 'Last Updated',
            status_draft: 'Draft',
            status_active: 'Active',
            status_completed: 'Completed',
            task_status_pending: 'Pending',
            task_status_in_progress: 'In Progress',
            task_status_completed: 'Completed',
            scope_systems: 'Systems',
            scope_flows: 'Flows',
            scope_activities: 'Activities',
            scope_categories: 'Categories',
            scope_purposes: 'Purposes',
            scope_retention: 'Retention',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Governance Assignments',
            assign: 'Assign Ownership',
            entity_type: 'Entity Type',
            entity: 'Entity',
            role: 'Role',
            assigned_by: 'Assigned By',
            assigned_at: 'Assigned At',
            remove: 'Remove',
            no_assignments: 'No governance assignments.',
            assign_success: 'Ownership assigned.',
            remove_success: 'Assignment removed.',
            assignee: 'Assignee',
            department: 'Department',
            all_departments: 'All Departments',
            roles: {
                data_owner: 'Data Owner',
                data_steward: 'Data Steward',
                data_custodian: 'Data Custodian',
                informed: 'Informed',
            },
            entity_types: {
                system: 'System',
                flow: 'Flow',
                activity: 'Activity',
                category: 'Category',
                purpose: 'Purpose',
                retention_policy: 'Retention Policy',
            },
        },
        risk_assessment: {
            title: 'Risk Assessment',
            create: 'New Assessment',
            safeguard_type: 'Safeguard Type',
            adequacy_status: 'Adequacy Status',
            risk_factors: 'Risk Factors',
            mitigations: 'Mitigation Measures',
            reviewer: 'Reviewer',
            decision: 'Decision',
            approve: 'Approve',
            reject: 'Reject',
            no_assessments: 'No risk assessments.',
            create_success: 'Risk assessment created.',
            approve_success: 'Assessment approved.',
            reject_success: 'Assessment rejected.',
            one_per_line: 'One per line',
            adequacy_adequate: 'Adequate',
            adequacy_inadequate: 'Inadequate',
            adequacy_under_review: 'Under Review',
            adequacy_not_assessed: 'Not Assessed',
            decision_approved: 'Approved',
            decision_rejected: 'Rejected',
        },
        // Sprint 27.8.1 — Data Discovery Workflow (UC-VN-10)
        review: {
            title: 'Review Tasks',
            description: 'Review tasks are created automatically when a data category is proposed. To create a new task, go to Data Categories and click Propose on any draft category.',
            count: 'review tasks',
            summary: 'Review Summary',
            pending: 'Pending',
            resolved_today: 'Resolved Today',
            escalated: 'Escalated',
            overdue: 'Overdue (>5 days)',
            all_statuses: 'All Statuses',
            status_pending: 'Pending',
            status_resolved: 'Resolved',
            status_escalated: 'Escalated',
            category_name: 'Category',
            system_name: 'System',
            owner: 'Owner',
            resolve: 'Resolve',
            resolve_task: 'Resolve Review Task',
            resolution: 'Resolution',
            resolution_approve: 'Approve',
            resolution_modify: 'Modify & Approve',
            resolution_reject: 'Reject',
            resolution_escalate: 'Escalate',
            notes: 'Resolution Notes',
            notes_required: 'Notes required (minimum 10 characters) for Modify, Reject, or Escalate',
            resolved_success: 'Review task resolved',
            bulk_approve: 'Bulk Approve',
            bulk_reject: 'Bulk Reject',
            bulk_success: '{count} tasks processed',
            no_tasks: 'No review tasks found',
        },
        inventory: {
            title: 'Classification Inventory',
            count: 'systems',
            summary: 'Inventory Summary',
            total_systems: 'Total Systems',
            total_labels: 'Total Labels',
            system_name: 'System',
            label_count: 'Labels',
            approved_count: 'Approved',
            pending_count: 'Pending',
            no_items: 'No inventory items found',
        },
        exceptions: {
            title: 'Classification Exceptions',
            count: 'exceptions',
            all_severities: 'All Severities',
            all_statuses: 'All Statuses',
            system_name: 'System',
            severity_label: 'Severity',
            status_open: 'Open',
            status_resolved: 'Resolved',
            status_accepted_risk: 'Accepted Risk',
            resolve: 'Resolve',
            resolve_exception: 'Resolve Exception',
            resolution_type: 'Resolution Type',
            notes: 'Notes',
            justification_required: 'Justification required (minimum 10 characters) for accepted risk',
            resolved_success: 'Exception resolved',
            no_exceptions: 'No exceptions found',
        },
        history: {
            title: 'Classification History',
            count: 'events',
            actor: 'Actor',
            action: 'Action',
            entity_type: 'Entity Type',
            changes: 'Changes',
            view_lineage: 'View Lineage',
            lineage_title: 'Classification Lineage',
            no_events: 'No history events found',
            no_lineage: 'No lineage data available',
            modified: 'Modified',
            created: 'Created',
            removed: 'Removed',
            action_created: 'Created',
            action_updated: 'Updated',
            action_archived: 'Archived',
            action_approved: 'Approved',
            action_deprecated: 'Deprecated',
            entity_label: 'Label',
            entity_taxonomy: 'Taxonomy',
            entity_review_task: 'Review Task',
            entity_exception: 'Exception',
            before: 'Before',
            after: 'After',
        },
    },
    // ---------- Governance module (Sprint 1.3.0) ----------
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        // Sprint 41.2.0 E6 — Governance Audit Trail page keys
        audit_trail_title: 'Governance Audit Trail',
        audit_trail_subtitle: 'Cross-entity audit stream for governance actions — approvals, playbook executions, evidence links, and more.',
        audit_trail_item_count: 'audit events',
        audit_trail_fetch_error: 'Failed to fetch audit events.',
        audit_trail_no_events: 'No audit events found for the current filters.',
        audit_trail_entity_type: 'Entity type',
        audit_trail_entity_id: 'Entity ID',
        audit_trail_action: 'Action',
        audit_trail_actor: 'Actor',
        audit_trail_timestamp: 'Timestamp',
        audit_trail_date_from: 'From',
        audit_trail_date_to: 'To',
        audit_trail_no_rows_to_export: 'No rows to export.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Vendors',
            subprocessors: 'Sub-Processors',
            cross_border: 'Cross-Border',
            drift: 'Drift Monitor',
            scores: 'Compliance Score',
            playbooks: 'Playbooks',
            approvals: 'Approvals',
            audit_trail: 'Audit Trail',
            board_report: 'Board Report',
            evidence: 'Evidence',
            onboarding: 'Onboarding Pipeline',
            dpa_reviews: 'DPA Reviews',
            periodic_reviews: 'Periodic Reviews',
            offboarding: 'Offboarding',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Vendor Register',
            new: 'New Vendor',
            edit: 'Edit Vendor',
            name: 'Name',
            jurisdiction: 'Jurisdiction',
            dpa_status: 'DPA Status',
            risk_level: 'Risk Level',
            onboarding_status: 'Onboarding Status',
            contact_email: 'Contact Email',
            contact_name: 'Contact Name',
            contact_phone: 'Contact Phone',
            website: 'Website',
            services_csv: 'Services (comma-separated)',
            country: 'Country',
            country_placeholder: 'e.g. VN',
            last_reviewed: 'Last Reviewed',
            offboard: 'Offboard',
            offboard_vendor: 'Offboard Vendor',
            offboard_reason: 'Offboarding reason',
            no_vendors: 'No vendors found',
            vendor_count: '{count} vendor(s)',
            all_dpa_status: 'All DPA Status',
            all_risk_levels: 'All Risk Levels',
            all_onboarding: 'All Onboarding',
            fetch_error: 'Failed. Please try again.',
            save_error: 'Failed to save vendor',
            offboard_error: 'Failed to offboard',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Processor',
            sub_processor: 'Sub Processor',
            controller: 'Controller',
            joint_controller: 'Joint Controller',
        },
        onboarding_statuses: {
            pending_review: 'Pending Review',
            approved: 'Approved',
            active: 'Active',
            suspended: 'Suspended',
            offboarded: 'Offboarded',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Not Required',
            required_missing: 'Required Missing',
            draft: 'Draft',
            under_review: 'Under Review',
            signed: 'Signed',
            expired: 'Expired',
        },
        risk_levels: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        approval_statuses: {
            pending: 'Pending',
            approved: 'Approved',
            rejected: 'Rejected',
            expired: 'Expired',
        },
        approval_action_types: {
            vendor_onboarding: 'Vendor Onboarding',
            vendor_offboarding: 'Vendor Offboarding',
            cross_border_acceptance: 'Cross-Border Acceptance',
            playbook_activation: 'Playbook Activation',
            drift_accept_risk: 'Drift Accept Risk',
            activity_activation: 'Activity Activation',
        },
        drift_check_types: {
            dpa_expiry: 'DPA Expiry',
            dpa_missing: 'DPA Missing',
            review_overdue: 'Review Overdue',
            cross_border_gap: 'Cross-Border Gap',
            sub_processor_chain: 'Sub-Processor Chain',
            risk_assessment_overdue: 'Risk Assessment Overdue',
            retention_policy_missing: 'Retention Policy Missing',
            evidence_expiring: 'Evidence Expiring',
        },
        drift_statuses: {
            open: 'Open',
            acknowledged: 'Acknowledged',
            in_progress: 'In Progress',
            resolved: 'Resolved',
            accepted_risk: 'Accepted Risk',
        },
        drift_entity_types: {
            vendor: 'Vendor',
            system: 'System',
            data_flow: 'Data Flow',
            processing_activity: 'Processing Activity',
            evidence_item: 'Evidence Item',
        },
        playbook_statuses: {
            draft: 'Draft',
            active: 'Active',
            archived: 'Archived',
        },
        playbook_action_types: {
            delete_data: 'Delete Data',
            verify_deletion: 'Verify Deletion',
            notify_vendor: 'Notify Vendor',
            confirm_receipt: 'Confirm Receipt',
            document_evidence: 'Document Evidence',
            other: 'Other',
        },
        playbook_target_types: {
            system: 'System',
            vendor: 'Vendor',
        },
        dpa_chain_statuses: {
            complete: 'Complete',
            incomplete: 'Incomplete',
            unknown: 'Unknown',
        },
        sub_processor_statuses: {
            active: 'Active',
            terminated: 'Terminated',
        },
        evidence_types: {
            dpa_agreement: 'DPA Agreement',
            risk_assessment: 'Risk Assessment',
            audit_report: 'Audit Report',
            certification: 'Certification',
            deletion_confirmation: 'Deletion Confirmation',
            training_record: 'Training Record',
            incident_report: 'Incident Report',
            document: 'Document',
            screenshot: 'Screenshot',
            policy: 'Policy',
            contract: 'Contract',
            assessment: 'Assessment',
            correspondence: 'Correspondence',
            other: 'Other',
        },
        linkable_entity_types: {
            vendor: 'Vendor',
            transfer_mechanism: 'Transfer Mechanism',
            playbook: 'Playbook',
            execution: 'Execution',
            system: 'System',
            activity: 'Activity',
        },
        subprocessors: {
            title: 'Sub-Processor Relationships',
            new: 'New Link',
            vendor: 'Vendor',
            subprocessor: 'Sub-Processor',
            relationship_type: 'Relationship Type',
            countries: 'Countries',
            countries_csv: 'Countries (comma-separated)',
            dpa_chain: 'DPA Chain',
            dpa_chain_status: 'DPA Chain Status',
            no_processors: 'No processors found.',
            no_subprocessors: 'No sub-processors found.',
            processor_vendor: 'Processor Vendor',
            select_processor: 'Select Processor',
            select_vendor: 'Select Vendor',
            services: 'Services',
            sub_processor_vendor_id: 'Sub-Processor Vendor ID',
            terminate: 'Terminate',
            confirm_terminate: 'Are you sure you want to terminate this relationship?',
            create_error: 'Failed to create link.',
            fetch_error: 'Failed to fetch sub-processors.',
            processor: 'Processor',
            services_csv: 'Services (comma-separated)',
            terminate_error: 'Failed to terminate.',
            // --- AC info boxes (versioning) ---
            version_history_heading: 'Sub-Processor Change Versioning',
            change_tracking_note: 'All sub-processor relationship changes are versioned. Each modification records the before/after state for audit trail.',
            history_retention_note: 'Change history is retained for the full data retention period. Review change logs via the vendor detail view.',
            vendor_risk_cross_view_heading: 'Sub-processor register (Vendor Risk)',
            vendor_risk_cross_view_source: 'Source: Vendor Risk Register',
            vendor_risk_cross_view_empty: 'No entries in the Vendor Risk register.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Cross-Border Governance Report',
            flows: 'Cross-Border Flows',
            transfers: 'Cross-Border Transfers',
            detected_gaps: 'Detected Gaps',
            safeguards: 'Safeguards',
            cross_border_vendors: 'Cross-Border Vendors',
            destination: 'Destination',
            no_cross_border_vendors: 'No cross-border vendors found.',
            no_safeguard: 'No safeguard',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Processing Locations',
            export_error: 'Failed to export report.',
            export_pdf: 'Export PDF',
            export_success: 'Report exported successfully.',
            exporting: 'Exporting...',
            fetch_error: 'Failed to fetch cross-border data.',
            source: 'Source',
            // --- AC info boxes (PDPL cross-border) ---
            pdpl_p2_heading: 'PDPL-P2 Cross-Border Transfer Compliance',
            dossier_prep_note: 'Dossier preparation: assemble impact assessment, legal basis, and safeguard documentation before filing.',
            filing_tracking_note: 'Filing tracking: monitor submission status with the authority from initial application through approval.',
            supplement_loop_note: 'Supplement loop: respond to Authority information requests and re-submit amended dossiers as needed.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Vendor search',
            vendor_search_placeholder: 'Search by vendor name…',
            jurisdiction_filter_label: 'Jurisdiction',
            // --- Sprint 41.5.0 G1 — Cross-Border Filings lifecycle ---
            filings_section_heading: 'Cross-Border Filings',
            filings_empty: 'No filings yet. Create one to start a dossier.',
            filings_create_button: 'New filing',
            filings_table_id: 'Filing ID',
            filings_table_jurisdiction: 'Jurisdiction',
            filings_table_type: 'Form type',
            filings_table_status: 'Status',
            filings_table_updated: 'Updated',
            filings_table_open: 'Open',
            filings_detail_title: 'Cross-Border Filing',
            filings_back_to_list: 'Back to Cross-Border',
            filings_status_heading: 'Lifecycle',
            filings_evidence_heading: 'Evidence',
            filings_history_heading: 'Audit trail',
            filings_field_filing_id: 'Filing ID',
            filings_field_jurisdiction: 'Jurisdiction',
            filings_field_type: 'Form type',
            filings_field_status: 'Status',
            filings_field_reference: 'Authority reference',
            filings_field_mps_receipt: 'MPS receipt',
            filings_field_submitted_at: 'Submitted at',
            filings_field_acknowledged_at: 'Acknowledged at',
            filings_field_archived_at: 'Archived at',
            filings_field_version: 'Version',
            filings_field_notes: 'Notes',
            filings_action_advance: 'Advance status',
            filings_action_reject: 'Reject',
            filings_action_archive: 'Archive',
            filings_action_submit: 'Mark submitted',
            filings_action_acknowledge: 'Mark acknowledged',
            filings_action_resume_draft: 'Resume as draft',
            filings_status_draft: 'Draft',
            filings_status_assessed: 'Assessed',
            filings_status_pending_review: 'Pending review',
            filings_status_ready_to_file: 'Ready to file',
            filings_status_submitted: 'Submitted',
            filings_status_acknowledged: 'Acknowledged',
            filings_status_rejected: 'Rejected',
            filings_status_archived: 'Archived',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'New cross-border filing',
            filings_new_form_jurisdiction: 'Jurisdiction',
            filings_new_form_type: 'Form type',
            filings_new_form_notes: 'Notes (optional)',
            // Sprint 64.69.0 — the select no longer arrives with a Vietnamese form
            // number already chosen, so "not chosen yet" needs a visible label.
            filings_type_placeholder: 'Select a form type…',
            filings_new_submit: 'Create',
            filings_new_cancel: 'Cancel',
            filings_reason_label: 'Reason',
            filings_reference_label: 'Authority reference number',
            filings_mps_receipt_label: 'MPS receipt reference',
            filings_create_error: 'Failed to create filing.',
            filings_create_success: 'Filing created.',
            filings_update_error: 'Failed to update filing.',
            filings_update_success: 'Filing updated.',
            filings_fetch_error: 'Failed to fetch filings.',
            filings_archive_confirm: 'Archive this filing? This cannot be undone.',
            // Sprint 41.5.0 G9.1 / G9.3 — UX polish keys
            filings_status_rejected_detour: 'Filing was rejected after review. Resume as draft to restart the flow.',
            recent_exports_heading: 'Recent exports',
            recent_exports_empty: 'No exports yet. Click "Export PDF" to generate one.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
        },
        drift: {
            title: 'Configuration Drift Monitor',
            drift_type: 'Drift Type',
            severity: 'Severity',
            detected_at: 'Detected At',
            resolve: 'Resolve',
            all_check_types: 'All Check Types',
            all_entity_types: 'All Entity Types',
            event_count: 'Event Count',
            accept_risk: 'Accept Risk',
            acknowledge: 'Acknowledge',
            assign_error: 'Failed to assign.',
            assign_event: 'Assign',
            assigned: 'Assigned',
            enter_resolution_notes: 'Enter resolution notes...',
            enter_user_id: 'Enter user ID...',
            entity: 'Entity',
            fetch_error: 'Failed to fetch drift events.',
            no_events: 'No drift events found.',
            resolution_notes: 'Resolution Notes',
            update_status_error: 'Failed to update status.',
        },
        scores: {
            title: 'Compliance Score Dashboard',
            overall: 'Overall Compliance Score',
            breakdown: 'Score Breakdown by Module',
            trend: 'Historical Trend',
            module: 'Module',
            score: 'Score',
            grade: 'Grade',
            calculated_at: 'Calculated At',
            generate_board_report: 'Generate Board Report',
            lang_english: 'English',
            lang_vietnamese: 'Vietnamese',
            fetch_error: 'Failed to fetch compliance scores.',
            generate_report: 'Generate Report',
            generating: 'Generating...',
            recommendations: 'Recommendations',
            report_error: 'Failed to generate report.',
            factor_owner_assigned: 'Owner assigned',
            factor_data_categories_linked: 'Data categories linked',
            factor_purposes_linked: 'Purposes linked',
            factor_data_flows_documented: 'Data flows documented',
            factor_processing_activity_linked: 'Processing activity linked',
            factor_dpa_status: 'DPA status',
            factor_retention_policy_linked: 'Retention policy linked',
            factor_last_reviewed: 'Last reviewed within threshold',
            factor_risk_assessment_current: 'Risk assessment current',
            factor_sub_processors_documented: 'Sub-processors documented',
            factor_cross_border_safeguards: 'Cross-border safeguards',
            factor_contact_info_complete: 'Contact information complete',
            factor_onboarding_approved: 'Onboarding approved',
            factor_avg_system_score: 'Average system score',
            factor_avg_vendor_score: 'Average vendor score',
            factor_processing_activity_coverage: 'Processing activity coverage',
            factor_ropa_completeness: 'RoPA completeness',
            factor_open_drift_events: 'Open drift events',
        },
        playbooks: {
            title: 'Deletion Playbooks',
            new: 'New Playbook',
            edit: 'Edit Playbook',
            steps: 'Steps',
            add_step: 'Add Step',
            remove_step: 'Remove Step',
            data_categories_csv: 'Data Categories (comma-separated)',
            estimated_duration: 'Estimated Duration',
            estimated_duration_label: 'Estimated Duration',
            evidence_description: 'Evidence Description',
            no_playbooks: 'No playbooks found.',
            playbook_count: 'Playbook Count',
            requires_evidence: 'Requires Evidence',
            step_label: 'Step Label',
            target_name: 'Target Name',
            target_type: 'Target Type',
            action_type: 'Action Type',
            archive_error: 'Failed to archive.',
            confirm_archive: 'Are you sure you want to archive this playbook?',
            data_categories: 'Data Categories',
            days: 'days',
            fetch_error: 'Failed to fetch playbooks.',
            sla_days: 'SLA (days)',
            target_id: 'Target ID',
            // --- AC info boxes (template classification) ---
            template_class_heading: 'Template Classification',
            official: 'Official',
            internal: 'Internal',
            classification: 'Classification',
            official_note: 'Official: regulatory-mandated templates aligned with PDPL, GDPR, or sector-specific requirements.',
            internal_note: 'Internal: organization-specific procedures not derived from regulatory templates.',
            active_executions_heading: 'Active executions',
            no_active_executions: 'No active executions.',
            playbook_label: 'Playbook',
            ticket_id_label: 'Ticket',
            progress_label: 'Progress',
            actions_label: 'Actions',
            view_steps_label: 'View steps',
            execution_detail_title: 'Execution detail',
            playbook_missing: 'Playbook not found.',
            evidence_required_label: 'Evidence required',
            upload_evidence_label: 'Upload evidence',
            mark_complete_label: 'Mark step complete',
            evidence_required_toast: 'Evidence required before completing this step.',
            evidence_blocking_message: 'Attach an evidence file to continue.',
            step_completed_toast: 'Step completed.',
            executions_fetch_error: 'Failed to load executions.',
            step_evidence_default_name: 'Playbook execution step evidence',
            attached_evidence_label: 'Attached evidence',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Approval Workflows',
            new: 'New Approval',
            edit: 'Edit Approval',
            request_type: 'Request Type',
            requester: 'Requester',
            approve: 'Approve',
            reject: 'Reject',
            justification: 'Justification',
            all_action_types: 'All Action Types',
            decided_by: 'Decided By',
            no_approvals: 'No approvals found.',
            approval_count: 'Approval Count',
            approval_notes_optional: 'Notes (optional)',
            approve_error: 'Failed to approve.',
            approve_request: 'Approve Request',
            decision: 'Decision',
            entity: 'Entity',
            fetch_error: 'Failed to fetch approvals.',
            reject_error: 'Failed to reject.',
            reject_request: 'Reject Request',
            rejection_notes_required: 'Rejection notes required',
            rejection_notes_required_msg: 'Please provide a reason for rejection.',
            requested_at: 'Requested At',
            // --- AC info boxes (regulatory context) ---
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Supporting evidence (optional)',
            decision_evidence_name: 'Decision Evidence',
        },
        evidence: {
            title: 'Governance Evidence',
            new: 'New Evidence',
            link_to: 'Link To',
            entity_id_placeholder: 'Entity ID',
            file_path_placeholder: '/path/to/file',
            evidence_label: 'Evidence Label',
            file_path: 'File Path',
            source_mode_label: 'Evidence source',
            source_mode_file: 'Upload file',
            source_mode_url: 'Link external URL',
            attachment_label: 'Attachment',
            item_count: 'Item Count',
            link_button: 'Link Evidence',
            confirm_delete: 'Delete this evidence record? Only the record and its links are deleted; the uploaded file or web address it refers to is left where it is. This cannot be undone.',
            enter_entity_id: 'Enter entity ID...',
            entity_id: 'Entity ID',
            entity_type: 'Entity Type',
            expires: 'Expires',
            expires_at: 'Expires At',
            fetch_error: 'Failed to fetch evidence.',
            link_error: 'Failed to link evidence.',
            no_evidence: 'No evidence found.',
            tags_csv: 'Tags (comma-separated)',
            uploaded: 'Uploaded',
            url: 'URL',
            // --- AC info boxes (framework-tagged evidence) ---
            framework_tag_heading: 'Framework-Tagged Evidence',
            framework_tag_note: 'Every evidence record captures framework code, actor, and timestamp. Filter by framework to isolate audit-relevant records.',
            framework_code: 'Framework Code',
            filter_by_framework: 'Filter by framework code...',
            actor: 'Actor',
        },
        certification: {
            not_applicable: 'Not Applicable',
            not_certified: 'Not Certified',
            pending: 'Pending',
            certified: 'Certified',
            revoked: 'Revoked',
        },
        onboarding_pipeline: {
            title: 'Onboarding Pipeline',
            new_workflow: 'New Onboarding Workflow',
            vendor_id: 'Vendor ID',
            stages: 'Stages',
            current_stage: 'Current Stage',
            advance: 'Advance Stage',
            advance_success: 'Stage advanced successfully',
            workflow_status: 'Workflow Status',
            initiated: 'Initiated',
            in_progress: 'In Progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
            no_workflows: 'No onboarding workflows found',
            stages_placeholder: 'Stage 1, Stage 2, Stage 3',
            assigned_to: 'Assigned To',
            notes: 'Notes',
            // --- AC info boxes (PDPL onboarding) ---
            pdpl_lifecycle_heading: 'PDPL Vendor Lifecycle — Onboarding',
            due_diligence_note: 'Due diligence stage is mandatory before onboarding any processor under PDPL. Verify data processing scope, legal basis, and security measures.',
            contract_review_note: 'Contract review must confirm DPA clauses, data retention limits, and sub-processor restrictions.',
            create_dpa_review_cta: 'Create DPA review',
            dpa_review_linked: 'DPA review linked',
        },
        dpa_review: {
            title: 'DPA Reviews',
            new_review: 'New DPA Review',
            review_type: 'Review Type',
            findings: 'Findings',
            outcome: 'Outcome',
            complete_review: 'Complete Review',
            complete_success: 'Review completed successfully',
            link_assessment: 'Link Security Assessment',
            link_success: 'Assessment linked successfully',
            assessment_id: 'Assessment ID',
            combined_title: 'Combined Assessment',
            no_reviews: 'No DPA reviews found',
            // --- AC info boxes (telecom disclosure governance) ---
            telecom_disclosure_heading: 'Telecom Disclosure Governance',
            disclosure_trigger_note: 'Disclosure-triggered reviews capture telecom subscriber data sharing events per VN Telecom Law.',
            telecom_t2_note: 'TELECOM-T2 tier approval is required before disclosing subscriber data to authorities.',
            quarterly_log_note: 'Use the quarterly log to document all disclosure events and maintain a continuous audit trail.',
        },
        periodic_review: {
            title: 'Periodic Reviews',
            new_review: 'New Periodic Review',
            review_type: 'Review Type',
            due_date: 'Due Date',
            next_review: 'Next Review Date',
            trigger_reassessment: 'Trigger Reassessment',
            reassessment_reason: 'Reassessment Reason',
            reassessment_success: 'Reassessment triggered successfully',
            complete_success: 'Review completed successfully',
            scheduled: 'Scheduled',
            in_progress: 'In Progress',
            completed: 'Completed',
            overdue: 'Overdue',
            material_change: 'Material Change',
            no_reviews: 'No periodic reviews found',
            mark_complete: 'Mark as Complete',
            // --- AC info boxes (PDPL periodic reviews) ---
            pdpl_periodic_heading: 'PDPL Vendor Lifecycle — Periodic Reviews',
            scheduled_review_note: 'Periodic reviews must be scheduled at least annually for all active processors. Overdue reviews trigger compliance alerts.',
            material_change_note: 'Material changes in data processing require immediate reassessment regardless of schedule.',
            overdue_badge: 'Overdue',
            overdue_aria: 'Overdue review',
            only_overdue_filter: 'Only overdue',
        },
        offboarding: {
            title: 'Vendor Offboarding',
            initiate: 'Initiate Offboarding',
            initiate_success: 'Offboarding initiated successfully',
            reason: 'Offboarding Reason',
            data_return_required: 'Data Return Required',
            verify_data: 'Verify Data Return',
            verify_success: 'Data return verified successfully',
            sign_off: 'Sign Off',
            sign_off_success: 'Offboarding signed off successfully',
            status_initiated: 'Initiated',
            status_data_pending: 'Data Return Pending',
            status_data_verified: 'Data Return Verified',
            status_completed: 'Completed',
            status_cancelled: 'Cancelled',
            task_id: 'Task ID',
            no_tasks: 'No offboarding tasks found',
            evidence_id: 'Evidence ID',
            // --- AC info boxes (PDPL offboarding) ---
            pdpl_offboarding_heading: 'PDPL Vendor Lifecycle — Offboarding & Deletion',
            deletion_capture_note: 'Offboarding must capture data deletion confirmation from the vendor. Verify data return before sign-off.',
            evidence_attachment_note: 'Attach deletion evidence (certificates, screenshots, vendor confirmations) to complete the offboarding record.',
            verify_mode_label: 'Verification method',
            verify_mode_upload: 'Upload new file',
            verify_mode_link: 'Link existing evidence',
            verify_mode_none: 'No data to return',
            upload_data_return_evidence: 'Upload data return evidence',
            no_data_justification_label: 'Justification',
            no_data_justification_placeholder: 'Explain why no data needs to be returned',
            no_data_justification_hint: 'Minimum 20 characters',
            upload_required_toast: 'Attach a file to continue',
            evidence_link_required_toast: 'Select an evidence record to continue',
            no_data_justification_required_toast: 'Provide a justification of at least 20 characters',
            data_return_evidence_name: 'Data Return Evidence',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Initial',
            annual: 'Annual',
            renewal: 'Renewal',
            incident_triggered: 'Incident Triggered',
        },
        periodic_review_types: {
            initial: 'Initial',
            annual: 'Annual',
            renewal: 'Renewal',
            incident_triggered: 'Incident Triggered',
            material_change: 'Material Change',
            disclosure_triggered: 'Disclosure Triggered',
            quarterly_log: 'Quarterly Log',
        },
        dpa_review_statuses: {
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
        onboarding_stage_names: {
            due_diligence: 'Due Diligence',
            contract_review: 'Contract Review',
            security_assessment: 'Security Assessment',
            data_mapping: 'Data Mapping',
            approval: 'Approval',
            integration: 'Integration',
        },
        access_denied_title: 'Access Denied',
        access_denied: 'You do not have permission to view this page. Please contact your administrator.',
    },
    // ---------- Consent module (Sprint 1.4.0) ----------
    consent: {
        nav: {
            policies: 'Policies',
            purposes: 'Purpose Catalog',
            workflows: 'Workflows',
            snapshots: 'CMP Snapshots',
            change_awareness: 'Change Awareness',
            audit: 'Consent Audit',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        // Sprint 48.1.4 — Forseti consent gap analysis page
        gap_analysis: {
            nav_label: 'Gap Analysis',
            page_title: 'Consent Gap Analysis',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Frameworks',
            org_flags_label: 'Organisation context',
            flag_withdrawal: 'Has global withdrawal mechanism documented',
            flag_parental: 'Has parental-consent flow for under-16 subjects',
            flag_cross_border: 'Has cross-border filing on record',
            run_label: 'Run gap analysis',
            running_label: 'Running…',
            requires_advisor_label: 'Running the analysis needs permission to use Forseti chat.',
            results_heading: 'Gaps surfaced ({count})',
            tokens_label: '≈ {count} tokens',
            empty_label: 'No gaps detected for the families analysed.',
            remediation_label: 'Suggested remediation:',
            dismiss_label: 'Dismiss as not applicable',
            resolve_label: 'Resolve',
            dismissed_label: 'Dismissed as not applicable',
            resolved_label: 'Resolved',
            // Sprint 62.27.0 gate E12.5 pass 2 — installed families, what was analysed,
            // translated attributes and scopes, and the OD40 acknowledgement.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Import Consent Workflows',
            nav_title: 'Bulk Import',
            description: 'Upload a CSV file to bulk import consent workflows.',
            select_file: 'Select CSV File',
            file_help: 'CSV with headers matching workflow fields.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Required columns: Workflow Type, Data Subject, Channel.',
            file_too_large: 'File too large. Maximum size is 2MB.',
            preview: 'Preview',
            columns_detected: 'Columns detected: {count}',
            column_mapping: 'Column Mapping',
            import_button: 'Import',
            import_complete: 'Import complete',
            import_error: 'Import failed',
            result: 'Import Result',
            total: 'Total',
            imported: 'Imported',
            skipped: 'Skipped',
            failed: 'Failed',
            errors: 'Errors',
            error_row: 'Row',
            error_field: 'Field',
            error_message: 'Message',
            history: 'Import History',
            no_history: 'No import history.',
            history_file: 'Filename',
            history_date: 'Date',
            create_profiles_label: 'Create Data Subject profiles for new subjects',
            create_profiles_help: 'Imports without a matching profile will create one automatically. Uncheck to import workflows only.',
            profiles_created: 'Profiles created',
            profiles_matched: 'Profiles matched',
            subject_kind_help: 'Optional columns: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Invalid email in subject_email column.',
            error_subject_unknown_kind: 'Unknown subject_kind value. Expected: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key is required when subject_kind=external_ref',
        },
        policies: {
            title: 'Consent Policies',
            new: 'New Policy',
            edit: 'Edit Policy',
            name: 'Name',
            version: 'Version',
            policy_text: 'Policy Text',
            publish: 'Publish',
            versions: 'Versions',
            hide_versions: 'Hide Versions',
            submit_for_review: 'Submit for Review',
            suspend: 'Suspend',
            retire: 'Retire',
            jurisdictions: 'Jurisdictions',
            jurisdictions_csv: 'Jurisdictions (comma-separated)',
            approval_mode: 'Approval Mode',
            reviewer_ids: 'Reviewer IDs (comma-separated)',
            tags_csv: 'Tags (comma-separated)',
            effective_date: 'Effective Date',
            review_date: 'Review Date',
            consent_model_statement: 'Consent Model Statement',
            consent_model_placeholder: 'Optional consent model statement...',
            default_settings: 'Default Settings (JSON)',
            changes: 'Changes',
            fetch_error: 'Failed to fetch consent policies. Please try again.',
            save_error: 'Failed to save consent policy. Please try again.',
            submit_review_error: 'Failed to submit policy for review. Please try again.',
            activate_error: 'Failed to activate policy. Please try again.',
            suspend_error: 'Failed to suspend policy. Please try again.',
            retire_error: 'Failed to retire policy. Please try again.',
            submit_review_success: 'Policy submitted for review.',
            activate_success: 'Policy activated.',
            suspend_success: 'Policy suspended.',
            retire_success: 'Policy retired.',
            versions_error: 'Failed to fetch policy versions. Please try again.',
            invalid_json: 'Invalid JSON in Default Settings field. Please try again.',
            page_title: 'Consent Policies',
            version_updated: 'Updated',
            version_initial: 'Initial version',
            json_format: 'JSON format',
            sensitive: 'Sensitive',
            flag_sensitive: 'Flag Sensitive',
            flag_sensitive_title: 'Flag Policy as Sensitive',
            require_enhanced_review: 'Require Enhanced Review',
            enhanced_review_success: 'Enhanced review requirement enabled.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Select the applicable PDPL Article 2 sensitive data categories:',
            no_policies: 'No consent policies found. Create your first policy to get started.',
            approval_success: 'Policy approved.',
            reviewers_label: 'Reviewers',
            // Sprint 28.1.3 — Enhanced Review Workflow
            enhanced_review_title: 'Enhanced Review Required',
            enhanced_review_badge: 'Enhanced Review Required',
            enhanced_review_status_pending: 'Pending Review',
            enhanced_review_status_approved: 'Review Approved',
            enhanced_review_status_rejected: 'Review Rejected',
            enhanced_review_reviewed_by: 'Reviewed by',
            enhanced_review_notes: 'Review Notes',
            enhanced_review_approve: 'Approve Review',
            enhanced_review_reject: 'Reject Review',
            enhanced_review_notes_required: 'Review notes are required',
            enhanced_review_approved: 'Enhanced review approved',
            enhanced_review_rejected: 'Enhanced review rejected — policy reset to draft',
            enhanced_review_reset_to_draft: 'Enhanced review rejected — policy reset to draft',
            enhanced_review_reject_warning: 'Rejection will reset this policy to Draft status. The DPO must resubmit for review after addressing the issues.',
            submitted_modal_title: 'Submitted for Enhanced Review',
            submitted_modal_body: 'This policy has been submitted. A Legal/Privacy reviewer must approve it before it can be activated.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Health Data',
            biometric: 'Biometric Data',
            political: 'Political Opinions',
            ethnic: 'Ethnic Origin',
            genetic: 'Genetic Data',
            criminal: 'Criminal Records',
        },
        purposes: {
            title: 'Purpose Catalog',
            new: 'New Purpose',
            edit: 'Edit Purpose',
            name: 'Name',
            legal_basis: 'Legal Basis',
            retention_days: 'Retention Period (days)',
            is_active: 'Active',
            filter_by_category: 'Filter by category',
            policy_ids: 'Policy IDs (comma-separated)',
            ropa_activity_ids: 'ROPA Activity IDs (comma-separated)',
            policies: 'Policies',
            deprecate: 'Deprecate',
            fetch_error: 'Failed to fetch consent purposes. Please try again.',
            save_error: 'Failed to save consent purpose. Please try again.',
            deprecate_error: 'Failed to deprecate consent purpose. Please try again.',
            page_title: 'Purpose Catalog',
            no_purposes: 'No consent purposes found. Create your first purpose to get started.',
            category_personal_data: 'Personal Data',
            category_sensitive_data: 'Sensitive Data',
            category_health_data: 'Health Data',
            category_financial_data: 'Financial Data',
            category_biometric_data: 'Biometric Data',
            category_children_data: "Children's Data",
            category_location_data: 'Location Data',
            category_communications_data: 'Communications Data',
            category_other: 'Other',
        },
        workflows: {
            title: 'Consent Workflows',
            new: 'New Workflow',
            edit: 'Edit Workflow',
            workflow_type: 'Workflow Type',
            user_id: 'User ID',
            purpose_id: 'Purpose ID',
            purpose_ids_csv: 'Purpose ID (comma-separated)',
            reason: 'Reason',
            process: 'Process',
            data_subject_ref: 'Data Subject Reference',
            data_subject: 'Data Subject',
            channel: 'Channel',
            policy_id: 'Policy ID',
            scope: 'Scope',
            legal_grounds: 'Legal Grounds',
            assessment: 'Assessment',
            select_outcome: 'Select Outcome',
            partially_approved: 'Partially Approved',
            denied: 'Denied',
            outcome_upheld: 'Upheld',
            outcome_pending: 'Pending',
            outcome_denied: 'Denied',
            status_count: 'Status Counts',
            channel_filter: 'Filter by channel',
            add_action: '+ Action',
            by_type: 'By Type',
            by_status: 'By Status',
            downstream_actions: 'Downstream Actions',
            downstream_actions_for: 'Downstream Actions for {subject} ({type})',
            add_downstream_action: 'Add Downstream Action',
            assigned_to: 'Assigned To',
            fetch_error: 'Failed to fetch consent workflows. Please try again.',
            dashboard_error: 'Failed to fetch workflow dashboard. Please try again.',
            save_error: 'Failed to save consent workflow. Please try again.',
            status_update_error: 'Failed to update workflow status. Please try again.',
            assessment_error: 'Failed to update workflow assessment. Please try again.',
            add_action_error: 'Failed to add workflow action. Please try again.',
            update_action_error: 'Failed to update workflow action. Please try again.',
            type_refusal: 'Refusal',
            type_withdrawal: 'Withdrawal',
            type_restriction: 'Restriction',
            type_objection: 'Objection',
            status_received: 'Received',
            status_processing: 'Processing',
            status_under_assessment: 'Under Assessment',
            status_implemented: 'Implemented',
            // Sprint 41.3.0 F8 — workflow evidence attachments
            evidence_button: 'Evidence',
            evidence_modal_title: 'Workflow Evidence',
            no_evidence: 'No evidence attached to this workflow.',
            upload_evidence_label: 'Upload workflow evidence',
            completed_at: 'Completed At',
            effective_date: 'Effective Date',
            page_title: 'Consent Workflows',
            no_workflows: 'No consent workflows found. Workflows are created when data subjects exercise their rights.',
            type_grant: 'Grant',
            propagation_none: 'None',
            propagation_pending: 'Pending',
            propagation_pending_warning: 'Cannot complete — withdrawal propagation still pending',
            propagation_completed: 'Completed',
            propagation_partial: 'Partial',
            propagation_incomplete_error: 'Cannot complete workflow while withdrawal propagation is still pending',
            propagation_status: 'Propagation Status',
        },
        snapshots: {
            title: 'CMP Snapshots',
            new: 'New Snapshot',
            label: 'Label',
            snapshot_date: 'Snapshot Date',
            purposes_count: 'Purposes Count',
            compare: 'Compare',
            snapshot_data: 'Snapshot Data (JSON)',
            select_left: 'Select Left Snapshot',
            select_right: 'Select Right Snapshot',
            comparison_results: 'Comparison Results',
            field: 'Field',
            left: 'Left',
            right: 'Right',
            close_comparison: 'Close Comparison',
            trigger: 'Trigger',
            trigger_manual: 'Manual',
            trigger_policy_approval: 'Policy Approval',
            cmp_version: 'CMP Version',
            policy_version_id: 'Policy Version ID',
            banner_config: 'Banner Config (JSON)',
            active_purposes: 'Active Purposes (JSON)',
            active_legal_bases: 'Active Legal Bases (JSON)',
            domain_scope: 'Domain Scope',
            domain_scope_csv: 'Domain Scope (comma-separated)',
            raw_config: 'Raw Config (JSON)',
            checksum: 'Checksum',
            created: 'Created',
            fetch_error: 'Failed to fetch CMP snapshots. Please try again.',
            save_error: 'Failed to save CMP snapshot. Please try again.',
            compare_error: 'Failed to compare snapshots. Please try again.',
            page_title: 'Consent Snapshots',
            json_format: 'JSON format',
            json_array_format: 'JSON array format',
            manual_snapshot_note: 'For admin/developer use only. In normal operation, snapshots are created automatically via CMP webhook or on policy approval. Paste JSON exported directly from your CMP vendor (e.g. OneTrust, Cookiebot).',
            banner_config_hint: 'Paste the banner configuration object from your CMP export.',
            active_purposes_hint: 'JSON array of active purpose objects copied from your CMP export.',
            active_legal_bases_hint: 'JSON array of active legal basis objects copied from your CMP export.',
            raw_config_hint: 'Full raw configuration JSON from your CMP. Other JSON fields are extracted from this. A SHA-256 checksum is computed automatically.',
            changed_field: 'Changed field: ',
            no_snapshots: 'No CMP snapshots found. Snapshots are created when policies are approved or manually triggered.',
            detail_title: 'Snapshot Detail',
            view_snapshot_detail: 'View snapshot detail',
            policy_version: 'Policy Version',
            removed: 'Before',
            added: 'After',
        },
        change_awareness: {
            title: 'Consent Change Awareness',
            change_type: 'Change Type',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            detected_at: 'Detected At',
            acknowledge: 'Acknowledge',
            new_alert: 'New Alert',
            new_alert_form: 'New Change Awareness Alert',
            total_open: 'Total Open',
            by_severity: 'By Severity',
            snapshot_id: 'Snapshot ID',
            update_status: 'Update Status',
            resolution_notes: 'Resolution Notes',
            resolution_required: 'Resolution Notes (required)',
            resolution_required_msg: 'Resolution notes are required when resolving an item.',
            status_open: 'Open',
            status_under_review: 'Under Review',
            status_resolved: 'Resolved',
            status_accepted: 'Accepted',
            severity_informational: 'Informational',
            severity_requires_review: 'Requires Review',
            severity_policy_violation: 'Policy Violation',
            create_reconciliation: 'Create Reconciliation Task',
            reconciliation_created: 'Reconciliation task created successfully.',
            reconciliation_error: 'Failed to create reconciliation task. Please try again.',
            fetch_error: 'Failed to fetch change awareness items. Please try again.',
            dashboard_error: 'Failed to fetch change awareness dashboard. Please try again.',
            create_error: 'Failed to create change awareness item. Please try again.',
            status_update_error: 'Failed to update change awareness status. Please try again.',
            page_title: 'Change Awareness',
            no_items: 'No change awareness items found. Items are created when consent configuration changes are detected.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Consent Audit Trail',
            event_type: 'Event Type',
            user_id: 'User ID',
            timestamp: 'Timestamp',
            details: 'Details',
            actor: 'Actor',
            filter_by_entity_type: 'Filter by entity type',
            filter_by_action: 'Filter by action',
            fetch_error: 'Failed to fetch consent audit trail. Please try again.',
            page_title: 'Consent Audit',
            export_csv: 'Export CSV',
            exporting: 'Exporting\u2026',
            export_success: 'Audit trail exported successfully.',
            export_error: 'Failed to export consent audit trail. Please try again.',
            no_entries: 'No audit entries found. Audit events are recorded automatically when consent data changes.',
            entity_policy: 'Policy',
            entity_purpose: 'Purpose',
            entity_workflow: 'Workflow',
            entity_consent_record: 'Consent Record',
            entity_change_awareness: 'Change Awareness',
            entity_cmp_snapshot: 'CMP Snapshot',
            action_created: 'Created',
            action_updated: 'Updated',
            action_status_changed: 'Status Changed',
            action_drift_detected: 'Drift Detected',
            action_snapshot_created: 'Snapshot Created',
            action_approval_submitted: 'Approval Submitted',
            action_archived: 'Archived',
            action_purpose_auto_mapped: 'Purpose Auto Mapped',
            action_purpose_mapping_updated: 'Purpose Mapping Updated',
            action_webhook_configured: 'Webhook Configured',
            entity_propagation: 'Propagation',
            entity_reconciliation: 'Reconciliation',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Propagation Confirmed',
            action_propagation_failed: 'Propagation Failed',
            action_consent_captured: 'Consent Captured',
            action_reconciliation_created: 'Reconciliation Created',
            action_reconciliation_resolved: 'Reconciliation Resolved',
            action_submitted_for_review: 'Submitted for Review',
            action_activated: 'Activated',
            action_suspended: 'Suspended',
            action_retired: 'Retired',
            action_enhanced_review_required: 'Enhanced Review Required',
            action_enhanced_review_approved: 'Enhanced review approved',
            action_enhanced_review_rejected: 'Enhanced review rejected — policy reset to draft',
            action_consented: 'Consented',
            action_consented_customize: 'Consent Customized',
            action_revoked: 'Revoked',
            action_rejected: 'Rejected',
        },
        web_records: {
            title: 'Web Consent Records',
            page_title: 'Web Consent Records',
            domain: 'Domain',
            visitor_id: 'Visitor ID',
            consent_action: 'Consent Action',
            received_at: 'Received At',
            admin_status: 'Admin Status',
            revoke: 'Revoke',
            reject: 'Reject',
            reason: 'Reason',
            reason_placeholder: 'Reason for this action (optional)',
            revoke_confirm: 'Revoke this consent record?',
            reject_confirm: 'Reject this consent record?',
            override_success: 'Record status updated successfully.',
            override_error: 'Failed to update record status.',
            no_records: 'No web consent records found.',
            fetch_error: 'Failed to load web consent records.',
            filter_by_domain: 'Filter by domain',
            filter_by_action: 'Filter by action',
            active: 'Active',
        },
        legal_bases: {
            all: 'All Legal Bases',
        },
        common_statuses: {
            suspended: 'Suspended',
            retired: 'Retired',
            deprecated: 'Deprecated',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'AesirX CMP Integration',
            back_to_consent: 'Back to Consent',
            back_to_integration: 'Back to Integration',
            webhook_status: 'Webhook Status',
            events_received: 'Events Received',
            web_consent_records: 'Web Consent Records',
            unmapped_purposes: 'Unmapped Purposes',
            integration_management: 'Integration Management',
            webhook_configuration: 'Webhook Configuration',
            webhook_config_desc: 'Configure the CMP webhook endpoint and manage the HMAC secret.',
            purpose_mapping: 'Purpose Mapping',
            purpose_mapping_desc: 'Map CMP purpose identifiers to GRC consent catalog purposes.',
            bulk_sync: 'Re-map Records',
            bulk_sync_desc: 'Re-apply purpose mappings to all unmapped consent records.',
            consent_analytics: 'Web Consent Analytics (Last 30 Days)',
            consent_grant_rate: 'Consent Grant Rate',
            top_domain: 'Top Domain',
            records: 'records',
            banner_interactions: 'Banner Interactions',
            consent_actions: 'Consent Actions',
            opt_in_trend: 'Opt-in/Opt-out Trend',
            records_by_domain: 'Records by Domain',
            date: 'Date',
            grants: 'Grants',
            denials: 'Denials',
            not_configured: 'Not Configured',
            load_error: 'Failed to load integration data',
            webhook_not_configured: 'Webhook not configured.',
            webhook_not_configured_msg: 'Please {link} first.',
            configure_webhook_link: 'configure the webhook',
            setup_wizard: 'Setup Wizard',
            step_configure_webhook: 'Configure Webhook',
            step_map_purposes: 'Map Purposes',
            step_run_sync: 'Run Initial Bulk Sync',
            step_verify_data: 'Verify Data in Dashboard',
            configure: 'Configure',
            view_dashboard: 'View Dashboard',
            syncing: 'Syncing...',
            sync_now: 'Sync Now',
            sync_completed: 'Sync completed: {imported} imported, {skipped} skipped, {failed} failed.',
            sync_failed: 'Sync failed',
            last_sync_result: 'Last Sync Result',
            imported: 'Imported',
            skipped: 'Skipped',
            errors: 'Errors',
            index: 'Index',
            error: 'Error',
            sync_history: 'Sync History',
            no_sync_jobs: 'No sync jobs recorded yet.',
            load_sync_error: 'Failed to load sync data',
            purpose_mapping_title: 'CMP Purpose Mapping',
            auto_mapping: 'Auto-Mapping...',
            auto_map_purposes: 'Auto-Map Purposes',
            unmapped_warning: '{count} unmapped purpose(s) -- these CMP purposes need to be mapped to GRC purposes.',
            mapping_saved: 'Mapping saved.',
            mapping_deleted: 'Mapping deleted.',
            auto_map_complete: 'Auto-map complete: {mapped} mapped, {unmatched} unmatched.',
            auto_map_failed: 'Auto-map failed',
            save_mapping_error: 'Failed to save mapping',
            delete_mapping_error: 'Failed to delete mapping',
            load_data_error: 'Failed to load data',
            cmp_purpose: 'CMP Purpose',
            grc_purpose: 'GRC Purpose',
            select_grc_purpose: '-- Select GRC Purpose --',
            auto_mapped: 'Auto-Mapped',
            mapped: 'Mapped',
            unmapped: 'Unmapped',
            no_mappings: 'No purpose mappings found. Mappings are created automatically when consent events are received.',
            total_mappings: 'Total: {count} mapping(s)',
            webhook_config_title: 'Webhook Configuration',
            current_configuration: 'Current Configuration',
            setup_webhook: 'Setup Webhook',
            setup_webhook_desc: 'Configure the AesirX CMP webhook to receive consent events.',
            allowed_domains: 'Allowed Domains (one per line)',
            webhook_secret_warning: 'Webhook Secret (save this now -- it will not be shown again):',
            last_event: 'Last Event',
            none_received: 'None',
            update_domains: 'Update Domains',
            pause_webhook: 'Pause Webhook',
            resume_webhook: 'Resume Webhook',
            regenerate_secret: 'Regenerate Secret',
            configuring: 'Configuring...',
            create_webhook_config: 'Create Webhook Configuration',
            webhook_endpoint: 'Webhook Endpoint',
            webhook_endpoint_desc: 'Configure your AesirX CMP to send consent events to:',
            webhook_signature_note: 'Include the HMAC-SHA256 signature in the {header} header.',
            configured_success: 'Webhook configured successfully.',
            config_updated: 'Configuration updated.',
            config_error: 'Failed to configure webhook',
            update_error: 'Failed to update configuration',
            toggle_error: 'Failed to toggle webhook',
            deactivated: 'Webhook deactivated.',
            deactivate_error: 'Failed to deactivate webhook',
            page_title: 'CMP Integration',
            webhook_config_page_title: 'CMP Webhook Configuration',
            bulk_sync_page_title: 'Re-map Records',
            purpose_mapping_page_title: 'Purpose Mapping',
            data_loaded: 'CMP integration data loaded',
            sync_all: 'Sync All',
            sync_selected: 'Sync Selected',
            sync_status: 'Sync Status',
            last_sync: 'Last Sync',
            no_records: 'No records found.',
            sync_in_progress: 'Sync in progress...',
            sync_complete: 'Sync complete.',
            sync_error: 'Sync failed. Please try again.',
            map_purpose: 'Map Purpose',
            unmap: 'Unmap',
            mapping_status: 'Mapping Status',
            save_mapping: 'Save Mapping',
            remap_unmapped_records: 'Re-map Unmapped Records',
            remap_records: 'Re-map Records',
            add_mappings_first: 'Add purpose mappings first (Step 2)',
            remap_result: 'Re-mapping Result',
            remap_result_desc: 'Processed all consent records with unmapped or partially mapped purposes.',
            total_processed: 'Total Processed',
            newly_mapped: 'Newly Mapped',
            still_unmapped_label: 'Still Unmapped',
            still_unmapped_msg: '{count} record(s) still have unmapped purposes. {link} then click Re-map again.',
            add_more_mappings: 'Add more mappings',
            remap_complete: 'Re-mapping complete: {remapped} newly mapped, {still_unmapped} still unmapped.',
            add_mapping: 'Add Mapping',
            cmp_purpose_id_label: 'CMP Purpose ID *',
            cmp_purpose_id_required: 'CMP Purpose ID is required',
            cmp_purpose_name_label: 'CMP Purpose Name',
            grc_purpose_label: 'GRC Purpose',
        },
        propagation: {
            title: 'Withdrawal Propagation',
            systems: 'Linked Systems',
            status: 'Status',
            confirmed: 'Confirmed',
            pending: 'Pending',
            failed: 'Failed',
            confirm: 'Confirm Withdrawal',
            fail: 'Mark Failed',
            failure_reason: 'Failure Reason',
            can_close: 'All systems confirmed — workflow can be closed',
            cannot_close: 'Waiting for system confirmations',
            no_tasks: 'No propagation tasks.',
            confirm_success: 'System withdrawal confirmed.',
            fail_success: 'System marked as failed.',
            propagate_success: 'Withdrawal propagated to linked systems.',
            propagate: 'Propagate to Systems',
            add_system: 'Add System',
            remove_system: 'Remove',
            system_id: 'System ID',
            system_name: 'System Name',
            owner_id: 'Owner ID',
            propagate_error: 'Failed to propagate withdrawal. Please try again.',
            no_systems: 'Add at least one system.',
            page_title: 'Withdrawal Propagation',
            workflow_id: 'Select Withdrawal Workflow',
            system_id_hint: 'e.g. crm-001, erp-prod',
            owner_id_hint: 'System owner',
            elapsed: 'Elapsed',
        },
        sensitive: {
            title: 'Sensitive Data Policies',
            flag: 'Flag as Sensitive',
            categories: 'Sensitive Categories',
            enhanced_review: 'Require Enhanced Review',
            enhanced_review_success: 'Enhanced review required.',
            flag_success: 'Policy flagged as sensitive.',
            no_sensitive: 'No sensitive data policies.',
        },
        reconciliation: {
            title: 'Mismatch Reconciliation',
            create: 'New Reconciliation Task',
            system: 'System',
            owner: 'Owner',
            description: 'Description',
            resolution: 'Resolution',
            resolve: 'Resolve',
            update_grc: 'Update GRC',
            update_cmp: 'Update CMP',
            accept_difference: 'Accept Difference',
            escalate: 'Escalate',
            resolution_notes: 'Resolution Notes',
            no_tasks: 'No reconciliation tasks.',
            resolve_success: 'Reconciliation task resolved.',
            create_success: 'Reconciliation task created.',
            open: 'Open',
            resolved: 'Resolved',
            escalated: 'Escalated',
            dashboard_open: 'Open Tasks',
            dashboard_resolved: 'Resolved Tasks',
            dashboard_escalated: 'Escalated Tasks',
            page_title: 'Mismatch Reconciliation',
            my_tasks: 'My Tasks',
            all_tasks: 'All Tasks',
            severity: 'Severity',
        },
        sync: {
            title: 'CMP Sync',
            direction: 'Direction',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Conflict',
            resolve_conflict: 'Resolve Conflict',
            no_events: 'No sync events.',
            resolve_success: 'Sync conflict resolved.',
            create_success: 'Sync event recorded.',
            page_title: 'CMP Sync',
            detect_drift: 'Detect Conflicts',
            detecting: 'Detecting...',
            drift_result: '{detected} conflict(s) detected out of {total} purpose(s) compared.',
            no_drift: 'No new conflicts detected.',
            drift_error: 'Failed to run conflict detection.',
            resolve_modal_title: 'Resolve Conflict',
            resolve_field: 'Field',
            resolve_grc_value: 'GRC Value',
            resolve_cmp_value: 'CMP Value',
            accept_cmp: 'Accept CMP Value',
            accept_cmp_desc: 'Update GRC to match the CMP — the CMP value becomes the new GRC value.',
            keep_grc: 'Keep GRC Value',
            keep_grc_desc: 'Close this conflict without changing GRC — notify the CMP owner to align.',
            resolve_applied: 'Conflict resolved. GRC purpose updated to match CMP.',
        },
    },
    // ---------- Assessments module (Sprint 1.5.0) ----------
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // Sprint 48.1.9 — Dossier pre-flight + auto-assembly panel
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'This readiness report is advisory. It does not approve, assemble, materialise, file, or send the dossier.',
            run_label: 'Run pre-flight',
            running: 'Running pre-flight…',
            assemble_label: 'Auto-assemble package',
            assembling: 'Assembling…',
            verdict_label: 'Pre-flight verdict',
            tokens_label: '≈ {count} tokens',
            remediation_label: 'Remediation',
            no_gaps: 'No pre-flight gaps detected. The filing is ready to assemble.',
            override_label: 'Override block verdict — assembly will be audit-stamped.',
            accept_label: 'Accept report',
            reject_label: 'Reject report',
            assembled_label: 'Submission package assembled',
            package_id_label: 'Package id',
            package_filename_label: 'Filename',
            package_hash_label: 'Package hash',
            evidence_count_label: '{count} evidence items bundled',
            error_generic: 'Could not run pre-flight or assemble the package. Try again or check the filing status.',
        },
        errors: {
            fetch: 'Failed to load assessments.',
            save: 'Failed to save assessment.',
            fetch_gap_report_failed: 'Failed to fetch gap report.',
            fetch_transfers_failed: 'Failed to fetch transfers.',
            save_transfer_failed: 'Failed to save transfer.',
            submit_review_failed: 'Failed to submit for review.',
            process_decision_failed: 'Failed to process decision.',
            deprecate_failed: 'Failed to deprecate.',
            evaluate_risk_failed: 'Failed to evaluate risk.',
            fetch_safeguards_failed: 'Failed to fetch safeguards.',
            save_safeguard_failed: 'Failed to save safeguard.',
            update_safeguard_status_failed: 'Failed to update safeguard status.',
        },
        nav: {
            dpias: 'DPIAs',
            transfers: 'Transfer Assessments',
            dashboard: 'Dashboard',
            gap_report: 'Gap Report',
        },
        risk_level_critical: 'Critical Risk',
        risk_level_high: 'High Risk',
        risk_level_medium: 'Medium Risk',
        risk_level_low: 'Low Risk',
        severity_critical: 'Critical severity',
        severity_high: 'High severity',
        severity_medium: 'Medium severity',
        severity_low: 'Low severity',
        overdue: 'Overdue',
        framework_filter_label: 'Framework',
        aria_filter_by_framework: 'Filter by framework',
        dashboard: {
            title: 'Assessment Dashboard',
            page_title: 'Assessments Dashboard',
            announce_loaded: 'Assessment dashboard loaded',
            announce_chart_view: 'Showing chart view',
            announce_table_view: 'Showing data table view',
            aria_metrics_table: 'Assessment dashboard metrics',
            metric_col: 'Metric',
            value_col: 'Value',
            dpia_prefix: 'DPIA',
            transfer_prefix: 'Transfer',
            risk_prefix: 'Risk',
            aria_dpias_by_status: 'DPIAs by status',
            aria_transfers_by_status: 'Transfers by status',
            aria_top_risks: 'Top risks',
            aria_top_risks_table: 'Top risks across DPIAs',
            overdue_dpias_attention: '{count} overdue DPIA assessments requiring attention',
            no_overdue_dpias: 'No overdue DPIA assessments',
            overdue_transfers_attention: '{count} overdue transfer assessments requiring attention',
            no_overdue_transfers: 'No overdue transfer assessments',
            dpias_overview: 'DPIAs Overview',
            transfers_overview: 'Transfer Assessments Overview',
            overdue_items: 'Overdue Items',
            overdue_dpias: 'Overdue DPIAs',
            overdue_transfers: 'Overdue Transfers',
            count: 'Count',
            no_data: 'No data available.',
            no_risks: 'No risks found.',
            no_dashboard_data: 'No dashboard data available.',
            fetch_error: 'Failed to fetch dashboard data. Please try again.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DPIAs',
            announce_submitted_for_review: 'DPIA submitted for review',
            announce_deprecated: 'DPIA deprecated',
            announce_risks_loaded: '{count} risk(s) loaded',
            announce_mitigations_loaded: '{count} mitigation(s) loaded',
            announce_risk_deleted: 'Risk deleted',
            announce_mitigation_status_updated: 'Mitigation status updated',
            aria_filter: 'Filter DPIAs',
            aria_filter_by_status: 'Filter by status',
            aria_filter_by_lead: 'Filter by assessment lead',
            aria_table: 'Data Protection Impact Assessments',
            announce_found: '{count} DPIA(s) found',
            announce_decision: 'DPIA {decision}',
            mitigation_start: 'Start',
            mitigation_mark_implemented: 'Mark Implemented',
            mitigation_verify: 'Verify',
            title: 'Data Protection Impact Assessments',
            new: 'New DPIA',
            edit: 'Edit DPIA',
            dpia_title: 'Title',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Activity',
            risk_score: 'Risk Score',
            risks: 'Risks',
            mitigations: 'Mitigations',
            add_risk: 'Add Risk',
            add_mitigation: 'Add Mitigation',
            necessity: 'Necessity / Proportionality',
            likelihood: 'Likelihood',
            impact: 'Impact',
            severity_label: 'Severity',
            purpose: 'Purpose',
            consultation_notes: 'Consultation Notes',
            dpo_opinion: 'DPO Opinion',
            processing_activity_ids: 'Processing Activity IDs (comma-separated)',
            system_ids: 'System IDs (comma-separated)',
            vendor_ids: 'Vendor IDs (comma-separated)',
            data_types: 'Data Types (comma-separated)',
            data_types_short: 'Data Types',
            data_subjects: 'Data Subjects (comma-separated)',
            assessment_lead_id: 'Assessment Lead ID',
            assessment_lead: 'Assessment Lead',
            dpo_id: 'DPO ID',
            reviewer_ids: 'Reviewer IDs (comma-separated)',
            approval_mode: 'Approval Mode',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Require DPO Sign-off',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Review Date',
            submit_for_review: 'Submit for Review',
            approve_reject: 'Approve/Reject',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Deprecate',
            deprecated: 'Deprecated',
            hide_risks: 'Hide Risks',
            hide_mitigations: 'Hide Mitigations',
            comments: 'Comments',
            submit_decision: 'Submit Decision',
            category_label: 'Category',
            category_data_breach: 'Data Breach',
            category_unauthorized_access: 'Unauthorized Access',
            category_function_creep: 'Function Creep',
            category_re_identification: 'Re-identification',
            category_discrimination: 'Discrimination',
            category_surveillance: 'Surveillance',
            category_other: 'Other',
            affected_rights: 'Affected Rights (comma-separated)',
            no_risks: 'No risks found.',
            no_mitigations: 'No mitigations found.',
            no_dpias: 'No DPIAs found.',
            mitigation_type: 'Type',
            mitigation_technical: 'Technical',
            mitigation_organizational: 'Organizational',
            mitigation_contractual: 'Contractual',
            risk_ids: 'Risk IDs (comma-separated)',
            responsible_party: 'Responsible Party',
            responsible_party_placeholder: 'Or enter name manually',
            affected_rights_placeholder: 'Selected rights appear here',
            affected_rights_freetext_placeholder: 'Enter affected rights (comma-separated)',
            responsible: 'Responsible',
            target_date: 'Target Date',
            created: 'Created',
            fetch_error: 'Failed to fetch DPIAs. Please try again.',
            save_error: 'Failed to save DPIA. Please try again.',
            submit_review_error: 'Failed to submit DPIA for review. Please try again.',
            decision_error: 'Failed to process decision. Please try again.',
            deprecate_error: 'Failed to deprecate DPIA. Please try again.',
            fetch_risks_error: 'Failed to fetch risks. Please try again.',
            save_risk_error: 'Failed to save risk. Please try again.',
            delete_risk_error: 'Failed to delete risk. Please try again.',
            fetch_mitigations_error: 'Failed to fetch mitigations. Please try again.',
            save_mitigation_error: 'Failed to save mitigation. Please try again.',
            update_mitigation_status_error: 'Failed to update mitigation status. Please try again.',
            link_form_template: 'Link Regulatory Form Template',
            link_form_template_placeholder: 'Select a DPIA form template (optional)',
            link_form_template_none: 'None',
            linked_form: 'Linked Form',
            linked_form_creating: 'Creating linked form...',
            linked_form_error: 'Failed to create linked form instance. The DPIA was created without a linked form.',
            view_linked_form: 'View Form',
            announce_created_with_form: 'DPIA created with linked form instance',
            creation_mode: 'DPIA Creation Mode',
            mode_standard: 'Standard DPIA',
            mode_template: 'Use Regulatory Form Template',
            select_form_template: 'Select Form Template',
            create_and_fill_form: 'Create & Fill Form',
            template_sections_title: 'Template Sections',
            field_singular: 'field',
            field_plural: 'fields',
            no_sections: 'No sections found in this template.',
            template_mode_hint: 'After creation, you will be redirected to fill out the full regulatory form.',
            linked_dossier_filings: 'Linked Dossier Filings',
            no_linked_dossier_filings: 'No dossier filings linked to this DPIA.',
            view_dossier_filing: 'View Filing',
            hide_dossier_filings: 'Hide Dossier Filings',
            // Sprint 41.3.0 F5 — supporting evidence
            supporting_evidence: 'Supporting Evidence',
            hide_supporting_evidence: 'Hide Evidence',
            no_supporting_evidence: 'No supporting evidence attached.',
            upload_supporting_evidence: 'Upload supporting evidence',
            view_form_responses: 'View Form Responses',
            regulatory_framework: 'Regulatory Framework',
            all_frameworks: 'All Frameworks',
            no_templates_for_market: 'No templates available for the selected market. Please select a different market or create a template.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Data Transfers',
            announce_found: '{count} transfer assessment(s) found',
            announce_submitted_for_review: 'Transfer submitted for review',
            announce_deprecated: 'Transfer deprecated',
            announce_decision: 'Transfer {decision}',
            announce_risk_eval_completed: 'Risk evaluation completed',
            announce_safeguard_status_updated: 'Safeguard status updated',
            announce_safeguards_loaded: '{count} safeguard(s) loaded',
            aria_filter: 'Filter transfer assessments',
            aria_filter_by_status: 'Filter by status',
            aria_filter_by_mechanism: 'Filter by transfer mechanism',
            aria_table: 'Data Transfer Assessments',
            title: 'Cross-Border Transfer Assessments',
            new: 'New Transfer',
            edit: 'Edit Transfer',
            create_success: 'Transfer assessment created.',
            update_success: 'Transfer assessment updated.',
            name: 'Name',
            from_country: 'From Country',
            to_country: 'To Country',
            safeguards: 'Safeguards',
            risk_level: 'Risk Level',
            assessment_notes: 'Assessment Notes',
            mechanism: 'Mechanism',
            transfer_mechanism: 'Transfer Mechanism',
            transfer_mechanism_detail: 'Transfer Mechanism Detail',
            all_mechanisms: 'All Mechanisms',
            mechanism_other: 'Other',
            data_types: 'Data Types (comma-separated)',
            data_subjects: 'Data Subjects (comma-separated)',
            purpose: 'Purpose',
            recipient_name: 'Recipient Name',
            recipient_details: 'Recipient Details',
            recipient_details_placeholder: 'Recipient role, location, systems, and onward-transfer details',
            vendor_id: 'Vendor ID',
            processing_activity_ids: 'Processing Activity IDs (comma-separated)',
            reviewer_ids: 'Reviewer IDs (comma-separated)',
            approval_mode: 'Approval Mode',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Require DPO Sign-off',
            review_date: 'Review Date',
            created: 'Created',
            deprecated: 'Deprecated',
            submit_for_review: 'Submit for Review',
            approve_reject: 'Approve/Reject',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Deprecate',
            risk_eval: 'Risk Eval',
            hide_safeguards: 'Hide Safeguards',
            // Sprint 41.3.0 F6 — mechanism proof
            mechanism_proof: 'Mechanism Proof',
            hide_mechanism_proof: 'Hide Mechanism Proof',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'No mechanism proof attached.',
            upload_mechanism_proof: 'Upload mechanism proof',
            comments: 'Comments',
            submit_decision: 'Submit Decision',
            data_sensitivity: 'Data Sensitivity (1-5)',
            country_risk: 'Country Risk (1-5)',
            safeguard_adequacy: 'Safeguard adequacy (1 = weak, 5 = strong; a higher rating lowers the risk)',
            volume_factor: 'Volume Factor (1-5)',
            evaluate: 'Evaluate',
            add_safeguard: 'Add Safeguard',
            responsible_party: 'Responsible Party',
            responsible_party_placeholder: 'Or enter name manually',
            responsible: 'Responsible',
            implementation_date: 'Implementation Date',
            implementation: 'Implementation',
            expiry_date: 'Expiry Date',
            expiry: 'Expiry',
            expire: 'Expire',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'No safeguards found.',
            no_transfers: 'No transfer assessments found.',
            fetch_error: 'Failed to fetch transfer assessments. Please try again.',
            save_error: 'Failed to save transfer assessment. Please try again.',
            submit_review_error: 'Failed to submit for review. Please try again.',
            decision_error: 'Failed to process decision. Please try again.',
            deprecate_error: 'Failed to deprecate transfer. Please try again.',
            risk_eval_error: 'Failed to evaluate risk. Please try again.',
            fetch_safeguards_error: 'Failed to fetch safeguards. Please try again.',
            save_safeguard_error: 'Failed to save safeguard. Please try again.',
            update_safeguard_status_error: 'Failed to update safeguard status. Please try again.',
            safeguard_type_encryption_in_transit: 'Encryption in Transit',
            safeguard_type_encryption_at_rest: 'Encryption at Rest',
            safeguard_type_access_controls: 'Access Controls',
            safeguard_type_contractual_clauses: 'Contractual Clauses',
            safeguard_type_data_minimization: 'Data Minimization',
            safeguard_type_pseudonymization: 'Pseudonymization',
            safeguard_type_audit_rights: 'Audit Rights',
            safeguard_type_breach_notification: 'Breach Notification',
            safeguard_type_data_localization: 'Data Localization',
            safeguard_type_other: 'Other',
            activate: 'Activate',
            all_statuses: 'All Statuses',
            any: 'Any',
            mechanism_detail: 'Mechanism Detail',
            safeguard_types: {
                encryption_in_transit: 'Encryption in Transit',
                encryption_at_rest: 'Encryption at Rest',
                access_controls: 'Access Controls',
                contractual_clauses: 'Contractual Clauses',
                data_minimization: 'Data Minimization',
                pseudonymization: 'Pseudonymization',
                audit_rights: 'Audit Rights',
                breach_notification: 'Breach Notification',
                data_localization: 'Data Localization',
                other: 'Other',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Assessment Gap Report',
            announce_loaded: 'Gap report loaded. {count} total gap(s) identified',
            aria_activities_without_dpia: 'Activities without DPIA assessment',
            // Sprint 44.7.0 — unclassified DPIA requirement section
            aria_activities_unclassified: 'Activities with unclassified DPIA requirement',
            activities_unclassified_section_title: 'Activities with unclassified DPIA requirement',
            activities_unclassified_description: 'These activities have not been reviewed to determine whether a DPIA is legally required. Classify each to clear the signal.',
            activities_unclassified_empty: 'All active activities have been classified.',
            aria_unmitigated_risks: 'Unmitigated high and critical risks',
            aria_transfers_without_safeguards: 'Transfers without adequate safeguards',
            aria_expired_assessments: 'Expired assessments requiring renewal',
            title: 'Assessment Gap Report',
            no_dpia: 'Activities Without DPIA',
            no_assessment: 'Transfers Without Assessment',
            unmitigated: 'DPIAs with Unmitigated Risks',
            activity_id: 'Activity ID',
            dpia_id: 'DPIA ID',
            assessment_id: 'Assessment ID',
            expired_assessments: 'Expired Assessments',
            no_gaps_activities: 'No gaps found. All activities have DPIAs.',
            no_unmitigated_risks: 'No unmitigated high/critical risks found.',
            no_gaps_safeguards: 'No gaps found. All transfers have adequate safeguards.',
            no_expired: 'No expired assessments found.',
            no_report_data: 'No gap report data available.',
            fetch_error: 'Failed to fetch gap report. Please try again.',
            no_expired_assessments: 'No expired assessments.',
            no_gaps_dpia: 'No DPIA gaps found.',
            no_data: 'No data available.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dossier Filing Detail',
            page_title_list: 'Dossier Filing Cases',
            announce_filings_found: '{count} filing(s) found',
            announce_results: '{count} results',
            aria_filter_by_status: 'Filter by status',
            title: 'Dossier Filing Cases',
            nav_label: 'Dossier Filings',
            create: 'Create Dossier Filing',
            form_type: 'Form Type',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Reference',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Submitted Date',
            outcome: 'Outcome',
            parent_filing: 'Parent',
            no_filings: 'No dossier filings found.',
            fetch_error: 'Failed to fetch dossier filings. Please try again.',
            save_error: 'Failed to create dossier filing. Please try again.',
            created: 'Dossier filing created.',
            loaded: 'Dossier filing loaded.',
            not_found: 'Dossier filing not found.',
            back_to_list: 'Back to list',
            section_assignments: 'Section Assignments',
            assign_sections: 'Assign Sections',
            section_id: 'Section',
            department: 'Department',
            assignee: 'Assignee',
            deadline: 'Deadline',
            no_assignments: 'No section assignments yet.',
            sections_assigned_msg: 'Sections assigned.',
            assign_error: 'Failed to assign sections. Please try again.',
            lifecycle: 'Filing Lifecycle',
            submission: 'Submission Details',
            submission_channel: 'Submission Channel',
            confirmation_number: 'Confirmation Number',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Submit for Review',
            submitted_for_review: 'Submitted for internal review.',
            submit_review_error: 'Failed to submit for review. Please try again.',
            legal_signoff: 'Legal Signoff',
            legal_signoff_done: 'Legal signoff completed.',
            legal_signoff_error: 'Failed to complete legal signoff. Please try again.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Failed to approve filing. Please try again.',
            // Sprint 44.4.0 — submission artifact materialisation status + retry
            materialisation_status_column: 'Submission artifact',
            materialisation_na: 'Not applicable',
            materialisation_pending: 'Pending',
            materialisation_success: 'Materialised',
            materialisation_failed: 'Failed',
            materialisation_banner_title: 'Submission artifact could not be created',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Retry',
            materialisation_retry_count: 'Previous retry attempts:',
            materialisation_retry_success: 'Submission artifact materialised successfully.',
            materialisation_retry_announce: 'Retry succeeded',
            filter_materialisation_failed: 'Show only materialisation failures',
            column_title: 'Title',
            column_form_type: 'Form',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Supplement Request',
            supplement_requests: 'Supplement Requests',
            supplement_response: 'Supplement Response',
            record_supplement: 'Record Supplement Request',
            supplement_recorded: 'Supplement request recorded.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Failed to record supplement request. Please try again.',
            submit_supplement: 'Submit Supplement Response',
            supplement_submitted_msg: 'Supplement response submitted.',
            supplement_submit_error: 'Failed to submit supplement response. Please try again.',
            response_status: 'Response Status',
            response_notes: 'Response Notes',
            resubmit: 'Resubmit',
            resubmitted_msg: 'Dossier resubmitted.',
            resubmit_error: 'Failed to resubmit dossier. Please try again.',
            outcome_error: 'Failed to record outcome. Please try again.',
            accepted: 'Accepted',
            rejected: 'Rejected',
            close: 'Close',
            closed_msg: 'Filing closed.',
            close_error: 'Failed to close filing. Please try again.',
            change_filing: 'Change this filing',
            change_filing_created: 'Revision created.',
            change_filing_error: 'Failed to create change filing. Please try again.',
            change_reason: 'Reason for Change',
            filing_lineage: 'Filing Lineage',
            timers: 'Active Timers',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Supplement Deadline',
            timer_resubmission_review: 'Resubmission Deadline',
            timer_department_input: 'Department Deadline',
            days_remaining: 'days remaining',
            days_overdue: 'days overdue',
            cover_letter: 'Cover Letter',
            checklist: 'Checklist',
            evidence_index: 'Evidence Index',
            status_draft: 'Draft',
            status_internal_review: 'Internal Review',
            status_legal_signoff: 'Legal Signoff',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Supplement Requested',
            status_supplement_submitted: 'Supplement Submitted',
            status_resubmitted: 'Resubmitted',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Closed',
            // Sprint 62.57.0 (gate E12.5 pass 1) — the lifecycle stepper's step names. A
            // completed step is a stage the filing went through, so it never reads
            // "not filed" or "no result recorded" once the filing is past it.
            step_draft: 'Draft',
            step_internal_review: 'Internal Review',
            step_legal_signoff: 'Legal Signoff',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Supplement Requested',
            step_supplement_submitted: 'Supplement Submitted',
            step_resubmitted: 'Resubmitted',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Closed',
            linked_dpia: 'Linked DPIA',
            linked_dpia_none: 'No DPIA linked to this filing.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'View DPIA',
            linked_dpia_label: 'This dossier filing is linked to the following DPIA:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        // Sprint 44.4.0 — submission artifact health (admin surface)
        materialisation_health: {
            nav_label: 'Submission artifact health',
            page_title: 'Submission Artifact Health',
            page_description: 'Detect and remediate dossier materialisation issues. Lists are scoped to your organization.',
            failed_title: 'Failed materialisations',
            failed_empty: 'No failed materialisations — good.',
            pending_title: 'Approved but not materialised',
            pending_empty: 'All approved filings have a backing dossier.',
            orphan_title: 'Orphan dossiers',
            orphan_description: 'Dossiers created by materialisation that are no longer referenced by any filing — typically the loser of a concurrent race. Listed for investigation; cleanup is manual.',
            orphan_empty: 'No orphan dossiers detected.',
            materialise_button: 'Materialise now',
            column_failure_reason: 'Failure reason',
            column_failed_at: 'Failed at',
            column_retries: 'Retries',
            column_approved_at: 'Approved at',
            column_dossier_id: 'Dossier ID',
            column_source_filing_id: 'Source filing',
            column_dossier_type: 'Type',
            column_created_at: 'Created at',
        },
        // Sprint 62.35.0 (Slice C) — the pre-collection protection assessment.
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            // Gate E12.5 pass 2 (P2-5b) — the form was re-read after a conflict and
            // now shows the saved version; the operator's unsaved entries are gone.
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            // Gate E12.5 pass 2 (P2-5c) — why New assessment is unavailable.
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    // ---------- Classification module (Sprint 1.6.0) ----------
    classification: {
        access_denied_title: 'Access Denied',
        access_denied: 'You do not have the required permissions to access the Classification module. Contact your administrator.',
        nav: {
            taxonomies: 'Taxonomies',
            labeling: 'Labeling',
            lifecycle: 'Lifecycle',
            gaps: 'Gap Report',
            exports: 'Exports',
        },
        exports: {
            title: 'Classification Exports',
            csv_section: 'CSV Exports',
            pdf_section: 'PDF Reports',
            inventory: 'Inventory',
            classification_register: 'Classification Register',
            raci: 'RACI Matrix',
            exceptions: 'Exceptions',
            evidence_pack: 'Evidence Pack',
            delta_report: 'Delta Report',
            executive_summary: 'Executive Summary',
            exporting: 'Exporting…',
            export_success: 'Export completed successfully.',
            export_error: 'Export failed. Please try again.',
            csv_download: 'CSV download ready.',
            pdf_download: 'PDF generation started.',
            no_data: 'No data available for export.',
        },
        delta: {
            title: 'Delta Drill-Down',
            added: 'Added',
            removed: 'Removed',
            changed: 'Changed',
            added_count: 'Added ({count})',
            removed_count: 'Removed ({count})',
            changed_count: 'Changed ({count})',
            object_id: 'Object ID',
            object_type: 'Object Type',
            taxonomy: 'Taxonomy',
            level: 'Level',
            previous: 'Previous',
            current: 'Current',
            no_records: 'No records in this section.',
            empty_section: 'This section is empty.',
        },
        schedule: {
            title: 'Schedule Discovery Cycle',
            enabled: 'Enabled',
            interval_days: 'Interval (days)',
            interval_label: 'Run every N days',
            manual_only: 'Manual only',
            every_n_days: 'Every {n} days',
            save: 'Save Schedule',
            cancel: 'Cancel',
            schedule_updated: 'Schedule updated successfully.',
        },
        taxonomies: {
            title: 'Classification Taxonomies',
            new: 'New Taxonomy',
            edit: 'Edit Taxonomy',
            name: 'Name',
            levels: 'Classification Levels',
            seed_defaults: 'Seed Defaults',
            scope: 'Scope',
            scope_organization: 'Organization',
            scope_module_specific: 'Module Specific',
            deprecate: 'Deprecate',
            hide_levels: 'Hide Levels',
            versions: 'Versions',
            hide_versions: 'Hide Versions',
            add_level: 'Add Level',
            new_level: 'New Level',
            edit_level: 'Edit Level',
            code: 'Code',
            label: 'Label',
            color: 'Color',
            ordinal: 'Ordinal',
            handling_requirements: 'Handling Requirements',
            version_history: 'Version History',
            changes: 'Changes',
            failed_fetch: 'Failed to fetch taxonomies. Please try again.',
            failed_save: 'Failed to save taxonomy. Please try again.',
            failed_activate: 'Failed to activate taxonomy. Please try again.',
            failed_deprecate: 'Failed to deprecate taxonomy. Please try again.',
            failed_seed: 'Failed to seed default taxonomies. Please try again.',
            failed_fetch_levels: 'Failed to fetch taxonomy levels. Please try again.',
            failed_save_level: 'Failed to save taxonomy level. Please try again.',
            failed_delete_level: 'Failed to delete taxonomy level. Please try again.',
            failed_fetch_versions: 'Failed to fetch taxonomy versions. Please try again.',
            loaded_count: 'Loaded {count} taxonomies',
            defaults_seeded: 'Default taxonomies seeded.',
            no_levels: 'No classification levels defined — add levels to define your classification hierarchy.',
            no_versions: 'No version history yet — versions are created when an active taxonomy is updated.',
        },
        labeling: {
            title: 'Data Labeling',
            new: 'New Label',
            edit: 'Edit Label',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            taxonomy: 'Taxonomy',
            classification_level: 'Classification Level',
            applied_by: 'Applied By',
            applied_at: 'Applied At',
            bulk_apply: 'Bulk Apply',
            bulk_remove: 'Bulk Remove',
            all_taxonomies: 'All Taxonomies',
            system: 'System',
            dataset: 'Dataset',
            level_code_placeholder: 'Level code...',
            select_taxonomy: 'Select taxonomy...',
            select_level: 'Select level...',
            rationale: 'Rationale',
            review_date: 'Review Date',
            review: 'Review',
            selected: 'selected',
            select_all_labels: 'Select all labels',
            loaded_count: 'Loaded {count} labels',
            hide_stale_labels: 'Hide Stale Labels',
            show_conflicts: 'Show Conflicts',
            hide_conflicts: 'Hide Conflicts',
            label_conflicts: 'Label Conflicts',
            conflict_reason: 'Conflict Reason',
            labels: 'Labels',
            failed_fetch: 'Failed to fetch labels. Please try again.',
            failed_fetch_taxonomies: 'Failed to fetch taxonomies. Please try again.',
            failed_fetch_levels: 'Failed to fetch taxonomy levels. Please try again.',
            failed_save: 'Failed to save label. Please try again.',
            failed_review: 'Failed to review label. Please try again.',
            failed_bulk: 'Failed to bulk label. Please try again.',
            failed_stale: 'Failed to fetch stale labels. Please try again.',
            failed_conflicts: 'Failed to fetch label conflicts. Please try again.',
            review_permission_denied: 'You do not have permission to review this label.',
            review_not_found: 'Label not found — it may have been deleted.',
            approve_selected: 'Approve Selected',
            reject_selected: 'Reject Selected',
            approved: 'Approved',
            rejected: 'Rejected',
            labels_count: 'labels',
            failed_count: 'failed',
            confirm_approve: 'Confirm Approve',
            confirm_reject: 'Confirm Reject',
            approve_n_labels: 'Approve {count} labels?',
            reject_n_labels: 'Reject {count} labels?',
            submit_for_review: 'Submit for Review',
            approve: 'Approve',
            reject: 'Reject',
            parent_system: 'Parent System',
            parent_system_placeholder: 'Select parent system...',
            parent_system_help: 'Used for conflict detection — select the system that owns this dataset.',
            dataset_level: 'Dataset Level',
            parent_level: 'Parent Level',
            bulk_created: 'Created {created} label(s)',
            bulk_skipped: '{skipped} skipped (already labelled)',
            bulk_all_skipped: 'All {skipped} skipped — entities already have a label for this taxonomy',
            ids_hint: 'Enter one or more IDs separated by commas, e.g. system-1, system-2',
            delete_confirm: 'Delete label for "{id}"?',
        },
        lifecycle: {
            title: 'Data Lifecycle Monitoring',
            new: 'New Entry',
            current_stage: 'Current Stage',
            last_transition: 'Last Transition',
            transition: 'Transition',
            view_history: 'View History',
            stages: {
                creation: 'Creation',
                collection: 'Collection',
                processing: 'Processing',
                storage: 'Storage',
                retention: 'Retention',
                disposal: 'Disposal',
                archived: 'Archived',
                active: 'Active',
                pending_deletion: 'Pending Deletion',
                deleted: 'Deleted',
                on_hold: 'On Hold',
            },
            all_stages: 'All Stages',
            object_id: 'Object ID',
            object_type: 'Object Type',
            policy_reference: 'Policy Reference',
            retention_period_days: 'Retention Period (days)',
            retention_basis: 'Retention Basis',
            deletion_method: 'Deletion Method',
            deletion_secure_erase: 'Secure Erase',
            deletion_anonymization: 'Anonymization',
            deletion_physical_destruction: 'Physical Destruction',
            deletion_other: 'Other',
            responsible_party: 'Responsible Party',
            responsible_user_id: 'Responsible User ID',
            retention_expiry: 'Retention Expiry',
            hold_reason: 'Hold Reason',
            hold: 'Hold',
            release: 'Release',
            hide_events: 'Hide Events',
            events_timeline: 'Events Timeline',
            add_event: 'Add Event',
            event_type: 'Event Type',
            create_event: 'Create Event',
            from_stage: 'From Stage',
            to_stage: 'To Stage',
            actor: 'Actor',
            reason_placeholder: 'Reason...',
            go: 'Go',
            show_expired_retentions: 'Show Expired Retentions',
            hide_expired_retentions: 'Hide Expired Retentions',
            expired_retentions_alert: 'Expired Retentions Alert',
            failed_fetch: 'Failed to fetch lifecycle records. Please try again.',
            failed_save: 'Failed to save lifecycle record. Please try again.',
            failed_transition: 'Failed to transition lifecycle stage. Please try again.',
            failed_hold: 'Failed to place hold. Please try again.',
            failed_release: 'Failed to release hold. Please try again.',
            failed_fetch_events: 'Failed to fetch lifecycle events. Please try again.',
            failed_create_event: 'Failed to create lifecycle event. Please try again.',
            failed_expired: 'Failed to fetch expired retentions. Please try again.',
            hold_reason_prompt: 'Enter hold reason:',
            loaded_count: 'Loaded {count} lifecycle records',
            stage_transition_for: 'Stage transition for {id}',
            event_created: 'Created',
            event_updated: 'Updated',
            event_approved: 'Approved',
            event_deprecated: 'Deprecated',
            event_proposed: 'Proposed',
            event_reviewed: 'Reviewed',
            event_archived: 'Archived',
            event_restored: 'Restored',
        },
        gap_report: {
            title: 'Classification Gap Report',
            unlabeled: 'Unlabeled Data',
            missing_lifecycle: 'Missing Lifecycle',
            stale_labels: 'Stale Labels',
            run_detection: 'Run Gap Detection',
            detecting: 'Detecting...',
            detection_complete: 'Gap detection complete. {count} gap(s) detected.',
            detection_failed: 'Gap detection failed.',
            by_severity: 'By Severity',
            by_gap_type: 'By Gap Type',
            all_gap_types: 'All Gap Types',
            conflicting_labels: 'Conflicting Labels',
            expired_retention: 'Expired Retention',
            missing_handling: 'Missing Handling',
            info: 'Info',
            gap_type: 'Gap Type',
            object_id: 'Object ID',
            object_type: 'Object Type',
            detected_at: 'Detected At',
            open: 'Open',
            acknowledged: 'Acknowledged',
            remediated: 'Remediated',
            accepted: 'Accepted',
            acknowledge: 'Acknowledge',
            remediate: 'Remediate',
            accept_risk: 'Accept Risk',
            accept_risk_prompt: 'Enter risk acceptance justification:',
            failed_fetch: 'Failed to fetch gaps. Please try again.',
            failed_acknowledge: 'Failed to acknowledge gap. Please try again.',
            failed_remediate: 'Failed to remediate gap. Please try again.',
            failed_accept_risk: 'Failed to accept gap risk. Please try again.',
            failed_detect: 'Failed to run gap detection. Please try again.',
            loaded_count: 'Loaded {count} classification gaps',
            related_object_id: 'Related Object ID',
            related_object_type: 'Related Object Type',
            gap_types: {
                unlabeled_asset: 'Unlabeled Asset',
                missing_lifecycle: 'Missing Lifecycle',
                expired_retention: 'Expired Retention',
                extended_pending_deletion: 'Extended Pending Deletion',
            },
        },
        label_workflow: {
            title: 'Label Workflow',
            proposed: 'Proposed',
            under_review: 'Under Review',
            approved: 'Approved',
            deprecated: 'Deprecated',
            submit_review: 'Submit for Review',
            approve: 'Approve Label',
            deprecate: 'Deprecate',
            bulk_approve: 'Bulk Approve',
            bulk_approve_success: 'Labels approved successfully.',
            submit_success: 'Label submitted for review.',
            approve_success: 'Label approved.',
            deprecate_success: 'Label deprecated.',
        },
        review_tasks: {
            title: 'Review Tasks',
            create: 'New Review Task',
            system: 'System',
            owner: 'Owner',
            description: 'Description',
            resolution: 'Resolution',
            resolve: 'Resolve',
            approve: 'Approve',
            modify: 'Modify',
            reject: 'Reject',
            escalate: 'Escalate',
            escalated: 'Escalated Tasks',
            no_tasks: 'No review tasks.',
            resolve_success: 'Review task resolved.',
            status_pending: 'Pending',
            status_resolved: 'Resolved',
            status_escalated: 'Escalated',
            notes: 'Notes',
            create_success: 'Review task created.',
            no_tasks_detail: 'No review tasks yet — create a task to assign label reviews to system owners.',
            label_id: 'Label ID',
            system_id: 'System ID',
            system_name: 'System Name',
            owner_id: 'Owner ID',
            resolution_col: 'Resolution',
            notes_col: 'Notes',
        },
        exceptions: {
            title: 'Exception Queue',
            create: 'New Exception',
            severity: 'Severity',
            system: 'System',
            owner: 'Owner',
            resolve: 'Resolve',
            no_exceptions: 'No exceptions found.',
            resolve_success: 'Exception resolved.',
            dashboard_open: 'Open Exceptions',
            dashboard_resolved: 'Resolved Exceptions',
            dashboard_total: 'Total',
            dashboard_by_severity: 'By Severity',
            status_open: 'Open',
            status_resolved: 'Resolved',
            status_accepted_risk: 'Accepted Risk',
            create_success: 'Exception created.',
            no_exceptions_detail: 'No exceptions yet — exceptions are created from high-severity gaps that require special handling.',
            gap_id: 'Gap ID',
            system_id: 'System ID',
            system_name: 'System Name',
            owner_id: 'Owner ID',
            resolution_notes: 'Resolution Notes',
            resolution_notes_placeholder: 'Describe how this exception was resolved...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Enterprise Data Map',
            inventory: 'Data Inventory',
            total_systems: 'Total Systems',
            total_labels: 'Total Labels',
            approved_count: 'Approved Labels',
            proposed_count: 'Proposed Labels',
            no_data: 'No classification data available.',
            no_approved_labels: 'No labelled objects yet — an object appears on the data map as soon as it has a classification label, whether proposed, under review or approved.',
            loaded_count: 'Loaded enterprise data map',
        },
        discovery: {
            title: 'Discovery Cycles',
            create: 'New Cycle',
            cycle_name: 'Cycle Name',
            run_delta: 'Run Delta Detection',
            delta_success: 'Delta detection completed.',
            create_success: 'Discovery cycle created.',
            no_cycles: 'No discovery cycles.',
            status_created: 'Created',
            status_scanning: 'Scanning',
            status_review: 'Review',
            status_approved: 'Approved',
            status_published: 'Published',
            status_draft: 'Draft',
            running: 'Running',
            completed: 'Completed',
            cancelled: 'Cancelled',
            delta_result_summary: 'Found {added} added, {removed} removed, {changed} changed labels.',
            delta_results: 'Delta Results',
            view_results: 'View Results',
            scope_type: 'Scope Type',
            scope_type_all: 'All',
            scope_type_systems: 'Systems',
            scope_type_datasets: 'Datasets',
            scope_ids: 'Scope IDs',
            scope_ids_placeholder: 'Enter IDs separated by commas',
            cycle_explanation: 'A discovery cycle scans your data infrastructure to identify, classify, and track data assets. Configure the scope to target specific systems or datasets.',
            empty_state_title: 'No Discovery Cycles Yet',
            empty_state_description: 'Create your first discovery cycle to begin scanning and classifying your data assets.',
            empty_state_cta: 'Create First Cycle',
            validation_name_required: 'Cycle name is required',
            validation_scope_type_required: 'Scope type is required',
            validation_scope_ids_required: 'At least one scope ID is required',
        },
        regulatory: {
            sensitivity_importance_info: 'Data Law (Law 60/2024/QH15, req. 18e4bfd1) requires classification by both sensitivity and importance as separate dimensions. Create one taxonomy for each dimension to satisfy this requirement.',
            handling_rules_info: 'Each classification level should define handling rules per req. b89981ac ("Define handling rules by classification level").',
            template_internal_ids_info: 'All classification templates use internal IDs (e.g. vn_data_law_data_classification_record). No template is presented as an official ministry form.',
            ai_risk_tiers_info: 'AI Law (Law 134/2025/QH15, req. 3a87ad63) defines four AI system risk tiers: minimal, limited, high, and unacceptable. Create an AI Risk taxonomy with these levels.',
            ai_risk_tier_minimal: 'Minimal',
            ai_risk_tier_limited: 'Limited',
            ai_risk_tier_high: 'High',
            ai_risk_tier_unacceptable: 'Unacceptable',
            ai_register_info: 'AI classification records should link to an AI System Register entry (req. 0f4475d3) capturing: system name, owner, purpose, deployment context, risk level, vendor involvement.',
            ai_template_internal_info: 'AI classification templates use internal IDs (e.g. AI-RISK-01). No official AI placeholder IDs (AI-HC-01 through AI-HC-08) are used.',
            ai_audit_trail_info: 'AI classification changes produce a versioned audit trail per UC-VN-06: actor, timestamp, previous value, new value, and rationale.',
            lifecycle_data_d1_info: 'Lifecycle transitions map to Data Law procedure DATA-D1 steps: catalog, classify, define handling rules, capture evidence, schedule reviews.',
            lifecycle_deletion_link_info: 'A classified data asset reaching end-of-life should trigger a deletion schedule record via the Deletion Orchestration module.',
            lifecycle_evidence_info: 'Every lifecycle state change (draft, active, under-review, archived, deleted) captures actor, timestamp, and justification as an audit trail entry.',
            escalation_timers_info: 'UC-VN-10 escalation deadlines: 5 business days for owner review, 48 hours for exception escalation, 72 hours for high-risk/sensitive items.',
            escalation_owner_review: '5 business days',
            escalation_exception: '48 hours',
            escalation_high_risk: '72 hours',
            gap_risk_areas_info: 'Data Law req. f6c3060a ("Data risk assessment") requires gap analysis across 4 risk areas: privacy, cybersecurity, access control, and operational risk.',
            gap_risk_area_privacy: 'Privacy',
            gap_risk_area_cybersecurity: 'Cybersecurity',
            gap_risk_area_access: 'Access Control',
            gap_risk_area_operational: 'Operational',
            gap_framework_filter_info: 'Filter gaps by regulatory framework (VN_DATA_LAW or VN_AI_LAW_2026) to see relevant gaps for each regulatory scope.',
            framework_vn_data_law: 'VN Data Law',
            framework_vn_ai_law: 'VN AI Law 2026',
            enterprise_map_approved_info: 'This data map shows only approved classification records per UC-VN-10 workflow step 10 ("Approved labels become the current enterprise truth"). Draft and proposed records are excluded.',
            exports_bundle_info: 'Export bundles include: classification register, handling rules, ownership/RACI, and evidence pack — matching UC-VN-10 export requirements and DATA-EVD-01 evidence sheet format.',
        },
    },
    // ---------- Monitoring module (Sprint 1.7.0) ----------
    monitoring: {
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access this monitoring module.',
        errors: {
            fetch: 'Failed to load.',
            save: 'Failed to save.',
            fetch_programs_failed: 'Failed to fetch programs.',
            fetch_kpi_failed: 'Failed to fetch KPI data.',
            create_snapshot_failed: 'Failed to create snapshot.',
            fetch_periods_failed: 'Failed to fetch periods.',
            create_period_failed: 'Failed to create period.',
            auto_generate_failed: 'Failed to auto-generate periods.',
            start_period_failed: 'Failed to start period.',
            close_period_failed: 'Failed to close period.',
            save_program_failed: 'Failed to save program.',
            activate_program_failed: 'Failed to activate program.',
            pause_program_failed: 'Failed to pause program.',
            complete_program_failed: 'Failed to complete program.',
            archive_program_failed: 'Failed to archive program.',
            fetch_ric_templates_failed: 'Failed to fetch RIC templates.',
            fetch_ric_cases_failed: 'Failed to fetch RIC cases.',
            create_ric_case_failed: 'Failed to create RIC case.',
            transition_ric_failed: 'Failed to transition RIC case.',
            add_follow_up_failed: 'Failed to add follow-up.',
            respond_follow_up_failed: 'Failed to respond to follow-up.',
            link_evidence_failed: 'Failed to link evidence.',
            fetch_task_templates_failed: 'Failed to fetch task templates.',
            save_task_template_failed: 'Failed to save task template.',
            delete_task_template_failed: 'Failed to delete task template.',
            delete_framework_link_failed: 'Failed to delete framework link.',
            seed_defaults_failed: 'Failed to seed defaults.',
            fetch_tasks_failed: 'Failed to fetch tasks.',
            create_task_failed: 'Failed to create task.',
            instantiate_tasks_failed: 'Failed to instantiate tasks.',
            start_task_failed: 'Failed to start task.',
            complete_task_failed: 'Failed to complete task.',
            defer_task_failed: 'Failed to defer task.',
            block_task_failed: 'Failed to block task.',
            override_complete_failed: 'Failed to override complete.',
            assign_task_failed: 'Failed to assign task.',
            update_checklist_failed: 'Failed to update checklist.',
        },
        nav: {
            programs: 'Programs',
            periods: 'Periods',
            task_templates: 'Task Templates',
            tasks: 'Tasks',
            kpi: 'KPI Dashboard',
            ric: 'Regulator Inquiries',
            framework_link: 'Framework Monitoring',
            authority_readiness: 'Authority Readiness',
            change_review: 'Change Review',
            raci: 'RACI Matrix',
        },
        programs: {
            page_title: 'Monitoring Programs',
            title: 'Monitoring Programs',
            new: 'New Program',
            edit: 'Edit Program',
            name: 'Name',
            scope: 'Scope',
            frequency: 'Frequency',
            owner: 'Owner',
            activate: 'Activate',
            pause: 'Pause',
            resume: 'Resume',
            complete: 'Complete',
            archive: 'Archive',
            custom_frequency_days: 'Custom Frequency (days)',
            select_user: 'Select user\u2026',
            assign_training: 'Assign Training',
            linked_forms: 'Linked Forms',
            linked_forms_none: 'No linked forms yet',
            loaded_count: 'Loaded {0} programs',
            scope_labels: {
                full: 'Full',
                partial: 'Partial',
                targeted: 'Targeted',
                consent: 'Consent',
                dpia: 'DPIA',
                vendors: 'Vendors',
                classification: 'Classification',
                rights_requests: 'Rights Requests',
                data_mapping: 'Data Mapping',
                transfers: 'Transfers',
                security_monitoring: 'Security Monitoring',
                authority_response: 'Authority Response',
                evidence_preservation: 'Evidence Preservation',
                localization_review: 'Localization Review',
                ai_system_registration: 'AI System Registration',
                ai_pre_deployment: 'AI Pre-Deployment Assessment',
                ai_transparency: 'AI Transparency Check',
                ai_incident_review: 'AI Incident Review',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous, monthly, or quarterly cadences. AI Law programs typically use annual review cadences for system register updates.',
        },
        periods: {
            page_title: 'Monitoring Periods',
            title: 'Review Periods',
            new: 'New Period',
            label: 'Period Label',
            start_date: 'Start Date',
            end_date: 'End Date',
            reviewers: 'Reviewers',
            auto_generate: 'Auto-Generate Periods',
            start: 'Start',
            close: 'Close',
            change_digest: 'Change Digest',
            no_digest: 'No digest available.',
            reviewers_count: 'Reviewers Count',
            select_program: 'Select Program',
            select_program_prompt: 'Select a program to view periods.',
            hide_digest: 'Hide Digest',
            loaded_count: 'Loaded {0} periods',
            periods_auto_generated: 'Periods auto-generated',
            label_placeholder: 'e.g., 2026-Q1',
        },
        task_templates: {
            page_title: 'Task Templates',
            title: 'Task Templates',
            new: 'New Template',
            edit: 'Edit Template',
            category: 'Category',
            priority: 'Priority',
            checklist_items: 'Checklist Items',
            effort: 'Estimated Effort (hours)',
            pre_seeded: 'Pre-seeded',
            seed_defaults: 'Seed Defaults',
            add_checklist: 'Add Checklist Item',
            guidance: 'Guidance Notes',
            all_categories: 'All Categories',
            loaded_count: 'Loaded {0} task templates',
            defaults_seeded: 'Default task templates seeded',
            checklist_item_label: 'Checklist item {0}',
            remove_checklist_item: 'Remove checklist item {0}',
            category_info: 'Categories cover PDPL compliance (Consent, DPIA, Vendors, etc.), Cybersecurity Law tasks (Security Monitoring, Authority Response, etc.), and AI Law tasks (System Registration, Pre-Deployment Assessment, etc.).',
        },
        tasks: {
            page_title: 'Review Tasks',
            title: 'Review Tasks',
            new: 'New Task',
            assigned_to: 'Assigned To',
            due_date: 'Due Date',
            checklist: 'Checklist',
            start: 'Start',
            complete: 'Complete',
            defer: 'Defer',
            block: 'Block',
            override_complete: 'Override Complete',
            link_evidence: 'Link Evidence',
            // Sprint 41.3.0 F4 — direct file attachments on a monitoring task
            direct_upload_label: 'Upload evidence file',
            instantiate: 'Instantiate from Templates',
            defer_reason: 'Reason for deferral',
            override_justification: 'Justification for override',
            filter_assignee: 'Filter by assignee',
            select_period: 'Select Period',
            select_program: 'Select Program',
            select_program_period_prompt: 'Select a program and period to view tasks.',
            assign_placeholder: 'Assign to...',
            loaded_count: 'Loaded {0} tasks',
            tasks_instantiated: 'Tasks instantiated from templates',
            overdue: 'Overdue',
            checklist_for: 'checklist for',
            manage_checklist: 'Manage Checklist',
            add_checklist_item: 'Add item',
            checklist_item_label: 'Item',
            checklist_item_description: 'Description',
            remove_checklist_item: 'Remove item',
            save_checklist: 'Save Checklist',
            no_checklist_items: 'No checklist items yet.',
            // fix-sprint-20260411-D F6 — confirm dialog before deleting audit evidence
            confirm_delete_attachment: 'Are you sure you want to delete this evidence file? This cannot be undone.',
        },
        kpi: {
            page_title: 'KPI Dashboard',
            title: 'KPI Dashboard',
            kpi_name: 'KPI Name',
            current_value: 'Current Value',
            target: 'Target',
            trends: 'Trends',
            capture_snapshot: 'Capture Snapshot',
            select_program: 'Select Program',
            select_program_prompt: 'Select a program to view KPIs.',
            snapshot: 'Snapshot',
            kpi_labels: {
                on_track: 'On Track',
                at_risk: 'At Risk',
                behind: 'Behind',
                rights_request_sla_rate: 'Rights Request SLA Rate',
                vendor_review_currency: 'Vendor Review Currency',
                dpia_completion_rate: 'DPIA Completion Rate',
                classification_coverage: 'Classification Coverage',
                consent_policy_currency: 'Consent Policy Currency',
                security_event_detection_rate: 'Security Event Detection Rate',
                evidence_preservation_compliance_rate: 'Evidence Preservation Compliance Rate',
                authority_response_timeliness: 'Authority Response Timeliness',
                localization_readiness_score: 'Localization Readiness Score',
                ai_register_completeness: 'AI System Register Completeness',
                pre_deployment_gate_pass_rate: 'Pre-Deployment Gate Pass Rate',
                transparency_disclosure_coverage: 'Transparency Disclosure Coverage',
                ai_incident_readiness_score: 'AI Incident Readiness Score',
            },
            pdpl_kpi_info: 'The predefined KPI codes above (Rights Request SLA Rate, DPIA Completion Rate, Vendor Review Currency, Classification Coverage, Consent Policy Currency) are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'Cybersecurity Law KPIs: Security Event Detection Rate, Evidence Preservation Compliance Rate, Authority Response Timeliness, Localization Readiness Score.',
            ai_law_kpi_info: 'AI Law KPIs: AI System Register Completeness, Pre-Deployment Gate Pass Rate, Transparency Disclosure Coverage, AI Incident Readiness Score.',
            no_data: 'No data available.',
            snapshot_date: 'Snapshot Date',
            loaded_count: 'Loaded {0} KPI snapshots',
            snapshot_captured: 'KPI snapshot captured',
            showing_chart: 'Showing chart view',
            showing_table: 'Showing data table view',
            aria_current_values: 'KPI current values',
            kpi_label: 'KPI',
            actual: 'Actual',
            trend: 'Trend',
            notes: 'Notes',
            trend_up: 'Trending up',
            trend_down: 'Trending down',
            trend_stable: 'Stable',
        },
        ric: {
            page_title: 'RIC Cases',
            title: 'Regulator Inquiry Cases',
            new: 'New RIC Case',
            reference: 'Reference',
            regulator: 'Regulator',
            inquiry_type: 'Inquiry Type',
            deadline: 'Response Deadline',
            lead: 'Lead',
            follow_ups: 'Follow-ups',
            add_follow_up: 'Add Follow-Up',
            respond: 'Respond',
            question: 'Question',
            response: 'Response',
            asked_at: 'Asked At',
            no_follow_ups: 'No follow-ups yet.',
            received_date: 'Received Date',
            responded_at: 'Responded At',
            hide_follow_ups: 'Hide Follow-ups',
            inquiry_types: {
                general: 'General',
                investigation: 'Investigation',
                audit: 'Audit',
                complaint: 'Complaint',
                general_audit: 'General Audit',
                rights_requests_review: 'Rights Requests Review',
                cross_border_transfer_review: 'Cross-Border Transfer Review',
                vendor_governance_review: 'Vendor Governance Review',
                consent_management_review: 'Consent Management Review',
                data_mapping_ropa_review: 'Data Mapping / ROPA Review',
                incident_readiness_review: 'Incident Readiness Review',
                security_access_control_review: 'Security & Access Control Review',
                targeted_processing_review: 'Targeted Processing Review',
                bank_readiness_pack: 'Bank Readiness Pack',
            },
            link_evidence: 'Link Evidence',
            loaded_count: 'Loaded {0} RIC cases',
            to_follow_up: 'to follow-up question',
            overdue: 'Overdue',
            overdue_indicator: 'Response deadline exceeded',
            template_info: 'RIC templates are internal platform templates for structuring inquiry responses. They are not official government-mandated forms.',
            status_labels: {
                received: 'Received',
                in_triage: 'In Triage',
                evidence_gathering: 'Evidence Gathering',
                response_drafting: 'Response Drafting',
                internal_review: 'Internal Review',
                submitted: 'Submitted',
                follow_up: 'Follow Up',
                closed: 'Closed',
            },
        },
        // Sprint 21.3.0: Monitoring Programs Maturity
        framework_link: {
            title: 'Framework-Linked Monitoring',
            link_program: 'Link Program to Framework',
            framework_code: 'Framework Code',
            select_framework: 'Select a framework...',
            select_program: 'Select a program...',
            readiness_score: 'Readiness Score',
            no_links: 'No framework-linked programs found',
            seed_defaults: 'Seed Default Templates',
            program_id: 'Program ID',
            program: 'Program',
        },
        authority_readiness: {
            title: 'Authority Request Readiness',
            create_entry: 'New Readiness Entry',
            select_framework: 'Select a framework...',
            framework_code: 'Framework',
            readiness_status: 'Readiness Status',
            readiness_package: 'Readiness Package',
            run_drill: 'Run Readiness Drill',
            drill_success: 'Readiness drill recorded',
            last_drill: 'Last Drill',
            never_drilled: 'Never',
            no_readiness: 'No authority readiness data found',
            entry_created: 'Readiness entry created',
            ready: 'Ready',
            partial: 'Partial',
            not_ready: 'Not Ready',
            items: 'items',
        },
        change_review: {
            title: 'Regulatory Change Review',
            new_review: 'New Change Review',
            change_id: 'Change ID',
            select_change: 'Select a change...',
            select_framework: 'Select a framework...',
            impact_assessment: 'Impact Assessment',
            decision: 'Decision',
            no_action: 'No Action',
            internal_update: 'Internal Update',
            authority_filing: 'Authority Filing',
            no_reviews: 'No change reviews found',
            auto_cycle: 'Auto-Trigger Review Cycle',
            cadence: 'Review Cadence',
            framework: 'Framework',
        },
        // Sprint 27.7.1: Monitoring Operational Completeness
        export_csv: 'Export CSV',
        exporting: 'Exporting\u2026',
        export_success: 'CSV exported successfully.',
        export_failed: 'CSV export failed.',
        export_attestation_pack: 'Export Attestation Pack',
        staleness: {
            badge_label: '{0} stale items',
            days_since: '{0} days since last update',
            detail_title: 'Stale Items',
            accept_justification: 'Accept with Justification',
            justification_label: 'Justification',
            no_stale: 'No stale items found.',
            threshold_label: 'Staleness Threshold (days)',
            threshold_min: 'Minimum 30 days',
            threshold_max: 'Maximum 730 days',
            threshold_saved: 'Staleness threshold updated.',
        },
        csv_columns: {
            name: 'Name',
            status: 'Status',
            owner: 'Owner',
            created_at: 'Created At',
            updated_at: 'Updated At',
            scope: 'Scope',
            frameworks: 'Frameworks',
            period_label: 'Period Label',
            start_date: 'Start Date',
            end_date: 'End Date',
            task_count: 'Task Count',
            completion_rate: 'Completion Rate',
            title: 'Title',
            assigned_to: 'Assigned To',
            due_date: 'Due Date',
            priority: 'Priority',
            completed_at: 'Completed At',
            kpi_code: 'KPI Code',
            kpi_label: 'KPI Label',
            target: 'Target',
            actual: 'Actual',
            trend: 'Trend',
            reference: 'Reference',
            regulator: 'Regulator',
            inquiry_type: 'Inquiry Type',
            lead: 'Lead',
            received_date: 'Received Date',
            deadline: 'Deadline',
            framework_code: 'Framework Code',
            readiness_status: 'Readiness Status',
            package_count: 'Package Count',
            change_id: 'Change ID',
            impact_assessment: 'Impact Assessment',
            decision: 'Decision',
            program: 'Program',
            readiness_score: 'Readiness Score',
            category: 'Category',
            checklist_count: 'Checklist Count',
            effort_hours: 'Effort (hours)',
            version: 'Version',
            department: 'Department',
            function_area: 'Function Area',
            role: 'Role',
            assignee: 'Assignee',
            frequency: 'Frequency',
        },
        raci: {
            page_title: 'Governance RACI Matrix',
            title: 'Governance RACI Matrix',
            department: 'Department',
            function_area: 'Function Area',
            responsible: 'Responsible',
            accountable: 'Accountable',
            consulted: 'Consulted',
            informed: 'Informed',
            assign_role: 'Assign RACI Role',
            select_program: 'Select Program',
            select_program_prompt: 'Select a program to view RACI matrix.',
            no_assignments: 'No RACI assignments found.',
            role_updated: 'RACI assignment updated.',
            role_labels: {
                R: 'Responsible',
                A: 'Accountable',
                C: 'Consulted',
                I: 'Informed',
            },
            loaded_count: 'Loaded {0} RACI assignments',
            single_accountability_warning: 'Only one Accountable (A) role is allowed per function area. Assigning a new Accountable role will replace the existing one.',
            cybersec_function_areas: 'Cybersecurity Law function areas: Incident Response, Evidence Preservation, Authority Cooperation, Localization Review.',
            ai_law_function_areas: 'AI Law function areas: System Registration, Pre-Deployment Assessment, Transparency, Incident Handling.',
            suggested_function_areas: 'Suggested Function Areas',
        },
        prior_cycle: {
            link_label: 'Link to Prior Cycle',
            select_prior: 'Select prior cycle\u2026',
            linked: 'Year-over-Year',
            no_prior: 'No prior cycles available.',
            linked_success: 'Prior cycle linked.',
        },
        digest_table: {
            module: 'Module',
            entity_type: 'Entity Type',
            change_type: 'Change Type',
            description: 'Description',
            changed_at: 'Changed At',
        },
        view_related_ric: 'View Related RIC Cases',
        linked_program: 'Linked Program',
        nav_raci: 'RACI Matrix',
    },
    // ---------- Incidents module (Sprint 1.8.0) ----------
    incidents: {
        // Sprint 48.1.6 — Forseti authority response draft
        authority_response_draft: {
            draft_label: 'Draft response',
            drafting: 'Drafting…',
            results_heading: 'Drafted answers ({count})',
            tokens_label: '≈ {count} tokens',
            counsel_label: 'Counsel review required:',
            counsel_default: 'At least one answer requires counsel review before sending.',
            requires_counsel_label: 'Requires counsel review.',
            accept_label: 'Accept draft',
            reject_label: 'Reject draft',
            error_generic: 'Could not draft the response. Try again or refine the inquiry text.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        // Sprint 48.1.5 — Forseti breach notification timeline panel
        breach_timeline: {
            compute_label: 'Compute breach notification timeline',
            computing: 'Computing…',
            results_heading: 'Breach notification deadlines ({count})',
            tokens_label: '≈ {count} tokens',
            handoff_label: 'Forseti suggests:',
            empty_label: 'No installed framework declares a notification duty for this incident.',
            mandatory_label: 'Mandatory fields:',
            draft_label: 'Notification draft',
            remaining_label: '{count}h remaining',
            overdue_label: 'Overdue by {count}h',
            acknowledge_label: 'Acknowledge timeline',
            acknowledging: 'Acknowledging…',
            error_generic: 'Could not compute the breach timeline. Try again.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Confirmed Breach',
            no: 'Not a Breach',
            uncertain: 'Undetermined',
        },
        errors: {
            fetch: 'Failed to load incidents.',
            save: 'Failed to save incident.',
            fetch_failed: 'Failed to fetch incidents.',
            save_failed: 'Failed to save incident.',
            transition_failed: 'Failed to transition incident.',
            close_failed: 'Failed to close incident.',
            fetch_timeline_failed: 'Failed to fetch timeline.',
            fetch_decisions_failed: 'Failed to fetch decisions.',
            triage_failed: 'Failed to update triage.',
            impact_update_failed: 'Failed to update impact assessment.',
            breach_update_failed: 'Failed to update breach assessment.',
            create_timeline_failed: 'Failed to create timeline entry.',
            create_decision_failed: 'Failed to create decision.',
            notification_update_failed: 'Failed to update notification.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Failed to fetch tasks.',
            create_task_failed: 'Failed to create task.',
            update_task_status_failed: 'Failed to update task status.',
            update_checklist_failed: 'Failed to update checklist.',
            assign_task_failed: 'Failed to assign task.',
            escalate_task_failed: 'Failed to escalate task.',
            fetch_templates_failed: 'Failed to fetch templates.',
            permission_denied: 'You do not have permission to access this section. Contact your administrator to request access.',
        },
        harm_likelihood: {
            unlikely: 'Unlikely',
            possible: 'Possible',
            likely: 'Likely',
            very_likely: 'Very Likely',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'Email',
            phone: 'Phone',
            internal_system: 'Internal System',
            third_party: 'Third Party',
            automated_detection: 'Automated Detection',
        },
        task_transition_labels: {
            start: 'Start',
            complete: 'Complete',
            block: 'Block',
            defer: 'Defer',
            cancel: 'Cancel',
            unblock: 'Unblock',
        },
        transition_labels: {
            start: 'Start',
            complete: 'Complete',
            block: 'Block',
            defer: 'Defer',
        },
        nav: {
            all: 'All Incidents',
            tasks: 'Response Tasks',
            reports: 'Reports',
            breach_notifications: 'Breach Notifications',
            supplements: 'Supplements',
            remediation: 'Remediation',
            authority_requests: 'Authority Requests',
            disclosure_register: 'Disclosure Register',
        },
        list: {
            page_title: 'Incidents',
            title: 'Incidents',
            new: 'New Incident',
            edit: 'Edit Incident',
            incident_title: 'Title',
            severity: 'Severity',
            reported_at: 'Reported At',
            reported_by: 'Reported By',
            assigned_to: 'Assigned To',
            view_detail: 'View Detail',
            reference: 'Reference',
            lead: 'Lead',
            reported_via: 'Reported Via',
            discovery_date: 'Discovery Date',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Hide Details',
            close_incident: 'Close Incident',
            close_justification_prompt: 'Please provide a justification for closing this incident.',
            justification: 'Justification',
            lead_id: 'Lead ID',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Contact',
            category: 'Category',
            not_assessed: 'Not assessed',
            // fix-sprint-20260717-2118-followup F11 — dedicated section identity
            // for the New Incident modal (title/description/eyebrow all reused
            // field labels).
            section_incident_details: 'Incident details',
        },
        detail: {
            page_title: 'Incident Detail',
            title: 'Incident Detail',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Overview',
                triage: 'Triage',
                impact: 'Impact Assessment',
                breach: 'Breach Assessment',
                timeline: 'Timeline',
                decisions: 'Decisions',
                // Sprint 41.3.0 F3 — evidence attachments tab
                evidence: 'Evidence',
                audit_trail: 'Audit Trail',
            },
            // Sprint 41.3.0 F3 — evidence attachments strings
            evidence_upload_label: 'Attach evidence file',
            evidence_uploaded: 'Evidence uploaded',
            evidence_deleted: 'Evidence deleted',
            evidence_delete_error: 'Failed to delete evidence',
            evidence_uploaded_at: 'Uploaded',
            evidence_empty: 'No evidence files attached to this incident.',
            evidence_size: 'Size',
            triage_notes: 'Triage Notes',
            initial_classification: 'Initial Classification',
            individuals_affected: 'Number of Individuals Affected',
            data_categories: 'Data Categories Involved',
            systems_compromised: 'Systems Compromised',
            geographic_scope: 'Geographic Scope',
            breach_determination: 'Breach Determination',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Notification Required',
            notification_deadline: 'Notification Deadline',
            pdpc_reportable: 'PDPC Reportable',
            add_timeline: 'Add Timeline Entry',
            add_decision: 'Add Decision',
            not_found: 'Incident not found.',
            team_members: 'Team Members',
            checklist: 'Checklist',
            completed: 'Completed',
            impact_severity_score: 'Calculated Impact Severity Score',
            records_affected: 'Records Affected',
            potential_consequences: 'Potential Consequences',
            harm_likelihood: 'Harm Likelihood',
            legal_basis: 'Legal Basis',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdictions',
            jurisdictions_comma: 'Jurisdictions (comma-separated)',
            jurisdiction: 'Jurisdiction',
            authority_status: 'Authority Status',
            subject_status: 'Subject Status',
            authority_deadline: 'Authority Deadline',
            subject_deadline: 'Subject Deadline',
            mark_authority_done: 'Mark Authority Done',
            mark_subject_done: 'Mark Subject Done',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — affected parties beyond the data subjects
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Not assessed',
            event_type: 'Event Type',
            timestamp: 'Timestamp',
            actor: 'Actor',
            no_timeline_events: 'No timeline events yet.',
            decision_type: 'Decision Type',
            decision: 'Decision',
            rationale: 'Rationale',
            decision_made: 'Decision Made',
            no_decisions: 'No decisions recorded yet.',
            no_audit_events: 'No audit events recorded yet.',
            export_pdf: 'Export PDF',
            pdf_title: 'Incident Summary Report',
            description: 'Description',
            linked_records: 'Linked Records',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Incident Response Tasks',
            title: 'Incident Response Tasks',
            new: 'New Task',
            edit: 'Edit Task',
            task_type: 'Task Type',
            assignee: 'Assignee',
            due_date: 'Due Date',
            dependencies: 'Dependencies',
            mark_complete: 'Mark Complete',
            templates: 'Task Templates',
            select_incident: 'Select Incident',
            all_statuses: 'All Statuses',
            all_categories: 'All Categories',
            new_response_task: 'New Response Task',
            category: 'Category',
            priority: 'Priority',
            depends_on: 'Depends On (comma-separated task IDs)',
            checklist_items: 'Checklist Items',
            remove: 'Remove',
            add: 'Add',
            new_checklist_item: 'New checklist item',
            no_templates: 'No templates available.',
            assign_task: 'Assign Task',
            assign_to: 'Assign To',
            assign: 'Assign',
            select_incident_prompt: 'Please select an incident to view its response tasks.',
            checklist: 'Checklist',
            hide_checklist: 'Hide Checklist',
            escalate: 'Escalate',
            escalations: 'Escalations',
            no_checklist_items: 'No checklist items.',
        },
        reports: {
            page_title: 'Incident Reports',
            title: 'Incident Reports',
            summary: 'Summary Statistics',
            by_severity: 'By Severity',
            by_status: 'By Status',
            recent: 'Recent Incidents',
            total_incidents: 'Total Incidents',
            open: 'Open',
            closed: 'Closed',
            export_csv: 'Export CSV',
            export_pdf: 'Export PDF',
            export_success: 'Report exported successfully.',
            export_error: 'Failed to export report.',
            reference: 'Reference',
            view_report: 'View Report',
        },
        statuses: {
            reported: 'Reported',
            triage: 'Triage',
            contained: 'Contained',
            investigation: 'Investigation',
            remediation: 'Remediation',
            closed: 'Closed',
            false_positive: 'False Positive',
        },
        task_statuses: {
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            blocked: 'Blocked',
            cancelled: 'Cancelled',
        },
        severity: {
            low: 'Low',
            medium: 'Medium',
            high: 'High',
            critical: 'Critical',
        },
        task_types: {
            containment: 'Containment',
            investigation: 'Investigation',
            remediation: 'Remediation',
            communication: 'Communication',
            documentation: 'Documentation',
            notification: 'Notification',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Data Breach',
            unauthorized_access: 'Unauthorized Access',
            data_loss: 'Data Loss',
            system_compromise: 'System Compromise',
            malware: 'Malware',
            phishing: 'Phishing',
            insider_threat: 'Insider Threat',
            other: 'Other',
        },
        reported_via_options: {
            email: 'Email',
            phone: 'Phone',
            internal_system: 'Internal System',
            third_party: 'Third Party',
            automated_detection: 'Automated Detection',
        },
        harm_likelihood_options: {
            unlikely: 'Unlikely',
            possible: 'Possible',
            likely: 'Likely',
            very_likely: 'Very Likely',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmed Breach',
            suspected_breach: 'Suspected Breach',
            not_a_breach: 'Not a Breach',
            pending: 'Pending',
        },
        timeline_event_types: {
            detection: 'Detection',
            escalation: 'Escalation',
            containment: 'Containment',
            investigation_start: 'Investigation Start',
            evidence_collected: 'Evidence Collected',
            notification_sent: 'Notification Sent',
            remediation_action: 'Remediation Action',
            status_change: 'Status Change',
            communication: 'Communication',
            other: 'Other',
        },
        decision_types: {
            containment: 'Containment',
            notification: 'Notification',
            remediation: 'Remediation',
            escalation: 'Escalation',
            closure: 'Closure',
            other: 'Other',
        },
        task_transitions: {
            start: 'Start',
            complete: 'Complete',
            block: 'Block',
            cancel: 'Cancel',
            unblock: 'Unblock',
        },
        announce: {
            loaded: '{count} incidents loaded',
            reports_loaded: '{count} incident reports loaded',
            tasks_loaded: '{count} tasks loaded',
            created: 'Incident created successfully',
            updated: 'Incident updated successfully',
            closed: 'Incident closed successfully',
            status_changed: 'Incident status changed',
            triage_completed: 'Triage completed successfully',
            impact_updated: 'Impact assessment updated',
            breach_updated: 'Breach assessment updated',
            timeline_added: 'Timeline event added',
            decision_recorded: 'Decision recorded',
            notification_updated: 'Notification status updated',
            task_created: 'Task created successfully',
            task_status_changed: 'Task status changed',
            checklist_completed: 'Checklist item completed',
            checklist_unchecked: 'Checklist item unchecked',
            task_assigned: 'Task assigned successfully',
            task_escalated: 'Task escalated',
            export_initiated: 'Export initiated',
        },
        // Sprint 21.1.0: Incident Response Maturity content blocks
        breach_notifications: {
            page_title: 'Breach Notifications',
            title: 'Breach Notifications',
            new_notification: 'New Notification',
            no_notifications: 'No breach notifications found',
            status: 'Status',
            submit_to_mps: 'Submit to MPS',
            decision: 'Decision',
            decision_maker: 'Decision Maker',
            notify: 'Notify',
            do_not_notify: 'Do Not Notify',
            rationale: 'Rationale',
            filing_deadline: 'Filing Deadline',
            overdue: 'Overdue',
            due_soon: 'Due Soon',
            on_track: 'On Track',
            // Sprint 62.0.3 — independent regulatory clocks (AC3–AC6)
            regulatory_clocks: 'Regulatory Deadlines',
            no_regulatory_clocks: 'No framework deadline records apply to this notification',
            clock_counts_from: 'Counts from',
            clock_undated: 'No date recorded for this trigger',
            clock_no_period: 'This framework records no period for this obligation',
            clock_provisional: 'Provisional',
            clock_lapse_label: 'Missing this',
            clock_lapse_sanction_exposure: 'exposes you to a penalty — the duty survives, late',
            clock_lapse_right_extinguished: 'ENDS the right permanently — there is no late version of it',
            clock_trigger_awareness: 'Awareness',
            clock_trigger_discovery: 'Discovery',
            clock_trigger_containment: 'Containment',
            clock_trigger_authority_request: 'Authority request',
            clock_trigger_assessment_complete: 'Assessment complete',
            clock_trigger_framework_specific: 'Framework-specific trigger',
            clock_trigger_unrecognised: '{event} — this platform does not model this trigger',
            clock_provisional_reconstructed_trigger_timestamp: 'Derived from a reconstructed trigger timestamp',
            clock_provisional_disputed_trigger_timestamp: 'Derived from a disputed trigger timestamp',
            clock_provisional_transposition_unavailable: 'The national transposition that governs this deadline is not installed',
            legal_review: 'Legal Review',
            incident_id: 'Incident ID',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Assessment',
                decision_to_notify: 'Decision to Notify',
                notification_drafted: 'Notification Drafted',
                legal_review: 'Legal Review',
                submitted_to_authority: 'Submitted to Authority',
                supplement_requested: 'Supplement Requested',
                supplement_submitted: 'Supplement Submitted',
                acknowledged: 'Acknowledged',
                closed: 'Closed',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Supplement Requests',
            title: 'Supplement Requests',
            new_supplement: 'New Supplement',
            no_supplements: 'No supplement requests found',
            request_details: 'Request Details',
            response_package: 'Response Package',
            submit_response: 'Submit Response',
            deadline: 'Deadline',
            notification_id: 'Notification ID',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Enter a notification ID to view supplements.',
            supplement_statuses: {
                requested: 'Requested',
                drafting: 'Drafting',
                submitted: 'Submitted',
                accepted: 'Accepted',
                rejected: 'Rejected',
            },
        },
        remediation: {
            page_title: 'Remediation Actions',
            title: 'Remediation Actions',
            new_action: 'New Action',
            no_actions: 'No remediation actions found',
            description: 'Description',
            owner: 'Owner',
            department: 'Department',
            deadline: 'Deadline',
            statuses: {
                planned: 'Planned',
                in_progress: 'In Progress',
                completed: 'Completed',
                verified: 'Verified',
            },
            verify: 'Verify',
            incident_id: 'Incident ID',
            enter_incident_id: 'Enter an incident ID to view remediation actions.',
            start: 'Start',
            complete_action: 'Complete',
        },
        authority_requests: {
            page_title: 'Authority Requests',
            title: 'Authority Requests',
            new_request: 'New Request',
            no_requests: 'No authority requests found',
            authority_name: 'Authority Name',
            request_details: 'Request Details',
            response_package: 'Response Package',
            submission_channel: 'Submission Channel',
            submit_response: 'Submit Response',
            deadline: 'Deadline',
            confirmation: 'Confirmation',
            incident_id_optional: 'Incident ID (optional)',
            // Sprint 44.5.0 — response-context + materialisation-status UI
            response_context_label: 'Response context (for submission-artifact materialisation)',
            response_context_none: '(skip materialisation)',
            response_context_supplement: 'Supplement response',
            response_context_information: 'Information-request response',
            response_context_inspection: 'Inspection response',
            materialisation_status_column: 'Submission artifact',
            materialisation_pending: 'Pending',
            materialisation_success: 'Materialised',
            materialisation_failed: 'Failed',
            materialisation_retry_button: 'Retry',
            ar_statuses: {
                received: 'Received',
                verified: 'Verified',
                assembling: 'Assembling',
                submitted: 'Submitted',
                closed: 'Closed',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Legal Review',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            // Sprint 63.33.0 — the page read these through a fallback, so every
            // locale saw the English fallback.
            checklist: 'Checklist',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Disclosure Register',
            title: 'Disclosure Register',
            new_entry: 'New Entry',
            no_entries: 'No disclosure register entries found',
            disclosure_type: 'Disclosure Type',
            disclosure_date: 'Disclosure Date',
            recipient: 'Recipient',
            authority_name: 'Authority Name',
            channel: 'Channel',
            content_summary: 'Content Summary',
            incident_id_optional: 'Incident ID (optional)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    // ---------- Onboarding module (Sprint 1.9.0) ----------
    onboarding: {
        nav: {
            wizard: 'Setup Wizard',
            tours: 'Module Tours',
            sample_data: 'Sample Data',
        },
        wizard: {
            title: 'Setup Wizard',
            welcome: 'Welcome to AesirX ComplianceOne',
            welcome_message: "Let's get your organization set up. This wizard will guide you through the initial configuration.",
            setup_complete: 'Setup Complete',
            skipped_message: 'The setup wizard was skipped. You can run it anytime to configure your organization.',
            start_setup: 'Start Setup',
            skip_wizard: 'Skip Wizard',
            go_to_admin: 'Go to Admin',
            skip_for_now: 'Skip for Now',
            next_step: 'Next Step',
            previous_step: 'Previous Step',
            finish_setup: 'Finish Setup',
            rerun: 'Re-run Setup Wizard',
            step_label: 'Step {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Organization name is required.',
            validation_admin_email_required: 'Admin email is required.',
            validation_admin_email_invalid: 'Please enter a valid email address.',
            validation_cmp_endpoint_required: 'CMP endpoint is required when CMP is enabled.',
            steps: {
                license_activation: 'License Activation',
                org_profile: 'Organization Profile',
                module_selection: 'Module Selection',
                cmp_integration: 'CMP Integration',
                notification_preferences: 'Notifications',
                review: 'Review & Confirm',
            },
            step_descriptions: {
                license_activation: 'Enter your license key to activate your package and determine which modules are available.',
                org_profile: 'Set up your organization name, jurisdiction, industry, size, and preferred language.',
                module_selection: 'Choose which compliance modules to activate for your organization.',
                cmp_integration: 'Configure Consent Management Platform integration (optional).',
                notification_preferences: 'Configure how you receive notifications for compliance events.',
                review: 'Review your configuration and finalize the setup.',
            },
            license_step: {
                validate_button: 'Validate License',
                skip_warning: 'Module access will be limited to the default package tier. You can activate a license later in Platform > License Settings.',
            },
            module_descriptions: {
                rights_requests: 'Handle data subject access and deletion requests.',
                data_mapping: 'Map systems, data flows, and processing activities.',
                governance: 'Oversee vendors, approvals, drift and the evidence registers.',
                consent: 'Manage consent policies and purpose catalogs.',
                consent_properties: 'Manage multi-property consent across digital properties.',
                assessments: 'Conduct impact assessments and cross-border transfers.',
                classification: 'Classify and label data assets with lifecycle tracking.',
                monitoring: 'Scheduled compliance reviews and KPI tracking.',
                incidents: 'Report and manage security and privacy incidents.',
                connectors: 'Connect to external systems for automated data exchange.',
                vendor_risk: 'Score vendors, track contracts, and manage due diligence.',
                deletion_orchestration: 'Coordinate data deletion across systems and vendors.',
                discovery: 'Discover and classify data across your infrastructure.',
                incident_ops: 'Advanced incident playbooks, SLA tracking, and escalations.',
                program_governance: 'Maturity assessments, executive reporting, and audit readiness.',
                access_accountability: 'Monitor user access, sessions, and justification workflows.',
                localization: 'Market-specific compliance packs and regulatory frameworks.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Partner and reseller management workspace.',
                privacy_scanner: 'Real-time privacy monitoring of digital properties.',
                onboarding: 'Setup wizard, guided tours, and sample data management.',
                compliance_forms: 'Interactive compliance form templates and document generation.',
                audit_trail: 'Comprehensive immutable audit logging and retention.',
                organizations: 'Multi-organization and group management.',
                task_management: 'Unified task planning, workflow engine, and calendar views.',
            },
            module_categories: {
                data_privacy_rights: 'Data Privacy & Rights',
                vendor_third_party: 'Vendor & Third-Party Management',
                risk_assessment: 'Risk & Assessment',
                operations_automation: 'Operations & Automation',
                platform_admin: 'Platform & Administration',
            },
            package_tier_label: 'Your Package',
            package_module_unavailable: 'Not included in your package tier',
            select_all: 'Select All',
            deselect_all: 'Deselect All',
            load_sample_data: 'Load sample data after setup',
            load_sample_data_description: 'Install realistic, fictional demo data to explore the platform. Can be removed later from Onboarding > Sample Data.',
            language_label: 'Preferred Language',
            language_description: 'Choose the default language for your organization.',
            admin_account_title: 'Administrator Account',
            admin_account_created: 'Administrator account created',
            admin_full_name: 'Full name',
            admin_email: 'Email address',
            admin_password: 'Password',
            admin_confirm_password: 'Confirm password',
            admin_show_password: 'Show password',
            admin_hide_password: 'Hide password',
            admin_name_placeholder: 'e.g. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Minimum 8 characters',
            admin_password_confirm_placeholder: 'Re-enter password',
            validation_admin_name_required: 'Administrator name is required',
            validation_admin_password_required: 'Password is required',
            validation_admin_password_min_length: 'Password must be at least 8 characters',
            validation_passwords_mismatch: 'Passwords do not match',
            notification_email_enabled: 'Enable Email Notifications',
            notification_email_description: 'Receive email notifications for task assignments, SLA breaches, and compliance events.',
            notification_digest_frequency: 'Digest Frequency',
            notification_digest_daily: 'Daily',
            notification_digest_weekly: 'Weekly',
            notification_digest_none: 'No digest',
            notification_in_app_enabled: 'Enable In-Platform Notifications',
            notification_in_app_description: 'Show notification badge and drawer for real-time alerts.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/EEA',
                global: 'Global',
            },
            languages: {
                en: 'English',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk Bokmål',
                nn: 'Norsk Nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Français',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'Português',
            },
            industries: {
                financial_services: 'Financial Services',
                healthcare: 'Healthcare',
                technology: 'Technology',
                telecommunications: 'Telecommunications',
                government: 'Government',
                education: 'Education',
                manufacturing: 'Manufacturing',
                retail: 'Retail',
                other: 'Other',
            },
            placeholders: {
                org_name: 'e.g., ABC Company Ltd.',
                admin_email: 'admin@company.vn',
                team_emails: 'user1@company.vn, user2@company.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organization Name',
                jurisdiction: 'Jurisdiction',
                industry: 'Industry',
                org_size: 'Organization Size',
                language: 'Preferred Language',
                admin_email: 'Admin Email',
                team_emails: 'Team Member Emails (comma-separated)',
                enable_cmp: 'Enable CMP Integration',
                cmp_endpoint: 'CMP API Endpoint',
                email_notifications: 'Email Notifications',
                in_app_notifications: 'In-Platform Notifications',
                digest_frequency: 'Digest Frequency',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Module Onboarding Tours',
            start: 'Start Tour',
            continue_tour: 'Continue Tour',
            replay: 'Replay Tour',
            exit: 'Exit Tour',
            skip: 'Skip Tour',
            next: 'Next',
            previous: 'Previous',
            finish: 'Finish Tour',
            step_of: 'Step {current} of {total}',
            info: 'Take guided tours of each module to learn key features and workflows. Tours can be replayed at any time.',
            autoplay: 'Auto-play',
            interactive: 'Interactive',
            play_all: 'Play All',
            search_tours: 'Search tours...',
            duration: 'Duration',
            steps_label: '{count} steps',
            mode: 'Mode',
            pause: 'Pause',
            resume: 'Resume',
            speed: 'Speed',
            restart: 'Restart',
            reset: 'Reset',
            reset_success: 'Tour progress has been reset.',
            category_core_privacy: 'Core Privacy',
            category_governance: 'Governance',
            category_operations: 'Operations',
            category_platform: 'Platform',
            category_advanced: 'Advanced',
            category_data_privacy_rights: 'Data Privacy & Rights',
            category_vendor_third_party: 'Vendor & Third-Party Management',
            category_risk_assessment: 'Risk & Assessment',
            category_operations_automation: 'Operations & Automation',
            category_platform_admin: 'Platform & Administration',
            steps_count: '{count} steps',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Sample Data Management',
            select_modules: 'Select Modules',
            current_status: 'Current Status',
            operation_history: 'Operation History',
            load: 'Load Sample Data',
            clear: 'Clear Sample Data',
            processing: 'Processing...',
            loaded: 'Loaded',
            not_loaded: 'Not Loaded',
            records: 'Sample Records',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Clear the cross-industry sample data of these modules only: {list}? Sample data in other modules is kept, and real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear_title: 'Clear Sample Data',
            confirm_load_title: 'Load Sample Data',
            confirm_load: 'Are you sure you want to load sample data for the selected modules?',
            action_load: 'Load',
            action_clear: 'Clear',
            info: 'Load realistic, fictional demo data for training and demonstrations. Sample data is tagged and can be safely removed without affecting real data.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Help Center',
            search: 'Search',
            search_placeholder: 'Search help articles...',
            clear: 'Clear',
            all_categories: 'All Categories',
            all_modules: 'All Modules',
            back: 'Back to Help Center',
            callout_important: 'Important',
            callout_tip: 'Tip',
            no_results: 'No articles found matching your search.',
            no_articles: 'No help articles available yet. Articles will appear here as content is added.',
            results_for: '{count} result(s) for "{query}"',
            quick_links: {
                getting_started: 'Getting Started',
                getting_started_desc: 'Installation, setup, and quick start guide.',
                user_guide: 'User Guide',
                user_guide_desc: 'Module-by-module usage documentation.',
                admin_guide: 'Admin Guide',
                admin_guide_desc: 'Configuration, maintenance, and troubleshooting.',
                faq: 'FAQ',
                faq_desc: 'Frequently asked questions and answers.',
            },
            search_articles_aria: 'Search help articles',
            article_metadata_aria: 'Article metadata',
            faq_editor_title: 'Help Center FAQ Editor',
            faq_editor_description: 'Add, edit, and remove FAQ question-and-answer pairs on any Help Center article. The editor is not yet available; this address is reserved for it.',
            faq_editor_empty: 'The FAQ editor is not available yet. Until it ships, ask your administrator to have FAQ sections added to a Help Center article for you.',
            articles_list_aria: 'Help articles',
            category_nav_aria: 'Category navigation',
            quick_links_aria: 'Quick links to help categories',
            returned_to_list: 'Returned to article list',
            opening_article: 'Opening article: {title}',
            // fix-sprint-20260504-2137 F20 — HelpCenterPage aria-labels.
            read_article_aria: 'Read article: {title}',
            pagination_nav_aria: 'Articles pagination',
            error_unavailable: 'Help center is currently unavailable. Please try again later.',
            retry: 'Retry',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    // ---------- Common / shared strings ----------
    common: {
        vendor: 'Vendor',
        /*
         * Sprint 63.9.0, locked decision 6 — the ONE sentence a short list owes the
         * operator, with one key per non-`complete` branch of `UserDirectoryResult`.
         *
         * Six wordings across eleven `pages/admin/*\/strings.ts` files said this one
         * fact, one of which — "Showing 200 of 200. Narrow the search to reach the
         * rest." — told the operator the list was complete and to narrow it in the
         * same breath. Four keys rather than one because a union with three
         * non-complete branches cannot be served by a single sentence, and leaving
         * any branch behind in a module file defeats the consolidation.
         *
         * `partial_with_total` is `partner_marketplace.selector_truncated`'s wording
         * and Vietnamese, character for character: it was the plurality (5 of 14), it
         * is the one already proven against a live region and an `aria-describedby`
         * association, and its rendering has been through review in all 16 locales.
         * Taking it verbatim is the R-44 reading of a governed rendering, not a
         * re-translation.
         *
         * `partial_without_total` exists BECAUSE `partial_with_total` produced the
         * self-contradicting sentence: when the response carried no envelope total,
         * the hook substituted `users.length` and the sentence read "of {shown}".
         * `UserDirectoryResult` carries that case as `total: 'unknown'`, which cannot
         * be interpolated, so this is the only sentence reachable for it.
         */
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'e.g. {value}',
        /*
         * Sprint 64.40.0's partially-redacted-export notice, moved here from the
         * `{ en, vi }` table that shipped beside `ExportFormatMenu` while this
         * directory was held by another agent. That table selected its text with
         * `locale.startsWith('vi')` — both patterns are named as banned in
         * I18N.md — and `ExportFormatMenu` is mounted on ~145 admin pages, so every
         * operator on the 14 other locales was told in English that their artefact
         * was incomplete. Per R-57 the real translations are EN and VI; the other
         * 14 bundles carry the English text for structural parity.
         */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Save',
        cancel: 'Cancel',
        system_actor: 'System',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        update: 'Update',
        loading: 'Loading...',
        error: 'An error occurred',
        success: 'Success',
        logout: 'Logout',
        actions: 'Actions',
        retrying: 'Retrying…',
        permission_denied: 'You do not have access to this page.',
        status: 'Status',
        description: 'Description',
        name: 'Name',
        created_at: 'Created At',
        updated_at: 'Updated At',
        yes: 'Yes',
        no: 'No',
        confirm: 'Confirm',
        confirm_dialog_title: 'Confirm action',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        search: 'Search',
        filter: 'Filter',
        // Sprint 47.9.1a — shared pagination labels reused across the
        // Forseti list pages (and any future list page). Per the family
        // plan EN+VI-only contract; the 14 other locales fan out in 51.0.0.
        page_x_of_y: 'Page {page} of {total}',
        pagination_label: 'Pagination',
        // fix-sprint-20260411-D F10/F11/F12 — shared aria-label keys.
        view_mode_label: 'View mode',
        export: 'Export',
        // fix-sprint-20260415-0434 F11/F12/F16/F17 — ExportFormatMenu + AdminLayout i18n.
        exporting: 'Exporting…',
        sidebar_navigation: 'Admin sidebar navigation',
        items_count: '{count} items',
        items_count_one: '{count} item',
        export_success: 'Document generated successfully.',
        generate_pdf: 'Generate PDF',
        import: 'Import',
        download: 'Download',
        upload: 'Upload',
        view: 'View',
        close: 'Close',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Submit',
        saved: 'Saved successfully',
        saving: 'Saving...',
        submitting: 'Submitting...',
        created: 'Created successfully',
        all: 'All',
        none: 'None',
        select: 'Select',
        select_user: 'Select user...',
        custom_input: 'Type manually',
        required: 'Required',
        optional: 'Optional',
        notes: 'Notes',
        tags: 'Tags',
        archive: 'Archive',
        clone: 'Clone',
        refresh: 'Refresh',
        retry: 'Retry',
        // fix-sprint-20260810-2128 residue (finding A8's class) — a failed
        // option read stated as such, never shown as an empty list.
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        // Inside a picker's empty menu, where the full sentence is already stated
        // as an alert beside it; says nothing that could read as "none".
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Reset',
        add: 'Add',
        remove: 'Remove',
        // fix-sprint-20260410-0410 F5/F6 — shared AttachmentField + EntityReferenceSelect
        removing: 'Removing…',
        attachment: 'Attachment',
        upload_file: 'Upload file',
        upload_file_required: 'Upload file (required)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Drag and drop or click to browse',
        upload_max_size: 'Max size: {size} MB',
        upload_accepted_types: 'Accepted: {types}',
        upload_in_progress: 'Uploading {name}...',
        upload_size_exceeded: 'File size exceeds the maximum allowed size of {size} MB.',
        upload_type_not_accepted: 'File type not accepted. Accepted types: {types}',
        upload_succeeded: 'File "{name}" uploaded successfully.',
        upload_failed: 'Failed to upload file.',
        select_entity_type_first: 'Select an entity type first',
        // fix-sprint-20260411-0337 H10/M12/M13/M14 — missing i18n keys
        failed_to_remove_file: 'Failed to remove file.',
        entity_type_label: 'Entity type',
        entity_label: 'Entity',
        entity_reference: 'Entity reference',
        failed_to_load_entities: 'Failed to load entities',
        enable: 'Enable',
        disable: 'Disable',
        no_data: 'No data found',
        confirm_delete: 'Are you sure you want to delete this item?',
        confirm_action: 'Are you sure you want to proceed?',
        save_success: 'Saved successfully',
        save_error: 'Failed to save',
        delete_success: 'Deleted successfully',
        delete_error: 'Failed to delete',
        hour: 'hour',
        hours: 'hours',
        load_error: 'Failed to load data',
        operation_error: 'Operation failed',
        something_went_wrong: 'Something went wrong',
        go_home: 'Go to Dashboard',
        access_denied: 'Access Denied',
        no_permission: 'You do not have permission to access this page.',
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access this page.',
        unknown: 'Unknown',
        back_to_dashboard: 'Back to Dashboard',
        id: 'ID',
        type: 'Type',
        date: 'Date',
        created_by: 'Created By',
        updated_by: 'Updated By',
        page_of: 'Page {page} of {total}',
        showing_items: 'Showing {count} items',
        items_per_page: 'Items per page',
        no_results: 'No records match your filters',
        no_items: 'No items found',
        sort_by: 'Sort by',
        ascending: 'Ascending',
        descending: 'Descending',
        all_statuses: 'All Statuses',
        all_types: 'All Types',
        all_categories: 'All Categories',
        all_severities: 'All Severities',
        date_range: 'Date Range',
        from_date: 'From',
        to_date: 'To',
        clear_filters: 'Clear Filters',
        apply_filters: 'Apply Filters',
        save_changes: 'Save Changes',
        discard_changes: 'Discard Changes',
        confirm_discard: 'You have unsaved changes. Discard them?',
        copy: 'Copy',
        duplicate: 'Duplicate',
        move: 'Move',
        assign: 'Assign',
        unassign: 'Unassign',
        approve: 'Approve',
        reject: 'Reject',
        deleting: 'Deleting...',
        processing: 'Processing...',
        loading_page: 'Loading page...',
        operation_success: 'Operation completed successfully',
        operation_failed: 'Operation failed. Please try again.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
            gov_vendor_read: 'read vendors',
            vendor_questionnaire_read: 'read vendor questionnaires',
            ai_governance_read: 'read AI systems',
            dpia_read: 'read data processing impact assessments',
            incident_read: 'read incidents',
            dm_evidence_read: 'read Data Mapping evidence',
            vehicle_monitoring_read: 'read vehicle monitoring devices',
            forms_instance_read: 'read form records',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Network error. Check your connection.',
        session_expired: 'Session expired. Please log in again.',
        test: 'Test',
        pause: 'Pause',
        resume: 'Resume',
        activate: 'Activate',
        last_sync: 'Last Sync',
        health: 'Health',
        email: 'Email',
        role: 'Role',
        title: 'Title',
        owner: 'Owner',
        priority: 'Priority',
        category: 'Category',
        details: 'Details',
        overview: 'Overview',
        configuration: 'Configuration',
        history: 'History',
        new_item: 'New',
        edit_item: 'Edit',
        select_option: 'Select...',
        total: 'Total',
        active: 'Active',
        inactive: 'Inactive',
        enabled: 'Enabled',
        disabled: 'Disabled',
        last_login: 'Last Login',
        never: 'Never',
        password_changed: 'Password changed. You will be logged out.',
        password_change_failed: 'Failed to change password',
        passwords_mismatch: 'Passwords do not match',
        install: 'Install',
        install_named: 'Install {name}',
        uninstall: 'Uninstall',
        installed: 'Installed',
        available: 'Available',
        catalog: 'Catalog',
        published: 'Published',
        version: 'Version',
        market: 'Market',
        language: 'Language',
        framework: 'Framework',
        template: 'Template',
        connector_type: 'Connector Type',
        polling_interval: 'Polling Interval (minutes)',
        new_instance: 'New Instance',
        change_password: 'Change Password',
        current_password: 'Current Password',
        new_password: 'New Password',
        confirm_password: 'Confirm Password',
        comments: 'Comments',
        completed: 'Completed',
        contact: 'Contact',
        hide: 'Hide',
        hide_details: 'Hide Details',
        label: 'Label',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Showing',
        page: 'Page',
        preview: 'Preview',
        print: 'Print',
        show: 'Show',
        status_active: 'Active',
        status_completed: 'Completed',
        status_draft: 'Draft',
        status_in_progress: 'In Progress',
        status_pending: 'Pending',
        view_details: 'View Details',
        all_priorities: 'All Priorities',
        pipeline: 'Pipeline',
        all_requests: 'All Requests',
        schedule: 'Schedule',
        deploy: 'Deploy',
        rollback: 'Rollback',
        advance_stage: 'Advance Stage',
        submit_for_approval: 'Submit for Approval',
        approval_log: 'Approval Log',
        approver: 'Approver',
        decision: 'Decision',
        decided_at: 'Decided At',
        rollout_stages: 'Rollout Stages',
        approval_comment: 'Approval Comment',
        optional_comment: 'Optional comment...',
        impact_summary: 'Impact Summary',
        target_property_ids: 'Target Property IDs (comma-separated)',
        policy_id: 'Policy ID',
        target_version: 'Target Version',
        target_version_placeholder: 'e.g., 2.0.0',
        scheduled_label: 'Scheduled',
        scheduled_start: 'Scheduled Start',
        scheduled_end: 'Scheduled End',
        properties_count: '{count} properties',
        results: 'Results',
        comparison_results: 'Comparison Results',
        past_comparison_reports: 'Past Comparison Reports',
        scanning: 'Scanning...',
        run_inconsistency_scan: 'Run Inconsistency Scan',
        property_ids_label: 'Property IDs (comma-separated, min 2)',
        comparison_type: 'Comparison Type',
        golden_template_id: 'Golden Template ID (optional)',
        leave_blank_compare: 'Leave blank to compare properties against each other',
        add_notes_placeholder: 'Add your analysis notes here...',
        covered_of_active: '{covered} of {active} active properties covered',
        fetch_error: 'Failed to fetch data. Please try again.',
        run_comparison_error: 'Failed to run comparison. Please try again.',
        save_notes_error: 'Failed to save notes. Please try again.',
        scan_complete: 'Inconsistency scan completed successfully.',
        scan_error: 'Failed to run inconsistency scan. Please try again.',
        min_two_properties: 'Please provide at least 2 property IDs for comparison.',
        comma_separated: 'comma-separated',
        language_english: 'English',
        language_vietnamese: 'Vietnamese',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Author',
        dependencies: 'Dependencies',
        checksum: 'Checksum',
        changelog: 'Changelog',
        tier: 'Tier',
        phone: 'Phone',
        launch: 'Launch',
        complete: 'Complete',
        back_to_list: 'Back to List',
        clear_selection: 'Clear Selection',
        deactivate: 'Deactivate',
        scope: 'Scope',
        deadline: 'Deadline',
        progress: 'Progress',
        frequency: 'Frequency',
        reviewer: 'Reviewer',
        user: 'User',
        campaign: 'Campaign',
        items: 'Items',
        next_run: 'Next Run',
        last_run: 'Last Run',
        overdue: 'OVERDUE',
        creating: 'Creating...',
        launching: 'Launching...',
        loaded: 'loaded',
        page_size: 'Page size',
        per_page: '/ page',
        urgent: 'Urgent',
        pagination: 'Pagination',
        metric: 'Metric',
        value: 'Value',
        country: 'Country',
        showing_chart_view: 'Showing chart view',
        showing_table_view: 'Showing data table view',
        policies: 'Policies',
        compliant: 'Compliant',
        non_compliant_status: 'Non-Compliant',
        all_data: 'All data',
        sr_urgent: 'Urgent',
        n_per_page: '{n} / page',
        select_user_placeholder: '-- Select user --',
        field_required: 'This field is required',
        results_found: 'results found',
        select_placeholder: '-- Select --',
        unknown_user: 'Unknown User',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // `other` names a system actor this build does not know by its id.
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Export CSV',
        not_applicable: 'N/A',
        aria_overdue_since: 'Overdue since {date}',
        placeholders: {
            enter_justification: 'Enter justification',
            justification: 'Justification',
            campaign_name_example: 'Q1 2026 Access Review',
            optional_description: 'Optional description',
            enter_user_id: 'Enter the user ID of the reviewer',
            enter_user_id_short: 'Enter user ID',
            schedule_name_example: 'Quarterly Access Attestation',
            enter_property_id: 'Enter property ID',
            enter_policy_id: 'Enter policy ID',
            property_or_group_id: 'Property or group ID',
            describe_template_purpose: 'Describe the template purpose...',
            step_title: 'Step title...',
            detailed_instructions: 'Detailed instructions...',
            filter_by_taxonomy_id: 'Filter by Taxonomy ID...',
            describe_classification_rule: 'Describe the classification rule...',
            describe_dedup_rule: 'Describe the dedup rule...',
            describe_import_template: 'Describe the import template...',
            reason_for_rejection: 'Reason for rejection...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Filter by locale...',
            filter_by_event_type: 'Filter by event type',
            filter_by_actor_id: 'Filter by actor ID',
            filter_by_market_code: 'Filter by market code...',
            filter_by_framework_id: 'Filter by framework ID...',
            add_comment: 'Add a comment...',
            provide_rejection_reason: 'Provide a reason for rejecting this submission...',
            filter_by_jurisdiction: 'Filter by jurisdiction code...',
            enter_report_title: 'Enter report title',
            enter_name: 'Enter name',
            enter_description: 'Enter description',
            enter_org_id: 'Enter Org ID to add',
            describe_evidence_gap: 'Describe the evidence gap...',
            expected_evidence: 'What evidence is expected',
            entity_identifier: 'Entity identifier',
            remediation_steps: 'Steps to remediate this gap',
            user_id_or_name: 'Search by name',
            reporting_period_id: 'Reporting period ID',
            maturity_model_id: 'Select a maturity model',
            describe_maturity_model: 'Describe this maturity model...',
            describe_role_purpose: "Describe this role\\'s purpose...",
            provide_deny_reason: 'Provide a reason for denying this request',
            describe_emergency: 'Describe the emergency situation requiring elevated access (minimum 20 characters)',
            enter_delegate_id: 'Enter user ID of the delegate',
            provide_delegation_reason: 'Provide a reason for this delegation',
            client_identifier: 'Client identifier',
            client_secret: 'Client secret',
            time_format: 'HH:MM',
            describe_sod_rule: 'Describe the purpose of this SoD rule...',
            enter_role_id: 'Enter role ID',
            explain_exception: 'Explain why this exception is needed...',
            describe_compensating_controls: 'Describe compensating controls in place...',
            full_name: 'Full name',
            min_chars: 'Min 12 chars',
            enter_override_justification: 'Enter justification for overriding this gate evaluation',
            enter_evidence_id: 'Enter evidence ID to link:',
            date_format: 'DD/MM/YYYY',
            describe_pack_contents: 'Describe the pack contents and purpose',
            select_reviewers: 'Select reviewers...',
        },
        aria_previous_page: 'Previous page',
        aria_next_page: 'Next page',
        aria_results_per_page: 'Results per page',
        per_page_25: '25 / page',
        per_page_50: '50 / page',
        per_page_100: '100 / page',
        select_language: 'Select language',
        today: 'Today',
        tomorrow: 'Tomorrow',
        dashboard_load_error: 'Failed to load dashboard. Please try again.',
        enum_grade: {
            red: 'Red',
            amber: 'Amber',
            yellow: 'Yellow',
            green: 'Green',
        },
        enum_severity: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        enum_priority: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        enum_status: {
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            blocked: 'Blocked',
            escalated: 'Escalated',
            cancelled: 'Cancelled',
            overdue: 'Overdue',
            not_started: 'Not Started',
            new: 'New',
            open: 'Open',
            closed: 'Closed',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
        },
        enum_department: {
            unassigned: 'Unassigned',
        },
        enum_form_category: {
            assessment: 'Assessment',
            assessment_report: 'Assessment Report',
            dpia: 'DPIA',
            authority_submission: 'Authority Submission',
            cross_border_transfer: 'Cross-Border Transfer',
            vendor: 'Vendor',
            breach: 'Breach',
            consent: 'Consent',
            governance: 'Governance',
            register: 'Register',
            monitoring: 'Monitoring',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Privacy',
            incident: 'Incident',
            org: 'Organization',
            sector: 'Sector',
            classification: 'Classification',
            discovery: 'Discovery',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Rights Requests',
            assessments: 'Assessments',
            incidents: 'Incidents',
            incident_ops: 'Incident Ops',
            monitoring: 'Monitoring',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion',
            access_review: 'Access Review',
            consent: 'Consent',
            consent_properties: 'Consent Properties',
            forms: 'Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Classification',
            discovery: 'Discovery',
            manual: 'Manual',
            data_mapping: 'Data Mapping',
        },
        enum_risk_level: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        enum_request_type: {
            all: 'All',
            access: 'Access (DSAR)',
            deletion: 'Deletion',
            correction: 'Correction',
            objection: 'Objection',
            portability: 'Portability',
            restriction: 'Restriction',
            consent_withdrawal: 'Consent Withdrawal',
            protective_measures: 'Protection Measures',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'English',
            vi: 'Vietnamese',
            all: 'Both',
        },
        enum_event_type: {
            case_created: 'Case Created',
            status_changed: 'Status Changed',
            case_assigned: 'Case Assigned',
            note_added: 'Note Added',
            attachment_added: 'Attachment Added',
            attachment_deleted: 'Attachment Deleted',
            reference_added: 'Reference Added',
            checklist_updated: 'Checklist Updated',
            identity_verified: 'Identity Verified',
            closure_summary_added: 'Closure Summary',
            approval_granted: 'Approval Granted',
            template_applied: 'Template Applied',
            export_generated: 'Export Generated',
            sla_warning: 'SLA Warning',
            sla_overdue: 'SLA Overdue',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Export failed',
        you: 'you',
        export_failed: 'Export failed. Please try again.',
        filters: 'Filters',
        failed: 'Failed',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    // ---------- Common status labels used across modules ----------
    common_statuses: {
        removed: 'Removed',
        draft: 'Draft',
        active: 'Active',
        inactive: 'Inactive',
        pending: 'Pending',
        in_progress: 'In Progress',
        completed: 'Completed',
        archived: 'Archived',
        published: 'Published',
        approved: 'Approved',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
        blocked: 'Blocked',
        deferred: 'Deferred',
        not_started: 'Not Started',
        dismissed: 'Dismissed',
        failed: 'Failed',
        in_review: 'In Review',
        deprecated: 'Deprecated',
        scheduled: 'Scheduled',
        deploying: 'Deploying',
        deployed: 'Deployed',
        paused: 'Paused',
        rolled_back: 'Rolled Back',
        received: 'Received',
        processing: 'Processing',
        under_assessment: 'Under Assessment',
        implemented: 'Implemented',
        pending_approval: 'Pending Approval',
        pending_deletion: 'Pending Deletion',
        on_hold: 'On Hold',
        deleted: 'Deleted',
        expired: 'Expired',
        outdated: 'Outdated',
        drifted: 'Drifted',
        decommissioned: 'Decommissioned',
        match: 'Match',
        different: 'Different',
        missing: 'Missing',
        confirmed: 'Confirmed',
        revoked: 'Revoked',
        escalated: 'Escalated',
        system: 'System',
        custom: 'Custom',
        warning: 'Warning',
        blocking: 'Blocking',
        analyst: 'Analyst',
        viewer: 'Viewer',
        daily: 'Daily',
        weekly: 'Weekly',
        policies: 'Policies',
        denied: 'Denied',
        verified: 'Verified',
        detected: 'Detected',
        ignored: 'Ignored',
        open: 'Open',
        resolved: 'Resolved',
        suspended: 'Suspended',
        retired: 'Retired',
        review: 'Review',
        generated: 'Generated',
        exported: 'Exported',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    // ---------- Common severity / risk labels ----------
    severity: {
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        critical: 'Critical',
    },
    // ---------- Common sensitivity labels ----------
    sensitivity: {
        standard: 'Standard',
        sensitive: 'Sensitive',
        special_category: 'Special Category',
        // fix-sprint-20260410-0410 F7 — moved from consent.policies to unify
        // placement across all 16 locales.
        badge_sensitive: 'Sensitive',
    },
    // ---------- Change request type labels ----------
    change_types: {
        policy_update: 'Policy Update',
        new_binding: 'New Binding',
        remove_binding: 'Remove Binding',
        configuration_change: 'Configuration Change',
        banner_update: 'Banner Update',
    },
    // ---------- Comparison type labels ----------
    comparison_types: {
        full: 'Full',
        banner_only: 'Banner Only',
        purposes_only: 'Purposes Only',
        legal_bases_only: 'Legal Bases Only',
        consent_settings: 'Consent Settings',
    },
    // ---------- Lifecycle stages ----------
    lifecycle_stages: {
        active: 'Active',
        archived: 'Archived',
        pending_deletion: 'Pending Deletion',
        deleted: 'Deleted',
        on_hold: 'On Hold',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    // ---------- Object type labels ----------
    object_types: {
        personal_data: 'Personal Data',
        consent_record: 'Consent Record',
        user_account: 'User Account',
        audit_log: 'Audit Log',
        document: 'Document',
        backup: 'Backup',
    },
    // ---------- Risk assessment categories ----------
    risk_categories: {
        data_breach: 'Data Breach',
        unauthorized_access: 'Unauthorized Access',
        data_loss: 'Data Loss',
        regulatory_non_compliance: 'Regulatory Non-Compliance',
        third_party_risk: 'Third-Party Risk',
        operational: 'Operational',
        technical: 'Technical',
        legal: 'Legal',
    },
    // ---------- Mitigation types ----------
    mitigation_types: {
        technical: 'Technical',
        organizational: 'Organizational',
        contractual: 'Contractual',
        procedural: 'Procedural',
    },
    // ---------- Safeguard types ----------
    safeguard_types: {
        encryption_in_transit: 'Encryption in Transit',
        encryption_at_rest: 'Encryption at Rest',
        access_controls: 'Access Controls',
        contractual_clauses: 'Contractual Clauses',
        data_minimization: 'Data Minimization',
        pseudonymization: 'Pseudonymization',
        audit_rights: 'Audit Rights',
        breach_notification: 'Breach Notification',
        data_localization: 'Data Localization',
        other: 'Other',
    },
    // ---------- Scoring methods ----------
    scoring_methods: {
        weighted_average: 'Weighted Average',
        simple_average: 'Simple Average',
        maximum: 'Maximum',
        manual: 'Manual',
    },
    // ---------- DPA status labels ----------
    dpa_status: {
        not_required: 'Not Required',
        required: 'Required',
        in_progress: 'In Progress',
        completed: 'Completed',
        signed: 'Signed',
    },
    // ---------- Disposal method labels ----------
    disposal_methods: {
        anonymize: 'Anonymize',
        pseudonymize: 'Pseudonymize',
        aggregate: 'Aggregate',
        delete: 'Delete',
    },
    // ---------- Main Dashboard ----------
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Dashboard',
        no_data: 'No dashboard data available.',
        no_data_heading: 'No live data yet',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        /* Sprint 43.5.0 E1 — hero-tile metrics */
        consent_coverage: 'Consent grant rate',
        ctia_filings: 'CTIA filings',
        next_dpia_review: 'Next DPIA review',
        next_review_none: 'None scheduled',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'Incident MPS filings',
        compliance_score: 'Compliance Score',
        grade: 'Grade',
        no_score: 'No compliance score calculated yet.',
        rights_requests: 'Rights Requests',
        open: 'Open',
        sla_breached: 'SLA Breached',
        sla_approaching: 'SLA Due Soon',
        closed_month: 'Closed (Month)',
        incidents: 'Incidents & Breaches',
        active: 'Active',
        breaches: 'Breaches',
        overdue_tasks: 'Overdue Tasks',
        breach_72h_notice: '72-hour regulatory notification deadline applies to active breaches',
        recommendations: 'Recommendations',
        take_action: 'Take Action',
        drift_events: 'Drift & Approvals',
        open_events: 'Open Events',
        pending_approvals: 'Pending Approvals',
        monitoring: 'Monitoring Tasks',
        programs: 'Programs',
        overdue: 'Overdue',
        pending: 'Pending',
        ric_cases: 'RIC Cases',
        coverage: 'Data Coverage',
        systems: 'Systems',
        activities: 'Activities',
        vendors: 'Vendors',
        dpias: 'DPIAs',
        open_gaps: 'open gaps',
        view_as_table: 'View as table',
        view_as_chart: 'View as chart',
        table_metric_header: 'Metric',
        table_value_header: 'Value',
        announce_chart_view: 'Showing chart view',
        announce_table_view: 'Showing data table view',
        section_core_metrics: 'Core Metrics',
        section_task_management: 'Task Management',
        section_compliance_insights: 'Compliance Insights',
        configure_dashboard: 'Configure Dashboard',
        config_col_active: 'Active',
        config_col_widget: 'Widget',
        collapse_section: 'Collapse',
        expand_section: 'Expand',
        reset_to_defaults: 'Reset to defaults',
        preferences_saved: 'Dashboard preferences saved',
        preferences_reset: 'Dashboard reset to role defaults',
        preferences_load_error: 'Failed to load dashboard preferences',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Average system score',
        factor_avg_vendor_score: 'Average vendor score',
        factor_processing_coverage: 'Processing activity coverage',
        factor_ropa_completeness: 'RoPA completeness',
        factor_drift_penalty: 'Drift events penalty',
        rec_improve_system_gov: 'Improve system governance documentation',
        rec_improve_vendor_gov: 'Improve vendor governance documentation',
        rec_document_processing: 'Document processing activities for uncovered systems',
        rec_generate_ropa: 'Generate RoPA records for active processing activities',
        rec_resolve_drift: 'Resolve open critical and high severity drift events',
        score_good: 'Good',
        score_fair: 'Fair',
        score_poor: 'Poor',
        score_critical: 'Critical',
        drag_to_reorder: 'Drag to reorder',
        widget_moved: 'Widget moved',
        press_space_to_grab: 'Press Space to grab, arrow keys to move, Space to drop',
        reorder_instructions: 'Use drag and drop or keyboard shortcuts to reorder widgets',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Tasks Overview',
            pending: 'Pending',
            in_progress: 'In Progress',
            overdue: 'Overdue',
            done_7d: 'Done (7d)',
            by_priority: 'By Priority',
            view_all_tasks: 'View All Tasks',
            upcoming_tasks: 'Upcoming Tasks (Next 7 Days)',
            no_upcoming: 'No tasks due in the next 7 days.',
            view_calendar: 'View Calendar',
            overdue_alerts: 'Overdue',
            no_overdue: 'No overdue tasks. Everything is on track.',
            assigned_to: 'Assigned to',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'View All Overdue',
            notifications: 'Recent Notifications',
            unread: 'unread',
            no_notifications: "No notifications. You're all caught up!",
            view_all_notifications: 'View All Notifications',
            load_error: 'Failed to load data.',
            day_late: 'day late',
            days_late_plural: 'days late',
            due_today: 'Due today',
            just_now: 'just now',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Cross-Organization Stats',
            by_organization: 'By Organization',
            overdue_across_orgs: 'overdue tasks across organizations',
            overdue_count_label: '{count} overdue tasks',
            total: 'Total',
            tasks: 'tasks',
            organization: 'Organization',
            current_org: 'Current Organization',
            all_orgs: 'All orgs',
            all_organizations: 'All Organizations',
            filter_by_org: 'Filter by organization',
            cross_org: 'Cross-org',
            empty_calendar: 'No tasks for this period. Create your first task or check your filters.',
            empty_my_tasks: 'You have no tasks assigned. Great work!',
            empty_overdue: 'No overdue tasks. Everything is on track.',
            empty_notifications: "No notifications. You're all caught up!",
            empty_workflows: 'No workflows configured. Browse templates to get started.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'My Tasks',
            no_tasks: 'No tasks assigned to you.',
            load_error: 'Failed to load tasks.',
            overdue: 'overdue',
            remaining: 'left',
            view_all: 'View All Tasks',
        },
        my_section_status_widget: {
            title: 'My Section Status',
            overall_completion: 'Overall',
            sections_count: 'Sections',
            no_sections: 'No sections assigned to your department.',
            load_error: 'Failed to load section status.',
            department_tasks_title: 'Department tasks',
            no_department_tasks: 'Your department has no tasks.',
            department_tasks_error: 'Failed to load department tasks.',
            no_department: 'You are not mapped to a department.',
        },
        revision_requests_widget: {
            title: 'Revision Requests',
            no_revisions: 'No revision requests. All sections are on track.',
            load_error: 'Failed to load revision requests.',
        },
        // Sprint 43.5.6 Epic B — new Light-tier widgets.
        consent_summary: {
            title: 'Consent',
            active: 'Active',
            withdrawn_30d: 'Withdrawn (30d)',
            expiring_7d: 'Expiring (7d)',
        },
        transfer_assessments: {
            title: 'Transfer Assessments',
            open: 'Open',
            approved: 'Approved',
            gaps_flagged: 'Gaps flagged',
        },
        breach_notifications: {
            title: 'Breach Notifications',
            deadline_72h: '72h deadline',
            submitted: 'Submitted',
            pending: 'Pending',
        },
        authority_requests: {
            title: 'Authority Requests',
            pending: 'Awaiting response',
            overdue: 'Overdue',
            responded_30d: 'Responded (30d)',
        },
        data_subjects_widget: {
            title: 'Data Subjects',
            total: 'Total',
            high_risk: 'High risk',
            active_30d: 'Active (30d)',
        },
        // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING Light-tier widgets.
        audit_activity_summary: {
            title: 'Audit Activity',
            events_today: 'Events today',
            events_7d: 'Events (7d)',
            unique_actors_30d: 'Unique actors (30d)',
        },
        onboarding_progress: {
            title: 'Onboarding Progress',
            completion_pct: 'Complete',
            status_label: 'Status',
            current_step: 'Current step',
            status: {
                in_progress: 'In progress',
                completed: 'Completed',
                skipped: 'Skipped',
                not_started: 'Not started',
            },
        },
        sector_overlay_reports_due: {
            title: 'Sector Overlay Reports Due',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'grant rate, last 30 days',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No mandatory framework installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            /**
             * fix-sprint-20260924-2200 F5 (#366) — the tiles' short visible hints,
             * as Automation Impact's `tile_hint` (63.13.0). Each fits the strip's
             * two-line clamp in the narrowest hint box any layout gives a tile from a
             * 768 px viewport up (`src/__tests__/kpi-strip-hint-fit.test.ts`); the
             * tile's accessible name says the short form and then the full sentence.
             */
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No mandatory framework installed. Frameworks installed for reference only, or in Draft or Legacy status, are not counted here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'Not measured — no automated job is registered as performing a declared control',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            /**
             * Sprint 63.13.0 (live read D1) — the TILE's visible hint. The tile's
             * value is a dash in every state but one, so the hint is its reading; the
             * full sentences above run four to five lines in a 121px hint box (the
             * 1280px 4+4 strip) and were clipped by the two-line clamp in every state.
             * These fit two lines at that width in EN and VI. The tile's accessible
             * name carries this text verbatim AND the full sentence; the panel keeps
             * the full sentence.
             */
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'Not measured',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filter by Framework',
            all_frameworks: 'All Frameworks',
            filing_lifecycle: 'Filing Lifecycle',
            filing_pipeline: 'Filing Pipeline',
            draft: 'Draft',
            internal_review: 'Internal Review',
            approved: 'Approved',
            submitted: 'Submitted',
            supplement_requested: 'Supplement Requested',
            resubmitted: 'Resubmitted',
            accepted: 'Accepted',
            rejected: 'Rejected',
            closed: 'Closed',
            deadline: 'Deadline',
            days_remaining: 'days remaining',
            days_overdue: 'days overdue',
            no_filings: 'No active filings.',
            department_consolidation: 'Department Consolidation',
            departments_completed: 'Departments Completed',
            departments_pending: 'Departments Pending',
            departments_overdue: 'Departments Overdue',
            no_consolidation_cases: 'No multi-department cases in progress.',
            vendor_risk: 'Vendor & Third-Party Risk',
            pending_assessment: 'Pending Assessment',
            overdue_questionnaires: 'Overdue Questionnaires',
            dpa_review_queue: 'DPA Review Queue',
            risk_distribution: 'Risk Distribution',
            total_vendors: 'Total Vendors',
            by_risk_level: 'By Risk Level',
            no_vendor_data: 'No vendor risk data available.',
            discovery_summary: 'Data Discovery',
            discovery_classification: 'Data Discovery & Classification',
            active_scans: 'Active Scans',
            labels_pending: 'Labels Pending Review',
            exceptions_unresolved: 'Exceptions Unresolved',
            pending_review: 'Pending Review',
            exceptions: 'Exceptions',
            approved_labels: 'Approved Labels',
            total_classified: 'Total Classified',
            no_discovery_data: 'No discovery data available.',
            ai_governance: 'AI System Governance',
            systems_inventory: 'Systems in Inventory',
            systems_in_inventory: 'Systems in Inventory',
            classification_breakdown: 'Classification Breakdown',
            dossier_completion: 'Dossier Completion',
            by_classification: 'By Classification',
            labeling_status: 'Labeling Status',
            not_started: 'Not Started',
            no_ai_data: 'No AI governance data available.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Breach & Incident Response',
            active_breaches: 'Active Breaches',
            notification_status: 'Notification Status',
            remediation_progress: 'Remediation Progress',
            supplement_loops: 'Supplement Loops',
            no_breach_data: 'No active breach cases.',
            audit_readiness: 'Audit & Assessment Readiness',
            assessments: 'Assessments',
            gap_findings: 'Gap Findings',
            evidence_completion: 'Evidence Completion',
            export_ready: 'Export Ready',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Not Ready',
            no_audit_data: 'No audit readiness data available.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cybersecurity 2018',
            framework_label_vn_ecommerce: 'VN E-Commerce 2023',
            framework_label_vn_telecom: 'VN Telecom 2023',
            framework_label_vn_ai: 'VN AI Law 2025',
            framework_label_gdpr: 'GDPR',
            framework_label_unspecified: 'Unspecified',
            my_department: 'My Department',
            my_tasks: 'My Tasks',
            section_status: 'Section Status',
            revision_requests: 'Revision Requests',
            completion: 'Completion',
            reason: 'Reason',
            requested_at: 'Requested At',
            no_tasks_assigned: 'No tasks assigned to you.',
            no_sections: 'No sections assigned to your department.',
            no_revisions: 'No revision requests.',
            load_error: 'Failed to load data.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    // ---------- Connectors module (Sprint 2.0.0) ----------
    connectors: {
        nav: {
            instances: 'Instances',
            credentials: 'Credential Vault',
            event_bus: 'Event Bus',
            definitions: 'Definitions',
            brds: 'BRD Capture',
            security_reviews: 'Security Reviews',
            poc_scorecards: 'POC Scorecards',
            automation_planning: 'Automation Planning',
            system_inventory: 'System Inventory',
            traceability_matrix: 'Traceability Matrix',
            gap_register: 'Gap Register',
        },
        instances: {
            title: 'Connector Instances',
            page_title: 'Connectors',
            add: 'Add Instance',
            test: 'Test Connection',
            sync_now: 'Sync Now',
            new_instance: 'New Connector Instance',
            no_instances: 'No connector instances found',
            instance_count: '{count} instance(s)',
            confirm_delete: 'Delete connector instance "{name}"?',
            all_connector_types: 'All Connector Types',
            select_connector: 'Select a connector...',
            fetch_error: 'Failed to fetch connector instances. Please try again.',
            fetch_definitions_error: 'Failed to fetch connector definitions. Please try again.',
            create_error: 'Failed to create connector instance',
            test_error: 'Failed to test connector. Please try again.',
            status_error: 'Failed to update connector status. Please try again.',
            delete_error: 'Failed to delete connector instance. Please try again.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Status updated',
            reactivate: 'Reactivate',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Credential Vault',
            page_title: 'Connector Credentials',
            add: 'Add Credential',
            rotate: 'Rotate',
            expired: 'Expired',
            expiring: 'Expiring Soon',
            orphaned: 'Orphaned',
            new_credential: 'New Credential',
            no_credentials: 'No credentials found',
            no_expiry: 'No Expiry',
            linked_connectors: 'Linked Connectors',
            expires_at: 'Expires At',
            value_label: 'Value * (write-only)',
            confirm_delete_title: 'Confirm Delete',
            confirm_delete_message: 'Delete this credential? Linked connectors will lose access.',
            rotate_title: 'Rotate Credential',
            new_credential_value: 'New credential value',
            fetch_error: 'Failed to load credentials. Please try again.',
            health_error: 'Failed to load health. Please try again.',
            create_error: 'Failed to create credential',
            rotate_error: 'Failed to rotate credential',
            delete_error: 'Failed to delete credential',
        },
        event_bus: {
            title: 'Event Bus',
            page_title: 'Event Bus',
            events: 'Events',
            subscriptions: 'Subscriptions',
            dlq: 'Dead Letter Queue',
            publish: 'Publish Event',
            retry: 'Retry',
            dismiss: 'Dismiss',
            search_events: 'Search events...',
            filter_event_type: 'Filter by event type...',
            event_type: 'Event Type',
            source_module: 'Source Module',
            timestamp: 'Timestamp',
            delivery_status: 'Delivery Status',
            correlation_id: 'Correlation ID',
            tracking_id: 'Tracking ID',
            delivery_status_hint: 'Delivery status is updated automatically as events are processed.',
            no_events: 'No events found',
            total_with_count: '{count} total',
            prev: 'Prev',
            new_subscription: '+ New Subscription',
            subscriber_type: 'Subscriber Type',
            subscriber_id: 'Subscriber ID',
            event_type_pattern: 'Event Type Pattern',
            event_type_pattern_placeholder: 'e.g. incident.*',
            filter_expression: 'Filter Expression',
            filter_expression_placeholder: 'Optional filter',
            subscriber_type_hint: 'Choose how events will be delivered: webhook sends HTTP requests, email sends notifications, queue routes to a message queue.',
            subscriber_id_hint: 'Enter the destination for events. For webhooks, use a URL. For email, use an address. For queues, use the queue name.',
            event_type_pattern_hint: 'Match events by type using wildcards. Use * to match any segment (e.g. incident.* matches incident.created, incident.resolved).',
            filter_expression_hint: 'Optional. Further filter events by their content (e.g. severity=high).',
            subscriber_id_placeholder_webhook: 'e.g. https://your-app.com/webhook',
            subscriber_id_placeholder_email: 'e.g. alerts@your-company.com',
            subscriber_id_placeholder_queue: 'e.g. events-queue',
            event_pattern: 'Event Pattern',
            filter: 'Filter',
            enabled: 'Enabled',
            delivery_stats: 'Delivery Stats',
            no_subscriptions: 'No subscriptions found',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} delivered / {failed} failed',
            confirm_delete_subscription: 'Delete this subscription?',
            unresolved: 'Unresolved',
            resolved: 'Resolved',
            event_id: 'Event ID',
            subscription_id: 'Subscription ID',
            error_code: 'Error Code',
            error_message: 'Error Message',
            attempts: 'Attempts',
            last_failed: 'Last Failed',
            no_dlq_entries: 'No dead letter entries found',
            dlq_count: '{count} entry/entries total',
            fetch_events_error: 'Failed to fetch events. Please try again.',
            fetch_subscriptions_error: 'Failed to fetch subscriptions. Please try again.',
            fetch_dlq_error: 'Failed to fetch DLQ entries. Please try again.',
            create_subscription_error: 'Failed to create subscription',
            toggle_subscription_error: 'Failed to toggle subscription',
            delete_subscription_error: 'Failed to delete subscription',
            retry_error: 'Retry failed',
            dismiss_error: 'Dismiss failed',
            // fix-sprint-20260411-D F14 — semantic toast keys (replaces tKey fallbacks)
            event_published: 'Event published',
            publish_event_error: 'Failed to publish event. Please try again.',
            priority_label: 'Priority',
            priority_low: 'Low',
            priority_normal: 'Normal',
            priority_high: 'High',
            payload_helper_text: 'Enter event data as JSON. Example: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Subscription created',
            subscription_toggled: 'Subscription toggled',
            subscription_deleted: 'Subscription deleted',
            dlq_retried: 'DLQ message retried',
            dlq_dismissed: 'DLQ message dismissed',
        },
        definitions: {
            title: 'Connector Definitions',
            page_title: 'Connector Definitions',
            add: 'Add Definition',
            new_definition: 'New Connector Definition',
            edit_definition: 'Edit Connector Definition',
            no_definitions: 'No connector definitions found',
            definition_count: '{count} definition(s)',
            all_categories: 'All Categories',
            select_category: 'Select a category...',
            capabilities: 'Capabilities',
            capabilities_hint: 'Capabilities (comma-separated)',
            capabilities_placeholder: 'e.g. read, write, sync',
            created: 'Connector definition created',
            updated: 'Connector definition updated',
            fetch_error: 'Failed to fetch connector definitions. Please try again.',
            create_error: 'Failed to create connector definition',
            update_error: 'Failed to update connector definition',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD Capture',
            page_title: 'BRD Capture',
            create_brd: 'Create BRD',
            brd_title: 'BRD Title',
            description: 'Description',
            requirements: 'Requirements',
            status: 'Status',
            stakeholders: 'Stakeholders',
            connector_label: 'Connector',
            select_connector: 'Select a connector...',
            approval: 'Approval',
            no_brds_found: 'No BRDs found',
            decision_records: 'Decision Records',
            create_decision: 'Create Decision Record',
            alternatives_considered: 'Alternatives Considered',
            alternative_name: 'Alternative Name',
            reason_rejected: 'Reason Rejected',
            add_alternative: 'Add Alternative',
            remove_alternative: 'Remove alternative {name}',
            alternatives: 'Alternatives',
            rationale: 'Rationale',
            selected_connector: 'Selected Connector',
            comma_separated_names: 'Comma-separated names',
            brd_created: 'BRD created',
            decision_record_created: 'Decision record created',
            no_decision_records_found: 'No decision records found',
            aria_brds_table: 'BRDs',
            aria_decision_records_table: 'Decision Records',
            aria_approve: 'Approve {name}',
            aria_archive: 'Archive {name}',
            brd_title_help: 'A short, human-readable name for this BRD — e.g. "Jira ticketing integration", "Okta SSO rollout".',
            brd_title_placeholder: 'e.g. Jira ticketing integration',
            description_help: 'The business problem this connector solves. What outcome do you expect once it is live?',
            description_placeholder: 'e.g. Automate incident ticket creation from monitoring alerts so on-call gets paged within 60s.',
            connector_help: 'Optional. Pick a connector this BRD is for — leave empty if the connector has not been chosen yet.',
            requirements_help: 'Comma-separated list of must-have capabilities — e.g. "OAuth2 login, webhook delivery, audit export".',
            requirements_placeholder: 'OAuth2 login, webhook delivery, audit export',
            stakeholders_help: 'Comma-separated names or emails of people who must sign off.',
            selected_connector_help: 'Pick the connector this decision applies to.',
            rationale_help: 'Why this connector was chosen. Cover the deciding factors so a future reader understands the call.',
            rationale_placeholder: 'e.g. Chose Jira because it already holds our ticket history and the team is trained on it.',
            alternatives_considered_help: 'Other connectors evaluated and why they were not picked. Adds defensibility for audit.',
            alternative_name_placeholder: 'e.g. ServiceNow',
            reason_rejected_placeholder: 'e.g. Cost 3× higher, no advantage for our use case',
        },
        security_reviews: {
            title: 'Security Reviews',
            page_title: 'Security Reviews',
            create_review: 'Create Security Review',
            connector_name: 'Connector Name',
            connector_label: 'Connector',
            select_connector: 'Select a connector...',
            reviewer: 'Reviewer',
            select_reviewer: 'Select a reviewer...',
            findings: 'Findings (one per line)',
            risk_level: 'Risk Level',
            risk_level_placeholder: 'e.g. high, medium, low',
            notes: 'Notes',
            review_status: 'Review Status',
            checklist: 'Checklist',
            privacy_impact: 'Privacy Impact',
            approval_gate: 'Approval Gate',
            no_reviews_found: 'No security reviews found',
            review_created: 'Security review created',
            create_control_mapping: 'Create Control Mapping',
            control_mapping_created: 'Control mapping created',
            connector_id_label: 'Connector ID',
            control_id_label: 'Control ID',
            control_name_label: 'Control Name',
            aria_approve: 'Approve {name}',
            aria_reject: 'Reject {name}',
            scan_imported: 'Scan imported successfully',
            import_scan: 'Import Scan',
            scan_format: 'Scan Format',
            scan_data: 'Scan Data',
            import_results: 'Import Results',
            recommendations: 'Recommendations',
            auto_populated_from: 'Auto-populated from {tool}',
            manual_entry: 'Manual entry',
            automation_type: 'Automation Type',
            select_automation_type: 'Select automation type...',
            automation_full: 'Full — connector fully automates this control',
            automation_partial: 'Partial — connector contributes evidence; humans still act',
            automation_monitoring: 'Monitoring only — connector observes, does not enforce',
            control_id_help: 'Use the code from your compliance scheme (e.g. ISO-27001-A.12.1, SOC2-CC6.1, or your internal control ID).',
            control_name_help: 'Short human-readable name (e.g. "Network access restriction").',
            description_label: 'Description',
            load_sarif_example: 'Load SARIF example',
            load_json_example: 'Load custom JSON example',
            scan_format_help: 'SARIF is the OASIS standard emitted by most scanners (Snyk, CodeQL, Semgrep). Use custom JSON for tools that do not emit SARIF.',
            scan_data_help: 'Paste the full scan output, upload a .sarif/.json file, or click a Load Example button to see the shape.',
            upload_file_label: 'Upload .sarif or .json file',
            invalid_scan_json: 'Could not parse scan data as JSON. Check for syntax errors near the highlighted character.',
            save_as_review: 'Save as Security Review',
            discard_preview: 'Discard preview',
            preview_hint: 'This is a preview — nothing is saved yet. Click "Save as Security Review" to create a pending review pre-filled with these values.',
            reviewer_required_for_save: 'Pick a reviewer to save this scan as a security review.',
        },
        poc_scorecards: {
            title: 'POC Scorecards',
            page_title: 'POC Scorecards',
            create_scorecard: 'Create Scorecard',
            connector_label: 'Connector',
            select_connector: 'Select a connector...',
            vendor_name: 'Vendor Name',
            recommendation: 'Recommendation',
            recommendation_placeholder: 'e.g. Proceed, Do not proceed, Needs further evaluation',
            criteria: 'Evaluation Criteria',
            criterion_name: 'Criterion Name',
            score: 'Score',
            max_score: 'Max Score',
            notes: 'Notes',
            add_criterion: 'Add Criterion',
            remove_criterion: 'Remove criterion {name}',
            total_score: 'Total Score',
            api_quality: 'API Quality',
            reliability: 'Reliability',
            support_quality: 'Support Quality',
            cost_score: 'Cost Score',
            overall_score: 'Overall Score',
            evidence: 'Evidence',
            no_scorecards_found: 'No POC scorecards found',
            scorecard_created: 'POC scorecard created',
            connector_help: 'Pick the connector this POC evaluation is for.',
            vendor_name_help: 'The vendor or product being evaluated — e.g. "Okta", "Datadog", "Snowflake".',
            vendor_name_placeholder: 'e.g. Okta, Datadog, Snowflake',
            recommendation_help: 'Your final call after the evaluation. Keep it short and decisive.',
            criteria_help: 'Score the vendor on each evaluation criterion. Add one row per criterion (API quality, reliability, support, cost, etc.).',
            criterion_name_help: 'What you are scoring — e.g. "API Quality", "Reliability", "Support", "Cost".',
            criterion_name_placeholder: 'e.g. API Quality',
            score_help: 'Score awarded for this criterion. Must not exceed Max Score.',
            max_score_help: 'Top score available for this criterion. Use the same scale across rows so totals are comparable.',
            notes_help: 'Optional. Add evidence, caveats, or links — anything that explains the score.',
        },
        common_labels: {
            aria_filter_instances: 'Filter connector instances',
            aria_filter_definitions: 'Filter connector definitions',
            aria_filter_credentials: 'Filter credentials',
            aria_filter_events: 'Filter events',
            aria_filter_dlq: 'Filter dead letter queue',
            aria_instance_summary: 'Connector instance summary',
            aria_active_connectors: 'Active connectors',
            aria_error_connectors: 'Error connectors',
            aria_credential_health: 'Credential health summary',
            aria_items_per_page: 'Items per page',
            show_password: 'Show password',
            hide_password: 'Hide password',
            payload_json_label: 'Payload (JSON)',
            payload_invalid_json: 'Payload must be valid JSON',
            placeholder_event_type: 'e.g. incident.created',
            placeholder_source_module: 'e.g. monitoring',
            placeholder_version: 'e.g. 1.0',
        },
        statuses: {
            configuring: 'Configuring',
            testing: 'Testing',
            active: 'Active',
            paused: 'Paused',
            error: 'Error',
            disabled: 'Disabled',
            draft: 'Draft',
            approved: 'Approved',
            archived: 'Archived',
            pending: 'Pending',
            rejected: 'Rejected',
        },
        system_types: {
            application: 'Application',
            database: 'Database',
            service: 'Service',
            infrastructure: 'Infrastructure',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'On-Premise',
            cloud: 'Cloud',
            hybrid: 'Hybrid',
            managed: 'Managed',
        },
        system_statuses: {
            active: 'Active',
            decommissioned: 'Decommissioned',
            planned: 'Planned',
            under_review: 'Under Review',
        },
        automation_statuses: {
            planning: 'Planning',
            in_progress: 'In Progress',
            completed: 'Completed',
            on_hold: 'On Hold',
        },
        gap_types: {
            functional: 'Functional',
            technical: 'Technical',
            security: 'Security',
            compliance: 'Compliance',
            process: 'Process',
        },
        severity_labels: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        gap_statuses: {
            open: 'Open',
            in_progress: 'In Progress',
            mitigated: 'Mitigated',
            closed: 'Closed',
            accepted: 'Accepted',
        },
        verification_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            verified: 'Verified',
            failed: 'Failed',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Internal',
            email: 'Email',
            queue: 'Queue',
        },
        credential_types: {
            password: 'Password',
            api_key: 'API Key',
            oauth2_client: 'OAuth2 Client',
            certificate: 'Certificate',
            token: 'Token',
            ssh_key: 'SSH Key',
            imap_credentials: 'IMAP Credentials',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Cloud',
            security: 'Security',
            itsm: 'ITSM',
            custom: 'Custom',
        },
        delivery_statuses: {
            pending: 'Pending',
            delivered: 'Delivered',
            partial: 'Partial',
            failed: 'Failed',
        },
        automation_planning: {
            title: 'Automation Planning',
            page_title: 'Automation Planning',
            program_name: 'Program Name',
            program_status: 'Status',
            description: 'Description',
            linked_artifacts: 'Linked Artifacts',
            create_program: 'Create Program',
            edit_program: 'Edit Program',
            no_programs_found: 'No automation programs found',
            program_created: 'Automation program created',
            program_updated: 'Automation program updated',
            target_date: 'Target Date',
            owner: 'Owner',
            select_status: 'Select a status...',
            aria_programs_table: 'Automation programs',
            aria_edit: 'Edit {name}',
            export_programs: 'Export Programs',
            title_label: 'Program Title',
            title_help: 'A short, human-readable name for the automation program — e.g. "Q3 incident-to-Jira rollout", "PDPL access-request automation".',
            title_placeholder: 'e.g. Q3 incident-to-Jira rollout',
            description_help: 'What this program covers and the outcome you expect when it is live. Optional but recommended.',
            description_placeholder: 'e.g. Wire monitoring alerts into Jira so on-call gets paged within 60s.',
            owner_label: 'Program Owner',
            owner_help: 'The person accountable for this program. They will receive notifications about progress and gates.',
            select_owner: 'Select an owner...',
            frameworks_in_scope: 'Frameworks in Scope',
            frameworks_in_scope_help: 'Compliance frameworks this program supports. Helps the audit trail show which regulations the automation is governed by.',
            select_frameworks: 'Select frameworks...',
            status_draft: 'Draft',
            status_active: 'Active',
            status_completed: 'Completed',
            status_archived: 'Archived',
            change_status: 'Change Status',
            status_updated: 'Status updated',
            aria_change_status: 'Change status for {name}',
        },
        system_inventory: {
            title: 'System Inventory',
            page_title: 'System Inventory',
            system_name: 'System Name',
            system_type: 'System Type',
            hosting: 'Hosting',
            status: 'Status',
            description: 'Description',
            create_system: 'Create System',
            edit_system: 'Edit System',
            no_systems_found: 'No systems found',
            system_created: 'System created',
            system_updated: 'System updated',
            select_type: 'Select a type...',
            select_hosting: 'Select hosting...',
            select_status: 'Select a status...',
            owner: 'Owner',
            department: 'Department',
            data_categories: 'Data Categories',
            data_categories_placeholder: 'Comma-separated, e.g. PII, Financial, Health',
            jurisdiction: 'Jurisdiction',
            jurisdiction_placeholder: 'e.g. EU, US, Global',
            interfaces: 'Interfaces',
            interfaces_placeholder: 'Comma-separated, e.g. REST API, SFTP, JDBC',
            owner_placeholder: 'Select an accountable owner...',
            department_placeholder: 'Select the responsible org unit...',
            system_name_hint: 'Use the business-recognized system or application name.',
            system_type_hint: 'Classify the system so reporting can group similar assets.',
            hosting_hint: 'Identify where the system runs for residency, security, and vendor review.',
            status_hint: 'Set the lifecycle state used by reviews, exports, and audit evidence.',
            owner_hint: 'Choose the active user accountable for system governance and follow-up tasks.',
            department_hint: 'Use the org unit responsible for operating or governing this system.',
            data_categories_hint: 'List the data classes processed by this system, separated by commas.',
            jurisdiction_hint: 'Record the main legal or hosting jurisdiction for compliance mapping.',
            interfaces_hint: 'List connector or integration paths used to exchange data.',
            description_hint: 'Summarize what the system does and why it matters to the data map.',
            owner_department_required: 'Owner and Department are required.',
            aria_systems_table: 'System inventory',
            aria_edit: 'Edit {name}',
            export_systems: 'Export Systems',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Traceability Matrix',
            page_title: 'Traceability Matrix',
            brd_selector: 'Select BRD',
            requirement: 'Requirement',
            linked_artifact: 'Linked Artifact',
            verification_status: 'Verification Status',
            verified_by: 'Verified By',
            create_entry: 'Create Entry',
            edit_entry: 'Edit Entry',
            no_entries_found: 'No traceability entries found',
            entry_created: 'Traceability entry created',
            entry_updated: 'Traceability entry updated',
            select_brd: 'Select a BRD...',
            select_status: 'Select a status...',
            aria_matrix_table: 'Traceability matrix',
            aria_edit: 'Edit entry for {name}',
            export_matrix: 'Export Matrix',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Requirement ID',
        },
        gap_register: {
            title: 'Gap Register',
            page_title: 'Gap Register',
            gap_title: 'Gap Title',
            gap_type: 'Gap Type',
            severity: 'Severity',
            status: 'Status',
            description: 'Description',
            remediation_plan: 'Remediation Plan',
            assigned_to: 'Assigned To',
            due_date: 'Due Date',
            create_gap: 'Create Gap',
            edit_gap: 'Edit Gap',
            no_gaps_found: 'No gaps found',
            gap_created: 'Gap created',
            gap_updated: 'Gap updated',
            select_type: 'Select a type...',
            select_severity: 'Select severity...',
            select_status: 'Select a status...',
            aria_gaps_table: 'Gap register',
            aria_edit: 'Edit {name}',
            export_gaps: 'Export Gaps',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'Each system in this inventory aligns with the Data Law enterprise governance model. System types (Application, Database, Middleware, Infrastructure, SaaS) map to regulatory categories, and the hosting classification (On-Premise, Cloud, Hybrid, Managed) determines which jurisdiction-specific controls apply. Use the framework linkage field to connect each system to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. The framework scope field determines which framework requirements the program addresses, and procedure linkage connects each program to documented compliance procedures. This ensures automation initiatives maintain a traceable chain from regulatory mandate to implementation.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. The regulatory source field identifies the specific regulation, directive, or standard that mandates the requirement. Verification status tracks whether the implementation has been confirmed to satisfy the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. The risk level field captures the assessed exposure, and findings document deviations from control requirements. Use the control mapping feature to link each review to the specific framework controls (e.g., ISO 27001 A.12, GDPR Art. 32) that were evaluated.',
            gap_register_framework_info: 'The Gap Register captures compliance gaps per regulatory framework. Use the framework filter to view gaps for a specific framework. Severity levels (Critical, High, Medium, Low) align with the framework risk classification, and the status workflow (Open, In Progress, Mitigated, Closed, Accepted) tracks remediation progress through the compliance lifecycle.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria specific to the applicable framework. Criteria structure supports weighted scoring with evidence notes. The vendor linkage field connects the evaluation to the vendor record, and the recommendation field provides the compliance-informed procurement decision.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity by including linked entity identifiers. Audit events are traceable across System Inventory, Automation Planning, Traceability Matrix, Security Reviews, Gap Register, and POC Scorecards.',
        },
        system_inventory_extra: {
            framework_linkage: 'Framework Linkage',
            framework_linkage_placeholder: 'e.g. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Framework Scope',
            framework_scope_placeholder: 'e.g. GDPR, PCI-DSS',
            procedure_linkage: 'Procedure Linkage',
            procedure_linkage_placeholder: 'e.g. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Regulatory Source',
            regulatory_source_placeholder: 'e.g. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Control Mapping',
            control_mappings_section: 'Control Mappings',
            no_control_mappings: 'No control mappings yet. Click "Create Control Mapping" to add one.',
            automation_type_col: 'Automation',
        },
        gap_register_extra: {
            framework_filter: 'Framework',
            framework_id: 'Framework ID',
            framework_id_placeholder: 'e.g. GDPR, ISO 27001',
            all_frameworks: 'All Frameworks',
        },
        poc_scorecards_extra: {
            criteria_count: 'Criteria',
        },
        export_labels: {
            export_brds: 'Export BRDs',
            export_decision_records: 'Export Decision Records',
            export_security_reviews: 'Export Security Reviews',
            export_poc_scorecards: 'Export POC Scorecards',
            export_instances: 'Export Instances',
            export_definitions: 'Export Definitions',
            export_credentials: 'Export Credentials',
            export_events: 'Export Events',
            exporting: 'Exporting...',
            export_success: 'Export completed',
            export_error: 'Export failed',
        },
    },
    // ---------- Vendor Risk Scoring module (Sprint 2.1.0) ----------
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Extract obligations',
            extracting: 'Extracting…',
            results_heading: 'Extracted obligations ({count})',
            tokens_label: '≈ {count} tokens',
            no_obligations: 'No obligation patterns detected in this contract text.',
            col_class: 'Class',
            col_summary: 'Summary (editable)',
            col_excerpt: 'Clause excerpt',
            col_due_date: 'Due date (editable)',
            col_priority: 'Priority',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Notice period',
            class_audit: 'Audit right',
            class_insurance: 'Insurance',
            class_sla: 'Response SLA',
            class_breach: 'Breach notification',
            class_renewal: 'Renewal window',
            citations_label: 'Citations',
            accept_hint: 'Accept will create one task per obligation (cap 8) with the priority + due date shown.',
            accept_label: 'Accept obligations',
            reject_label: 'Reject report',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created',
            error_generic: 'Could not extract obligations. Try again or check the contract text.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        // Sprint 48.2.4 — Forseti risk acceptance reasoning generator
        risk_acceptance_reasoning: {
            draft_label: 'Draft reasoning',
            drafting: 'Drafting reasoning…',
            results_heading: 'AI-drafted reasoning ({risk_level})',
            tokens_label: '≈ {count} tokens',
            narrative_label: 'Justification narrative',
            controls_heading: 'Compensating controls',
            no_controls: 'No compensating controls suggested.',
            citations_label: 'Citations',
            accept_label: 'Accept reasoning',
            reject_label: 'Reject draft',
            accept_hint: 'Accept records the reasoning on the persisted draft for IAT lineage. The narrative + controls do not auto-update the acceptance record — operator paste-back is required.',
            resolve_summary: 'Resolution: {status}',
            error_generic: 'Could not draft reasoning. Try again or check the risk acceptance status.',
        },
        // Sprint 48.2.3 — Forseti contract redline assistant + clause library
        redline_assistant: {
            run_label: 'Redline with library',
            running: 'Running redline…',
            results_heading: 'Redline suggestions ({count})',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'No standard-clause gaps detected.',
            jurisdiction_label: 'Jurisdiction',
            col_kind: 'Kind',
            col_severity: 'Severity',
            col_title: 'Suggestion',
            col_excerpt: 'Matched excerpt',
            col_apply: 'Apply',
            kind_missing: 'Missing standard',
            kind_weaker: 'Weaker than standard',
            kind_jurisdiction: 'Jurisdiction overlay',
            proposed_label: 'Proposed text',
            citations_label: 'Citations',
            accept_label: 'Accept selected',
            reject_label: 'Reject report',
            partial_label: 'Apply selection',
            accept_hint: 'Apply selection records the chosen suggestions on the draft for IAT lineage. No automatic edit is applied to the contract text — the operator drives the redline.',
            resolve_summary: 'Resolution: {status} · {count} suggestion(s) applied',
            error_generic: 'Could not produce a redline. Try again or check the contract status.',
            clause_library_link: 'Manage clause library',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        // Sprint 48.2.3 — Clause library admin page
        clause_library: {
            page_title: 'Clause Library',
            add_label: 'Add clause',
            edit_label: 'Edit',
            delete_label: 'Delete',
            slug_label: 'Slug',
            title_label: 'Title',
            body_label: 'Body',
            jurisdiction_label: 'Jurisdiction',
            tags_label: 'Tags (comma separated)',
            must_contain_label: 'Must-contain markers (comma separated)',
            severity_label: 'Severity when missing',
            no_clauses: 'No clauses yet. Add one to seed the redline assistant.',
            save_label: 'Save',
            cancel_label: 'Cancel',
            load_error: 'Could not load clause library.',
            save_error: 'Could not save clause.',
            delete_error: 'Could not delete clause.',
            delete_confirm: 'Delete clause "{slug}"? This cannot be undone.',
        },
        // Sprint 48.2.2 — Forseti contract obligation extraction panel
        // Sprint 48.2.1 — Forseti sub-processor change impact panel
        subprocessor_impact: {
            assess_label: 'Assess impact',
            assessing: 'Assessing impact…',
            results_heading: 'Sub-processor impact ({severity})',
            tokens_label: '≈ {count} tokens',
            risk_delta_label: 'Risk delta',
            notification_heading: 'Customer notification',
            subject_label: 'Subject',
            deadline_label: 'Notification deadline',
            body_label: 'Notification body',
            required_fields_label: 'Notice fields (Art.28(2))',
            mitigation_heading: 'Mitigation steps',
            citations_label: 'Citations',
            accept_label: 'Accept assessment',
            reject_label: 'Reject assessment',
            accept_hint: 'Accept will create a customer-notification task with the SLA deadline as the due date.',
            resolve_summary: 'Resolution: {status}{task}',
            error_generic: 'Could not assess this sub-processor change. Try again or check the change record.',
        },
        // Sprint 48.2.0 — Forseti onboarding evidence collector
        onboarding_evidence: {
            collect_label: 'Run AI evidence collection',
            collecting: 'Collecting evidence…',
            results_heading: 'Evidence collection ({tier})',
            tokens_label: '≈ {count} tokens',
            findings_heading: 'Existing evidence',
            missing_heading: 'Missing artefacts',
            no_findings: 'No existing evidence detected.',
            no_missing: 'No required artefacts missing for this risk tier.',
            next_step_label: 'Next step',
            class_dpa: 'Data Processing Agreement',
            class_soc2: 'SOC 2 report',
            class_iso27001: 'ISO 27001 certificate',
            class_insurance: 'Cyber insurance policy',
            class_incident_history: 'Prior incident',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Accept report',
            reject_label: 'Reject report',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Could not run AI evidence collection. Try again or check the onboarding case status.',
        },
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access vendor risk management.',
        unknown_vendor: 'Unknown Vendor',
        nav: {
            dashboard: 'Risk Dashboard',
            scoring: 'Risk Scoring',
            questionnaires: 'Questionnaires',
            contracts: 'Contracts',
            gates: 'Approval Gates',
            reviews: 'Vendor Reviews',
            signals: 'Risk Signals',
            reassessments: 'Reassessments',
            portfolio: 'Risk Portfolio',
            acceptances: 'Risk Acceptance',
            review_routing: 'Review Routing',
            onboarding_cases: 'Onboarding Cases',
            sub_processor_register: 'Sub-Processor Register',
        },
        dashboard: {
            title: 'Vendor Risk Dashboard',
            page_title: 'Vendor Risk Dashboard',
            loading: 'Loading dashboard data...',
            risk_tier_header: 'Risk Tier',
            count_header: 'Count',
            percentage_header: 'Percentage',
            sections_nav: 'Vendor risk sections',
            urgent_expiry: 'Urgent: expires within 14 days',
            no_previous_tier: 'No previous tier',
            view_as_chart: 'View as chart',
            view_as_table: 'View as table',
            showing_chart: 'Showing chart view',
            showing_table: 'Showing data table view',
            load_error: 'Failed to load vendor risk dashboard',
            risk_distribution: 'Risk Distribution Overview',
            recent_tier_changes: 'Recent Tier Changes',
            no_tier_changes: 'No recent tier changes.',
            review_kpis: 'Review KPIs',
            total_vendors_reviewed: 'Total Vendors Reviewed',
            reviews_by_outcome: 'Reviews by Outcome',
            contract_coverage: 'Contract Coverage',
            total_vendors: 'Total Vendors',
            with_active_dpa: 'With Active DPA',
            without_dpa: 'Without DPA',
            dpa_coverage: 'DPA Coverage',
            active_contracts_by_type: 'Active Contracts by Type',
            expiring_contracts: 'Expiring Contracts (Next 90 Days)',
            no_expiring_contracts: 'No contracts expiring within the next 90 days.',
            vendor: 'Vendor',
            previous_tier: 'Previous Tier',
            current_tier: 'Current Tier',
            changed_at: 'Changed At',
            expires_at: 'Expires At',
            days_remaining: 'Days Remaining',
            days: 'days',
            vendors_label: 'vendors',
            no_data: 'No data',
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
            expiring_acceptances_heading: 'Expiring risk acceptances',
            expiring_30d_label: 'Expiring in 30 days',
            expiring_60d_label: 'Expiring in 60 days',
            expiring_90d_label: 'Expiring in 90 days',
        },
        scoring: {
            title: 'Risk Scoring',
            page_title: 'Vendor Risk Scoring',
            filter_scores: 'Filter risk scores',
            filter_by_tier: 'Filter by risk tier',
            scores_pagination: 'Risk scores pagination',
            config_dimensions: 'Scoring dimensions configuration',
            scores_loaded: '{count} risk scores loaded',
            weight_must_equal_100: 'Weight total must equal 100 (current: {current})',
            out_of_10: 'out of 10, rated {tier}',
            dimension_name: 'Dimension {index} name',
            dimension_weight: '{name} weight (percentage)',
            must_equal_100: 'must equal 100',
            config_title: 'Scoring Configuration',
            scores_title: 'Vendor Risk Scores',
            new_score_title: 'Score a Vendor',
            new: 'New Score',
            vendor_id: 'Vendor ID',
            risk_tier: 'Risk Tier',
            score: 'Score',
            scored_at: 'Scored At',
            no_scores: 'No risk scores found.',
            all_tiers: 'All Tiers',
            load_config_error: 'Failed to load risk scoring configuration',
            update_config: 'Update Scoring Config',
            update_config_error: 'Failed to update risk scoring configuration',
            load_scores_error: 'Failed to load vendor risk scores',
            calculate_error: 'Failed to calculate vendor risk score',
            weight: 'weight',
            score_range: 'Score (1-5)',
            method: 'Method',
        },
        questionnaires: {
            title: 'Questionnaires',
            page_title: 'Vendor Questionnaires',
            filter_questionnaires: 'Questionnaire filters',
            questionnaire_views: 'Questionnaire views',
            templates_list: 'Questionnaire templates list',
            instances_list: 'Questionnaire instances list',
            filter_by_status: 'Filter by questionnaire status',
            templates_per_page: 'Templates per page',
            instances_per_page: 'Instances per page',
            select_template: 'Select questionnaire template',
            template_category: 'Template category',
            templates_tab: 'Templates',
            instances_tab: 'Instances',
            new: 'New Questionnaire',
            new_template: 'New Template',
            new_instance: 'New Instance',
            vendor_id: 'Vendor ID',
            template_id: 'Template ID',
            assigned_to: 'Assigned To',
            due_date: 'Due Date',
            responses: 'Responses',
            risk_score: 'Risk Score',
            no_questionnaires: 'No questionnaires found.',
            load_templates_error: 'Failed to load questionnaire templates',
            load_instances_error: 'Failed to load questionnaires',
            create_template_error: 'Failed to create questionnaire template',
            initiate_error: 'Failed to initiate questionnaire',
            submit_error: 'Failed to submit questionnaire',
            review_error: 'Failed to review questionnaire',
            resubmit: 'Resubmit',
            approve_error: 'Failed to approve questionnaire',
            reject_error: 'Failed to reject questionnaire',
            resubmit_error: 'Failed to resubmit questionnaire',
            templates_loaded: '{count} questionnaire templates loaded',
            instances_loaded: '{count} questionnaire instances loaded',
            fill_responses_cta: 'Fill responses',
            fill_responses_title: 'Fill questionnaire responses',
            template_missing: 'Template not found.',
            responses_saved: 'Responses saved.',
            load_template_error: 'Failed to load template.',
            // Sprint 41.2.0 E9 — version history disclosure for file_upload responses
            version_history: 'Version history',
            download_previous_version: 'Download previous version',
            // Sprint 43.6.0 W1 — Trigger label rename, Reviewer selector, choice renderers
            trigger: 'Trigger',
            template_label: 'Template',
            reviewer: 'Reviewer',
            reviewer_required: 'A reviewer must be assigned for {trigger} questionnaires (separation of duties).',
            select_option: 'Select an option',
            multi_choice_hint: 'Select all that apply',
            category_initial_onboarding: 'Initial onboarding',
            category_annual_review: 'Annual review',
            category_incident_triggered: 'Incident-triggered',
            category_contract_renewal: 'Contract renewal',
            // Sprint 43.6.0 W2 — Template Builder UI keys
            template_editor: {
                page_title: 'Edit questionnaire template',
                preview_page_title: 'Preview questionnaire template',
                edit_structure: 'Edit structure',
                preview: 'Preview',
                actions: 'Actions',
                back_to_list: 'Back to templates',
                load_error: 'Failed to load template',
                save_error: 'Failed to save template structure',
                saved: 'Template structure saved.',
                sections_heading: 'Sections',
                questions_heading: 'Questions',
                add_section: 'Add section',
                edit_section: 'Edit section',
                delete_section: 'Delete section',
                move_section_up: 'Move section up',
                move_section_down: 'Move section down',
                confirm_delete_section: 'Delete section "{title}"? All its questions will be removed.',
                section_title: 'Section title',
                section_description: 'Section description',
                section_id: 'Section ID',
                section_id_hint: 'Auto-generated unless you set one explicitly.',
                no_sections: 'No sections yet. Add a section to begin.',
                no_questions: 'No questions in this section yet.',
                add_question: 'Add question',
                edit_question: 'Edit question',
                delete_question: 'Delete question',
                move_question_up: 'Move question up',
                move_question_down: 'Move question down',
                confirm_delete_question: 'Delete question "{text}"?',
                question_text: 'Question text',
                question_type: 'Question type',
                question_id: 'Question ID',
                evidence_required: 'Evidence required',
                risk_weight: 'Risk weight',
                scoring_map: 'Scoring map',
                scoring_map_json_hint: 'JSON object mapping answer values to numeric scores.',
                scoring_map_invalid_json: 'Scoring map must be valid JSON.',
                options_heading: 'Answer options',
                add_option: 'Add option',
                remove_option: 'Remove option',
                option_label: 'Label',
                option_value: 'Value',
                option_risk_weight: 'Risk weight',
                min_options_error: 'Choice questions must have at least 2 options.',
                option_label_required: 'Each option needs a label.',
                option_value_required: 'Each option needs a value.',
                option_value_unique: 'Option values must be unique.',
                branching_rules_heading: 'Branching rules',
                branching_rules_hint: 'Show or hide this question based on the answer to an earlier question.',
                add_branching_rule: 'Add branching rule',
                remove_branching_rule: 'Remove branching rule',
                branching_condition_question: 'If question',
                branching_condition_value: 'has value',
                branching_action: 'then',
                branching_action_show: 'show this question',
                branching_action_hide: 'hide this question',
                branching_select_condition_question: 'Select earlier question',
                branching_select_condition_value: 'Select value',
                save: 'Save structure',
                saving: 'Saving...',
                cancel: 'Cancel',
                close: 'Close',
                question_text_required: 'Question text is required.',
                section_title_required: 'Section title is required.',
                // Question type display labels
                type_single_choice: 'Single choice',
                type_multi_choice: 'Multiple choice',
                type_free_text: 'Free text',
                type_numeric: 'Numeric',
                type_date: 'Date',
                type_file_upload: 'File upload',
                type_yes_no: 'Yes / No',
                preview_heading: 'Template preview',
                preview_subtitle: 'Read-only preview of how the questionnaire appears to a vendor.',
                preview_no_content: 'This template has no sections or questions yet.',
                preview_branching_notice: 'Branching rules are evaluated during fill-out; preview shows every question.',
            },
            // Sprint 43.6.0 W3 — Review interface keys
            review_modal: {
                title: 'Review questionnaire responses',
                open_cta: 'Review',
                loading: 'Loading questionnaire...',
                template_missing: 'Template not found.',
                load_error: 'Failed to load questionnaire for review.',
                annotation_heading: 'Annotations',
                annotation_placeholder: 'Add a reviewer annotation for this question...',
                annotation_status_label: 'Annotation status',
                annotation_status_acceptable: 'Acceptable',
                annotation_status_concern: 'Concern',
                annotation_status_requires_action: 'Requires action',
                review_comments_label: 'Overall review comments',
                review_comments_placeholder: 'Summarize your review findings...',
                justification_label: 'Decision justification',
                justification_placeholder: 'Explain the rationale for your decision...',
                reject_requires_comments: 'Rejecting a questionnaire requires review comments.',
                decision_heading: 'Decision',
                decision: {
                    approved: 'Approve',
                    rejected: 'Reject',
                    additional_info_requested: 'Request additional information',
                },
                submit: 'Submit review',
                submitting: 'Submitting review...',
                cancel: 'Cancel',
                submit_success: 'Review submitted.',
                submit_error: 'Failed to submit review.',
                separation_of_duties_error: 'You cannot review a questionnaire you were assigned to complete. Separation of duties requires a different reviewer.',
                decision_required: 'Please select a review decision.',
                announce_opened: 'Review modal opened.',
                announce_submitted: 'Review submitted.',
            },
            // Sprint 43.6.0 W3 — Instance detail review-field labels
            instance_detail: {
                submitted_at: 'Submitted',
                reviewed_at: 'Reviewed',
                reviewer: 'Reviewer',
                review_decision: 'Decision',
                review_comments: 'Review comments',
                decision_approved: 'Approved',
                decision_rejected: 'Rejected',
                decision_additional_info_requested: 'More info requested',
                decision_pending: 'Pending review',
                expand_comments: 'Show full comments',
                collapse_comments: 'Hide full comments',
                no_reviewer: 'No reviewer assigned',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            // Sprint 62.58.0 gate E12.5 pass 1 remainder — a questionnaire instance's status, the one
            // map the list page, the onboarding case's linked questionnaires, the portfolio's
            // outstanding list and both exports read (`questionnaireInstanceStatusLabel`).
            instance_statuses: {
                draft: 'Draft',
                in_progress: 'In Progress',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Approved',
                rejected: 'Rejected',
                expired: 'Expired',
            },
            // Sprint 43.6.0 W4 — Export + branching i18n
            export: {
                portfolio_label: 'Export portfolio',
                instance_label: 'Export',
                portfolio_success: 'Portfolio export ready.',
                instance_success: 'Questionnaire export ready.',
                portfolio_error: 'Failed to export questionnaire portfolio.',
                instance_error: 'Failed to export questionnaire.',
                // Sprint 62.58.0 gate E12.5 pass 1 remainder (P3-4) — the instance export's
                // header lines (`{label}: {value}`) and body labels no existing key words.
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                // The portfolio export's own header labels (`exportInstanceList`).
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Some questions are hidden by branching rules.',
                cycle_warning: 'A cycle was detected in branching rules. Affected questions default to visible.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Contracts',
            page_title: 'Vendor Contracts',
            new: 'New Contract',
            // fix-sprint-20260717-2118-followup F17 — real section identity that
            // replaces the section title `new` (which duplicated the modal
            // header) and its `document_type` field-label description.
            section_contract_details: 'Contract details',
            vendor_id: 'Vendor ID',
            filter_contracts: 'Filter contracts',
            filter_by_document_type: 'Filter by document type',
            filter_by_status: 'Filter by contract status',
            per_page: 'Contracts per page',
            expired_notice: 'This contract has expired',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Document Type',
            expiry_date: 'Expiry Date',
            no_contracts: 'No contracts found.',
            all_document_types: 'All Document Types',
            load_error: 'Failed to load contracts',
            create_error: 'Failed to create contract',
            delete_error: 'Failed to delete contract',
            update_error: 'Failed to update status',
            confirm_delete: 'Delete contract "{title}"?',
            renewal_date: 'Renewal Date',
            effective_date: 'Effective Date',
            parties: 'Parties',
            summary: 'Summary',
            n_contracts: '{count} contracts',
            n_contract: '{count} contract',
            contracts_loaded: '{count} contracts loaded',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Security Addendum',
            doc_type_scc_annex: 'SCC Annex',
            doc_type_sub_processor_list: 'Sub-Processor List',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Other',
            status_draft: 'Draft',
            status_under_negotiation: 'Under Negotiation',
            status_pending_signature: 'Pending Signature',
            status_active: 'Active',
            status_expired: 'Expired',
            status_terminated: 'Terminated',
            status_superseded: 'Superseded',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Approval Gates',
            page_title: 'Gate Evaluations',
            new: 'New Gate Evaluation',
            vendor_id: 'Vendor ID',
            gate_name: 'Gate Name',
            outcome: 'Outcome',
            evaluator: 'Evaluator',
            no_gates: 'No gate evaluations found.',
            load_error: 'Failed to load gate evaluations',
            evaluate_error: 'Failed to evaluate vendor gate',
            detail_error: 'Failed to fetch evaluation detail',
            override_error: 'Failed to override gate evaluation',
            criteria_results: 'Criteria Results',
            evaluate_form: 'Evaluate vendor gate',
            per_page: 'Evaluations per page',
            passed: 'Passed',
            failed: 'Failed',
            override: 'Override',
            override_justification: 'Override Justification',
            override_placeholder: 'Enter justification for overriding this gate evaluation',
            approval_id: 'Approval ID',
            overall_passed: 'Passed',
            overall_failed: 'Failed',
            overall_overridden: 'Overridden',
            evaluations_loaded: '{count} gate evaluations loaded',
            create_approval_cta: 'Create approval',
        },
        reviews: {
            title: 'Vendor Reviews',
            page_title: 'Vendor Reviews',
            new: 'New Review',
            vendor_id: 'Vendor ID',
            filter_by_outcome: 'Filter by review outcome',
            per_page: 'Reviews per page',
            kpi_summary: 'Review KPI summary',
            review_outcome_label: 'Review outcome',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Edit review outcome',
            reviews_loaded: '{count} vendor reviews loaded',
            reviewer: 'Reviewer',
            review_type: 'Review Type',
            outcome: 'Outcome',
            findings: 'Findings',
            no_reviews: 'No vendor reviews found.',
            load_kpis_error: 'Failed to fetch vendor review KPIs',
            load_error: 'Failed to fetch vendor reviews',
            update_error: 'Failed to update vendor review',
            create_error: 'Failed to create vendor review',
            total_reviewed: 'Total Vendors Reviewed',
            total_reviews: 'Total Reviews',
            satisfactory: 'Satisfactory',
            concerns_identified: 'Concerns Identified',
            remediation_required: 'Remediation Required',
            escalated: 'Escalated',
            program_id: 'Program ID',
            period_id: 'Period ID',
            task_id: 'Task ID',
            recommendations: 'Recommendations',
            // fix-sprint-20260717-2118-followup F9 — dedicated section identities
            // replace field-label pairs used as `title`+`description`.
            section_assignment: 'Assignment',
            section_findings_recommendations: 'Findings & recommendations',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Risk Signals',
            page_title: 'Risk Signals',
            new_signal: 'New Risk Signal',
            signal_type: 'Signal Type',
            severity: 'Severity',
            description: 'Description',
            source: 'Source',
            vendor_id: 'Vendor ID',
            certification_expiry: 'Certification Expiry',
            breach_notification: 'Breach Notification',
            financial_instability: 'Financial Instability',
            compliance_change: 'Compliance Change',
            custom: 'Custom',
            active: 'Active',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            no_signals: 'No risk signals found',
            source_external_feed: 'External Feed',
            source_manual: 'Manual',
            source_integration: 'Integration',
            source_regulatory: 'Regulatory',
            source_media: 'Media',
            trigger_reassessment_prompt: 'This signal is high severity. Start a vendor reassessment now?',
            reassessment_triggered: 'Reassessment triggered.',
            reassessment_trigger_error: 'Failed to trigger reassessment.',
        },
        reassessments: {
            title: 'Reassessment Tasks',
            page_title: 'Reassessment Tasks',
            trigger: 'Trigger Reassessment',
            trigger_success: 'Reassessment triggered successfully',
            reason: 'Reason',
            deadline: 'Deadline',
            assigned_to: 'Assigned To',
            vendor_id: 'Vendor ID',
            outcome: 'Outcome',
            new_risk_level: 'New Risk Level',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Complete Reassessment',
            complete_success: 'Reassessment completed successfully',
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
            no_tasks: 'No reassessment tasks found',
            risk_trend: 'Risk Trend',
            notes: 'Notes',
        },
        portfolio: {
            title: 'Risk Portfolio',
            page_title: 'Risk Portfolio',
            dashboard: 'Portfolio Dashboard',
            vendor_id: 'Vendor ID',
            total_vendors: 'Total Vendors',
            average_score: 'Average Risk Score',
            high_risk_count: 'High Risk Vendors',
            trend_direction: 'Trend Direction',
            improved: 'Improved',
            stable: 'Stable',
            degraded: 'Degraded',
            vendor_trend: 'Vendor Trend',
            outstanding_questionnaires: 'Outstanding Questionnaires',
            overdue_assessments: 'Overdue Assessments',
            no_outstanding: 'No outstanding questionnaires',
            no_overdue: 'No overdue assessments',
            due_date: 'Due Date',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Risk Acceptance Register',
            page_title: 'Risk Acceptance Register',
            new_acceptance: 'New Risk Acceptance',
            vendor_id: 'Vendor ID',
            risk_level: 'Risk Level',
            conditions: 'Conditions',
            justification: 'Justification',
            expiry_date: 'Expiry Date',
            create_success: 'Risk acceptance created successfully',
            review_condition: 'Review Condition',
            condition_met: 'Condition Met',
            condition_not_met: 'Condition Not Met',
            review_success: 'Condition reviewed successfully',
            export_register: 'Export Register',
            active: 'Active',
            expired: 'Expired',
            revoked: 'Revoked',
            no_acceptances: 'No risk acceptances found',
            met_count: '{met}/{total} met',
        },
        review_routing: {
            title: 'Review Routing',
            page_title: 'Review Routing',
            route_to_reviewers: 'Route to Reviewers',
            route_success: 'Routed to reviewers successfully',
            vendor_id: 'Vendor ID',
            reviewer_id: 'Reviewer ID',
            reviewer_role: 'Reviewer Role',
            questionnaire_id: 'Questionnaire ID',
            review_status: 'Review Status',
            reviewers_label: 'Reviewers (user_id, role per line)',
            pending: 'Pending',
            in_review: 'In Review',
            completed: 'Completed',
            no_routing: 'No review routing found',
            primary_reviewer: 'Primary Reviewer',
            secondary_reviewer: 'Secondary Reviewer',
            approver: 'Approver',
        },
        onboarding: {
            title: 'Vendor Onboarding Cases',
            page_title: 'Vendor Onboarding Cases',
            new_case: 'New Onboarding Case',
            case_detail: 'Onboarding Case',
            // Sprint 41.2.0 E8 — UC-VN-14 Step 10 MPS dossier filing from case
            open_mps_dossier_filing: 'Open MPS Dossier Filing',
            dossier_filing_description: 'Create a new MPS dossier filing pre-filled from this onboarding case. The filing will be linked back on the case timeline.',
            dossier_filing_dpia_id: 'DPIA ID',
            dossier_filing_dpia_id_placeholder: 'e.g. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Filing title',
            dossier_filing_form_type: 'Form type',
            dossier_filing_submit: 'Create filing',
            dossier_filing_validation_error: 'DPIA ID and filing title are required.',
            dossier_filing_created: 'MPS dossier filing created',
            dossier_filing_error: 'Failed to create MPS dossier filing.',
            // Sprint 41.2.0 E10 — Enhanced tab summary metadata
            linked_questionnaire_due_date: 'Due date',
            linked_questionnaire_assignee: 'Assignee',
            linked_questionnaire_submitted_at: 'Submitted at',
            score_last_scored: 'Last scored',
            score_dimensions: 'Dimensions',
            linked_contract_document_type: 'Document type',
            linked_contract_effective_date: 'Effective date',
            linked_contract_expiry_date: 'Expiry date',
            linked_sub_processor_countries: 'Processing countries',
            linked_sub_processor_last_assessment: 'Last assessment',
            linked_sub_processor_next_assessment: 'Next assessment',
            case_id: 'Case ID',
            vendor: 'Vendor',
            case_type: 'Case Type',
            service_description: 'Service Description',
            data_categories: 'Data Categories',
            systems_accessed: 'Systems Accessed',
            jurisdiction: 'Jurisdiction',
            hosting_model: 'Hosting Model',
            controller_role: 'Controller/Processor Role',
            status_pipeline: 'Status Pipeline',
            tabs_label: 'Onboarding case sections',
            per_page: 'Cases per page',
            filter_cases: 'Filter onboarding cases',
            cases_count: 'cases',
            cases_loaded: 'onboarding cases loaded',
            no_cases: 'No onboarding cases found.',
            vendor_lookup_placeholder: 'Enter vendor ID to look up',
            data_categories_placeholder: 'e.g., personal_data, financial, health',
            systems_placeholder: 'e.g., CRM, ERP, HR System',
            // Case statuses
            status_open: 'Open',
            status_in_progress: 'In Progress',
            status_awaiting_approval: 'Awaiting Approval',
            status_approved: 'Approved',
            status_remediation_required: 'Remediation Required',
            status_closed: 'Closed',
            // Case types
            type_new_vendor: 'New Vendor',
            type_service_change: 'Service Change',
            type_renewal: 'Renewal',
            type_reassessment: 'Reassessment',
            // Tab names
            tab_overview: 'Overview',
            tab_due_diligence: 'Due Diligence',
            tab_security_assessment: 'Security Assessment',
            tab_contractual_controls: 'Contractual Controls',
            tab_sub_processors: 'Sub-Processors',
            tab_approval_gates: 'Approval Gates',
            tab_evidence: 'Evidence',
            tab_placeholder: 'Content for this section will be populated during the onboarding process.',
            // Completion indicators
            completion_not_started: 'Not Started',
            completion_in_progress: 'In Progress',
            completion_completed: 'Completed',
            completion_requiring_attention: 'Requiring Attention',
            // Approval gates
            gate_name: 'Gate Name',
            approver: 'Approver',
            decision: 'Decision',
            comments: 'Comments',
            decide: 'Make Decision',
            decision_approved: 'Approved',
            decision_rejected: 'Rejected',
            decision_deferred: 'Deferred',
            decision_pending: 'Pending',
            no_approvals: 'No approval gates configured for this case.',
            approval_success: 'Approval decision recorded successfully',
            approval_error: 'Failed to record approval decision',
            // Status transitions
            transition_status: 'Transition Status',
            confirm_transition: 'Transition case from "{from}" to "{to}"?',
            transition_success: 'Case status transitioned successfully',
            transition_error: 'Failed to transition case status',
            // CRUD
            create_success: 'Onboarding case created successfully',
            create_error: 'Failed to create onboarding case',
            load_error: 'Failed to load onboarding cases',
            detail_error: 'Failed to load case details',
            export: 'Export',
            export_error: 'Failed to export onboarding case',
            evidence_tab_description: 'Upload and export evidence for the onboarding case.',
            upload_evidence_label: 'Upload evidence',
            attached_evidence_heading: 'Attached evidence',
            no_evidence_attached: 'No evidence attached yet.',
            export_bundle_button: 'Export evidence bundle',
            load_attachments_error: 'Failed to load evidence.',
            linked_questionnaires_heading: 'Linked questionnaires',
            no_linked_questionnaires: 'No linked questionnaires.',
            linked_scores_heading: 'Linked risk scores',
            no_linked_scores: 'No linked scores.',
            latest_score_label: 'Latest score',
            linked_contracts_heading: 'Linked contracts',
            no_linked_contracts: 'No linked contracts.',
            linked_sub_processors_heading: 'Linked sub-processors',
            no_linked_sub_processors: 'No linked sub-processors.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Mark Complete',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Sub-Processor Register',
            page_title: 'Sub-Processor Register',
            new_record: 'New Sub-Processor',
            edit_record: 'Edit Sub-Processor',
            onboarding_case: 'Onboarding Case',
            services: 'Services Provided',
            data_categories: 'Data Categories',
            processing_countries: 'Processing Countries',
            dpa_chain_status: 'DPA Chain Status',
            certifications: 'Certifications',
            last_assessment: 'Last Assessment',
            next_assessment: 'Next Assessment',
            change_history: 'Change History',
            history: 'History',
            changed_by: 'Changed by',
            comma_separated: 'Comma-separated values',
            filter_records: 'Filter sub-processor records',
            filter_by_dpa: 'Filter by DPA chain status',
            all_dpa_statuses: 'All DPA Statuses',
            per_page: 'Records per page',
            records_count: 'sub-processors',
            records_loaded: 'sub-processor records loaded',
            no_records: 'No sub-processor records found.',
            no_history: 'No change history available.',
            // DPA statuses
            dpa_active: 'Active',
            dpa_pending: 'Pending',
            dpa_expired: 'Expired',
            dpa_not_required: 'Not Required',
            // CRUD
            create_success: 'Sub-processor created successfully',
            create_error: 'Failed to create sub-processor',
            update_success: 'Sub-processor updated successfully',
            update_error: 'Failed to update sub-processor',
            load_error: 'Failed to load sub-processor register',
            history_error: 'Failed to load change history',
            // Export
            export_csv: 'Export CSV',
            export_json: 'Export JSON',
            export_error: 'Failed to export sub-processor data',
        },
    },
    // ---------- Deletion Orchestration module (Sprint 2.2.0) ----------
    deletion_orchestration: {
        nav: {
            dashboard: 'Orchestration Dashboard',
            runs: 'Orchestration Runs',
            tasks: 'Execution Tasks',
            templates: 'Checklist Templates',
            vendor_requests: 'Vendor Requests',
            proof_chain: 'Proof Chain',
            dsr_integration: 'DSR Integration',
            vendor_verification: 'Vendor Verification',
            completeness: 'Completeness Dashboard',
        },
        dashboard: {
            title: 'Orchestration Dashboard',
            loading: 'Loading dashboard data...',
            active_runs: 'Active Runs',
            completion_rate: 'Completion Rate',
            avg_completion_time: 'Avg Completion Time',
            overdue_tasks: 'Overdue Tasks',
            vendor_compliance: 'Vendor Compliance',
            // fix-sprint #431 — shown instead of a rate when no vendor deletion
            // request reached a vendor in the window (it read 100 % before).
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Recent Runs',
            no_recent_runs: 'No recent orchestration runs.',
            run_id: 'Run ID',
            ticket_id: 'Ticket ID',
            progress: 'Progress',
            systems: 'Systems',
            vendors: 'Vendors',
            load_error: 'Failed to load orchestration dashboard. Please try again.',
            aria_table: 'Recent orchestration runs',
        },
        runs: {
            title: 'Orchestration Runs',
            new: 'New Run',
            title_label: 'Title',
            run_type: 'Run Type',
            run_id: 'Run ID',
            ticket_id: 'Ticket ID',
            progress: 'Progress',
            systems: 'Systems',
            vendors: 'Vendors',
            data_subject_ref: 'Data Subject Ref',
            data_categories: 'Data Categories',
            enable_proof_chain: 'Enable Proof Chain',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            start: 'Start',
            started: 'Run started',
            mark_ready: 'Mark Ready',
            marked_ready: 'Run marked as ready',
            complete: 'Complete',
            complete_exceptions: 'Complete w/ Exceptions',
            pause: 'Pause',
            resume: 'Resume',
            cancel: 'Cancel',
            no_runs: 'No orchestration runs found.',
            load_error: 'Failed to load orchestration runs. Please try again.',
            create_error: 'Failed to create orchestration run',
            aria_filter_status: 'Filter by run status',
            aria_table: 'Orchestration runs',
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            placeholder_ticket_id: 'e.g., TKT-000123',
            placeholder_data_subject_ref: 'e.g., DS-REF-001',
            placeholder_data_categories: 'e.g., personal_data, financial, health',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Execution Tasks',
            task_id: 'Task ID',
            run_id: 'Run ID',
            system_name: 'System Name',
            assigned_to: 'Assigned To',
            sla_deadline: 'SLA Deadline',
            progress: 'Progress',
            enter_run_id: 'Enter Run ID to filter...',
            select_run: 'Select a run to view tasks',
            unassigned: 'Unassigned',
            overdue: 'Overdue',
            assign_task: 'Assign Task',
            assignee_id: 'Assignee ID',
            update_status: 'Update Status',
            new_status: 'New Status',
            no_transitions: 'This task is in a final state and cannot change status.',
            select_status_placeholder: 'Select a new status',
            skip_justification: 'Skip Justification',
            failure_reason: 'Failure Reason',
            no_tasks: 'No execution tasks found.',
            new_task: 'New Task',
            system_id: 'System ID',
            data_classification: 'Data Classification',
            load_error: 'Failed to load execution tasks. Please try again.',
            assign_error: 'Failed to assign task',
            status_error: 'Failed to update task status',
            no_tasks_for_run: 'This deletion run has no execution tasks yet.',
            aria_filter_run_id: 'Filter by run ID',
            aria_filter_status: 'Filter by task status',
            aria_table: 'Execution tasks',
            // Sprint 41.3.0 F2 — step-level evidence
            view_steps: 'View Steps',
            steps_modal_title: 'Execution Steps',
            step_title: 'Step',
            step_status: 'Step Status',
            step_notes: 'Notes',
            step_evidence: 'Evidence Files',
            step_evidence_required: 'Evidence is required for this step',
            step_evidence_missing: 'No evidence uploaded yet',
            step_save: 'Save Step',
            step_saved: 'Step updated',
            step_save_error: 'Failed to update step',
            create_success: 'Execution task created',
            create_error: 'Failed to create execution task',
            step_upload_label: 'Attach evidence',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Checklist Templates',
            new: 'New Template',
            template_id: 'Template ID',
            system_type: 'System Type',
            est_duration: 'Est. Duration',
            steps: 'Steps',
            action_type: 'Action Type',
            instructions: 'Instructions',
            mandatory: 'Mandatory',
            add_step: 'Add Step',
            step_n: 'Step',
            no_templates: 'No checklist templates found.',
            all_system_types: 'All System Types',
            load_error: 'Failed to load checklist templates. Please try again.',
            create_error: 'Failed to create checklist template',
            aria_filter_system_type: 'Filter by system type',
            aria_filter_status: 'Filter by template status',
            aria_table: 'Checklist templates',
            placeholder_name: 'e.g., Database Deletion Checklist',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Template updated successfully',
            create_success_msg: 'Template created successfully',
            update_error: 'Failed to update template',
            activate_success: 'Template activated successfully',
            activate_error: 'Failed to activate template',
            archive_success: 'Template archived successfully',
            archive_error: 'Failed to archive template',
            clone_success: 'Template cloned successfully',
            clone_error: 'Failed to clone template',
        },
        vendor_requests: {
            title: 'Vendor Deletion Requests',
            new: 'New Request',
            // fix-sprint #431 — the create form's run picker. The form never sent
            // `run_id`, so no request recorded here counted toward a run's progress.
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Request ID',
            vendor_name: 'Vendor Name',
            vendor_id: 'Vendor ID',
            data_subject_ref: 'Data Subject Ref',
            data_categories: 'Data Categories',
            legal_basis: 'Legal Basis',
            sla_days: 'SLA Days',
            requested_completion: 'Requested Completion',
            communications: 'Communications',
            total_requests: 'Total Requests',
            no_requests: 'No vendor deletion requests found.',
            filter_vendor_id: 'Filter by Vendor ID...',
            change_status: 'Change Status',
            new_status: 'New Status',
            actions: 'Actions',
            load_error: 'Failed to load vendor deletion requests. Please try again.',
            create_error: 'Failed to create vendor deletion request',
            dashboard_error: 'Failed to load vendor deletion dashboard. Please try again.',
            aria_filter_status: 'Filter by request status',
            aria_filter_vendor_id: 'Filter by vendor ID',
            aria_table: 'Vendor deletion requests',
            placeholder_vendor_id: 'e.g., VND-001',
            placeholder_vendor_name: 'e.g., Acme Analytics',
            placeholder_data_subject_ref: 'e.g., DS-REF-001',
            placeholder_data_categories: 'e.g., personal_data, analytics',
            placeholder_legal_basis: 'e.g., PDPL Article 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Proof Chain Viewer',
            enter_run_id: 'Enter Run ID...',
            load_chain: 'Load Chain',
            verify_chain: 'Verify Chain',
            verifying: 'Verifying...',
            chain_valid: 'Chain is valid',
            chain_invalid: 'Chain is broken',
            chain_length: 'Chain Length',
            broken_at_index: 'Broken at index',
            no_proof_records: 'No proof records found for this run.',
            proof_id: 'Proof ID',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            event_hash: 'Event Hash',
            previous_hash: 'Previous Hash',
            attestation: 'Attestation',
            attestation_actor: 'Performed by',
            attestation_signed_at: 'Signed At',
            attestation_signature: 'Signature',
            attestation_evidence_refs: 'Evidence References',
            run_field_label: 'Deletion Run',
            select_run: 'Select a deletion run…',
            aria_select_run: 'Deletion run for proof chain lookup',
            chain_broken_here: 'Chain broken here',
            load_error: 'Failed to load proof chain. Please try again.',
            verify_error: 'Failed to verify proof chain. Please try again.',
            aria_run_id: 'Run ID for proof chain lookup',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — a destruction record's identification.
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Change status',
            request_type: 'Request Type',
            data_subject_hint: 'Identifier of the data subject whose data will be erased (e.g. email or customer ID). Usually matches the requester on the selected DSR case.',
            title: 'DSR Deletion Integration',
            create_run: 'Create DSR Deletion Run',
            dsr_case_id: 'DSR Case ID',
            data_subject_id: 'Data Subject ID',
            systems: 'Target Systems',
            route_to_department: 'Route to Department',
            confirm_deletion: 'Confirm Deletion',
            no_runs: 'No DSR deletion runs found',
            status: 'Deletion Status',
            deadline: 'Deadline',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Vendor Deletion Verification',
            create_followup: 'Create Follow-up',
            deadline: 'Confirmation Deadline',
            escalation_contact: 'Escalation Contact',
            // fix-sprint #431 — `record_confirmation`, `overdue` ("Overdue
            // Deletions") and `no_overdue` named a tab and an action this page does
            // not have, and are gone. Escalating changes the follow-up, not the
            // vendor deletion request, and sends nothing.
            escalate: 'Escalate',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'No vendor follow-ups found',
            vendor_request: 'Vendor Request',
            vendor: 'Vendor',
            unknown_vendor: 'Unknown vendor',
            overdue_badge: 'Overdue',
            mark_confirmed: 'Mark Confirmed',
            mark_confirmed_for: 'Mark deletion confirmed for {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Follow-up marked confirmed',
            confirmation_failed: 'Failed to mark the follow-up confirmed',
            select_request: 'Select a vendor request…',
            escalation_contact_placeholder: 'e.g. dpo@vendor.example',
            create_success: 'Vendor follow-up created successfully',
            create_failed: 'Failed to create vendor follow-up',
            // fix-sprint #431 — the page lists every follow-up whatever its
            // deadline; the old hint ("lists only follow-ups whose deadline has
            // passed") and its future-deadline warning described a page that was
            // not this one.
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            // fix-sprint-20260411-D F7 — semantic toast/announce keys
            escalation_success: 'Follow-up escalated',
            escalation_failed: 'Failed to escalate the follow-up',
            // fix-sprint #431 — Overdue is not a status a follow-up holds: it is
            // worked out on read, and this filter asks the server for exactly the
            // rows the Overdue badge marks.
            overdue_filter: 'Overdue (pending, deadline passed)',
            // fix-sprint #431 — the states a follow-up holds
            // (`VENDOR_FOLLOWUP_STATUSES`). The page read the vendor REQUEST labels,
            // which have no `pending` or `confirmed`, so both showed raw.
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Deletion Completeness',
            check_conflicts: 'Check Retention Conflicts',
            no_conflicts: 'No retention conflicts found',
            conflict_resolution: 'Conflict Resolution',
            extend_retention: 'Extend Retention',
            proceed_deletion: 'Proceed with Deletion',
            escalate_conflict: 'Escalate',
            resolve: 'Resolve Conflict',
            data_subject_id: 'Data Subject ID',
            placeholder_data_subject_id: 'Enter data subject ID',
            data_subject_hint: 'Identifier of the person whose deletion you are verifying (e.g. email, customer ID, or DSR reference). Pick a known subject or type one.',
            guidance: 'Enter or select a data subject ID, optionally choose systems, then run the check to see deletion completeness and retention conflicts.',
            completion: 'Deletion Completion',
            pending_items: 'Pending Items',
            system: 'System',
            policy: 'Policy',
            retention_end: 'Retention End',
            completed_items: 'Completed Items',
            last_review_label: 'Last review',
            days_ago: 'days ago',
            review_overdue: 'Review cycle exceeded. Re-review required.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        // ── Enum label maps (Sprint 28.4.0) ──────────────────────────
        run_status_labels: {
            pending: 'Pending',
            planning: 'Planning',
            ready: 'Ready',
            in_progress: 'In Progress',
            paused: 'Paused',
            completed: 'Completed',
            completed_with_exceptions: 'Completed with Exceptions',
            cancelled: 'Cancelled',
        },
        task_status_labels: {
            pending: 'Pending',
            assigned: 'Assigned',
            in_progress: 'In Progress',
            awaiting_verification: 'Awaiting Verification',
            verified: 'Verified',
            failed: 'Failed',
            skipped: 'Skipped',
        },
        system_type_labels: {
            database: 'Database',
            saas_application: 'SaaS Application',
            file_storage: 'File Storage',
            email_system: 'Email System',
            analytics_platform: 'Analytics Platform',
            backup_system: 'Backup System',
            vendor_managed: 'Vendor Managed',
            custom: 'Custom',
        },
        template_status_labels: {
            draft: 'Draft',
            active: 'Active',
        },
        action_type_labels: {
            manual_check: 'Manual Check',
            api_call: 'API Call',
            sql_query: 'SQL Query',
            script_execution: 'Script Execution',
            file_deletion: 'File Deletion',
            verification: 'Verification',
            notification: 'Notification',
            approval: 'Approval',
        },
        request_status_labels: {
            draft: 'Draft',
            sent: 'Sent',
            acknowledged: 'Acknowledged',
            in_progress: 'In Progress',
            completed: 'Completed',
            overdue: 'Overdue',
            escalated: 'Escalated',
            disputed: 'Disputed',
            // fix-sprint #431 — reachable from Draft (`updateStatus`); shown raw before.
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Initiation',
            data_deletion: 'Data Deletion',
            completion: 'Completion',
            verification: 'Verification',
            rollback: 'Rollback',
        },
        // ── Count labels ─────────────────────────────────────────────
        count_labels: {
            one_template: '1 template',
            n_templates: '{n} templates',
            one_run: '1 run',
            n_runs: '{n} runs',
            one_task: '1 task',
            n_tasks: '{n} tasks',
            one_request: '1 request',
            n_requests: '{n} requests',
        },
        // ── Aria labels ──────────────────────────────────────────────
        aria: {
            completion_rate: 'Completion rate: {pct}%',
            vendor_compliance: 'Vendor compliance: {pct}%',
            run_progress: 'Run progress: {pct}%',
            run_progress_systems: 'Run progress: {completed} of {total} systems processed',
            chain_broken: 'Chain broken at this record',
            chain_valid: 'Chain valid at this record',
            chain_unknown: 'Chain status unknown',
            escalate_vendor: 'Escalate {id}',
            resolve_conflict: 'Resolve conflict for {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'Deletion completeness must be reviewed at least every 365 days. Records exceeding this threshold are flagged for mandatory re-review to maintain regulatory compliance.',
            runs_data_d1_lifecycle_info: 'Orchestration run states map to the DATA-D1 lifecycle: planning = catalog, ready = classify, in_progress = apply rules, completed = evidence generated, completed_with_exceptions = review required. Each transition is recorded in the proof chain.',
            vendor_record_linkage_info: 'Each vendor deletion request is linked to its vendor record via vendor_id. Completion confirmation requires cryptographic evidence from the proof chain before the request can be marked as completed.',
            templates_internal_classification_info: 'All deletion checklist templates are classified as internal organizational documents. They do not carry official regulatory form IDs and should not be submitted as regulatory filings.',
            proof_chain_evidence_info: 'Each proof record captures the data subject/category, timestamp, actor, legal basis, and a SHA-256 hash. Together these fields produce cryptographic deletion evidence that satisfies regulatory audit requirements.',
            retention_data_law_info: 'Retention schedules on this dashboard are derived from the Data Law classification assigned to each data category. When a classification changes, linked retention rules update automatically to ensure regulatory alignment.',
            dsr_erasure_route_info: 'DSR erasure runs are linked to the originating Rights Request case. The regulatory deadline (typically 30 days under GDPR Art. 17) is tracked automatically. Overdue cases are flagged for escalation.',
        },
    },
    // ---------- Data Discovery module (Sprint 2.3.0) ----------
    discovery: {
        nav: {
            dashboard: 'Discovery Dashboard',
            scans: 'Discovery Scans',
            staging_area: 'Staging Area',
            dedup_rules: 'Dedup Rules',
            classification_rules: 'Classification Rules',
            suggestions: 'Suggestion Queue',
            analytics: 'Suggestion Analytics',
            import_templates: 'Import Templates',
            connectors: 'Connector Management',
            discovery_cycles: 'Discovery Cycles',
            owner_review: 'Owner Review',
            classification_lineage: 'Classification Lineage',
        },
        dashboard: {
            title: 'Discovery Dashboard',
            loading: 'Loading dashboard data...',
            total_scans: 'Total Scans',
            active_scans: 'Active Scans',
            completed_30d: 'Completed (30d)',
            failed_30d: 'Failed (30d)',
            systems_discovered: 'Systems Discovered',
            pending_staged_records: 'Pending Staged Records',
            recent_scans: 'Recent Scans',
            no_recent_scans: 'No recent scans.',
            scan_id: 'Scan ID',
            discovered: 'Discovered',
            view_all_scans: 'View All Scans',
            staging_area: 'Staging Area',
            suggestion_queue: 'Suggestion Queue',
            records_reviewed: 'Records Reviewed',
            records_approved: 'Records Approved',
            exceptions_pending: 'Exceptions Pending',
            view_as_chart: 'View as chart',
            view_as_table: 'View as table',
        },
        scans: {
            title: 'Discovery Scans',
            new: 'New Scan',
            scan_id: 'Scan ID',
            scan_type: 'Scan Type',
            progress: 'Progress',
            discovered: 'Discovered',
            no_scans: 'No discovery scans found.',
            all_scan_types: 'All Scan Types',
            source_config: 'Source Config (JSON)',
            invalid_json: 'Invalid JSON in source config',
            load_error: 'Failed to load discovery scans. Please try again.',
            create_error: 'Failed to create discovery scan',
            change_status: 'Change Status',
            new_status: 'New Status',
            actions: 'Actions',
            type_endpoint: 'Endpoint',
            type_spreadsheet_import: 'Spreadsheet Import',
            type_cmdb_sync: 'CMDB Sync',
            type_cloud_sync: 'Cloud Sync',
            status_pending: 'Pending',
            status_running: 'Running',
            status_completed: 'Completed',
            status_failed: 'Failed',
            status_cancelled: 'Cancelled',
            source_config_placeholder: '{"host": "...", "port": 443}',
            adapter_not_implemented_toast: 'This scan type is not yet available. Only endpoint scans can be created.',
            adapter_not_implemented_warning: 'Not yet available. Scans of this type cannot be created until support for it is released.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Staging Area',
            record_id: 'Record ID',
            source_scan: 'Source Scan',
            entity_type: 'Entity Type',
            suggested_name: 'Suggested Name',
            confidence: 'Confidence',
            no_records: 'No staged records found.',
            promote: 'Promote',
            discard: 'Discard',
            review: 'Review',
            merge: 'Merge',
            merge_title: 'Merge Record',
            merge_target_id: 'Merge Target ID',
            reject_reason_placeholder: 'Reason for rejection...',
            promotion_status: 'Promotion Status',
            dedup_status: 'Dedup Status',
            all_source_types: 'All Source Types',
            all_promotion_statuses: 'All Promotion Statuses',
            all_dedup_statuses: 'All Dedup Statuses',
            load_error: 'Failed to load staged records. Please try again.',
            promote_error: 'Failed to promote record',
            reject_error: 'Failed to reject record',
            merge_error: 'Failed to merge record',
            review_error: 'Failed to mark record for review',
            bulk_promote_error: 'Failed to bulk promote records',
            bulk_reject_error: 'Failed to bulk reject records',
            service_type_web_server: 'Web Server',
            service_type_database: 'Database',
            service_type_api_gateway: 'API Gateway',
            service_type_storage: 'Storage',
            service_type_authentication: 'Authentication',
            service_type_analytics: 'Analytics',
            service_type_unknown: 'Unknown',
            source_type_endpoint_scan: 'Endpoint Scan',
            source_type_spreadsheet: 'Spreadsheet',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Cloud Inventory',
            promotion_status_new: 'New',
            promotion_status_under_review: 'Under Review',
            promotion_status_promoted: 'Promoted',
            promotion_status_rejected: 'Rejected',
            promotion_status_merged: 'Merged',
            dedup_status_unmatched: 'Unmatched',
            dedup_status_potential_match: 'Potential Match',
            dedup_status_exact_match: 'Exact Match',
            dedup_status_resolved: 'Resolved',
            confirm_bulk_reject: 'Are you sure you want to reject the selected records?',
            scan_batch_id: 'Batch ID',
            promote_to_registry_title: 'Promote to Data Registry',
            promote_modal_instructions: 'Creates a Data Registry item with collection_method: observed and provenance linkage to this discovery record. The item starts in draft status for review.',
            promote_label_category: 'Category',
            promote_label_name_override: 'Name (optional override)',
            placeholder_name_override: "Defaults to the discovery record's name/hostname",
            promote_label_sensitivity: 'Proposed sensitivity',
            inherit_from_category: 'Inherit from category',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Dedup Rules',
            new: 'New Rule',
            rule_name: 'Rule Name',
            match_fields: 'Match Fields',
            threshold: 'Threshold',
            no_rules: 'No dedup rules found.',
            seed_defaults: 'Seed Defaults',
            match_type: 'Match Type',
            similarity_threshold: 'Similarity Threshold',
            field_name: 'Field Name',
            load_error: 'Failed to load dedup rules. Please try again.',
            create_error: 'Failed to create dedup rule',
            toggle_error: 'Failed to toggle dedup rule',
            seed_error: 'Failed to seed defaults',
            priority: 'Priority',
            enabled_status: 'Enabled',
            disabled_status: 'Disabled',
            default_badge: 'Default',
            enable: 'Enable',
            disable: 'Disable',
            match_type_exact: 'Exact',
            match_type_fuzzy: 'Fuzzy',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP Range',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Classification Rules',
            new: 'New Rule',
            rule_name: 'Rule Name',
            pattern: 'Pattern',
            classification: 'Classification',
            no_rules: 'No classification rules found.',
            seed_defaults: 'Seed Defaults',
            run_rules_now: 'Run Rules Now',
            run_rules_success: 'Generated {count} new classification suggestion(s)',
            run_rules_no_results: 'No new suggestions generated — all matching rules already have pending suggestions',
            run_rules_error: 'Failed to run classification rules. Please try again.',
            taxonomy_id: 'Taxonomy ID',
            level_id: 'Level ID',
            condition_logic: 'Condition Logic',
            weight: 'Weight',
            conditions: 'Conditions',
            condition_field: 'Field',
            condition_operator: 'Operator',
            condition_value: 'Value',
            effectiveness: 'Effectiveness',
            match_count: 'Matches',
            approval_count: 'Approvals',
            rejection_count: 'Rejections',
            filter_taxonomy: 'Filter by Taxonomy ID...',
            load_error: 'Failed to load classification rules. Please try again.',
            create_error: 'Failed to create classification rule',
            seed_error: 'Failed to seed defaults',
            toggle_error: 'Failed to toggle rule. Please try again.',
            priority: 'Priority',
            enabled_status: 'Enabled',
            disabled_status: 'Disabled',
            enable: 'Enable',
            disable: 'Disable',
            logic_and: 'AND',
            logic_or: 'OR',
            operator_equals: 'Equals',
            operator_contains: 'Contains',
            operator_matches_regex: 'Matches Regex',
            operator_in_list: 'In List',
            operator_not_equals: 'Not Equals',
            operator_not_contains: 'Not Contains',
            enabled_label: 'Enabled',
            disabled_label: 'Disabled',
            taxonomy_label: 'Taxonomy',
            operator_label: 'Operator',
            value_label: 'Value',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Suggestion Queue',
            entity_type: 'Entity Type',
            suggested_name: 'Suggested Name',
            source: 'Source',
            confidence: 'Confidence',
            accept: 'Accept',
            reject: 'Reject',
            no_suggestions: 'No suggestions in queue.',
            modify_approve: 'Modify & Approve',
            delegate: 'Delegate',
            delegate_user_id: 'Delegate User ID',
            new_level_id: 'New Level ID',
            reject_reason_placeholder: 'Reason for rejection...',
            current_level: 'Current Level',
            suggested_level: 'Suggested Level',
            filter_taxonomy: 'Filter by Taxonomy ID...',
            all_confidence: 'All Confidence Bands',
            load_error: 'Failed to load suggestions. Please try again.',
            approve_error: 'Failed to approve suggestion',
            reject_error: 'Failed to reject suggestion',
            modify_error: 'Failed to modify & approve suggestion',
            delegate_error: 'Failed to delegate suggestion',
            bulk_approve_error: 'Failed to bulk approve suggestions',
            bulk_reject_error: 'Failed to bulk reject suggestions',
            suggestion_type_new_label: 'New Label',
            suggestion_type_validation: 'Validation',
            suggestion_type_reclassification: 'Reclassification',
            status_pending: 'Pending',
            status_approved: 'Approved',
            status_rejected: 'Rejected',
            status_superseded: 'Superseded',
            confidence_high: 'High',
            confidence_medium: 'Medium',
            confidence_low: 'Low',
            confirm_bulk_reject: 'Are you sure you want to reject the selected suggestions?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'All taxonomies',
        },
        analytics: {
            title: 'Suggestion Analytics',
            total_suggestions: 'Total Suggestions',
            accepted: 'Accepted',
            rejected: 'Rejected',
            pending: 'Pending',
            acceptance_rate: 'Acceptance Rate',
            by_entity_type: 'By Entity Type',
            by_source: 'By Source',
            avg_decision_time: 'Avg Decision Time',
            rule_effectiveness: 'Rule Effectiveness',
            by_status: 'By Status',
            by_confidence: 'By Confidence Band',
            load_error: 'Failed to load suggestion analytics. Please try again.',
            confidence_high: 'High',
            confidence_medium: 'Medium',
            confidence_low: 'Low',
            by_confidence_band: 'By Confidence Band',
            approval_rate: 'Approval Rate',
            by_label_state: 'By Label State',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Import Templates',
            new: 'New Template',
            template_name: 'Template Name',
            file_format: 'File Format',
            column_mappings: 'Column Mappings',
            no_templates: 'No import templates found.',
            source_column: 'Source Column',
            target_field: 'Target Field',
            transform: 'Transform',
            source_column_label: 'Source Column',
            target_field_label: 'Target Field',
            transform_label: 'Transform',
            confirm_delete: 'Are you sure you want to delete this import template?',
            load_error: 'Failed to load import templates. Please try again.',
            save_error: 'Failed to save import template',
            delete_error: 'Failed to delete import template',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Connector Management',
            new: 'New Connector',
            connector_name: 'Connector Name',
            connector_type: 'Type',
            last_sync: 'Last Sync',
            no_connectors: 'No connectors found.',
            cmdb_syncs: 'CMDB Syncs',
            cloud_syncs: 'Cloud Syncs',
            new_sync: 'New Sync',
            no_syncs: 'No syncs found.',
            cmdb_error: 'Failed to load CMDB scans. Please try again.',
            cloud_error: 'Failed to load cloud scans. Please try again.',
            trigger_error: 'Failed to trigger sync',
            trigger_sync_error: 'Failed to trigger {type}',
            cmdb_section_title: 'CMDB Syncs',
            cloud_section_title: 'Cloud Syncs',
            column_connector: 'Connector',
            action_start: 'Start',
            confirm_cancel_sync: 'Cancel this sync?',
            start_error: 'Failed to start sync',
            cancel_error: 'Failed to cancel sync',
            type_endpoint: 'Endpoint Scan',
            type_spreadsheet_import: 'Spreadsheet Import',
            type_cmdb_sync: 'CMDB Sync',
            type_cloud_sync: 'Cloud Sync',
        },
        discovery_cycles: {
            title: 'Discovery Cycles',
            new_cycle: 'New Discovery Cycle',
            cycle_name: 'Cycle Name',
            scope_type: 'Scope Type',
            scope_ids: 'Scope IDs',
            cadence_days: 'Cadence (Days)',
            status: 'Status',
            no_cycles: 'No discovery cycles found',
            compare: 'Compare Cycles',
            delta_report: 'Delta Report',
            new_records: 'New Records',
            changed_records: 'Changed Records',
            removed_records: 'Removed Records',
            schedule: 'Schedule Scan',
            scope_organization: 'Organization',
            scope_department: 'Department',
            scope_system: 'System',
            scope_systems: 'Systems',
            bulk_actions: 'Bulk Actions',
            selected: '{count} selected',
            bulk_create: 'Bulk Create Cycles',
            bulk_assign_scope: 'Bulk Assign Scope',
            bulk_advance: 'Bulk Advance Status',
            confirm_bulk_action: 'Confirm Bulk Action',
            bulk_confirm_message: 'Are you sure you want to perform this action?',
            cycles_affected: '{count} cycles will be affected',
            select_all: 'Select all',
            select_cycle: 'Select cycle {name}',
            from_template: 'from template',
            bulk_results: 'Bulk Results',
            failed_label: 'failed',
            template_id: 'Template ID',
            count: 'Count',
            advance_to: 'Target status',
            bulk_success: 'Bulk operation completed',
        },
        owner_review: {
            title: 'Owner Review Tasks',
            assign_owner: 'Assign to Owner',
            owner_id: 'Owner',
            decision: 'Decision',
            approve: 'Approve',
            modify: 'Modify',
            reject: 'Reject',
            escalate: 'Escalate',
            no_tasks: 'No review tasks found',
            bulk_resolve: 'Bulk Resolve',
            exceptions: 'Exception Items',
            no_exceptions: 'No exception items found',
            record_id_label: 'Record ID',
            selected: 'selected',
            notes: 'Notes',
            select_all: 'Select All',
            select: 'Select',
            confirm_resolve: 'Confirm',
        },
        classification_lineage: {
            title: 'Classification Lineage',
            link_suggestion: 'Link to Proposed Label',
            suggestion_id: 'Suggestion',
            proposed_label_id: 'Proposed Label',
            no_lineage: 'No lineage data found',
            full_lineage: 'Full Lineage',
            record_id_label: 'Record ID',
            staged_record_label: 'Staged Record',
            enter_staged_record_id: 'Enter staged record ID',
            scan_id_label: 'Scan ID',
            select_record: 'Select a record',
            select_suggestion: 'Select a suggestion',
            select_label: 'Select a label',
        },
        common_labels: {
            n_rules: '{n} rules',
            one_rule: '1 rule',
            n_scans: '{n} scans',
            one_scan: '1 scan',
            n_syncs: '{n} syncs',
            one_sync: '1 sync',
            n_records: '{n} records',
            one_record: '1 record',
            n_templates: '{n} templates',
            one_template: '1 template',
            n_suggestions: '{n} suggestions',
            one_suggestion: '1 suggestion',
            n_selected: '{n} selected',
            dashboard_loaded: 'Discovery dashboard loaded',
            n_scans_loaded: '{n} scans loaded',
            n_staged_records_loaded: '{n} staged records loaded',
            confirm_delete_import_template: 'Are you sure you want to delete this import template?',
            invalid_json_source_config: 'Invalid JSON in source_config',
            scope_organization: 'Organization',
            scope_department: 'Department',
            scope_system: 'System',
            aria_filter_scans: 'Filter scans',
            aria_filter_staged_records: 'Filter staged records',
            aria_filter_suggestions: 'Filters',
            aria_filter_by_taxonomy_id: 'Filter by taxonomy ID',
            aria_filter_by_enabled_status: 'Filter by enabled status',
            aria_filter_by_source_type: 'Filter by source type',
            aria_filter_by_promotion_status: 'Filter by promotion status',
            aria_filter_by_dedup_status: 'Filter by dedup status',
            aria_filter_by_scan_type: 'Filter by scan type',
            aria_filter_by_status: 'Filter by status',
            aria_filter_by_confidence: 'Filter by confidence band',
            aria_select_all_records: 'Select all records',
            aria_select_record: 'Select record {id}',
            aria_select_all_suggestions: 'Select all suggestions',
            aria_select_suggestion: 'Select suggestion {id}',
            aria_promote_record: 'Promote record {id}',
            aria_discard_record: 'Discard record {id}',
            aria_merge_record: 'Merge record {id}',
            aria_review_record: 'Review record {id}',
            aria_accept_suggestion: 'Accept suggestion {id}',
            aria_reject_record: 'Reject record',
            aria_merge_record_dialog: 'Merge record',
            aria_create_new_scan: 'Create new scan',
            aria_sync_progress: 'Sync progress: {processed} of {total} items processed',
            aria_scan_progress: 'Scan progress: {processed} of {total} items processed',
            aria_confidence: 'Confidence: {pct}%',
            aria_effectiveness: 'Effectiveness: {pct}',
            aria_promote_n_records: 'Promote {n} selected records',
            aria_discard_n_records: 'Discard {n} selected records',
            placeholder_pii_detection_rule: 'e.g., PII Detection Rule',
            placeholder_tax_id: 'e.g., TAX-001',
            placeholder_lvl_id: 'e.g., LVL-001',
            placeholder_service_type: 'e.g., service_type',
            placeholder_database: 'e.g., database',
            placeholder_hostname_match: 'e.g., Hostname Exact Match',
            placeholder_hostname: 'e.g., hostname',
            placeholder_hr_systems_csv: 'e.g., HR Systems CSV Import',
            placeholder_server_name: 'e.g., Server Name',
            placeholder_hostname_field: 'e.g., hostname',
            placeholder_lowercase: 'e.g., lowercase',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 'e.g., STG-000123',
            placeholder_lvl_002: 'e.g., LVL-002',
            placeholder_usr_id: 'e.g., USR-000123',
            decision_for: 'Decision for {id}',
        },
        exports: {
            title: 'Export',
            inventory: 'Data Inventory',
            classification_register: 'Classification Register',
            raci: 'RACI Matrix',
            exceptions: 'Exceptions Report',
            evidence_pack: 'Evidence Pack',
            delta_report: 'Delta Report',
            executive_summary: 'Executive Summary',
            export_button: 'Export',
            downloading: 'Downloading...',
            download_complete: 'Download complete.',
            download_error: 'Failed to download export. Please try again.',
        },
        flags: {
            title: 'Downstream Flags',
            pending: 'Pending Flags',
            cross_border_review_needed: 'Cross-Border Review Needed',
            sharing_disclosure_needed: 'Sharing Disclosure Needed',
            dpia_review_needed: 'DPIA Review Needed',
            acknowledge: 'Acknowledge',
            resolve: 'Resolve',
            no_flags: 'No downstream flags.',
        },
        delta: {
            title: 'Delta Summary',
            new_count: '{n} new',
            removed_count: '{n} removed',
            changed_count: '{n} changed',
            re_review_triggered: 'Re-review Triggered',
            create_re_review: 'Create Re-review',
            recent_alerts: 'Recent Delta Alerts',
            no_alerts: 'No recent delta alerts.',
            threshold_exceeded: 'Delta threshold exceeded (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'This dashboard shows cycle-level governance status: systems scanned, records staged, records reviewed, records approved, and exceptions pending. This matches the governance lead success criteria: "live dashboard of who has updated their inventory, classification, and sharing logs" (P-VN-05).',
            export_artifacts_info: 'Discovery exports include all required artifact types per UC-VN-10 and DATA-EVD-01: approved data inventory, classification register, ownership/RACI mapping, exception queue, executive summary, evidence pack, and delta report.',
            scan_inventory_pipeline_info: 'Each completed scan produces records compatible with the vn_data_law_data_inventory_register template (requirement 88dd9877). Discovered data assets automatically populate the data inventory register to satisfy the Data Law obligation to maintain a digital data inventory.',
            connector_linkage_info: 'Each connector configuration must include: system type (e.g., CMDB, cloud platform), data store reference (database, file store, SaaS), and business process linkage (UC-VN-10 required supporting records). These fields ensure discovered assets are traceable to their source system and business context.',
            owner_routing_info: 'Review tasks are routed to the correct department or application owner based on system/data ownership (UC-VN-10 step 5). Routing uses the system ownership register, not ad-hoc assignment.',
            owner_actions_audit_info: 'Owners can approve, modify, reject, or escalate each classification (UC-VN-10 step 6). Every action captures actor, timestamp, and reason, producing governance audit evidence events per requirement f12fc090 (Annual governance attestation). The audit trail shows who reviewed, what was changed, and when.',
            escalation_timers_info: 'Escalation timers: owner review deadline is 5 business days; exception escalation is 48 hours (UC-VN-10 timers). Unresolved items auto-escalate when the deadline expires. Timers are configurable per organizational policy.',
            original_suggestion_preserved_info: 'The original machine/system classification suggestion is preserved alongside every owner override (UC-VN-10 evidence requirement). The original suggestion is never overwritten and remains visible for audit purposes.',
            dedup_lineage_info: 'Deduplication rules prevent duplicate inventory entries when the same data asset is discovered across multiple scans or connectors. The most recent scan metadata is preserved as the primary record while maintaining lineage references to all prior discoveries for audit traceability.',
            label_states_info: 'Analytics distinguish 4 label states for governance review: machine-suggested (pending), owner-approved, owner-modified, and rejected. These states enable auditors to assess automation accuracy and owner engagement.',
            import_pipeline_info: 'Import templates must produce records compatible with the discovery pipeline. Imported records enter staging with the same metadata requirements as scanned records: source system identification, batch ID, and timestamp. Column mappings should target fields defined in vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'Classification lineage traces the full path: scan/import -> staging -> owner review -> approved label -> register entry (UC-VN-10 steps 1-10). Each stage shows record ID, actor, timestamp, and state transition for complete audit traceability.',
            staging_workflow_info: 'Staged records are visually distinguished from approved inventory entries by their promotion status badge (UC-VN-10 step 3). Each record retains provenance metadata: source system, scan batch ID, and discovery timestamp for full audit traceability.',
            suggestion_data_d1_info: 'Suggestions align with Data Law classification procedure DATA-D1: (1) catalog data assets, (2) classify by sensitivity level, (3) define handling rules. Each suggestion includes sensitivity and importance dimensions per requirement 18e4bfd1.',
            downstream_flags_info: 'Items flagged for sharing, cross-border transfer, or high-risk treatment trigger linked downstream workflows: cross-border transfer assessment, sharing risk assessment, and DPIA review (UC-VN-10 step 8).',
            delta_detection_info: 'Discovery cycles support delta detection per UC-VN-10 step 11: each cycle shows which records are new, changed, or removed since the last approved baseline. Material changes (>10% delta) automatically trigger re-review workflows.',
        },
    },
    // ---------- Incident Operations module (Sprint 2.4.0) ----------
    incident_ops: {
        errors: {
            fetch: 'Failed to load.',
            save: 'Failed to save.',
            fetch_escalation_log_failed: 'Failed to fetch escalation log.',
            fetch_activations_failed: 'Failed to fetch activations.',
            deactivate_failed: 'Failed to deactivate.',
            complete_activation_failed: 'Failed to complete activation.',
            fetch_playbook_templates_failed: 'Failed to fetch playbook templates.',
            create_playbook_template_failed: 'Failed to create playbook template.',
            clone_template_failed: 'Failed to clone template.',
            update_status_failed: 'Failed to update status.',
            deprecate_template_failed: 'Failed to deprecate template.',
            seed_defaults_failed: 'Failed to seed defaults.',
            fetch_regulator_exports_failed: 'Failed to fetch regulator exports.',
            create_regulator_export_failed: 'Failed to create regulator export.',
            update_submission_status_failed: 'Failed to update submission status.',
            fetch_response_metrics_failed: 'Failed to fetch response metrics.',
            fetch_sla_dashboard_failed: 'Failed to fetch SLA dashboard.',
            fetch_sla_definitions_failed: 'Failed to fetch SLA definitions.',
            create_sla_definition_failed: 'Failed to create SLA definition.',
            seed_sla_defaults_failed: 'Failed to seed SLA defaults.',
            fetch_vendor_coordination_failed: 'Failed to fetch vendor coordination.',
            fetch_vendor_detail_failed: 'Failed to fetch vendor detail.',
            create_vendor_coordination_failed: 'Failed to create vendor coordination.',
            add_communication_failed: 'Failed to add communication.',
            add_info_request_failed: 'Failed to add info request.',
            add_action_request_failed: 'Failed to add action request.',
        },
        nav: {
            playbooks: 'Playbook Templates',
            sla_definitions: 'SLA Definitions',
            sla_dashboard: 'SLA Dashboard',
            escalation_log: 'Escalation Log',
            regulator_exports: 'Regulator Exports',
            deadlines: 'Notification Deadlines',
            vendor_coordination: 'Vendor Coordination',
            metrics: 'Response Metrics',
            playbook_links: 'Playbook Links',
            regulatory_sla: 'Regulatory SLA',
            coordination_dashboard: 'Coordination',
            evidence_chain: 'Evidence Chain',
            post_review: 'Post-Incident Review',
        },
        playbooks: {
            page_title: 'Playbook Templates',
            activations_page_title: 'Playbook Activations',
            title: 'Playbook Templates',
            new: 'New Template',
            seed_defaults: 'Seed Defaults',
            scenario_type: 'Scenario Type',
            severity_applicability: 'Severity Applicability',
            all_scenario_types: 'All Scenario Types',
            new_playbook: 'New Playbook Template',
            clone: 'Clone',
            clone_template: 'Clone Template',
            template_name: 'Template name',
            deprecate: 'Deprecate',
            confirm_deprecate: 'Deprecate template "{name}"?',
            create_template: 'Create Template',
            update_status: 'Update Status',
            no_templates: 'No playbook templates found',
            deactivation_reason_placeholder: 'Reason for deactivation...',
            description_placeholder: 'Playbook description...',
            name_placeholder: 'Playbook name...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA Definitions',
            title: 'SLA Definitions',
            new: 'New SLA Definition',
            sla_name: 'SLA Name',
            target_hours: 'Target Hours',
            escalation_after: 'Escalation After',
            applies_to: 'Applies To',
            no_definitions: 'No SLA definitions found.',
            target_value_placeholder: 'Target value',
            name_placeholder: 'SLA name...',
            threshold_hours_label: 'Threshold Hours',
            severity_label: 'Severity',
            all_types: 'All Types',
            all_severities: 'All Severities',
            default_label: 'Default',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Compliant',
            page_title: 'SLA Dashboard',
            title: 'SLA Dashboard',
            loading: 'Loading SLA data...',
            compliance_rate: 'Compliance Rate',
            breached: 'Breached',
            at_risk: 'At Risk',
            on_track: 'On Track',
            by_severity: 'By Severity',
            total_records: 'Total Records',
            met: 'Met',
            sla_records: 'SLA Records',
            sla_reference: 'SLA Reference',
            record_id: 'Record ID',
            metric_type: 'Metric Type',
            severity: 'Severity',
            elapsed: 'Elapsed',
            clock_start: 'Clock Start',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Escalation Log',
            title: 'Escalation Log',
            incident_id: 'Incident ID',
            escalated_by: 'Escalated By',
            escalated_to: 'Escalated To',
            reason: 'Reason',
            escalated_at: 'Escalated At',
            no_escalations: 'No escalations found.',
            filter_incident_id: 'Filter by Incident ID',
            escalation_id: 'Escalation ID',
            level: 'Level',
            level_range: 'Level (L1\u2013L4)',
            create_escalation: 'Create Escalation',
            incident_id_placeholder: 'e.g., INC-001',
            target_role_placeholder: 'e.g., dpo, incident_manager',
            select_placeholder: '— select —',
        },
        regulator_exports: {
            page_title: 'Regulator Exports',
            title: 'Regulator Exports',
            new: 'New Export',
            incident_id: 'Incident ID',
            regulator: 'Regulator',
            export_format: 'Format',
            exported_at: 'Exported At',
            no_exports: 'No regulator exports found.',
            enter_incident_id: 'Enter Incident ID',
            export_id: 'Export ID',
            watermark: 'Watermark',
            submission_status: 'Submission Status',
            all_jurisdictions: 'All Jurisdictions',
            no_transitions: 'No further transitions available from',
            select_new_status: '\u2014 select new status \u2014',
            sections_included: 'Sections Included',
            form_classification: 'Classification',
            submission_channel: 'Channel',
        },
        deadlines: {
            title: 'Notification Deadlines',
            incident_id: 'Incident ID',
            deadline: 'Deadline',
            regulator: 'Regulator',
            remaining: 'Remaining',
            no_deadlines: 'No notification deadlines found.',
        },
        vendor_coordination: {
            page_title: 'Vendor Coordination',
            title: 'Vendor Coordination',
            new: 'New Coordination',
            incident_id: 'Incident ID',
            vendor_name: 'Vendor Name',
            contact: 'Contact',
            coordination_status: 'Coordination Status',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'No vendor coordination items found.',
            assessment_placeholder: 'Initial assessment from vendor...',
            coordination_id: 'Coordination ID',
            communications: 'Communications',
            info_requests: 'Info Requests',
            action_requests: 'Action Requests',
            direction: 'Direction',
            channel_label: 'Channel',
            no_transitions: 'No further transitions available.',
            vendor_ref_number: 'Reference Number',
            report_date: 'Report Date',
            contact_email: 'Email',
            contact_phone: 'Phone',
            channel_email: 'Email',
            channel_meeting: 'Meeting',
            channel_phone: 'Phone',
            channel_portal: 'Portal',
            incident_id_placeholder: 'Filter by Incident ID...',
            priority_critical: 'Critical',
            priority_high: 'High',
            priority_low: 'Low',
            priority_medium: 'Medium',
            vendor_id_placeholder: 'Filter by Vendor ID...',
            description_placeholder: 'Description...',
            filter_vendor_id: 'Filter by Vendor ID',
            inbound: 'Inbound',
            outbound: 'Outbound',
            summary_placeholder: 'Summary...',
            priority_label: 'Priority',
            due_date_label: 'Due date',
            info_request_desc: 'Information request description',
            action_request_desc: 'Action request description',
            content_label: 'Content',
            select_incident: 'Select Incident',
            select_vendor: 'Select Vendor',
            discovery_date: 'Discovery Date',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Actor',
        },
        metrics: {
            page_title: 'Response Metrics',
            title: 'Response Metrics',
            loading: 'Loading metrics data...',
            avg_response_time: 'Avg Response Time',
            avg_resolution_time: 'Avg Resolution Time',
            total_incidents: 'Total Incidents',
            by_severity: 'By Severity',
            by_status: 'By Status',
            trend: 'Trend',
            start_sla_clock: 'Start SLA Clock',
            sla_records: 'SLA Records',
            no_sla_records: 'No SLA records yet. Click "Start SLA Clock" to create one.',
            record_id: 'Record ID',
            severity: 'Severity',
            progress: 'Progress',
            incident_id_required: 'Incident ID is required',
            sla_definition_required: 'SLA Definition is required',
            metric_type_required: 'Metric type is required',
            threshold_required: 'Threshold hours is required',
            sla_clock_started: 'SLA clock started',
            sla_definition: 'SLA Definition',
            no_definitions: 'No SLA definitions found. Create one under SLA Definitions first.',
            select_definition: 'Select SLA definition\u2026',
            select_metric_type: 'Select metric type\u2026',
            select_severity: 'Select severity\u2026',
            threshold_hours: 'Threshold (hours)',
            warning_threshold_pct: 'Warning threshold (%)',
            start_clock: 'Start Clock',
            starting: 'Starting\u2026',
            metric_label: 'Metric',
            value_label: 'Value',
            incident_id: 'Incident ID',
            threshold_placeholder: 'e.g. 24',
            metric_types: {
                triage_time: 'Triage Time',
                containment_time: 'Containment Time',
                notification_time: 'Notification Time',
                resolution_time: 'Resolution Time',
            },
            severities: {
                critical: 'Critical',
                high: 'High',
                medium: 'Medium',
                low: 'Low',
            },
            sla_reference: 'SLA Reference',
        },
        // Sprint 21.2.0: Incident Operations Maturity
        playbook_links: {
            page_title: 'Playbook Links',
            title: 'Playbook Notification Links',
            new_link: 'Link Playbook to Notification',
            activation_id: 'Activation ID',
            notification_id: 'Notification ID',
            select_activation: 'Select an activation...',
            select_notification: 'Select a notification...',
            no_links: 'No playbook links found',
            seed_pdpl: 'Add template playbook links',
            // fix-sprint-20260411-D F7 — semantic toast keys
            created: 'Playbook link created',
            seeded: 'Template playbook links added',
            create_failed: 'Failed to create playbook link',
            seed_failed: 'Failed to add template playbook links',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'No installed framework governs incident response, so there are no deadlines to add.',
                declared_but_none_active: 'A framework governing incident response is installed but not in force.',
                no_declared_deadlines: 'The installed incident-response frameworks declare no notification deadlines.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Deadline',
            deadline_units: {
                hours: '{count} hours',
                working_days: '{count} working days',
                calendar_days: '{count} calendar days',
                days: '{count} days',
                weeks: '{count} weeks',
                months: '{count} months',
                years: '{count} years',
            },
            deadline_unit_unknown: '{count} {unit}',
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Regulatory SLA Mappings',
            title: 'Regulatory SLA Mappings',
            new_mapping: 'New SLA Mapping',
            sla_definition: 'SLA Definition',
            select_sla_definition: 'Select an SLA definition...',
            framework_code: 'Framework Code',
            deadline_type: 'Deadline Type',
            deadline_hours: 'Deadline Hours',
            // Fix sprint #447 (round 2) — was "Seed PDPL Defaults"; the action reads whatever
            // installed framework declares the deadlines, not one law. (Key name kept: every
            // language pack carries it.)
            seed_pdpl: 'Add mappings from installed deadlines',
            no_mappings: 'No regulatory SLA mappings found',
            dashboard: 'Regulatory SLA Dashboard',
            deadline_types: {
                initial_notification: 'Initial Notification',
                supplement_response: 'Supplement Response',
                authority_response: 'Authority Response',
                remediation_report: 'Remediation Report',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Statutory Reference',
        },
        coordination_dashboard: {
            page_title: 'Coordination Dashboard',
            title: 'Coordination Dashboard',
            incident_id: 'Incident ID',
            select_incident: 'Select an incident...',
            team_role: 'Team Role',
            members: 'Members',
            tasks: 'Tasks',
            sla_status: 'SLA Status',
            no_teams: 'No coordination teams found',
            new_team: 'New Coordination Team',
            enter_incident_id: 'Enter an incident ID to view coordination teams.',
            member_ids_placeholder: 'user1, user2',
            edit_team: 'Edit Team',
            task_count: 'Total Tasks',
            tasks_completed: 'Completed',
            team_roles: {
                security: 'Security',
                legal: 'Legal',
                it: 'IT',
                management: 'Management',
                department_owner: 'Department Owner',
                external: 'External',
            },
        },
        evidence_chain: {
            page_title: 'Evidence Chain',
            title: 'Evidence Chain of Custody',
            new_entry: 'New Evidence Entry',
            select_incident: 'Select an incident...',
            actor: 'Actor',
            select_actor: 'Select an actor...',
            evidence_id: 'Evidence ID',
            action: 'Action',
            recipient: 'Recipient',
            no_entries: 'No evidence chain entries found',
            enter_incident_id: 'Enter an incident ID to view evidence chain.',
            evidence_id_placeholder: 'e.g. EVD-2026-0001',
            evidence_id_help: 'A unique reference for the evidence item — a log file, disk image, or screenshot. A suggestion is prefilled; edit it to match your evidence catalogue.',
            select_incident_first: 'Select an incident above before adding an evidence entry.',
            for_incident: 'For incident',
            actions: {
                collected: 'Collected',
                shared: 'Shared',
                exported: 'Exported',
                archived: 'Archived',
            },
        },
        post_review: {
            page_title: 'Post-Incident Review',
            title: 'Post-Incident Review',
            new_review: 'New Review',
            select_incident: 'Select an incident...',
            lessons_learned: 'Lessons Learned',
            recommendations: 'Recommendations',
            improvement_actions: 'Improvement Actions',
            submit_for_review: 'Submit for Review',
            aria_submit_review: 'Submit review {name} for review',
            aria_sign_off: 'Sign off review {name}',
            sign_off: 'Sign Off',
            no_reviews: 'No post-incident reviews found',
            enter_incident_id: 'Enter an incident ID to view post-incident review.',
            one_per_line: 'One per line',
            statuses: {
                draft: 'Draft',
                in_review: 'In Review',
                signed_off: 'Signed Off',
                closed: 'Closed',
            },
        },
        activations: {
            title: 'Playbook Activations',
            activation_id: 'Activation ID',
            phases: 'Phases',
            activated_at: 'Activated At',
            activated_by: 'Activated By',
            deactivate: 'Deactivate',
            complete: 'Complete',
            deactivation_reason: 'Deactivation Reason',
            // Sprint 41.3.0 F9 — activation evidence
            evidence_button: 'Evidence',
            evidence_modal_title: 'Activation Evidence',
            no_evidence: 'No evidence attached to this activation.',
            upload_evidence_label: 'Upload activation evidence',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    // ---------- Program governance (Sprint 2.5.0) ----------
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        // Sprint 48.2.5 — Forseti maturity walkthrough panel
        maturity_walkthrough: {
            run_label: 'Walk me through gaps',
            running: 'Walking gaps…',
            results_heading: 'Improvement roadmap ({count} gap{plural})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'No gaps detected — all domains at or above target.',
            col_priority: 'Priority',
            col_domain: 'Domain',
            col_levels: 'Current → Target',
            col_severity: 'Severity',
            col_actions: 'Remediation actions',
            citations_label: 'Citations',
            accept_label: 'Accept roadmap',
            reject_label: 'Reject draft',
            export_label: 'Export DOCX',
            exported_label: 'Roadmap exported',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Resolution: {status}',
            resolve_status_accepted: 'Accepted',
            resolve_status_rejected: 'Rejected',
            error_generic: 'Could not run gap walkthrough. Try again or check the assessment status.',
        },
        // Sprint 48.2.6 — Forseti policy gap detection panel
        policy_gap: {
            run_label: 'Detect policy gaps',
            running: 'Detecting gaps…',
            results_heading: 'Policy gaps ({count})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'No coverage gaps detected.',
            col_severity: 'Severity',
            col_kind: 'Kind',
            col_clause: 'Clause / requirement',
            kind_missing: 'Missing clause',
            kind_weak: 'Weak coverage',
            kind_stale: 'Stale (review overdue)',
            remediation_label: 'Suggested remediation',
            citations_label: 'Citations',
            accept_label: 'Accept gap report',
            reject_label: 'Reject report',
            export_label: 'Export DOCX',
            accept_hint: 'Accept records the gap report on the persisted draft. Use Export DOCX to generate a Policy Audit Report; the operator drives the actual policy edit.',
            resolve_summary: 'Resolution: {status}',
            error_generic: 'Could not detect policy gaps. Try again or check the document status.',
        },
        // Sprint 48.2.7 — Forseti remediation plan generator panel
        remediation_plan_generator: {
            run_label: 'Generate plan from gaps',
            running: 'Generating plan…',
            results_heading: 'Remediation plan ({phase_count} phase{plural})',
            tokens_label: '≈ {count} tokens',
            phase_label: 'Phase {index}',
            window_label: 'Window',
            owner_label: 'Owner role',
            evidence_label: 'Evidence types required',
            tasks_label: 'Tasks',
            no_phases: 'No phases produced — input gap-set may be empty.',
            citations_label: 'Citations',
            accept_label: 'Accept plan',
            reject_label: 'Reject plan',
            export_label: 'Export DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created',
            error_generic: 'Could not generate remediation plan. Try again or check the gap-set input.',
            // Sprint 48.2.7 fix — cross-module gap-set ingestion controls.
            source_picker_label: 'Source',
            source_all: 'All',
            source_consent: 'Consent gap analysis',
            source_policy: 'Policy gap detection',
            source_vendor: 'Vendor onboarding',
            load_upstream_label: 'Load from upstream',
            loading_upstream: 'Loading…',
            upstream_count: '{count} gap(s) loaded',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Maturity Models',
            maturity_assessments: 'Maturity Assessments',
            trend_dashboard: 'Trend Dashboard',
            reporting_periods: 'Reporting Periods',
            executive_summaries: 'Executive Summaries',
            audit_readiness: 'Audit Readiness',
            evidence_gaps: 'Evidence Gaps',
            program_health: 'Program Health',
            obligation_mappings: 'Obligation Mappings',
            governance_documents: 'Governance Documents',
            remediation_plans: 'Remediation Plans',
            governance_model: 'Governance Model',
            document_drafting: 'Document Drafting',
            document_issuance: 'Document Issuance',
            acknowledgment_tracking: 'Acknowledgment Tracking',
            change_log: 'Change Log',
            review_cadence: 'Review Cadence',
        },
        maturity_models: {
            title: 'Maturity Models',
            new: 'New Model',
            seed_defaults: 'Seed Defaults',
            domains: 'Domains',
            activate: 'Activate',
            no_models: 'No maturity models found',
            new_maturity_model: 'New Maturity Model',
            create_model: 'Create Model',
            load_error: 'Failed to load maturity models. Please try again.',
            create_error: 'Failed to create maturity model',
            status_error: 'Failed to update model status',
            seed_error: 'Failed to seed defaults',
            aria_seed_defaults: 'Seed default maturity models',
            aria_create_new: 'Create new maturity model',
            loading_sr: 'Loading maturity models',
            model_name_placeholder: 'e.g., Privacy Maturity Model',
            status_changed: 'Model status changed to {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Maturity Assessments',
            new: 'New Assessment',
            model: 'Model',
            assessor: 'Assessor',
            overall_score: 'Overall Score',
            overall_score_help: 'A weighted 0–5 maturity rating, calculated automatically when an assessment is finalized. Increase it by scoring each domain higher during evaluation, then moving the assessment through Submit for Review → Finalize.',
            assessed_at: 'Assessed At',
            no_assessments: 'No maturity assessments found.',
            load_error: 'Failed to load maturity assessments. Please try again.',
            create_error: 'Failed to create assessment',
            start_error: 'Failed to start assessment',
            archive_error: 'Failed to archive assessment',
            eval_error: 'Failed to load assessment for evaluation',
            eval_min_score: 'Please score at least one domain',
            eval_save_error: 'Failed to save domain evaluations',
            review_error: 'Failed to submit for review',
            finalize_error: 'Failed to finalize assessment',
            start: 'Start',
            start_confirm_title: 'Start Assessment',
            start_confirm_message: 'Are you sure you want to start this assessment?',
            archive_confirm_title: 'Archive Assessment',
            archive_confirm_message: 'Are you sure you want to archive this assessment?',
            evaluate_domains: 'Evaluate',
            evaluate_domains_title: 'Evaluate Domains',
            submit_for_review: 'Submit for Review',
            finalize: 'Finalize',
            archive: 'Archive',
            all_reviewers: 'All reviewers must approve',
            any_reviewer: 'Any reviewer may approve',
            select_level: 'Select maturity level...',
            findings_placeholder: 'Findings, notes, evidence references...',
            no_domains: 'No domains found for this model.',
        },
        trend_dashboard: {
            title: 'Trend Dashboard',
            loading: 'Loading trend data...',
            score_trend: 'Score Trend',
            domain_comparison: 'Domain Comparison',
            period: 'Period',
            load_error: 'Failed to load trend summary. Please try again.',
            metrics_load_error: 'Failed to load trend metrics. Please try again.',
            module: 'Module',
            metric: 'Metric',
            value: 'Value',
            trend: 'Trend',
            aria_filters: 'Trend metrics filters',
            showing_chart: 'Showing chart view',
            showing_table: 'Showing data table view',
            create_metric: 'Create Metric',
            data_points: 'Data Points',
            trend_direction: 'Trend Direction',
            unit: 'Unit',
        },
        reporting_periods: {
            title: 'Reporting Periods',
            new: 'New Period',
            period_name: 'Period Name',
            start_date: 'Start Date',
            end_date: 'End Date',
            no_periods: 'No reporting periods found.',
            load_error: 'Failed to load reporting periods. Please try again.',
            create_error: 'Failed to create reporting period',
            auto_generate_error: 'Failed to auto-generate reporting periods',
            status_error: 'Failed to update period status',
            start_collecting: 'Start Collecting',
            mark_as_draft: 'Mark as Draft',
            submit_for_review: 'Submit for Review',
            publish: 'Publish',
            auto_generate: 'Auto-generate reporting periods',
            period_label_placeholder: 'e.g., Q1 2026',
            status_updated: 'Period status updated',
        },
        executive_summaries: {
            title: 'Executive Summaries',
            new: 'New Summary',
            period: 'Period',
            generated_at: 'Generated At',
            generated_by: 'Generated By',
            no_summaries: 'No executive summaries found.',
            generate: 'Generate',
            load_error: 'Failed to load executive summaries. Please try again.',
            generate_error: 'Failed to generate executive summary',
            save_error: 'Failed to save executive summary',
        },
        audit_readiness: {
            title: 'Audit Readiness',
            loading: 'Loading audit readiness data...',
            overall_readiness: 'Overall Readiness',
            by_domain: 'By Domain',
            evidence_coverage: 'Evidence Coverage',
            gaps: 'Gaps',
            load_error: 'Failed to load latest readiness. Please try again.',
            history_error: 'Failed to load readiness history. Please try again.',
            calculate_error: 'Failed to calculate audit readiness',
            aria_recalculate: 'Recalculate audit readiness score',
            aria_current_readiness: 'Current audit readiness',
            aria_history: 'Readiness score history',
            recalculated: 'Audit readiness recalculated',
            loading_sr: 'Loading audit readiness data',
        },
        evidence_gaps: {
            title: 'Evidence Gaps',
            // fix-sprint-20260717-2118-followup F14 — real section identity that
            // replaces `title=Generated by, description=End date` (a nonsensical
            // pairing of two per-record field labels).
            section_ownership_timeline: 'Ownership & timeline',
            domain: 'Domain',
            control: 'Control',
            gap_type: 'Gap Type',
            severity: 'Severity',
            no_gaps: 'No evidence gaps found.',
            load_error: 'Failed to load evidence gaps. Please try again.',
            stats_error: 'Failed to load gap stats. Please try again.',
            create_error: 'Failed to create evidence gap',
            update_error: 'Failed to update evidence gap',
            status_error: 'Failed to update status',
            aria_create_new: 'Create new evidence gap',
            aria_statistics: 'Evidence gap statistics',
            loading_sr: 'Loading evidence gaps',
            status_updated: 'Gap status updated',
            entity_type_placeholder: 'e.g., vendor, system',
            all_modules: 'All Domains',
            all_severities: 'All Severities',
        },
        program_health: {
            title: 'Program Health',
            loading: 'Loading program health data...',
            overall_health: 'Overall Health',
            by_module: 'By Module',
            risk_areas: 'Risk Areas',
            recommendations: 'Recommendations',
            load_error: 'Failed to load program health data. Please try again.',
            aria_key_metrics: 'Program health key metrics',
            aria_module_scorecard: 'Module scorecard breakdown',
            loading_sr: 'Loading program health data',
            trending_up: 'Trending up',
            trending_down: 'Trending down',
            trending_stable: 'Stable',
            data_loaded: 'Program health data loaded',
        },
        obligation_mappings: {
            title: 'Obligation Mappings',
            create_mapping: 'Create Obligation Mapping',
            // fix-sprint-20260717-2118-followup F12 — real section identity that
            // replaces `title=Assigned Role, description=Control IDs`.
            section_ownership: 'Ownership',
            framework: 'Framework',
            select_framework: 'Select a framework...',
            obligation: 'Obligation',
            obligation_ref: 'Obligation Reference',
            obligation_description: 'Obligation Description',
            role: 'Role',
            assigned_role: 'Assigned Role',
            select_role: 'Select a role...',
            assigned_department: 'Assigned Department',
            select_department: 'Select a department...',
            control_ids: 'Control IDs',
            control_ids_placeholder: 'e.g. A.5.1, CC6.1',
            control_ids_hint: 'Comma-separated control reference codes.',
            raci_assignments: 'RACI Assignments',
            create_raci: 'Create RACI Assignment',
            activity: 'Activity',
            activity_name: 'Activity Name',
            activity_description: 'Activity Description',
            responsible: 'Responsible',
            select_users: 'Select users...',
            accountable: 'Accountable',
            select_user: 'Select a user...',
            consulted: 'Consulted',
            informed: 'Informed',
            review_frequency: 'Review Frequency',
            frequency_monthly: 'Monthly',
            frequency_quarterly: 'Quarterly',
            frequency_semi_annual: 'Semi-Annual',
            frequency_annual: 'Annual',
            no_mappings_found: 'No obligation mappings found.',
            no_raci_found: 'No RACI assignments found.',
            mapping_created: 'Obligation mapping created',
            mapping_updated: 'Obligation mapping updated',
            mapping_deleted: 'Obligation mapping deleted',
            raci_created: 'RACI assignment created',
            raci_updated: 'RACI assignment updated',
            raci_deleted: 'RACI assignment deleted',
            aria_edit_mapping: 'Edit mapping {name}',
            aria_delete_mapping: 'Delete mapping {name}',
            aria_edit_raci: 'Edit RACI {name}',
            aria_delete_raci: 'Delete RACI {name}',
            confirm_delete_mapping: 'Delete this obligation mapping?',
            confirm_delete_raci: 'Delete this RACI assignment?',
            table_view: 'Table View',
            matrix_view: 'Matrix View',
            print_export: 'Print / Export',
            filter_framework: 'Filter by framework',
            no_matrix_data: 'No RACI assignments to display in matrix view.',
            aria_print_export: 'Print or export RACI matrix',
        },
        governance_documents: {
            title: 'Governance Documents',
            create_document: 'Create Governance Document',
            document_title: 'Document Title',
            document_type: 'Document Type',
            content_url: 'Content URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Framework Codes',
            select_frameworks: 'Select frameworks...',
            effective_date: 'Effective Date',
            review_date: 'Review Date',
            status: 'Status',
            version: 'Version',
            acknowledgments: 'Acknowledgments',
            acknowledge: 'Acknowledge',
            acknowledged_by: 'Acknowledged By',
            department: 'Department',
            no_documents_found: 'No governance documents found.',
            draft: 'Draft',
            review: 'Review',
            approved: 'Approved',
            issued: 'Issued',
            superseded: 'Superseded',
            document_created: 'Document created',
            document_acknowledged: 'Document acknowledged',
            policy: 'Policy',
            procedure: 'Procedure',
            standard: 'Standard',
            guideline: 'Guideline',
        },
        governance_model: {
            title: 'Governance Model',
            create_model: 'Create Governance Model',
            edit_model: 'Edit Governance Model',
            model_name: 'Model Name',
            description: 'Description',
            governance_type: 'Governance Type',
            type_centralized: 'Centralized',
            type_federated: 'Federated',
            type_hybrid: 'Hybrid',
            framework_codes: 'Framework Codes',
            select_frameworks: 'Select frameworks...',
            review_frequency: 'Review Frequency',
            freq_monthly: 'Monthly',
            freq_quarterly: 'Quarterly',
            freq_semi_annual: 'Semi-Annual',
            freq_annual: 'Annual',
            committees: 'Committees',
            committee_name: 'Committee Name',
            committee_members: 'Members (comma-separated)',
            committee_mandate: 'Mandate',
            add_committee: 'Add Committee',
            no_committees: 'No committees defined.',
            delegation_rules: 'Delegation Rules',
            from_role: 'From Role',
            to_role: 'To Role',
            scope: 'Scope',
            add_delegation_rule: 'Add Delegation Rule',
            control_owner_summary: 'Control Owner Summary',
            no_raci_data: 'No RACI data linked to this model.',
            activity: 'Activity',
            responsible: 'Responsible',
            accountable: 'Accountable',
            consulted: 'Consulted',
            informed: 'Informed',
            no_models_found: 'No governance models found.',
            model_created: 'Governance model created',
            model_updated: 'Governance model updated',
            model_deleted: 'Governance model deleted',
            confirm_delete: 'Delete this governance model?',
            expand: 'Expand',
            collapse: 'Collapse',
            aria_expand: 'Expand details for {name}',
            aria_edit: 'Edit governance model {name}',
            aria_delete: 'Delete governance model {name}',
            aria_export_csv: 'Export {name} as CSV',
            aria_export_json: 'Export {name} as JSON',
            // Sprint 43.7.0 Wave 5 — typed selectors + export error surfacing
            framework_read_permission_warning: 'You need the regulatory_framework:read permission to select frameworks. Ask an admin to grant it.',
            select_members: 'Select committee members',
            select_from_role: 'Select delegating role',
            select_to_role: 'Select receiving role',
            // Sprint 43.7.1 Wave 4 — markdown mandate + tabbed scope picker.
            // scope_tab_obligation renamed from scope_tab_control (Sprint 43.7.1
            // refinement) since the platform tracks regulatory obligations.
            mandate_markdown_hint: 'Markdown supported: **bold**, lists, links.',
            scope_tab_obligation: 'Obligation',
            scope_tab_framework: 'Framework',
            scope_tab_free_text: 'Free text',
            select_obligation: 'Select an obligation',
            select_framework: 'Select a framework',
            aria_remove_committee: 'Remove committee {n}',
            aria_remove_delegation_rule: 'Remove delegation rule {n}',
            no_frameworks_available: 'No frameworks available. Add regulatory frameworks first.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Document Drafting & Approval',
            document_title: 'Document Title',
            document_type: 'Document Type',
            status: 'Status',
            drafter: 'Drafter',
            deadline: 'Deadline',
            assign_drafter: 'Assign Drafter',
            select_drafter: 'Select a drafter...',
            assign: 'Assign',
            submit_for_review: 'Submit for Review',
            legal_review: 'Legal Review',
            reviewer: 'Reviewer',
            select_reviewer: 'Select a reviewer...',
            comments: 'Comments',
            review_action: 'Review Action',
            approve: 'Approve',
            request_changes: 'Request Changes',
            submit_review: 'Submit Review',
            executive_approval: 'Executive Approval',
            no_drafts_found: 'No documents in draft, review, or approval stage.',
            drafter_assigned: 'Drafter assigned',
            status_updated: 'Document status updated',
            review_approved: 'Review approved',
            changes_requested: 'Changes requested',
            document_approved: 'Document approved',
            aria_assign: 'Assign drafter to {name}',
            aria_submit_review: 'Submit {name} for review',
            aria_review: 'Review {name}',
            aria_approve: 'Approve {name}',
        },
        document_issuance: {
            title: 'Document Issuance & Distribution',
            document_title: 'Document Title',
            document_type: 'Document Type',
            status: 'Status',
            version: 'Version',
            effective_date: 'Effective Date',
            distribution_scope: 'Distribution Scope',
            distribution_placeholder: 'e.g., All departments, Legal team',
            issuance_number: 'Issuance Number',
            issuance_number_placeholder: 'e.g., ISS-2026-001',
            supersedes: 'Supersedes Document',
            none: 'None',
            issue_document: 'Issue Document',
            ready_for_issuance: 'Ready for Issuance',
            issued_documents: 'Issued Documents',
            no_issued_documents: 'No issued documents found.',
            view_lineage: 'View Lineage',
            supersession_lineage: 'Supersession Lineage',
            document_issued: 'Document issued',
            document_superseded: 'Document superseded',
            aria_issue: 'Issue {name}',
            aria_view_lineage: 'View supersession lineage for {name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Acknowledgment Tracking',
            document_title: 'Document',
            acknowledged_count: 'Acknowledged',
            pending_count: 'Pending',
            percentage: 'Completion',
            details: 'Details',
            send_reminder: 'Send Reminder',
            acknowledgment_details: 'Acknowledgment Details',
            user: 'User',
            department: 'Department',
            acknowledged_at: 'Acknowledged At',
            status: 'Status',
            acknowledged: 'Acknowledged',
            no_issued_documents: 'No issued documents to track.',
            no_acknowledgments: 'No acknowledgments recorded yet.',
            reminder_sent: 'Reminder sent for pending acknowledgments',
            aria_expand: 'View acknowledgment details for {name}',
            aria_send_reminder: 'Send reminder for {name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Document Change Log',
            date: 'Date',
            document: 'Document',
            change_type: 'Change Type',
            actor: 'Actor',
            summary: 'Summary',
            export_csv: 'Export CSV',
            filters: 'Change log filters',
            filter_document: 'Filter by document',
            all_documents: 'All documents',
            filter_change_type: 'Filter by change type',
            all_types: 'All types',
            date_from: 'Date from',
            date_to: 'Date to',
            version_history: 'Version History',
            version_archive: 'Version Archive',
            no_entries: 'No change log entries found.',
            no_history: 'No version history available.',
            exported: 'Change log exported',
            aria_export: 'Export change log as CSV',
            aria_view_history: 'View version history for {name}',
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Review Cadence',
            configure_cadence: 'Configure Review Cadence',
            edit_cadence: 'Edit Review Cadence',
            document: 'Document',
            select_document: 'Select a document...',
            review_frequency: 'Review Frequency',
            freq_30: '30 days',
            freq_60: '60 days',
            freq_90: '90 days',
            freq_180: '180 days',
            freq_365: '365 days',
            last_reviewed: 'Last Reviewed',
            next_review: 'Next Review',
            status: 'Status',
            overdue: 'Overdue',
            on_track: 'On Track',
            days: 'days',
            horizon: 'Review Horizon',
            overdue_warning: 'Overdue Reviews',
            overdue_count: '{count} document(s) have overdue reviews.',
            no_cadences: 'No review cadences configured.',
            cadence_created: 'Review cadence created',
            cadence_updated: 'Review cadence updated',
            aria_edit: 'Edit review cadence for {name}',
        },
        remediation_plans: {
            title: 'Remediation Plans',
            create_plan: 'Create Remediation Plan',
            gap_id: 'Gap Reference',
            select_gap: 'Select a gap...',
            plan_title: 'Title',
            description: 'Description',
            priority: 'Priority',
            priority_critical: 'Critical',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            assigned_to: 'Assigned To',
            select_user: 'Select a user...',
            target_date: 'Target Date',
            framework_code: 'Framework',
            select_framework: 'Select a framework...',
            gap_finding: 'Gap Finding',
            action_items: 'Action Items',
            owner: 'Owner',
            deadline: 'Deadline',
            planned: 'Planned',
            in_progress: 'In Progress',
            completed: 'Completed',
            verified: 'Verified',
            no_plans_found: 'No remediation plans found.',
            evidence: 'Evidence',
            plan_created: 'Remediation plan created',
            action_items_hint: 'Action Items (one per line)',
        },
        // --- Enum label maps (Sprint 27.6.0) ---
        statuses: {
            identified: 'Identified',
            assigned: 'Assigned',
            in_remediation: 'In Remediation',
            resolved: 'Resolved',
            accepted_risk: 'Accepted Risk',
            planned: 'Planned',
            in_progress: 'In Progress',
            under_review: 'Under Review',
            finalized: 'Finalized',
            archived: 'Archived',
            draft: 'Draft',
            active: 'Active',
            upcoming: 'Upcoming',
            collecting: 'Collecting',
            published: 'Published',
            review: 'Review',
            approved: 'Approved',
            issued: 'Issued',
            superseded: 'Superseded',
            completed: 'Completed',
            verified: 'Verified',
        },
        severities: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        priorities: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        gap_types: {
            missing: 'Missing',
            expired: 'Expired',
            insufficient: 'Insufficient',
        },
        document_types: {
            policy: 'Policy',
            procedure: 'Procedure',
            standard: 'Standard',
            guideline: 'Guideline',
            template: 'Template (Internal)',
        },
        maturity_levels: {
            level_1: 'Level 1 — Initial',
            level_2: 'Level 2 — Developing',
            level_3: 'Level 3 — Defined',
            level_4: 'Level 4 — Managed',
            level_5: 'Level 5 — Optimizing',
        },
        grades: {
            excellent: 'Excellent',
            good: 'Good',
            fair: 'Fair',
            poor: 'Poor',
            critical: 'Critical',
            needs_improvement: 'Needs Improvement',
            not_assessed: 'Not Assessed',
        },
        modules: {
            data_mapping: 'Data Mapping',
            consent: 'Consent',
            governance: 'Governance',
            incidents: 'Incidents',
            monitoring: 'Monitoring',
            classification: 'Classification',
            vendor_risk: 'Vendor Risk',
            discovery: 'Discovery',
            deletion: 'Deletion',
        },
        cadences: {
            monthly: 'Monthly',
            quarterly: 'Quarterly',
            semi_annual: 'Semi-Annual',
            annual: 'Annual',
        },
        section_labels: {
            opening: 'Opening',
            key_metrics: 'Key Metrics',
            highlights: 'Highlights',
            concerns: 'Concerns',
            recommendations: 'Recommendations',
            outlook: 'Outlook',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} gap',
        count_gaps_plural: '{count} gaps',
        count_summaries: '{count} summary',
        count_summaries_plural: '{count} summaries',
        count_assessments: '{count} assessment',
        count_assessments_plural: '{count} assessments',
        count_models: '{count} model',
        count_models_plural: '{count} models',
        count_periods: '{count} period',
        count_periods_plural: '{count} periods',
        count_metrics: '{count} metric',
        count_metrics_plural: '{count} metrics',
        unknown_user: 'Unknown User',
        unknown_gap: 'Unknown Gap',
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access this page.',
        // --- AC info boxes (Sprint 32.2.0 QA) ---
        ac_vn_frameworks_title: 'VN Framework Coverage',
        ac_vn_frameworks_body: 'Maturity assessments cover all 6 Vietnamese regulatory frameworks: PDPL, Data Law, Telecom Law, Cybersecurity Law, AI Law, and E-Commerce Law — each with framework-specific assessment criteria.',
        ac_controller_processor_title: 'Controller vs. Processor',
        ac_controller_processor_body: 'Obligation mappings distinguish between data controller and data processor responsibilities. Assign roles using the RACI matrix to ensure each obligation has a clear owner, whether the organization acts as controller or processor.',
        ac_versioned_evidence_title: 'Versioned Evidence Trail',
        ac_versioned_evidence_body: 'Every status transition (Draft, Review, Approved, Issued) creates a timestamped, versioned record that serves as auditable evidence. The Change Log captures the full document lifecycle history.',
        ac_remediation_gap_link_title: 'Linked to Assessment Gaps',
        ac_remediation_gap_link_body: 'Each remediation plan links directly to an identified assessment gap. Assign an owner and set a target deadline to track resolution progress from gap to closure.',
        ac_annual_attestation_title: 'Annual Attestation Cycle',
        ac_annual_attestation_body: 'Reporting periods align with the Vietnamese Data Law annual attestation requirement. Use the "Annual" cadence (365-day cycle) to generate attestation-aligned periods automatically.',
        ac_evidence_per_obligation_title: 'Evidence per Framework Obligation',
        ac_evidence_per_obligation_body: 'Evidence gaps identify missing, expired, or insufficient proof for each framework obligation. Filter by domain to see which regulatory requirements lack supporting evidence.',
        ac_cross_framework_title: 'Cross-Framework Completeness',
        ac_cross_framework_body: 'Audit readiness provides a cross-framework completeness view. Component scores reflect coverage across all mapped frameworks, highlighting areas that need attention before an audit.',
        ac_cadence_default_title: 'Default Review Cadence',
        ac_cadence_default_body: 'The default review cadence is 180 days. Overdue reviews are automatically flagged. Adjust frequency per document as needed — options range from 30 to 365 days.',
        ac_templates_internal_title: 'Template Classification',
        ac_templates_internal_body: 'Templates are classified as internal documents. Use the "Template" document type to mark reusable governance artifacts with internal-only distribution scope.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    // ---------- MCP Phase 2 — Pending writes queue (Sprint 48.3.0) ----------
    mcp_pending: {
        title: 'MCP — Pending writes',
        subtitle: 'External Claude clients propose writes here. Approve or decline each entry — Forseti only executes after admin sign-off.',
        no_pending: 'No pending writes.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Tool',
        col_preview: 'Preview',
        col_key: 'API key',
        col_requested: 'Requested',
        col_expires: 'Expires',
        col_status: 'Status',
        status_pending: 'Pending',
        status_approved: 'Approved',
        status_declined: 'Declined',
        status_expired: 'Expired',
        status_executed: 'Executed',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Approve',
        decline_label: 'Decline',
        refresh_label: 'Refresh',
        error_generic: 'Could not load pending writes.',
        confirm_approve: 'Approving will execute the tool against your org with the API key holder’s resolved permissions. Continue?',
        confirm_decline: 'Decline this pending write? This cannot be undone.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Pending writes expire 24 hours after creation. The hourly sweep marks expired entries as Expired; an expired write is never carried out.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Actions',
    },
    // ---------- Audit walkthrough + Privilege creep (Sprint 48.2.9) ----------
    audit_walkthrough: {
        title: 'Forseti — Audit walkthrough generator',
        subtitle: 'Generate a structured walkthrough for an audit kickoff: per-control opening question, expected evidence, follow-up probes, potential gaps.',
        audit_picker_label: 'Audit',
        audit_picker_hint: 'Pick a planned audit to generate a walkthrough.',
        run_label: 'Generate walkthrough',
        running: 'Generating…',
        results_heading: 'Walkthrough — {count} control(s)',
        tokens_label: '≈ {count} tokens',
        no_controls: 'No controls in scope. Add controls on the audit record.',
        col_control: 'Control',
        col_question: 'Opening question',
        col_evidence: 'Evidence',
        col_gaps: 'Potential gaps',
        expected_evidence_label: 'Expected evidence',
        followup_probes_label: 'Follow-up probes',
        accept_label: 'Accept walkthrough',
        reject_label: 'Reject',
        export_label: 'Export DOCX',
        resolve_summary: 'Resolution: {status}',
        resolve_status_accepted: 'Accepted',
        resolve_status_rejected: 'Rejected',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Could not generate walkthrough. Try again or check the audit status.',
    },
    privilege_creep: {
        title: 'Forseti — Privilege creep detection',
        subtitle: 'Periodic scan of role assignments. Findings are advisory — Forseti never auto-revokes.',
        run_label: 'Scan now',
        running: 'Scanning…',
        findings_heading: '{count} finding(s)',
        no_findings: 'No privilege creep flagged.',
        col_user: 'User',
        col_job_function: 'Job function',
        col_severity: 'Severity',
        col_unexpected: 'Unexpected permissions',
        col_remediation: 'Suggested remediation',
        col_status: 'Status',
        severity_critical: 'Critical',
        severity_high: 'High',
        severity_medium: 'Medium',
        severity_low: 'Low',
        status_open: 'Open',
        status_dismissed: 'Dismissed',
        status_remediated: 'Remediated',
        dismiss_label: 'Dismiss',
        remediate_label: 'Mark remediated',
        note_label: 'Note (optional)',
        error_generic: 'Could not scan privilege creep. Try again later.',
        advisory_banner: 'These findings are advisory. Forseti never revokes permissions automatically — review with the role owner before acting.',
    },
    // ---------- Compliance Rules (Sprint 48.2.8) ----------
    compliance_rules: {
        rule_mapping: {
            title: 'Compliance rule auto-mapping',
            subtitle: 'Forseti walks an installed regulatory pack and suggests new or refreshed compliance rules grounded in the source articles.',
            pack_picker_label: 'Regulatory pack',
            pack_picker_hint: 'Select an installed pack to map.',
            run_label: 'Map from pack',
            running: 'Mapping…',
            results_heading: '{count} suggestion(s)',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'No suggestions — pack content may already be fully covered by existing rules.',
            kind_new: 'New rule',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'No change required',
            col_kind: 'Kind',
            col_title: 'Proposed title',
            col_existing: 'Existing rule',
            col_source: 'Source',
            proposed_body_label: 'Proposed body',
            source_quote_label: 'Source quote',
            accept_selected: 'Accept selected',
            reject_all: 'Reject all',
            accept_all: 'Accept all',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Could not run rule mapping. Try again or check the pack code.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    // ---------- Access Accountability (Sprint 2.6.0) ----------
    access_accountability: {
        nav: {
            session_dashboard: 'Session Dashboard',
            access_log: 'Access Log',
            justification_review: 'Justification Review',
            investigations: 'Investigations',
            pattern_analysis: 'Pattern Analysis',
            anchoring_status: 'Anchoring Status',
            configuration: 'Configuration',
            access_policies: 'Access Policies',
            review_campaigns: 'Review Campaigns',
            evidence_packages: 'Evidence Packages',
        },
        session_dashboard: {
            title: 'Session Dashboard',
            loading: 'Loading dashboard data...',
            total_sessions: 'Total Sessions',
            active_sessions: 'Active Sessions',
            avg_duration: 'Avg Duration',
            peak_day_sessions: 'Busiest Day',
            auth_method_breakdown: 'Auth Method Breakdown',
            geo_country_breakdown: 'Geo Country Breakdown',
            recent_session_events: 'Recent Session Events',
            user_id: 'User ID',
            session_id: 'Session ID',
            ip_address: 'IP Address',
            auth_method: 'Auth Method',
            started_at: 'Started At',
            event_type: 'Event Type',
            all_event_types: 'All Event Types',
            filter_by_user: 'Filter by user',
            no_active_sessions: 'No active sessions',
            no_session_events: 'No session events found',
            revoke: 'Revoke',
            revoke_session: 'Revoke Session',
            aria_revoke_session: 'Revoke session {user}',
            revoke_reason: 'Reason',
            revoke_placeholder: 'Provide a reason for revoking this session...',
            session_label: 'Session:',
            load_failed: 'Failed to load session dashboard. Please try again.',
            events_load_failed: 'Failed to load session events. Please try again.',
            revoke_failed: 'Failed to revoke session',
            session_events_loaded: '{n} session events loaded',
            event_types: {
                session_started: 'Session Started',
                session_refreshed: 'Session Refreshed',
                session_expired: 'Session Expired',
                session_revoked: 'Session Revoked',
                session_terminated: 'Session Terminated',
            },
        },
        access_log: {
            title: 'Access Log',
            user_id: 'User ID',
            resource: 'Resource',
            action: 'Action',
            ip_address: 'IP Address',
            timestamp: 'Timestamp',
            no_logs: 'No access log entries found.',
            filter_by_user: 'Filter by User ID...',
            load_failed: 'Failed to load access log. Please try again.',
            export_success: 'Export initiated successfully',
            export_failed: 'Failed to export access log',
            record_count: 'record',
            record_count_plural: 'records',
            unclassified: 'Unclassified',
            classification_missing: 'Classification Missing',
            classification_level: 'Classification Level',
            response_code: 'Response Code',
            // Gate E12.5, F1 — the transport a read was performed over, and the API
            // key it was performed under. `Access Type` says what shape of read
            // happened; these say which door it came through, which is what separates
            // an MCP call on an exfiltrated key from the key owner's browser behind
            // the same egress address.
            access_surface: 'Access Surface',
            api_key: 'API Key',
            classification_levels: {
                public: 'Public',
                internal: 'Internal',
                confidential: 'Confidential',
                restricted: 'Restricted',
                unclassified: 'Unclassified',
            },
        },
        justification_review: {
            title: 'Justification Review',
            user_id: 'User ID',
            resource: 'Resource',
            justification: 'Justification',
            submitted_at: 'Submitted At',
            reviewer: 'Reviewer',
            no_reviews: 'No justification reviews found.',
            load_failed: 'Failed to load justifications. Please try again.',
            review_failed: 'Failed to review justification',
            decline_failed: 'Failed to decline access',
            justification_count: 'justification',
            justification_count_plural: 'justifications',
            reviewed: 'Reviewed',
            concerning: 'Concerning',
            escalated: 'Escalated',
            link_investigation_placeholder: 'Link to an investigation (optional)',
            review_queue: 'Review Queue',
            access_id_label: 'Access ID',
            access_id_placeholder: 'Access log entry ID',
            resource_type_label: 'Resource Type',
            resource_type_placeholder: 'e.g. document, database',
            resource_id_label: 'Resource ID',
            resource_id_placeholder: 'Resource identifier',
            reason_placeholder: 'Reason for accessing this resource...',
            create_failed: 'Failed to create justification',
            unclassified: 'Unclassified',
            review_statuses: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                concerning: 'Concerning',
                escalated: 'Escalated',
            },
            reason_codes: {
                business_need: 'Business Need',
                legal_obligation: 'Legal Obligation',
                data_subject_request: 'Data Subject Request',
                audit_requirement: 'Audit Requirement',
                incident_response: 'Incident Response',
                research: 'Research',
                administrative: 'Administrative',
                other: 'Other',
            },
            classification_level: 'Classification Level',
            review_decision: 'Review Decision',
        },
        investigations: {
            title: 'Investigation Workspace',
            new: 'New Investigation',
            subject: 'Subject',
            investigator: 'Investigator',
            opened_at: 'Opened At',
            findings: 'Findings',
            no_investigations: 'No investigations found.',
            load_failed: 'Failed to load investigations. Please try again.',
            create_failed: 'Failed to create investigation',
            timeline_add_failed: 'Failed to add timeline entry',
            link_access_failed: 'Failed to link access IDs',
            link_justification_failed: 'Failed to link justification IDs',
            close_failed: 'Failed to close investigation',
            export_success: 'Export initiated successfully',
            export_failed: 'Failed to export investigation',
            refresh_failed: 'Failed to refresh investigation detail. Please try again.',
            update_failed: 'Failed to update investigation',
            investigation_count: 'investigation',
            investigation_count_plural: 'investigations',
            title_placeholder: 'Investigation title',
            description_placeholder: 'Detailed description of the investigation...',
            assignee_placeholder: 'User ID of assignee (optional)',
            timeline_action_placeholder: 'e.g., Reviewed access logs',
            timeline_note_placeholder: 'Optional note',
            access_ids_placeholder: 'access-id-1, access-id-2',
            justification_ids_placeholder: 'justification-id-1, justification-id-2',
            findings_placeholder: 'Summarize the investigation findings...',
            by_actor: 'by',
            close_investigation: 'Close Investigation',
            add_timeline_failed: 'Failed to add timeline entry.',
            fetch_failed: 'Failed to fetch investigations.',
            change_status: 'Change investigation status',
            details: 'Investigation details',
            link_evidence: 'Link evidence',
            link_justifications: 'Link justifications',
            access_ids_label: 'Access IDs',
            justification_ids_label: 'Justification IDs',
            access_ids_link_placeholder: 'access-id-1, access-id-2',
            justification_ids_link_placeholder: 'justification-id-1, justification-id-2',
            investigations_loaded: '{n} investigations loaded',
            status_changed_to: 'Status changed to {status}',
            add_timeline_entry: 'Add timeline entry',
            add_access_ids: 'Add access IDs',
            add_justification_ids: 'Add justification IDs',
            investigation_types: {
                anomaly_review: 'Anomaly Review',
                complaint_driven: 'Complaint Driven',
                scheduled_review: 'Scheduled Review',
                whistleblower_report: 'Whistleblower Report',
            },
            severity_labels: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            status_labels: {
                open: 'Open',
                in_progress: 'In Progress',
                pending_review: 'Pending Review',
                closed_substantiated: 'Closed (Substantiated)',
                closed_unsubstantiated: 'Closed (Unsubstantiated)',
                closed_inconclusive: 'Closed (Inconclusive)',
            },
            disposition_labels: {
                substantiated: 'Substantiated',
                unsubstantiated: 'Unsubstantiated',
                inconclusive: 'Inconclusive',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Pattern Analysis',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Loading pattern data...',
            anomalies_detected: 'Anomalies Detected',
            patterns: 'Patterns',
            by_user: 'By User',
            by_resource: 'By Resource',
            time_range: 'Time Range',
            load_failed: 'Failed to analyze patterns. Please try again.',
            top_resources: 'Top 10',
            access_by_hour: 'Access by Hour of Day',
            access_by_day: 'Access by Day of Week',
            fetch_failed: 'Failed to fetch pattern analysis.',
            analysis_loaded: 'Pattern analysis loaded for {user}, {n} sessions found',
            date_format_placeholder: 'DD/MM/YYYY',
            access_by_hour_heading: 'Access by Hour of Day',
            access_by_day_heading: 'Access by Day of Week',
            by_classification: 'By Classification Level',
            top_accessed_resources: 'Most Accessed Resources',
            user_access_summary: 'User Access Summary',
            days: {
                sunday: 'Sunday',
                monday: 'Monday',
                tuesday: 'Tuesday',
                wednesday: 'Wednesday',
                thursday: 'Thursday',
                friday: 'Friday',
                saturday: 'Saturday',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Anchoring Status',
            loading: 'Loading anchoring data...',
            anchored: 'Anchored',
            pending: 'Pending',
            failed: 'Failed',
            last_anchor: 'Last Anchor',
            no_records: 'No anchoring records found.',
            status_load_failed: 'Failed to load anchoring status. Please try again.',
            proofs_load_failed: 'Failed to load access proofs. Please try again.',
            verify_failed: 'Failed to verify proof chain. Please try again.',
            anchor_failed: 'Failed to anchor access proof',
            batch_anchor_failed: 'Failed to batch anchor access proofs',
            proof_count: 'proof',
            proof_count_plural: 'proofs',
            anchor_single: 'Anchor Single',
            anchor_batch: 'Anchor Batch',
            access_id_placeholder: 'Enter access ID to anchor',
            batch_ids_placeholder: 'access-id-1, access-id-2, access-id-3',
            verify_chain: 'Verify Chain',
            chain_breaks: 'Chain Breaks',
            total_checked: 'Total Checked',
            anchor_placeholder: 'Enter access ID to anchor',
            fetch_failed: 'Failed to fetch anchoring status.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Failed to fetch proofs.',
            access_id: 'Access ID',
            chain_sequence: 'Chain Seq',
            proof_hash_label: 'Proof Hash',
            batch_id: 'Batch ID',
            proofs_loaded: '{n} access proofs loaded',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: "The chain still verifies for these proofs. The platform's retention sweep recorded destroying the access-log row each one anchors after its retention date had passed, so its content can no longer be proven.",
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Pending',
                confirmed: 'Confirmed',
                failed: 'Failed',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Chain',
                blockchain_anchor: 'Blockchain Anchor',
                zkp_stub: 'ZKP Stub',
            },
        },
        configuration: {
            title: 'Configuration',
            session_settings: 'Session Settings',
            log_retention: 'Log Retention (days)',
            anomaly_threshold: 'Anomaly Threshold',
            anchoring_enabled: 'Anchoring Enabled',
            save_config: 'Save Configuration',
            load_failed: 'Failed to load configuration. Please try again.',
            save_success: 'Configuration saved successfully',
            save_failed: 'Failed to save configuration. Please try again.',
            justification_rules: 'Justification Rules',
            reason_codes: 'Reason Codes',
            anchoring_settings: 'Anchoring Settings',
            anchoring_method: 'Anchoring Method',
            anchoring_frequency: 'Anchoring Frequency',
            batch_interval: 'Batch Interval (minutes)',
            eligibility_criteria: 'Eligibility Criteria',
            add_criterion_placeholder: 'Add eligibility criterion...',
            retention_settings: 'Retention Settings',
            standard_retention: 'Standard Retention (days)',
            sensitive_retention: 'Sensitive Retention (days)',
            working_hours: 'Working Hours',
            work_start: 'Start Time',
            work_end: 'End Time',
            timezone: 'Timezone',
            timezone_placeholder: 'e.g., Asia/Ho_Chi_Minh',
            working_days: 'Working Days',
            concurrent_sessions: 'Concurrent Session Limits',
            concurrent_threshold: 'Concurrent Session Threshold',
            classification_level: 'Classification Level',
            prompt_mode: 'Prompt Mode',
            grace_period: 'Grace Period (s)',
            code_placeholder: 'code',
            is_custom: 'Custom',
            label_placeholder: 'Label',
            description_placeholder: 'Description',
            custom: 'Custom',
            fetch_failed: 'Failed to fetch configuration.',
            classification_levels: {
                public: 'Public',
                internal: 'Internal',
                confidential: 'Confidential',
                restricted: 'Restricted',
                unclassified: 'Unclassified',
            },
            prompt_modes: {
                reason_only: 'Reason Only',
                reason_text: 'Reason + Text',
                reason_text_supervisor: 'Reason + Text + Supervisor',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Chain',
                blockchain_anchor: 'Blockchain Anchor',
                zkp_stub: 'ZKP Stub',
            },
            anchoring_frequencies: {
                realtime: 'Real-time',
                batched: 'Batched',
                on_demand: 'On Demand',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Access Policies',
            create_policy: 'Create Access Policy',
            policy_name: 'Policy Name',
            framework_code: 'Framework',
            select_framework: 'Select a framework...',
            review_frequency: 'Review Frequency',
            scope: 'Scope',
            no_policies_found: 'No access policies found.',
            policy_created: 'Access policy created',
            frequency_labels: {
                monthly: 'Monthly',
                quarterly: 'Quarterly',
                semi_annual: 'Semi-Annual',
                annual: 'Annual',
            },
        },
        review_campaigns: {
            title: 'Access Review Campaigns',
            create_campaign: 'Create Review Campaign',
            reviewer: 'Reviewer',
            due_date: 'Due Date',
            certify: 'Certify',
            no_campaigns_found: 'No access review campaigns found.',
            status_open: 'Open',
            status_in_review: 'In Review',
            status_completed: 'Completed',
            campaign_created: 'Review campaign created',
            review_certified: 'Access review certified',
            certify_campaign: 'Certify campaign {id}',
            policy_id: 'Policy ID',
            select_policy: 'Select a policy...',
            reviewer_ids_label: 'Reviewers',
            select_reviewers: 'Select reviewers...',
            status_labels: {
                open: 'Open',
                in_review: 'In Review',
                in_progress: 'In Progress',
                cancelled: 'Cancelled',
                completed: 'Completed',
            },
        },
        evidence_packages: {
            title: 'Evidence Packages',
            generate_package: 'Generate Evidence Package',
            export_type: 'Export Type',
            package_type: 'Package Type',
            framework_scope: 'Framework Scope',
            period: 'Period',
            generated_by: 'Generated By',
            generated_at: 'Generated At',
            no_packages_found: 'No evidence packages found.',
            package_generated: 'Evidence package generated',
            export_type_full: 'Full Export',
            export_type_summary: 'Summary',
            export_type_certification_only: 'Certification Only',
            package_type_access_review: 'Access Review',
            package_type_investigation: 'Investigation',
            package_type_certification: 'Certification',
            package_type_authority_request: 'Authority Request',
            period_start: 'Period Start',
            period_end: 'Period End',
            select_frameworks: 'Select frameworks...',
            completeness_col: 'Completeness',
            status_col: 'Status',
            generated_by_col: 'Generated By',
        },
        evidence_package_detail: {
            title: 'Evidence Package Detail',
            not_found: 'Evidence package not found.',
            package_metadata: 'Package Metadata',
            status: 'Status',
            item_count: 'Item Count',
            disposition: 'Disposition',
            closed_at: 'Closed At',
            completeness_dashboard: 'Completeness Dashboard',
            no_completeness_data: 'No completeness data available.',
            controls_covered: 'controls covered',
            missing: 'Missing',
            sign_off_tracker: 'Sign-Off Tracker',
            stage_framework_owner: 'Framework Owner',
            stage_legal_compliance: 'Legal & Compliance',
            stage_internal_audit: 'Internal Audit',
            approved: 'Approved',
            returned: 'Returned',
            pending: 'Pending',
            approve: 'Approve',
            return_btn: 'Return',
            sign_off_submitted: 'Sign-off submitted',
            artifacts: 'Artifacts',
            no_artifacts: 'No artifacts found.',
            template_id: 'Template ID',
            description: 'Description',
            classification: 'Classification',
            evidence_type: 'Evidence Type',
            framework_section: 'Framework Section',
            history: 'Approval History',
            expand: 'Expand',
            collapse: 'Collapse',
            action: 'Action',
            actor: 'Actor',
            timestamp: 'Timestamp',
            comment: 'Comment',
            close_case: 'Close Case',
            confirm_close: 'Confirm Close',
            closure_notes: 'Closure Notes',
            disposition_compliant: 'Compliant',
            disposition_non_compliant: 'Non-Compliant',
            disposition_partial: 'Partial',
            case_closed: 'Evidence package closed',
            export_package: 'Export Package',
            export_complete: 'Evidence package exported',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    // ---------- Consent Properties (Sprint 2.7.0) ----------
    consent_properties: {
        nav: {
            dashboard: 'Dashboard',
            properties: 'Properties',
            groups: 'Groups',
            bindings: 'Bindings',
            change_requests: 'Change Requests',
            comparison: 'Comparison',
            inconsistencies: 'Inconsistencies',
            snapshot_schedules: 'Snapshot Schedules',
            golden_templates: 'Golden Templates',
            evidence_packs: 'Evidence Packs',
            audit: 'Audit',
        },
        select_property: 'Select property…',
        select_policy: 'Select policy…',
        select_properties: 'Select properties…',
        select_target_properties: 'Select target properties…',
        select_owner: 'Select owner…',
        select_technical_contact: 'Select technical contact…',
        property_statuses: {
            active: 'Active',
            inactive: 'Inactive',
            pending: 'Pending',
            decommissioned: 'Decommissioned',
        },
        dashboard: {
            title: 'Cross-Property Dashboard',
            loading: 'Loading dashboard data...',
            total_properties: 'Total Properties',
            active_properties: 'Active Properties',
            snapshot_coverage: 'Snapshot Coverage',
            properties_covered: 'properties covered',
            total_bindings: 'Total Bindings',
            deployed_bindings: 'Deployed',
            pending_bindings: 'Pending Deployment',
            deployment_progress: 'Deployment Progress',
            on_target: 'On target',
            below_target: 'Below target',
            target_label: 'Target',
            total_groups: 'Total Groups',
            pending_changes: 'Pending Changes',
            inconsistencies: 'Inconsistencies',
            recent_activity: 'Recent Activity',
            no_recent_activity: 'No recent activity.',
            fetch_error: 'Failed to load dashboard. Please try again.',
        },
        properties: {
            title: 'Property Registry',
            new: 'New Property',
            edit: 'Edit Property',
            // fix-sprint-20260717-2118-followup F15 — real section identities
            // that replace field-label pairs used as `title`+`description` on
            // the property create/edit/withdrawal modals.
            section_property_details: 'Property details',
            section_ownership: 'Ownership',
            section_withdrawal_detail: 'Withdrawal detail',
            property_name: 'Name',
            property_type: 'Type',
            identifier: 'Identifier',
            environment: 'Environment',
            owner_id: 'Owner ID',
            technical_contact_id: 'Technical Contact ID',
            decommission: 'Decommission',
            confirm_decommission: 'Are you sure you want to decommission this property?',
            no_properties: 'No properties found.',
            all_environments: 'All Environments',
            all_types: 'All Types',
            cmp_instance_id: 'CMP Instance ID',
            deployment_date: 'Deployment Date',
            identifier_placeholder: 'e.g., www.example.com or com.example.app',
            owner_name: 'Owner',
            technical_contact_name: 'Technical Contact',
            sensitive_data_categories: 'Sensitive Data Categories',
            sensitive_data_categories_placeholder: 'Select sensitive data categories',
            requires_enhanced_review: 'Requires Enhanced Review',
            sensitive_review_status: 'Sensitive Data Review',
            sensitive_review_pending: 'Pending Review',
            sensitive_review_approved: 'Approved',
            sensitive_review_rejected: 'Rejected',
            sensitive_review_submit: 'Submit Review',
            sensitive_review_approve: 'Approve',
            sensitive_review_reject: 'Reject',
            sensitive_review_notes: 'Review Notes',
            sensitive_review_notes_placeholder: 'Enter review notes...',
            record_withdrawal: 'Record Withdrawal',
            withdrawal_subject_id: 'Subject ID',
            withdrawal_consent_id: 'Consent ID',
            withdrawal_reason: 'Reason',
            withdrawal_downstream_systems: 'Downstream Systems',
            withdrawal_downstream_placeholder: 'system-1, system-2',
            withdrawal_success: 'Withdrawal propagated successfully.',
            withdrawal_error: 'Failed to propagate withdrawal.',
            withdrawal_data_subject_id: 'Data Subject ID',
            withdrawal_data_subject_id_placeholder: 'e.g. usr_abc123',
            withdrawal_consent_record_id: 'Consent Record ID',
            withdrawal_consent_record_id_placeholder: 'e.g. consent_xyz789',
            withdrawal_purposes: 'Withdrawn Purposes',
            withdrawal_purposes_placeholder: 'Select purposes to withdraw consent for...',
            withdrawal_withdrawn_at: 'Withdrawal Date',
            withdrawal_tasks_created: 'Tasks Created',
            withdrawal_affected_bindings: 'Affected Bindings',
            withdrawal_affected_activities: 'Affected Processing Activities',
            withdrawal_history: 'Withdrawal History',
        },
        groups: {
            title: 'Property Groups',
            new: 'New Group',
            group_name: 'Group Name',
            member_count: 'Members',
            no_groups: 'No property groups found.',
            policies: 'Policies',
            confirm_delete: 'Are you sure you want to delete this group?',
        },
        bindings: {
            title: 'Binding Matrix',
            property: 'Property',
            purpose: 'Purpose',
            legal_basis: 'Legal Basis',
            bound: 'Bound',
            no_bindings: 'No bindings found.',
            bind_policy: 'Bind Policy',
            bulk_bind: 'Bulk Bind',
            bind_policy_to_property: 'Bind Policy to Property',
            bulk_bind_policy: 'Bulk Bind Policy',
            unbound: 'Unbound',
            policy_id: 'Policy ID',
            version_placeholder: 'e.g., 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Binding',
            property_name: 'Property Name',
            policy_name: 'Policy Name',
            bindings_list: 'Bindings List',
            binding_actions: {
                start_deploy: 'Start Deploy',
                mark_deployed: 'Mark Deployed',
                rollback: 'Rollback',
            },
        },
        change_requests: {
            title: 'Change Request Pipeline',
            new: 'New Change Request',
            requester: 'Requester',
            change_type: 'Change Type',
            property: 'Property',
            submitted_at: 'Submitted At',
            no_requests: 'No change requests found.',
            fetch_pipeline_error: 'Failed to fetch pipeline. Please try again.',
            fetch_list_error: 'Failed to fetch change requests. Please try again.',
            create_error: 'Failed to create change request. Please try again.',
            action_error: 'Failed to perform action. Please try again.',
            approver_name: 'Approver',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'All',
            view_my_properties: 'My Properties',
            stage_requests: '{stage} requests',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Comparison Workspace',
            select_properties: 'Select Properties to Compare',
            compare: 'Compare',
            no_comparison: 'Select at least two properties to compare.',
            fetch_error: 'Failed to fetch comparison reports. Please try again.',
            run_error: 'Failed to run comparison. Please try again.',
            save_notes_error: 'Failed to save notes. Please try again.',
            scan_complete: 'Inconsistency scan completed successfully.',
            scan_error: 'Failed to run inconsistency scan. Please try again.',
            min_two_properties: 'Please provide at least 2 property IDs for comparison.',
            view_inconsistencies: 'View Inconsistencies',
            click_to_select: 'Click to select two or more properties',
            no_properties_available: 'No properties available',
            no_matching_properties: 'No matching properties',
            select_at_least_two_props: 'Select at least two properties from the dropdown above.',
            no_differences_found: 'No differences found between the selected properties.',
            found_inconsistencies: 'Found {count} inconsistencies across {properties} properties and {bindings} bindings.',
        },
        inconsistencies: {
            title: 'Inconsistency List',
            property: 'Property',
            field: 'Field',
            expected: 'Expected',
            actual: 'Actual',
            severity: 'Severity',
            no_inconsistencies: 'No inconsistencies found.',
            trend: 'Inconsistency Trend',
            trend_chart_label: 'Inconsistency trend chart showing counts over time',
            acknowledge: 'Acknowledge',
            resolve: 'Resolve',
            suppress: 'Suppress',
            cr_id_placeholder: 'CR ID',
            view_all: 'All',
            view_my_properties: 'My Properties',
            view_dpo_review: 'DPO Review Queue',
            change_request_id_required: 'Change request ID is required to resolve this inconsistency.',
        },
        snapshot_schedules: {
            title: 'Snapshot Schedules',
            new: 'New Schedule',
            schedule_name: 'Schedule Name',
            frequency: 'Frequency',
            next_run: 'Next Run',
            last_run: 'Last Run',
            no_schedules: 'No snapshot schedules found.',
            target_type: 'Target Type',
            target_id: 'Target ID',
            custom_frequency_days: 'Custom Frequency (days)',
            time_of_day: 'Time of Day',
            time: 'Time',
            retention_days: 'Retention (days)',
            execute_now: 'Execute Now',
            placeholder_days: 'e.g., 10',
            target_name: 'Target Name',
        },
        golden_templates: {
            title: 'Golden Templates',
            new: 'New Template',
            template_name: 'Template Name',
            property_type: 'Property Type',
            no_templates: 'No golden templates found.',
            applicable_group_ids: 'Applicable Group IDs (comma-separated)',
            applicable_groups: 'Applicable Groups',
            purposes: 'Purposes',
            legal_bases: 'Legal Bases',
            banner_config: 'Banner Config',
            consent_settings: 'Consent Settings',
            group_ids_placeholder: 'group-id-1, group-id-2',
            apply_to_property: 'Apply to Property',
            apply_to_group: 'Apply to Group',
            apply_success: 'Template applied successfully.',
            applications: 'Application History',
            no_applications: 'No template applications yet.',
            no_configuration_details: 'Template has no configuration details. Edit the template to add purposes, legal bases, banner settings, or consent settings.',
        },
        rollouts: {
            title: 'Template Rollouts',
            rollout_status: 'Rollout Status',
            confirmed: 'Confirmed',
            pending: 'Pending',
            failed: 'Failed',
            confirm: 'Confirm Property',
            confirm_success: 'Property confirmed in rollout.',
            can_close: 'All properties confirmed — rollout complete',
            cannot_close: 'Awaiting property confirmations',
            no_rollouts: 'No rollouts found.',
            in_progress: 'In Progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
            reject: 'Reject Property',
            reject_success: 'Property rejected from rollout.',
            confirm_all: 'Confirm All',
            reject_all: 'Reject All',
            confirm_label: 'Confirm rollout for',
            reject_label: 'Reject rollout for',
            confirm_all_label: 'Confirm All Pending Rollouts',
            reject_all_label: 'Reject All Pending Rollouts',
            no_properties_in_rollout: 'This rollout has no properties.',
        },
        analytics: {
            title: 'Property Analytics',
            // These measure the property's policy bindings (their deployment status), not
            // visitors' consent choices: the page holds no consent-event data.
            opt_in_rate: 'Deployed bindings (%)',
            opt_out_rate: 'Rolled-back bindings (%)',
            withdrawal_rate: 'Rollback-pending bindings (%)',
            total_consents: 'Total bindings',
            total_withdrawals: 'Rolled-back bindings',
            compliance_score: 'Compliance Score',
            non_compliant: 'Non-Compliant Properties',
            no_non_compliant: 'All properties are compliant.',
            threshold: 'Compliance Threshold',
            property_id: 'Property ID',
            compliant: 'Compliant',
            non_compliant_status: 'Non-Compliant',
            compliance_help_summary: 'How is the compliance score calculated, and how do I fix it?',
            compliance_help_intro: 'A property is flagged as Non-Compliant when its score is below 80%. The score is calculated as:',
            compliance_help_with_template_label: 'If a Golden Template is applied:',
            compliance_help_with_template_text: '% of configuration keys (purposes, legal_bases, banner_config, consent_settings) that match the template exactly.',
            compliance_help_no_template_label: 'If no Golden Template is applied:',
            compliance_help_no_template_text: '% of bindings (consent types assigned to the property) with deployment_status = deployed. A property with no bindings defaults to 100%.',
            compliance_help_fix_intro: 'To remove a property from this list, do one of the following:',
            compliance_help_fix_deploy_label: 'Deploy its bindings',
            compliance_help_fix_deploy_text: "open the Binding Matrix page and set each binding's deployment status to deployed.",
            compliance_help_fix_template_label: 'Apply a Golden Template',
            compliance_help_fix_template_text: "use Apply to Group on the template, then confirm the rollout for this property. Make sure the property's configuration matches the template.",
            compliance_help_fix_decommission_label: 'Decommission the property',
            compliance_help_fix_decommission_text: "use the Decommission button in the property detail modal if it's no longer in use.",
            compliance_help_fix_no_bindings_label: 'Remove all bindings',
            compliance_help_fix_no_bindings_text: 'if a property has no template and no bindings, it scores 100% by default.',
        },
        evidence_packs: {
            title: 'Evidence Packs',
            new: 'New Pack',
            pack_name: 'Pack Name',
            properties: 'Properties',
            generated_at: 'Generated At',
            no_packs: 'No evidence packs found.',
            generate: 'Generate',
            content_type_consent_records: 'Consent Records',
            content_type_policy_versions: 'Policy Versions',
            content_type_binding_history: 'Binding History',
            content_type_change_requests: 'Change Requests',
            content_type_inconsistency_reports: 'Inconsistency Reports',
            content_type_snapshot_data: 'Snapshot Data',
            content_type_audit_trail: 'Audit Trail',
        },
        audit: {
            title: 'Consent Property Audit',
            event_type: 'Event Type',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            actor: 'Actor',
            timestamp: 'Timestamp',
            no_events: 'No audit events found.',
            entity_type_property: 'Property',
            entity_type_group: 'Group',
            entity_type_binding: 'Binding',
            entity_type_change_request: 'Change Request',
            entity_type_golden_template: 'Golden Template',
            entity_type_snapshot_schedule: 'Snapshot Schedule',
            entity_type_inconsistency: 'Inconsistency',
            entity_type_evidence_pack: 'Evidence Pack',
            action_created: 'Created',
            action_updated: 'Updated',
            action_deleted: 'Deleted',
            action_decommissioned: 'Decommissioned',
            action_status_changed: 'Status Changed',
            action_submitted: 'Submitted',
            action_approved: 'Approved',
            action_rejected: 'Rejected',
            action_deployed: 'Deployed',
            action_rolled_back: 'Rolled Back',
            action_paused: 'Paused',
            action_resumed: 'Resumed',
            action_cancelled: 'Cancelled',
            action_acknowledged: 'Acknowledged',
            action_resolved: 'Resolved',
            action_suppressed: 'Suppressed',
            action_executed: 'Executed',
        },
        property_types: {
            website: 'Website',
            mobile_app: 'Mobile App',
            ott: 'OTT',
            iot_device: 'IoT Device',
            kiosk: 'Kiosk',
            api_service: 'API Service',
            sdk: 'SDK',
        },
        environments: {
            production: 'Production',
            staging: 'Staging',
            development: 'Development',
            testing: 'Testing',
        },
        group_types: {
            brand: 'Brand',
            region: 'Region',
            product_line: 'Product Line',
            business_unit: 'Business Unit',
            custom: 'Custom',
        },
        target_types: {
            property: 'Property',
            group: 'Group',
            all: 'All',
        },
        frequencies: {
            hourly: 'Hourly',
            daily: 'Daily',
            weekly: 'Weekly',
            monthly: 'Monthly',
            custom: 'Custom',
        },
        binding_statuses: {
            current: 'Current',
            outdated: 'Outdated',
            pending: 'Pending',
            deploying: 'Deploying',
            rolled_back: 'Rolled Back',
        },
        sensitive_categories: {
            health: 'Health Data',
            biometric: 'Biometric Data',
            genetic: 'Genetic Data',
            racial_ethnic: 'Racial / Ethnic Origin',
            political_opinions: 'Political Opinions',
            religious_beliefs: 'Religious Beliefs',
            trade_union: 'Trade Union Membership',
            sexual_orientation: 'Sexual Orientation',
            criminal_records: 'Criminal Records',
            financial: 'Financial Data',
            children: "Children's Data",
            geolocation: 'Precise Geolocation',
        },
        export_csv: 'Export CSV',
        exporting: 'Exporting...',
        page_x_of_y: 'Page {page} of {total}',
        regulatory: {
            capture_method_guidance: 'PDPL requires consent proof to match the collection channel. Property types map to consent capture methods: website/OTT = banner-based consent, mobile app/SDK = in-app consent flow, kiosk = physical/on-screen consent capture, API service = programmatic consent, IoT device = device-level consent mechanism.',
            dashboard_metrics_info: 'This dashboard provides the DPO with a single view of consent posture across all digital properties. Key metrics include: binding coverage percentage (deployed vs total bindings), template adherence via the inconsistencies widget, pending change requests requiring review, and property status breakdown showing properties with active, inactive, or decommissioned states. Properties with sensitive data categories are tracked via the property registry.',
            change_request_impact_info: 'Change requests affecting consent properties must document regulatory impact: which PDPL requirements are affected, whether the change affects sensitive data processing (Art. 2.4), and whether the change requires re-consent from data subjects. The impact summary in each change request details the PDPL regulatory references for audit evidence.',
            outdated_binding_warning: 'Bindings with "outdated" status indicate that a property runs a policy version that is not current. These properties may not reflect the latest PDPL requirements or organizational policy updates. Review and update outdated bindings promptly to maintain PDPL compliance across all consent properties.',
        },
    },
    // ---------- Sprint 2.8.0: Localization ----------
    localization: {
        // fix-sprint-20260827-2129 residue (B-F18 class) — failure fallbacks for the
        // localization pages, which ended catch blocks in `err?.message || '<English>'`.
        errors: {
            install_from_zip_failed: 'Failed to install the pack from the ZIP file. Please try again.',
            load_installed_packs_failed: 'Failed to load installed packs. Please try again.',
            load_catalog_failed: 'Failed to load the pack catalog. Please try again.',
            load_market_packs_failed: 'Failed to load market packs. Please try again.',
            install_pack_failed: 'Failed to install the pack. Please try again.',
            uninstall_pack_failed: 'Failed to uninstall the pack. Please try again.',
            install_cluster_failed: 'Failed to install the pack cluster. Please try again.',
            install_market_pack_failed: 'Failed to install the market pack. Please try again.',
            uninstall_market_pack_failed: 'Failed to uninstall the market pack. Please try again.',
            details_partial: 'Could not load full details. Showing the data available.',
            load_dashboard_failed: 'Failed to load localization dashboard data. Please try again.',
            load_market_pack_failed: 'Failed to load the market pack. Please try again.',
            save_mapping_failed: 'Failed to save the module mapping. Please try again.',
            delete_mapping_failed: 'Failed to delete the module mapping. Please try again.',
            load_language_packs_failed: 'Failed to load language packs. Please try again.',
            activate_language_pack_failed: 'Failed to activate the language pack. Please try again.',
            deactivate_language_pack_failed: 'Failed to deactivate the language pack. Please try again.',
            uninstall_language_pack_failed: 'Failed to uninstall the language pack. Please try again.',
            load_completeness_failed: 'Failed to load completeness data. Please try again.',
            update_featured_failed: 'Failed to update the featured status. Please try again.',
            load_available_language_packs_failed: 'Failed to load available language packs. Please try again.',
            install_language_pack_failed: 'Failed to install the language pack. Please try again.',
            load_audit_failed: 'Failed to load localization audit events. Please try again.',
            save_translation_failed: 'Failed to save the translation. Please try again.',
        },
        nav: {
            dashboard: 'Dashboard',
            market_packs: 'Market Packs',
            language_packs: 'Language Packs',
            frameworks: 'Regulatory Frameworks',
            mappings: 'Module Mappings',
            submissions: 'Pack Submissions',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Translation Preview',
            audit: 'Audit Trail',
        },
        dashboard: {
            title: 'Localization Dashboard',
            loading: 'Loading dashboard data...',
            active_market_packs: 'Active Market Packs',
            published_packs_available: 'Published market packs available',
            language_packs: 'Language Packs',
            total_language_packs: 'Total language packs registered',
            active_regulatory_frameworks: 'Active Regulatory Frameworks',
            frameworks_active: 'Frameworks with active status',
            pending_pack_submissions: 'Pending Pack Submissions',
            submissions_awaiting_review: 'Submissions awaiting review',
            recent_audit_events: 'Recent Audit Events',
            no_recent_audit: 'No recent audit events.',
            event_type: 'Event Type',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            actor: 'Actor',
            timestamp: 'Timestamp',
        },
        market_packs: {
            title: 'Market Packs Catalog',
            new: 'New Market Pack',
            pack_name: 'Pack Name',
            market: 'Market',
            locale: 'Locale',
            no_packs: 'No market packs found.',
        },
        language_packs: {
            title: 'Language Packs',
            new: 'New Language Pack',
            language: 'Language',
            locale_code: 'Locale Code',
            completeness: 'Completeness',
            completeness_detail: 'Completeness Detail',
            overall_completeness: 'Overall Completeness',
            total_keys: 'Total Keys',
            translated_keys: 'Translated Keys',
            missing_keys: 'Missing Keys',
            namespace_breakdown: 'Namespace Breakdown',
            namespace: 'Namespace',
            translated: 'Translated',
            no_packs: 'No language packs found.',
            install_language_pack: 'Install Language Pack',
            available_packs: 'Available Language Packs',
            install: 'Install',
            all_installed: 'All language packs are already installed.',
            install_success: 'Language pack installed successfully.',
            market: 'Market',
            featured: 'Featured',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Regulatory Frameworks',
            new: 'New Framework',
            framework_name: 'Framework Name',
            jurisdiction: 'Jurisdiction',
            effective_date: 'Effective Date',
            no_frameworks: 'No regulatory frameworks found.',
            view_requirements: 'View Requirements',
            hide_requirements: 'Hide Requirements',
            show_legacy: 'Show legacy frameworks',
            effective_until: 'Effective until',
            effective_on: 'Effective on',
            replaced_by: 'Replaced by',
            replaces_label: 'Replaces',
            status_active: 'Active',
            status_draft: 'Draft',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Legacy',
            status_upcoming: 'Upcoming',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Watchlist',
            watchlist_empty: 'No draft or upcoming frameworks',
            watchlist_empty_desc: 'Frameworks with status "draft" or "upcoming" will appear here for monitoring.',
            expected_effective: 'Expected effective',
            effective_date_tbd: 'Effective date: TBD',
            activate: 'Activate',
            // AI Law (Sprint 15.9.0)
            ai_law_title: 'Vietnam AI Law',
            ai_risk_classification: 'AI Risk Classification',
            ai_system_dossier: 'AI System Dossier',
            ai_labeling_checklist: 'AI Labeling & Disclosure',
            ai_incident_report: 'AI Incident Report',
            ai_impact_assessment: 'AI Impact Assessment',
            ai_sandbox_application: 'Sandbox Application',
            ai_sandbox_monitoring: 'Sandbox Monitoring',
            ai_sandbox_completion: 'Sandbox Completion',
            ai_submission_tracker: 'Submission Tracker',
            ai_evidence_capture: 'Evidence Capture',
            risk_level_minimal: 'Minimal Risk',
            risk_level_limited: 'Limited Risk',
            risk_level_high: 'High Risk',
            risk_level_unacceptable: 'Unacceptable Risk',
            submission_status_draft: 'Draft',
            submission_status_submitted: 'Submitted',
            submission_status_returned: 'Returned for Amendment',
            submission_status_accepted: 'Accepted',
            submission_status_rejected: 'Rejected',
            submission_status_closed: 'Closed',
            // E-Commerce Law (Sprint 16.0.0)
            ecommerce_law_title: 'Vietnam E-Commerce Law',
            ecommerce_platform_classification: 'Platform Classification',
            ecommerce_seller_register: 'Seller Register',
            ecommerce_complaint_register: 'Complaint Register',
            ecommerce_takedown_register: 'Takedown Register',
            ecommerce_submission_tracking: 'Submission Tracking',
            ecommerce_dossier_checklist: 'Dossier Checklist',
            ecommerce_cross_border: 'Cross-Border Compliance',
            ecommerce_trustmark: 'Trustmark Program',
            ecommerce_notification: 'Notification',
            ecommerce_registration: 'Registration',
            ecommerce_termination: 'Termination',
            ecommerce_reporting_readiness: 'Reporting Readiness',
            ecommerce_evidence_capture: 'Evidence Capture',
            submission_status_approved: 'Approved',
            cybersec_law_2025_title: 'Vietnam Cybersecurity Law (2025)',
            cybersec_system_classification: 'System Classification',
            cybersec_risk_assessment: 'Risk Assessment',
            cybersec_incident_report: 'Incident Report',
            cybersec_authority_cooperation: 'Authority Cooperation',
            cybersec_data_localization: 'Data Localization',
            cybersec_vendor_security: 'Vendor Security',
            cybersec_submission_tracker: 'Submission Tracker',
            cybersec_evidence_capture: 'Evidence Capture',
            cybersec_dossier_checklist: 'Dossier Checklist',
            cybersec_classification_general: 'General',
            cybersec_classification_important: 'Important',
            cybersec_classification_critical: 'Critical',
            data_law_title: 'Vietnam Data Law',
            data_inventory: 'Data Inventory',
            data_classification: 'Data Classification',
            data_sharing: 'Data Sharing',
            data_governance: 'Data Governance',
            data_submission_tracker: 'Submission Tracker',
            data_evidence_capture: 'Evidence Capture',
            data_dossier_checklist: 'Dossier Checklist',
            telecom_law_title: 'Vietnam Telecom Law',
            telecom_authority_request: 'Authority Request',
            telecom_disclosure: 'Disclosure',
            telecom_confidentiality: 'Confidentiality',
            telecom_submission_tracker: 'Submission Tracker',
            telecom_evidence_capture: 'Evidence Capture',
            telecom_dossier_checklist: 'Dossier Checklist',
            pdpl_law_title: 'Vietnam Personal Data Protection Law',
            pdpl_breach_notification: 'Breach Notification',
            pdpl_dpo_notification: 'DPO Appointment Notification',
            pdpl_data_subject_notification: 'Data Subject Notification',
            pdpl_submission_tracker: 'Submission Tracker',
            pdpl_evidence_capture: 'Evidence Capture',
            pdpl_dossier_checklist: 'Dossier Checklist',
            pdpl_authority_filing: 'Authority Filing',
            pdpl_mops_filing: 'MoPS Filing',
        },
        mappings: {
            title: 'Module Mappings',
            new: 'New Mapping',
            module: 'Module',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Consent',
            module_dpia: 'DPIA',
            module_incident: 'Incident',
            module_rights_request: 'Rights Request',
            module_data_mapping: 'Data Mapping',
            module_classification: 'Classification',
            module_monitoring: 'Monitoring',
            module_governance: 'Governance',
            module_vendor: 'Vendor',
            module_risk: 'Risk',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Market Pack',
            mapping_status: 'Mapping Status',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'No module mappings found.',
            incomplete_entries_warning: '{count} mapping(s) hidden due to missing framework ID, requirement ID, or module.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Pack Submissions',
            new: 'New Submission',
            submitter: 'Submitter',
            pack: 'Pack',
            submitted_at: 'Submitted At',
            no_submissions: 'No pack submissions found.',
            installed_sector_overlays_title: 'Sector Overlays ({count})',
            installed_sector_overlays_aria: 'Installed Sector Overlays',
            installed_col_name: 'Name',
            installed_col_code: 'Code',
            installed_col_sector: 'Sector',
            installed_col_mode: 'Mode',
            installed_col_status: 'Status',
            installed_refresh: 'Refresh',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Translation Preview',
            select_locale: 'Select Locale',
            select_module: 'Select Module',
            key: 'Key',
            value: 'Value',
            no_translations: 'No translations found.',
        },
        audit: {
            title: 'Localization Audit Trail',
            event_type: 'Event Type',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            entity_name: 'Entity Name',
            actor: 'Actor',
            timestamp: 'Timestamp',
            payload: 'Payload',
            collapse_payload: 'Collapse payload details',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Expand payload details',
            collapse: 'Collapse',
            expand: 'Expand',
            all_entity_types: 'All Entity Types',
            no_events: 'No audit events found.',
        },
        market_packs_detail: {
            installed_at: 'Installed At',
            pack_details_label: 'Pack Details',
            market_code: 'Market Code',
            supported_locales: 'Supported Locales',
            min_platform_version: 'Min Platform Version',
            pack_information: 'Pack information',
            pack_contents: 'Pack Contents',
            included_frameworks: 'Included Frameworks',
            pack_artifacts: 'Pack Artifacts',
            no_artifacts: 'No artifacts found for this pack',
            pack_not_found: 'Pack not found',
        },
        mappings_form: {
            framework_id: 'Framework ID',
            requirement_id: 'Requirement ID',
            module_label: 'Module',
            mapping_type: 'Mapping Type',
            configuration_json: 'Configuration (JSON)',
            conflict_resolution: 'Conflict Resolution',
            is_override: 'Is Override',
            all_modules: 'All Modules',
            all_mapping_types: 'All Mapping Types',
        },
        submissions_detail: {
            submission_id: 'Submission ID',
            validation: 'Validation',
            reviewers: 'Reviewers',
            start_review: 'Start Review',
            hide_comments: 'Hide Comments',
            // Sprint 63.33.0 — the row action labels, named by submission.
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Review Comments',
            loading_comments: 'Loading comments...',
            no_comments: 'No comments yet.',
            add_comment: 'Add Comment',
            reject_submission: 'Reject Submission',
            reason_for_rejection: 'Reason for Rejection',
            reviewer_ids: 'Reviewer IDs (comma-separated)',
            submit_new_pack: 'Submit New Pack',
            pack_type: 'Pack Type',
            market_code: 'Market Code',
            target_locales: 'Target Locales',
            pack_file: 'Pack File',
            pack_format_info: 'Pack format: Market packs should include locale-specific JSON files with namespace/key translations. Language packs should provide translations for the specified target locales. See documentation for detailed schema.',
            accepted_formats: 'Accepted formats: .json or .zip. JSON files should contain a flat or namespaced object of translation key-value pairs. ZIP files should contain one or more JSON locale files.',
            comma_separated_locales: 'Comma-separated locale codes for this language pack',
            confirm_approval: 'Confirm Approval',
            approve_message: 'Approve this pack submission?',
            submission_label: 'Submission',
            all_pack_types: 'All Pack Types',
            passed: 'passed',
            failed_label: 'failed',
            market_code_required: 'Market code is required',
            version_required: 'Version is required',
            pack_type_regulatory: 'Regulatory',
            framework_code: 'Framework Code',
            jurisdiction_code: 'Jurisdiction Code',
            pack_source: 'Source',
            pack_source_manual: 'Manual',
            pack_version: 'Pack Version',
            install_regulatory_pack: 'Install Regulatory Pack',
            regulatory_pack_desc: 'Upload a regulatory framework pack (.zip)',
            framework_required: 'Framework code is required',
            jurisdiction_required: 'Jurisdiction code is required',
        },
        catalog_detail: {
            packs_installed_of: 'of {total} packs installed',
            install_all: 'Install All',
            installing: 'Installing...',
            removing: 'Removing...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Requires',
            tab_catalog: 'Catalog',
            tab_installed: 'Installed',
            details: 'Details',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Source Locale',
            target_locale: 'Target Locale',
            locales_must_differ: 'Source and target locales must be different.',
            all_keys_translated: 'All keys translated',
            keys_missing: '{count} key(s) missing',
            show_missing_only: 'Show missing only',
            namespace: 'Namespace',
            source: 'Source',
            target: 'Target',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Frameworks',
            tab_watchlist: 'Watchlist',
            authority: 'Authority',
            requirements_count: 'Requirements',
            all_types: 'All Types',
            collapse_all: 'Collapse All',
            expand_all: 'Expand All',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Filtered to',
            clear_filter: 'Clear filter',
            no_requirements: 'No requirements found for this framework.',
            requirements_label: 'Requirements',
            status_updated: 'Status updated successfully',
            // Sprint 25.5.0: Framework detail tabs
            overview_tab: 'Overview',
            forms_tab: 'Forms',
            procedures_tab: 'Procedures',
            fines_risks_tab: 'Fines & Risks',
            deadlines_tab: 'Deadlines',
            // Sprint 46.9.0: Guidance tab + actions
            guidance_tab: 'Guidance',
            guidance_empty: 'No guidance content for this framework.',
            guidance_loading: 'Loading guidance…',
            guidance_section: 'Guidance section',
            guidance_view: 'View Guidance',
            guidance_map_to_workflow: 'Map to Workflow',
            guidance_link_evidence: 'Link Evidence',
            guidance_filter_type: 'Type',
            guidance_filter_category: 'Category',
            guidance_filter_module: 'Module',
            guidance_filter_status: 'Status',
            guidance_filter_search: 'Search guidance',
            guidance_filter_all: 'All',
            guidance_table_reference: 'Reference',
            guidance_table_name: 'Guidance Name',
            guidance_table_type: 'Type',
            guidance_table_category: 'Category',
            guidance_table_effective_date: 'Effective Date',
            guidance_table_modules: 'Related Modules',
            guidance_table_actions: 'Actions',
            guidance_reference_only_banner: 'Reference only — applies to {{sector}} sector but not to your organization.',
            guidance_open_official_source: 'Open official source',
            guidance_no_detail: 'No additional detail available for this guidance.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring (EU regimes)',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Related Guidance',
            deadlines_empty: 'No regulatory deadlines found for this framework.',
            deadlines_sla_type: 'SLA Type',
            deadlines_value: 'Deadline',
            deadlines_unit: 'Unit',
            deadlines_extension: 'Extension',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Legal Reference',
            framework_stack: 'Framework Stack',
            layer_parent_law: 'Parent Law',
            layer_implementing_decree: 'Implementing Decree',
            layer_enforcement_decree: 'Enforcement Decree',
            layer_admin_procedures: 'Administrative Procedures',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Child Instruments',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Active',
            status_draft: 'Draft',
            draft_enforcement_badge: 'Draft — not yet promulgated',
            effective_date_draft: 'Draft — not yet in force',
            effective_date: 'Effective Date',
            forms_table_id: 'Form ID',
            forms_table_name: 'Form Name',
            forms_table_source: 'Source Layer',
            forms_table_category: 'Category',
            forms_table_type: 'Type',
            fill_form: 'Fill Form',
            view_reference: 'View Reference',
            forms_empty: 'No forms found for this framework.',
            forms_loading: 'Loading forms...',
            filter_by_source_layer: 'Filter by source layer',
            all_layers: 'All Layers',
            procedures_id: 'Procedure ID',
            procedures_name: 'Procedure',
            procedures_trigger: 'Trigger',
            procedures_steps: 'Steps',
            procedures_outputs: 'Outputs',
            procedures_related_forms: 'Related Forms',
            procedures_submission_channels: 'Submission Channels',
            // Sprint 62.0.3 (AC8) — frameworks with no authority that accepts filings
            no_filing_authority_title: 'No filing authority',
            no_filing_authority_body: 'No authority accepts a filing against this framework, so no filing path is offered and no deadline can be overdue. Obligations here are evidenced, not filed.',
            no_filing_authority_basis_certification_body: 'Conformity is assessed by an accredited certification body.',
            no_filing_authority_basis_commercially_engaged_auditor: 'Assurance is provided by a commercially engaged auditor.',
            no_filing_authority_basis_internal_governance_only: 'This framework is discharged through internal governance only.',
            procedures_empty: 'No procedures found for this framework.',
            fines_area: 'Violation Area',
            fines_articles: 'Articles',
            fines_individual: 'Fine (Individual)',
            fines_organization: 'Fine (Organization)',
            fines_revenue: 'Revenue Escalation',
            fines_deadlines: 'Deadlines',
            fines_remediation: 'Remediation',
            fines_related: 'Related Procedures',
            fines_empty: 'No enforcement data available for this framework.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Risk Type',
            fines_risk_triggers: 'What Triggers It',
            fines_risk_only_note: 'These entries describe compliance and operational risk, not monetary penalties. Where fines apply, they are defined in the parent framework(s).',
            fines_enforcement_pending: 'Enforcement framework not yet published. Sanctions data will be added when the implementing instrument is promulgated.',
            view_details: 'View Details',
            back_to_list: 'Back to Frameworks',
            description: 'Description',
            jurisdiction: 'Jurisdiction',
            lineage: 'Lineage',
            replaces: 'Replaces',
            replaced_by: 'Replaced By',
            requirements: 'Requirements',
            tab_navigation: 'Framework detail tabs',
            // Sprint 25.5.0.1: Forms tab action link
            forms_table_actions: 'Actions',
            create_form: 'Create Form',
            // Sprint 25.5.0.2: Enforcement decree status tracking
            mark_promulgated: 'Mark as Promulgated',
            status_promulgated: 'Promulgated',
            promulgated_success: 'Layer status updated to promulgated',
            // Transition Review (framework succession lifecycle)
            transition_review: 'Transition Review',
            transition_review_desc: 'This framework has a pending transition review for successor law adoption.',
            transition_review_by: 'Review Deadline',
            transition_review_status: 'Review Status',
            transition_review_notes: 'Notes',
            transition_review_pending: 'Pending',
            transition_review_in_progress: 'In Progress',
            transition_review_completed: 'Completed',
            transition_review_successor: 'Successor Framework',
            transition_review_lead_time: 'Lead Time',
            transition_review_lead_time_days: '{days} days before effective date',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Form Transition Reviews',
            transition_review_forms_desc: 'Review each operational template for relevance under the successor framework.',
            transition_review_outcome: 'Outcome',
            transition_review_outcome_carry_forward: 'Carry Forward',
            transition_review_outcome_carry_forward_revised: 'Carry Forward (Revised)',
            transition_review_outcome_retire: 'Retire',
            transition_review_outcome_split: 'Split',
            transition_review_outcome_awaiting_decree: 'Awaiting Decree',
            transition_review_no_forms: 'No form transition reviews configured.',
            transition_review_update_success: 'Transition review updated successfully',
            transition_review_operational_note: 'These are process-enabling operational templates, not statutory form counts. Legal attribution remains with the legacy framework until the successor law takes effect.',
            // Sprint 26.4.0.2: Rights tab
            rights_tab: 'Rights',
            rights_empty: 'No data subject rights defined for this framework.',
            rights_code: 'Code',
            rights_name: 'Right',
            rights_legal_basis: 'Legal Basis',
            rights_elaborating_articles: 'Elaborating Articles',
            rights_description: 'Description',
            // Requirement table columns
            requirements_reference: 'Reference',
            requirements_mandatory: 'Mandatory',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            // Fix sprint 20261102 F3 — the requirement's basis note under its applicability; the toggle shows a clamped note in full.
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Source',
            // Requirement category labels
            category_legal_basis: 'Legal Basis',
            category_data_subject_rights: 'Data Subject Rights',
            category_notification: 'Notification',
            category_assessment: 'Assessment',
            category_governance: 'Governance',
            category_record_keeping: 'Record Keeping',
            category_cross_border: 'Cross-Border',
            category_technical_measures: 'Technical Measures',
            category_incident_reporting: 'Incident Reporting',
            category_risk_management: 'Risk Management',
            category_supply_chain: 'Supply Chain',
            category_certification: 'Certification',
            // Framework list labels
            child_frameworks: '{count} child framework',
            child_frameworks_plural: '{count} child frameworks',
            replaces_prefix: 'Replaces:',
            replaced_by_prefix: 'Replaced by:',
            responsible_prefix: 'Responsible:',
            watchlist_empty_hint: 'Frameworks with status "draft" or "upcoming" will appear here for monitoring.',
            framework_count: '{count} framework',
            framework_count_plural: '{count} frameworks',
            hiding_legacy: '(hiding {count} legacy)',
            // Framework code display labels
            label_VN_PDPD_DECREE_13: 'PDPD Decree 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Law 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Decree 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'Draft Cyber+PDPL Enforcement 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Data Law',
            label_VN_DATA_LAW_DECREE_165_2025: 'Data Law Decree 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Data Exchanges Draft 2026',
            label_VN_TELECOM_LAW_2023: 'Telecommunications Law 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Telecom Decree 163/2024',
            label_VN_CYBERSEC_LAW: 'Cybersecurity Law (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec Decree 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Cybersecurity Law 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'E-Commerce Law 122/2025',
            label_VN_AI_LAW_2026: 'AI Law 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            // Issuer labels
            issuer_company: 'Company',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Authority',
            // Deadline SLA type labels
            sla_type_rights_request_ack: 'Rights request acknowledgment',
            'sla_type_Access control review': 'Access control review',
            'sla_type_Annual governance attestation': 'Annual governance attestation',
            'sla_type_Audit review cycle': 'Audit review cycle',
            'sla_type_Authority request response': 'Authority request response',
            'sla_type_Change notification processing': 'Change notification processing',
            'sla_type_Complaint acknowledgment': 'Complaint acknowledgment',
            'sla_type_Complaint resolution': 'Complaint resolution',
            'sla_type_Confidentiality policy review': 'Confidentiality policy review',
            'sla_type_Cybersecurity incident full report': 'Cybersecurity incident full report',
            'sla_type_Cybersecurity incident initial report': 'Cybersecurity incident initial report',
            'sla_type_Data inventory review': 'Data inventory review',
            'sla_type_Disclosure log review cycle': 'Disclosure log review cycle',
            'sla_type_Incident classification decision': 'Incident classification decision',
            'sla_type_Information system classification review': 'Information system classification review',
            'sla_type_Notification processing (sales website/app)': 'Notification processing (sales website/app)',
            'sla_type_Pen test cycle': 'Pen test cycle',
            'sla_type_Registration processing (service platform)': 'Registration processing (service platform)',
            'sla_type_Risk assessment cycle': 'Risk assessment cycle',
            'sla_type_Takedown decision (after review)': 'Takedown decision (after review)',
            'sla_type_Termination notice (public)': 'Termination notice (public)',
            sla_type_ai_impact_assessment_completion: 'AI impact assessment completion',
            sla_type_ai_sandbox_application_review: 'AI sandbox application review',
            sla_type_ai_serious_incident_report: 'AI serious incident report',
            sla_type_ai_system_registration: 'AI system registration',
            sla_type_authority_request_response: 'Authority request response',
            sla_type_certificate_issuance_review: 'Certificate issuance review',
            sla_type_complaint_handling_approach_notice: 'Complaint handling approach notice',
            sla_type_complaint_incomplete_info_request: 'Complaint incomplete info request',
            sla_type_data_governance_attestation: 'Data governance attestation',
            sla_type_dsr_acknowledgment: 'DSR acknowledgment',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Withdraw / restrict / object',
            // fix-sprint #431 (gate E12.5 remediation 1) — vn-pdpl 1.58.0 splits the third-party
            // clocks by request type; each label names the requests its record dates.
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Withdrawal / objection, third party',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Access / correct / provide',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Correction, third party',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Deletion',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Deletion, third party',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Protective measures',
            sla_type_telecom_authority_request_response: 'Telecom authority request response',
            sla_type_rights_request_completion: 'Rights request completion',
            sla_type_incident_authority_notification: 'Incident authority notification',
            sla_type_incident_subject_notification: 'Incident subject notification',
            sla_type_dpia_completion: 'DPIA completion',
            sla_type_data_retention_review: 'Data retention review',
            sla_type_authority_filing_review: 'Authority filing review',
            sla_type_authority_supplement_response: 'Authority supplement response',
            sla_type_authority_resubmission: 'Authority resubmission',
            sla_type_authority_filing_supplement_response: 'Authority filing supplement response',
            sla_type_authority_filing_resubmission: 'Authority filing resubmission',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            // Deadline unit labels
            unit_working_days: 'working days',
            unit_calendar_days: 'calendar days',
            unit_hours: 'hours',
            // Form category labels
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Consent',
            form_category_privacy: 'Privacy',
            form_category_breach: 'Breach',
            form_category_dpia: 'DPIA',
            form_category_register: 'Register',
            form_category_monitoring: 'Monitoring',
            form_category_vendor: 'Vendor',
            form_category_org: 'Organizational',
            form_category_sector: 'Sector',
            form_category_data_mapping: 'Data Mapping',
            form_category_governance: 'Governance',
            form_category_incident: 'Incident',
            form_category_authority_submission: 'Authority Submission',
            form_category_notice: 'Notice',
            form_category_assessment_report: 'Assessment Report',
            form_category_request: 'Request',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Filing',
            // Module code labels
            module_consent: 'Consent',
            module_rights_request: 'Rights Request',
            module_classification: 'Classification',
            module_dpia: 'DPIA',
            module_governance: 'Governance',
            module_monitoring: 'Monitoring',
            module_register: 'Register',
            module_risk: 'Risk',
            module_data_mapping: 'Data Mapping',
            module_incident: 'Incident',
            module_assessment: 'Assessment',
            module_vendor: 'Vendor',
            module_policy: 'Policy',
            module_audit: 'Audit',
            module_training: 'Training',
            module_discovery: 'Discovery',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            // Sprint 47.3.0 — Guidance enum maps + requirement category
            // headers + per-tab Related-Guidance advisories. See
            // FrameworkDetailView.humanizeEnum() for the lookup contract.
            enum_guidance_type_calculation_guidance: 'Calculation Guidance',
            enum_guidance_type_implementation_guidance: 'Implementation Guidance',
            enum_guidance_type_interpretive_note: 'Interpretive Note',
            enum_guidance_type_legal_guidance: 'Legal Guidance',
            enum_guidance_type_procedural_guidance: 'Procedural Guidance',
            enum_guidance_type_sector_guidance: 'Sector Guidance',
            enum_guidance_type_technical_methodology: 'Technical Methodology',
            enum_guidance_category_annual_reporting: 'Annual Reporting',
            enum_guidance_category_authority_cooperation: 'Authority Cooperation',
            enum_guidance_category_authority_coordination: 'Authority Coordination',
            enum_guidance_category_business_continuity: 'Business Continuity',
            enum_guidance_category_capital_adequacy: 'Capital Adequacy',
            enum_guidance_category_complaint_handling: 'Complaint Handling',
            enum_guidance_category_consent: 'Consent',
            enum_guidance_category_consent_management: 'Consent Management',
            enum_guidance_category_consumer_protection: 'Consumer Protection',
            enum_guidance_category_control_environment: 'Control Environment',
            enum_guidance_category_cross_border: 'Cross-Border',
            enum_guidance_category_cross_border_transfers: 'Cross-Border Transfers',
            enum_guidance_category_data_classification: 'Data Classification',
            enum_guidance_category_data_localization: 'Data Localization',
            enum_guidance_category_data_protection_impact_assessment: 'Data Protection Impact Assessment',
            enum_guidance_category_direct_marketing: 'Direct Marketing (EU regimes)',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Governance',
            enum_guidance_category_ict_risk_management: 'ICT Risk Management',
            enum_guidance_category_incident_management: 'Incident Management',
            enum_guidance_category_internal_audit: 'Internal Audit',
            enum_guidance_category_isms_documentation: 'ISMS Documentation',
            enum_guidance_category_lawful_basis: 'Lawful Basis',
            enum_guidance_category_licensing: 'Licensing',
            enum_guidance_category_local_presence: 'Local Presence',
            enum_guidance_category_logging_and_retention: 'Logging & Retention',
            enum_guidance_category_logical_and_physical_access: 'Logical & Physical Access',
            enum_guidance_category_operational_resilience_testing: 'Operational Resilience Testing',
            enum_guidance_category_outsourcing: 'Outsourcing',
            enum_guidance_category_platform_registration: 'Platform Registration',
            enum_guidance_category_quality_of_service: 'Quality of Service',
            enum_guidance_category_risk_classification: 'Risk Classification',
            enum_guidance_category_risk_management: 'Risk Management',
            enum_guidance_category_scope_definition: 'Scope Definition',
            enum_guidance_category_sectoral_localisation: 'Sectoral Localisation',
            enum_guidance_category_subscriber_protection: 'Subscriber Protection',
            enum_guidance_category_supply_chain: 'Supply Chain',
            enum_guidance_category_system_classification: 'System Classification',
            enum_guidance_category_system_operations: 'System Operations',
            enum_guidance_category_telecommunications_data: 'Telecommunications Data',
            enum_guidance_category_third_party_risk: 'Third-Party Risk',
            enum_guidance_category_transitional_provisions: 'Transitional Provisions',
            enum_guidance_category_vulnerability_management: 'Vulnerability Management',
            enum_guidance_status_active: 'Active',
            enum_guidance_status_draft: 'Draft',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Access Management',
            enum_module_ai_governance: 'AI Governance',
            enum_module_asset_management: 'Asset Management',
            enum_module_audit_trail: 'Audit Trail',
            enum_module_breach_notification: 'Breach Notification',
            enum_module_capital_adequacy: 'Capital Adequacy',
            enum_module_classification: 'Classification',
            enum_module_consent: 'Consent',
            enum_module_consent_management: 'Consent Management',
            enum_module_cross_border_transfers: 'Cross-Border Transfers',
            enum_module_data_localization: 'Data Localization',
            enum_module_data_mapping: 'Data Mapping',
            enum_module_data_protection: 'Data Protection',
            enum_module_data_subject_rights: 'Data Subject Rights',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Encryption',
            enum_module_governance: 'Governance',
            enum_module_incident: 'Incident',
            enum_module_incident_management: 'Incident Management',
            enum_module_internal_audit: 'Internal Audit',
            enum_module_isms_documentation: 'ISMS Documentation',
            enum_module_isms_governance: 'ISMS Governance',
            enum_module_logging_and_monitoring: 'Logging & Monitoring',
            enum_module_monitoring: 'Monitoring',
            enum_module_policy_management: 'Policy Management',
            enum_module_risk: 'Risk',
            enum_module_risk_management: 'Risk Management',
            enum_module_training: 'Training',
            enum_module_vendor: 'Vendor',
            enum_module_vendor_management: 'Vendor Management',
            enum_module_vendor_risk: 'Vendor Risk',
            enum_module_vulnerability_management: 'Vulnerability Management',
            // Sprint 62.58.0 (walk O-6, 2026-11-01) — every guidance_type, category and
            // related_modules value in packs/regulatory/**/guidances.json has a label;
            // a hyphenated value is looked up with underscores.
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Access',
            category_availability: 'Availability',
            category_change_management: 'Change Management',
            category_classification: 'Classification',
            category_communication_and_information: 'Communication & Information',
            category_compliance: 'Compliance',
            category_confidentiality: 'Confidentiality',
            category_control_activities: 'Control Activities',
            category_control_environment: 'Control Environment',
            category_incident: 'Incident',
            category_internal_audit: 'Internal Audit',
            category_internal_control: 'Internal Control',
            category_logical_and_physical_access: 'Logical & Physical Access',
            category_mis_data: 'Management Information System Data',
            category_monitoring: 'Monitoring',
            category_monitoring_activities: 'Monitoring Activities',
            category_outsourcing: 'Outsourcing',
            category_privacy: 'Privacy',
            category_processing_integrity: 'Processing Integrity',
            category_reporting: 'Reporting',
            category_risk_assessment: 'Risk Assessment',
            category_risk_mitigation: 'Risk Mitigation',
            category_sector_applicability: 'Sector Applicability',
            category_system_operations: 'System Operations',
            category_training: 'Training',
            category_vendor: 'Vendor',
            guidance_related_forms_advisory: 'Some forms have related guidance — view in the',
            guidance_related_procedures_advisory: 'Some procedures have related guidance — view in the',
            guidance_related_requirements_advisory: 'Some requirements have related guidance — view in the',
        },
        channels: {
            title: 'Submission Channels & Deadlines',
            channels: 'Submission Channels',
            add_channel: 'Add Channel',
            channel_type: 'Type',
            name: 'Name',
            instructions: 'Instructions',
            active: 'Active',
            no_channels: 'No submission channels configured',
            portal: 'Portal',
            in_person: 'In Person',
            postal: 'Postal',
            email: 'Email',
            add: 'Add',
            deadlines: 'Regulatory Deadlines',
            add_deadline: 'Add Deadline',
            label: 'Label',
            days: 'Days',
            day_type: 'Day Type',
            calendar_days: 'Calendar Days',
            working_days: 'Working Days',
            no_deadlines: 'No deadlines configured',
            description: 'Description',
            failed_load: 'Failed to load channel data',
            channel_added: 'Channel added',
            channel_added_announce: 'Submission channel added',
            failed_add_channel: 'Failed to add channel',
            deadline_added: 'Deadline added',
            deadline_added_announce: 'Regulatory deadline added',
            failed_add_deadline: 'Failed to add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Framework Linkage',
            modules: 'Linked Modules',
            add_module: 'Add Module',
            module_code: 'Module Code',
            required: 'Required',
            link: 'Link',
            no_modules: 'No modules linked',
            notes: 'Notes',
            personas: 'Linked Personas',
            add_persona: 'Add Persona',
            persona_id: 'Persona ID',
            role_description: 'Role Description',
            no_personas: 'No personas linked',
            failed_load: 'Failed to load framework links',
            module_linked: 'Module linked',
            module_linked_announce: 'Module linked successfully',
            failed_link_module: 'Failed to link module',
            persona_linked: 'Persona linked',
            persona_linked_announce: 'Persona linked successfully',
            failed_link_persona: 'Failed to link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Pending',
                assessed: 'Assessed',
                applied: 'Applied',
                dismissed: 'Dismissed',
            },
            title: 'Regulatory Updates & Framework Dashboard',
            page_description: 'Coverage at a glance for every installed regulatory framework, plus the in-product change log of regulatory updates: amendments, effective dates, and impact assessments.',
            coverage_dashboard: 'Framework Coverage Dashboard',
            no_frameworks: 'No frameworks found',
            framework: 'Framework',
            modules: 'Modules',
            templates: 'Templates',
            forms: 'Forms',
            channels: 'Channels',
            deadlines: 'Deadlines',
            pending: 'Pending Updates',
            updates: 'Regulatory Updates',
            create_update: 'Create Update',
            framework_id: 'Framework ID',
            update_title: 'Title',
            description: 'Description',
            effective_date: 'Effective Date',
            amendment_ref: 'Amendment Reference',
            create: 'Create',
            no_updates: 'No regulatory updates',
            status: 'Status',
            impact: 'Impact',
            change_summary: 'Change Summary',
            assess: 'Assess',
            failed_load: 'Failed to load data',
            update_created: 'Update created',
            update_created_announce: 'Regulatory update created',
            failed_create: 'Failed to create update',
            update_assessed: 'Update assessed',
            failed_assess: 'Failed to assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        // ---------- Sprint 46.2.0 — Sector overlay pattern ----------
        framework_relationships: {
            panel_title: 'Related frameworks',
            panel_intro: 'How this framework relates to others. An overlay can apply beneath several parent frameworks at once; each appears here exactly once.',
            outgoing_heading: 'This framework applies to',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Applies beneath this framework',
            outgoing_caption: 'Frameworks this framework points at',
            incoming_caption: 'Frameworks that resolve beneath this framework',
            col_framework: 'Framework',
            col_relationship: 'Relationship',
            col_scope: 'Source',
            scope_global: 'From pack content',
            scope_org: 'Organization-specific',
            load_failed: 'Failed to load related frameworks',
            relationship_kinds: {
                parent: 'Parent framework',
                applies_to: 'Applies to',
                cross_reference: 'Cross reference',
                supersedes: 'Supersedes',
            },
        },
        sector_overlay: {
            // Sector display labels — one per `SECTOR_VALUES` member (16)
            sector_labels: {
                banking: 'Banking',
                insurance: 'Insurance',
                healthcare: 'Healthcare',
                education: 'Education',
                ict_critical: 'Critical ICT',
                telecom: 'Telecommunications',
                energy: 'Energy',
                transport: 'Transport',
                digital_services: 'Digital services',
                digital_technology_industry: 'Digital technology industry',
                manufacturing: 'Manufacturing',
                public_administration: 'Public administration',
                payments: 'Payments',
                publishing: 'Publishing',
                crypto_assets: 'Crypto-assets',
                other: 'Other',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sector',
            direct_scope_label: 'Direct legal scope',
            // Mode chip + banner
            chip_direct: 'Directly applicable',
            chip_reference: 'Reference only',
            banner_direct_copy: 'Directly applicable to your organization. Sector reports and deadlines are active.',
            banner_reference_copy: 'Reference only — your organization is not classified in this sector. Sector reports and deadlines are not active.',
            // Edit-mode form
            edit_mode_button: 'Edit mode',
            edit_form_title: 'Change install mode',
            mode_direct_label: 'Direct — legally applicable',
            mode_direct_help: 'The pack applies to your organization: the deadlines, registers and dashboard widgets it declares are active.',
            mode_reference_label: 'Reference only',
            mode_reference_help: 'Readable and authorable for reference: the deadlines, registers and dashboard widgets the pack declares are not active.',
            reason_note_label: 'Reason (optional)',
            reason_note_placeholder: 'e.g. Bank subsidiary acquired',
            cancel: 'Cancel',
            confirm: 'Confirm',
            confirming: 'Saving…',
            save: 'Save',
            saving: 'Saving…',
            toast_mode_changed: 'Sector overlay mode updated.',
            toast_installed: 'Sector overlay installed.',
            toast_sectors_saved: 'Sector self-identification saved.',
            // Install modal (AC-6)
            install_modal_title: 'Install sector overlay',
            install_modal_intro: 'Sector overlays are opt-in regulatory packs scoped to a single industry. Choose how this pack should apply to your organization.',
            install_modal_footer: 'You can change install mode later from the framework detail page.',
            install_button: 'Install',
            installed_label: 'Installed',
            uninstall_button: 'Uninstall',
            uninstall_confirm: 'Confirm uninstall',
            uninstall_cancel: 'Cancel',
            suggested_label: 'Suggested',
            // Catalog page (AC-6)
            catalog_page_title: 'Optional regulatory overlays',
            catalog_page_intro: 'Opt-in regulatory packs that are never installed automatically. Some are scoped to an industry sector, others apply across sectors or are triggered by a specific activity. Choose Direct if the pack applies to your organization, or Reference for browse-only access with no deadlines, scoring or automated workflows.',
            catalog_empty_title: 'No optional overlays available',
            catalog_empty_description: 'Optional regulatory overlay packs published in your platform will appear here.',
            authority_label: 'Authority',
            // Org Sectors page (AC-5 FE)
            org_sectors_title: 'Industry sectors',
            org_sectors_intro: 'Select the industry sectors your organization operates in. Drives the default install mode (Direct vs Reference) for sector-overlay regulatory packs. Sector changes do not affect existing installs — flip those manually from the framework detail page.',
            // Dashboard widget (AC-8)
            widget_title: 'Sector overlay reports due',
            widget_aria_label: 'Sector overlay reports due',
            widget_empty: 'No upcoming sector overlay deadlines.',
            // Audit-log event labels
            audit_event_mode_changed: 'Sector overlay install mode changed',
            audit_event_installed: 'Sector overlay installed',
            view_details_button: 'View details →',
            banner_aria_label: 'Sector overlay status',
            toast_load_failed: 'Failed to load optional overlays',
            toast_install_failed: 'Failed to install sector overlay',
            toast_uninstall_confirm_required: 'Type "{packCode}" to confirm uninstall.',
            toast_uninstalled: 'Uninstalled {packCode}.',
            toast_uninstall_failed: 'Failed to uninstall sector overlay',
            uninstall_warning_destructive_label: 'Destructive action.',
            uninstall_warning_body: 'Uninstalling will delete the framework record and any persisted regulatory deadlines tied to {frameworkCode} from this organisation.',
            uninstall_warning_type_to_confirm: 'Type {packCode} to confirm.',
            uninstall_in_progress: 'Uninstalling…',
            widget_view_all: 'View all sector overlays →',
            widget_due_in_days: 'Due in {days}d',
            widget_overdue_days: 'Overdue by {days}d',
            widget_due_today: 'Due today',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
                product_identification_certificate_receipt: 'Certificate synchronisation receipt',
                product_identification_connection_readiness: 'Connection and transition readiness record',
                product_identification_event_record: 'Significant supply-chain event record',
                product_identification_provider_agreement: 'Service-provider data-responsibility agreement',
                product_identification_scope_determination: 'Product scope and risk-tier determination',
                product_identification_uid_register: 'UID and master-data register entry',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_identification_scope_determinations: 'Product Scope Determinations',
                product_identification_sector_watch: 'Sector Watch',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        // ---------- Sprint 46.3.0 — Phased deadline engine ----------
        phased_obligations: {
            panel_title: 'Phased obligations',
            panel_aria_label: 'Phased obligations',
            panel_intro: 'Some clauses in this framework activate at a later date than the framework itself. Track activation status and record early implementation here.',
            phased_effective_date_label: 'Phased effective:',
            status_pending: 'Pending activation',
            status_active: 'Active',
            status_early: 'Active (early-implemented)',
            notice_window_label: 'Authority notice window:',
            days_label: 'days',
            recipient_label: 'Recipient:',
            early_implement_button: 'Mark early-implemented',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Mark phased obligation as early-implemented',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'Effective from',
            reason_label: 'Reason (optional)',
            reason_placeholder: 'e.g. Board approved early adoption 2026-09-01',
            confirm: 'Confirm',
            toast_early_implemented: 'Early implementation recorded.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Phased obligation marked early-implemented',
            widget_rolling_window_label: 'Phased activations within 90 days',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        // Sprint 62.26.0 — declared in `en-extras.ts` under a named type: merged
        // with Sprint 62.45.0's calendar strings, inlined here it took `en`'s
        // inferred type past what the compiler will serialise (TS7056).
        deadline_activation: enDeadlineActivation,
        // Sprint 62.58.0 (Slice C) — the framework summary export and the briefing record.
        framework_briefings: enFrameworkBriefings,
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: enRegulatoryUpdateComparison,
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section; named type in `en-extras.ts`.
        framework_suspension: enFrameworkSuspension,
        // ---------- fix-sprint-20260719-0024-followup — HelpContentPacksPage ----------
        help_content_packs: {
            page_title: 'Help-Content Packs',
            permission_denied: 'You do not have permission to manage help-content packs.',
            page_description: 'Manage the help-center article corpus shipped with the platform. Reinstall after editing articles in packs/help-content/articles/, or uninstall to remove in-product help for this organisation.',
            tabs_aria_label: 'Help-Content Packs',
            tab_installed: 'Installed ({count})',
            tab_catalog: 'Catalog ({count})',
            upgrade_available_badge: 'Upgrade available: v{version} on disk',
            reinstall: 'Reinstall',
            reinstalling: 'Reinstalling…',
            uninstall: 'Uninstall',
            uninstalling: 'Uninstalling…',
            confirm_uninstall: 'Confirm uninstall',
            cancel: 'Cancel',
            installed: 'Installed (v{version})',
            install: 'Install',
            installing: 'Installing…',
            meta_articles: 'Articles',
            meta_locale_coverage: 'Locale coverage',
            meta_locale_none: '(none)',
            meta_installed: 'Installed',
            meta_source: 'Source',
            empty_installed: 'No help-content packs installed. Open the Catalog tab to install the canonical help-center-core pack.',
            empty_catalog: 'No help-content packs found on disk. The canonical pack should live at packs/help-content/.',
            destructive_intro: 'Destructive action. Uninstalling will delete all {count} help articles tagged with {pack_code} from this organisation. Customers will lose in-product help until you reinstall. Type {pack_code} to confirm.',
            toast_reinstall_success: 'Reinstalled help-content pack — {installed} article(s) upserted, {orphaned} orphaned cleaned up.',
            toast_uninstall_success: 'Uninstalled {pack_code} — {deleted} article(s) deleted.',
            toast_reinstall_error: 'Reinstall failed',
            toast_uninstall_error: 'Uninstall failed',
            toast_load_error: 'Failed to load help-content packs',
            toast_confirm_required: 'Type "{pack_code}" to confirm uninstall.',
            announce_reinstalled: 'Help-content pack reinstalled',
            announce_uninstalled: 'Help-content pack {pack_code} uninstalled',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    // ---------- Partner Marketplace (Sprint 3.5.0) ----------
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Dashboard',
            partner_orgs: 'Partner Orgs',
            service_tiers: 'Service Tiers',
            deliverable_templates: 'Deliverable Templates',
            deliverables: 'Deliverables',
            brand_profiles: 'Brand Profiles',
            support_tickets: 'Support Tickets',
            workflow_templates: 'Workflow Templates',
            workflows: 'Workflows',
            escalation: 'Escalation',
            reports: 'Reports',
            audit: 'Audit Trail',
        },
        dashboard: {
            title: 'Partner Dashboard',
            loading: 'Loading dashboard data...',
            total_managed_tenants: 'Total Managed Tenants',
            avg_health_score: 'Avg Health Score',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Open Tickets',
            upcoming_deliverables: 'Upcoming Deliverables',
            escalated_tickets: 'Escalated Tickets',
            managed_tenants: 'Managed Tenants',
            partner_tier: 'Partner Tier',
            managed_tenants_count: 'Managed Tenants Count',
            onboarding_date: 'Onboarding Date',
            view_details: 'View Details',
            no_partner_orgs: 'No partner organizations found.',
        },
        partner_orgs: {
            title: 'Partner Organizations',
            new: 'New Partner Org',
            contact_name: 'Contact Name',
            contact_email: 'Contact Email',
            partner_tier: 'Partner Tier',
            managed_orgs: 'Managed Orgs',
            onboarding_date: 'Onboarding Date',
            no_orgs: 'No partner organizations found.',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contact Phone',
            metadata_json: 'Metadata (JSON)',
            change_status: 'Change Status',
            new_status: 'New Status',
            invalid_json_metadata: 'Invalid JSON in metadata field',
            suspended: 'Suspended',
            offboarded: 'Offboarded',
            all_tiers: 'All Tiers',
            silver: 'Silver',
            gold: 'Gold',
            platinum: 'Platinum',
            status_offboarded: 'Offboarded',
            status_suspended: 'Suspended',
            tier_gold: 'Gold',
            tier_platinum: 'Platinum',
            tier_silver: 'Silver',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Service Tiers',
            new: 'New Tier',
            tier_name: 'Tier Name',
            monthly_fee: 'Monthly Fee',
            features: 'Features',
            no_tiers: 'No service tiers found.',
            tier_code: 'Tier Code',
            response_time_hrs: 'Response Time (hrs)',
            resolution_time_hrs: 'Resolution Time (hrs)',
            review_frequency: 'Review Frequency',
            sla_response_time: 'SLA Response Time (hours)',
            sla_resolution_time: 'SLA Resolution Time (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Comparing...',
            compare: 'Compare',
            comparison: 'Service Tier Comparison',
            attribute: 'Attribute',
            deprecate: 'Deprecate',
            deprecated: 'Deprecated',
            select_compare: 'Select at least 2 tiers to compare',
            comparison_title: 'Service Tier Comparison',
            status_deprecated: 'Deprecated',
            activate: 'Activate',
            resolution_time: 'Resolution Time',
            response_time: 'Response Time',
            select_at_least_two: 'Select at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Deliverable Templates',
            new: 'New Template',
            template_name: 'Template Name',
            deliverable_type: 'Deliverable Type',
            no_templates: 'No deliverable templates found.',
            frequency: 'Frequency',
            est_effort_hrs: 'Est. Effort (hrs)',
            estimated_effort: 'Estimated Effort (hours)',
            associated_module: 'Associated Module',
            linked_tiers_count: 'Linked Tiers Count',
            evidence_requirements: 'Evidence Requirements (comma-separated)',
            tier_ids: 'Tier IDs (comma-separated)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'One Time',
            frequency_monthly: 'Monthly',
            frequency_quarterly: 'Quarterly',
            frequency_annually: 'Annually',
            type_report: 'Report',
            type_review: 'Review',
            type_assessment: 'Assessment',
            type_meeting: 'Meeting',
            type_configuration: 'Configuration',
            freq_one_time: 'One Time',
            freq_monthly: 'Monthly',
            freq_quarterly: 'Quarterly',
            freq_annually: 'Annually',
            status_deprecated: 'Deprecated',
        },
        deliverables: {
            title: 'Deliverables',
            new: 'New Deliverable',
            partner: 'Partner',
            deliverable_type: 'Deliverable Type',
            due_date: 'Due Date',
            no_deliverables: 'No deliverables found.',
            assigned_to: 'Assigned To',
            completion_date: 'Completion Date',
            update_status: 'Update Status',
            hide_details: 'Hide Details',
            view_details: 'View Details',
            evidence_ids: 'Evidence IDs',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Client Approval',
            bulk_update: 'Bulk Update',
            bulk_update_status: 'Bulk Update Status',
            generate_schedule: 'Generate Schedule',
            generate_deliverable_schedule: 'Generate Deliverable Schedule',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organization ID',
            service_tier_id: 'Service Tier ID',
            filter_assigned_to: 'Filter by Assigned To',
            select_status: 'Select status...',
            new_status: 'New Status',
            upcoming: 'Upcoming',
            overdue: 'Overdue',
            status_deferred: 'Deferred',
            status_overdue: 'Overdue',
            status_upcoming: 'Upcoming',
        },
        brand_profiles: {
            title: 'Brand Profiles',
            new: 'New Brand Profile',
            partner: 'Partner',
            brand_name: 'Brand Name',
            primary_color: 'Primary Color',
            logo_url: 'Logo URL',
            no_profiles: 'No brand profiles found.',
            secondary_color: 'Secondary Color',
            header_text: 'Header Text',
            footer_text: 'Footer Text',
            contact_email: 'Contact Email',
            contact_phone: 'Contact Phone',
            contact_website: 'Contact Website',
            contact_address: 'Contact Address',
            is_default: 'Is Default',
            set_default: 'Set Default',
            preview: 'Preview',
            report_header_preview: 'Report Header Preview',
            report_footer_preview: 'Report Footer Preview',
            preview_description: 'This is a preview of the report header with the selected brand colors.',
            contact_website_short: 'Website',
            logo_preview: 'Logo preview',
        },
        support_tickets: {
            title: 'Support Tickets',
            new: 'New Ticket',
            partner: 'Partner',
            subject: 'Subject',
            assigned_to: 'Assigned To',
            no_tickets: 'No support tickets found.',
            category: 'Category',
            priority: 'Priority',
            escalation_tier: 'Escalation Tier',
            sla_status: 'SLA Status',
            update_status: 'Update Status',
            escalate: 'Escalate',
            add_note: 'Add Note',
            escalate_ticket: 'Escalate Ticket',
            escalate_to_tier: 'Escalate To Tier',
            notes_timeline: 'Notes Timeline',
            no_notes: 'No notes yet.',
            attachments: 'Attachments',
            no_attachments: 'No attachments.',
            client_org_id: 'Client Org ID',
            content: 'Content',
            note_type: 'Type',
            view_detail: 'View Detail',
            hide: 'Hide',
            new_status: 'New Status',
            all_categories: 'All Categories',
            all_priorities: 'All Priorities',
            all_tiers: 'All Tiers',
            ticket_not_found: 'Ticket not found',
            ticket_id: 'Ticket ID',
            client_org: 'Client Org',
            deescalate: 'De-escalate',
            deescalate_ticket: 'De-escalate Ticket',
            deescalate_to_tier: 'De-escalate To Tier',
            add_attachment: 'Add Attachment',
            file: 'File',
            linked_entity: 'Linked Entity',
            sla_target: 'Target',
            breached: 'Breached',
            remaining_hours: 'remaining',
            na: 'N/A',
            tickets_found: 'tickets found',
            reason: 'Reason',
            target: 'Target',
            upload: 'Upload',
        },
        workflow_templates: {
            title: 'Workflow Templates',
            new: 'New Template',
            template_name: 'Template Name',
            steps: 'Steps',
            no_templates: 'No workflow templates found.',
            category: 'Category',
            view_steps: 'View Steps',
            hide_steps: 'Hide Steps',
            step: 'Step',
            step_title: 'Title',
            step_description: 'Description',
            assignee_role: 'Assignee Role',
            sla_hours: 'SLA Hours',
            evidence_required: 'Evidence Required',
            guidance_notes: 'Guidance Notes',
            add_step: 'Add Step',
            no_steps: 'No steps defined.',
            no_steps_defined: 'No steps defined.',
            templates_found: 'templates found',
            all_categories: 'All Categories',
        },
        workflows: {
            client_org: 'Client Org',
            title: 'Workflow Instances',
            new: 'New Workflow',
            template: 'Template',
            partner: 'Partner',
            current_step: 'Current Step',
            progress: 'Progress',
            no_workflows: 'No workflow instances found.',
            instances_found: 'instances found',
        },
        escalation: {
            title: 'Escalation Dashboard',
            ticket: 'Ticket',
            partner: 'Partner',
            escalated_at: 'Escalated At',
            reason: 'Reason',
            no_escalations: 'No escalations found.',
            time_in_tier: 'Time in Tier',
            auto_evaluate: 'Auto Evaluate',
            priority_critical: 'Critical',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            deescalated_from_dashboard: 'De-escalated from dashboard',
            sla_breached: 'SLA Breached',
            sla_remaining: 'SLA Remaining: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Client Org',
            title: 'Reports',
            partner: 'Partner',
            report_type: 'Report Type',
            generated_at: 'Generated At',
            no_reports: 'No reports found.',
            generate: 'Generate Report',
        },
        audit: {
            title: 'Partner Audit Trail',
            event_type: 'Event Type',
            entity_type: 'Entity Type',
            entity: 'Entity',
            entity_id: 'Entity ID',
            actor: 'Actor',
            timestamp: 'Timestamp',
            no_events: 'No audit events found.',
            events_found: 'events found',
            client_org: 'Client Org',
            all_actors: 'All actors',
            event_type_search: 'Search event type',
            event_type_created: 'Created',
            event_type_updated: 'Updated',
            event_type_deleted: 'Deleted',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Partner Org',
            entity_type_service_tier: 'Service Tier',
            entity_type_deliverable_template: 'Deliverable Template',
            entity_type_deliverable_instance: 'Deliverable Instance',
            entity_type_brand_profile: 'Brand Profile',
            entity_type_support_ticket: 'Support Ticket',
            entity_type_workflow_template: 'Workflow Template',
            entity_type_workflow_instance: 'Workflow Instance',
            entity_type_generated_report: 'Generated Report',
            entity_type_audit_export: 'Audit Export',
        },
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access the partner workspace.',
        // Enum labels
        status_active: 'Active',
        status_suspended: 'Suspended',
        status_offboarded: 'Offboarded',
        status_completed: 'Completed',
        status_cancelled: 'Cancelled',
        status_deprecated: 'Deprecated',
        status_new: 'New',
        status_triaged: 'Triaged',
        status_in_progress: 'In Progress',
        status_waiting_on_client: 'Waiting on Client',
        status_waiting_on_vendor: 'Waiting on Vendor',
        status_escalated: 'Escalated',
        status_resolved: 'Resolved',
        status_closed: 'Closed',
        status_pending: 'Pending',
        status_skipped: 'Skipped',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Onboarding',
        category_incident_escalation: 'Incident Escalation',
        category_config_change: 'Config Change',
        category_periodic_review: 'Periodic Review',
        category_offboarding: 'Offboarding',
        category_custom: 'Custom',
        category_configuration: 'Configuration',
        category_incident: 'Incident',
        category_compliance_question: 'Compliance Question',
        category_feature_request: 'Feature Request',
        category_bug_report: 'Bug Report',
        priority_critical: 'Critical',
        priority_high: 'High',
        priority_medium: 'Medium',
        priority_low: 'Low',
        tier_silver: 'Silver',
        tier_gold: 'Gold',
        tier_platinum: 'Platinum',
        report_type_compliance_summary: 'Compliance Summary',
        report_type_risk_overview: 'Risk Overview',
        report_type_incident_report: 'Incident Report',
        report_type_consent_status: 'Consent Status',
        report_type_program_maturity: 'Program Maturity',
        report_type_executive_pack: 'Executive Pack',
        note_type_internal: 'Internal',
        note_type_client_visible: 'Client Visible',
        // Announce success messages
        status_updated: 'Status updated successfully',
        deliverable_status_updated: 'Deliverable status updated successfully',
        ticket_status_updated: 'Ticket status updated successfully',
        ticket_escalated: 'Ticket escalated successfully',
        note_added: 'Note added successfully',
        attachment_uploaded: 'Attachment uploaded successfully',
        ticket_deescalated: 'Ticket de-escalated successfully',
        auto_evaluation_completed: 'Auto-evaluation completed',
        template_created: 'Workflow template created successfully',
        template_updated: 'Workflow template updated successfully',
        brand_set_default: 'Brand profile set as default successfully',
        brand_created: 'Brand profile created successfully',
        brand_updated: 'Brand profile updated successfully',
        tier_created: 'Service tier created successfully',
        tier_updated: 'Service tier updated successfully',
        tier_deprecated: 'Service tier deprecated successfully',
        tier_activated: 'Service tier activated successfully',
        // Reports page labels
        period: 'Period',
        format: 'Format',
        period_start: 'Period Start',
        period_end: 'Period End',
        reports_count: '{count} reports',
        reports_count_one: '{count} report',
        // Misc labels
        metric: 'Metric',
        value: 'Value',
        showing_chart_view: 'Showing chart view',
        showing_table_view: 'Showing data table view',
        evidence_ids_placeholder: 'e.g. ev-001, ev-002',
        confirm_cancel_workflow: 'Are you sure you want to cancel this workflow? This action cannot be undone.',
        export_csv: 'Export CSV',
        // QA sweep — compliance context info boxes (Sprint 32.8.0)
        compliance_qualifications_title: 'Compliance Service Qualifications',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Regulatory Output Alignment',
        regulatory_output_desc: 'Each template type maps to a regulatory output category: Reports produce compliance documentation, Reviews validate control effectiveness, Assessments cover risk evaluations (DPIA, gap analysis), Meetings document governance sessions. Evidence Requirements define the artifacts each deliverable must reference for audit readiness.',
        sla_compliance_title: 'SLA Tracking for Compliance Delivery',
        sla_compliance_desc: 'Response and resolution time SLAs are contractually binding for compliance service delivery. Review Frequency determines how often partner compliance deliverables are evaluated. SLA breaches on compliance-critical tickets trigger automatic escalation and are logged in the Partner Audit Trail for regulatory evidence.',
        regulatory_engagement_title: 'Regulatory Engagement Patterns',
        regulatory_engagement_desc: 'Workflow categories map to regulatory engagement types: Onboarding covers initial compliance setup and gap analysis, Periodic Review handles recurring compliance assessments, Incident Escalation manages regulatory breach response, Offboarding ensures data retention obligations are met. Each step can require evidence collection for audit trail completeness.',
        compliance_escalation_title: 'Compliance-Critical Escalation Procedures',
        compliance_escalation_desc: 'L1 handles standard operational issues. L2 covers compliance-impacting failures (missed regulatory deadlines, control gaps). L3 is reserved for regulatory breach scenarios requiring immediate executive attention. SLA breach on L2/L3 tickets generates audit trail entries automatically. All escalation and de-escalation actions are permanently logged.',
        audit_regulatory_title: 'Regulatory Evidence Trail',
        audit_regulatory_desc: 'This audit trail provides tamper-evident records for regulatory compliance. Every partner action (create, update, delete, escalation, status change) is logged with actor identity, timestamp, and full payload diff. Use Entity Type filters to isolate evidence by compliance domain. Export filtered results to produce audit-ready documentation for regulatory inspections.',
        evidence_trail_title: 'Cross-Module Evidence Trail',
        evidence_trail_desc: 'Each workflow step can collect Evidence IDs linking to deliverables, audit events, and compliance artifacts across modules. Advancing a step with evidence IDs creates a verifiable chain connecting partner workflows to regulatory documentation. Completed workflows with full evidence chains serve as proof of compliance engagement delivery.',
        evidence_ids_label: 'Evidence IDs',
    },
    // ---------- Org Groups (Sprint 11.5.0) ----------
    org_groups: {
        title: 'Org Groups',
        new: 'Create Group',
        group_name: 'Group Name',
        member_count: 'Member Count',
        root_org: 'Root Org',
        no_groups: 'No org groups found.',
        add_organization: 'Add Organization',
        select_organization: 'Select an organization...',
        no_available_orgs: 'No available organizations to add.',
        no_members: 'No member organizations in this group.',
        member_added: 'Organization added to group.',
        member_removed: 'Organization removed from group.',
        already_in_group: 'This organization is already in a group.',
        org_not_found: 'Organization not found.',
        add_member_error: 'Failed to add organization to group.',
        remove_member_error: 'Failed to remove organization from group.',
        confirm_remove_member: 'Are you sure you want to remove "{name}" from this group?',
        load_error: 'Failed to load org group details.',
        // Page titles for usePageTitle
        page_title_list: 'Organization Groups',
        page_title_detail: 'Organization Group Details',
        page_title_create: 'Create Organization Group',
        page_title_settings: 'Organization Group Settings',
        page_title_users: 'Group Users',
        page_title_reports: 'Group Reports',
        page_title_dashboard: 'Group Dashboard',
        page_title_audit: 'Group Audit',
        page_title_org_audit: 'Organization Group Audit',
        page_title_shared_vendors: 'Shared Vendors',
        page_title_shared_frameworks: 'Shared Frameworks',
        page_title_shared_forms: 'Shared Form Templates',
        page_title_merged_vendors: 'Merged Vendors',
        page_title_settings_inheritance: 'Settings Inheritance',
        // Announce messages
        announce_loaded_groups: 'Loaded {count} organization groups',
        announce_loaded_group: 'Loaded group {name}',
        announce_loaded_users: 'Loaded {count} group users',
        announce_loaded_reports: 'Loaded {count} reports',
        announce_loaded_vendors: 'Loaded {count} shared vendors',
        announce_loaded_frameworks: 'Loaded {count} shared frameworks',
        announce_loaded_templates: 'Loaded {count} shared form templates',
        announce_loaded_audit: 'Loaded {count} audit events',
        announce_loaded_merged: 'Loaded {shared} shared and {local} local vendors',
        announce_dashboard_loaded: 'Dashboard loaded.',
        announce_dashboard_refreshed: 'Dashboard refreshed.',
        announce_dashboard_refresh_failed: 'Failed to refresh dashboard.',
        announce_refreshing: 'Refreshing dashboard...',
        announce_settings_loaded: 'Settings inheritance loaded.',
        announce_settings_saved: 'Settings saved successfully.',
        announce_saving_settings: 'Saving settings...',
        announce_error_saving: 'Error saving settings.',
        announce_group_dissolved: 'Group dissolved.',
        announce_creating_group: 'Creating organization group...',
        announce_group_created: 'Organization group created successfully.',
        announce_error_creating: 'Error creating organization group.',
        announce_vendor_published: 'Vendor published successfully.',
        announce_vendor_retired: 'Vendor retired.',
        announce_framework_published: 'Framework published successfully.',
        announce_framework_retired: 'Framework retired.',
        announce_template_published: 'Form template published successfully.',
        announce_template_retired: 'Form template retired.',
        announce_role_assigned: 'Role assigned successfully.',
        announce_error_assign_role: 'Error assigning role.',
        announce_role_revoked: 'Role revoked successfully.',
        announce_report_generating: 'Generating report...',
        announce_report_requested: 'Report generation requested.',
        announce_error_report: 'Error requesting report.',
        announce_report_deleted: 'Report deleted.',
        announce_search_applied: 'Search applied.',
        announce_search_cleared: 'Search cleared.',
        announce_tab_selected: '{tab} tab selected',
        announce_error_add_member: 'Error adding member to group.',
        announce_category_reverted: '{category} reverted to inherited from parent.',
        announce_category_overridden: '{category} set to local override.',
        announce_override_saved: '{category} override saved.',
        announce_export_started: 'Exporting audit as {format}...',
        announce_export_completed: 'Export as {format} completed.',
        announce_export_failed: 'Export failed.',
        announce_filter_org: 'Filtered by organization {name}',
        announce_filter_org_cleared: 'Organization filter cleared',
        announce_filter_event: 'Filtered by event type {type}',
        announce_filter_cleared: 'Filter cleared',
        announce_download: 'Downloading {name} as {format}',
        // Error messages
        error_load_groups: 'Failed to load org groups. Please try again.',
        error_load_users: 'Failed to load group users.',
        error_load_reports: 'Failed to load group reports.',
        error_load_dashboard: 'Failed to load group dashboard.',
        error_load_settings: 'Failed to load group settings.',
        error_load_vendors: 'Failed to load merged vendors.',
        error_save_settings: 'Failed to save settings.',
        error_dissolve_group: 'Failed to dissolve group.',
        error_assign_role: 'Failed to assign role.',
        error_create_group: 'Failed to create org group. Please try again.',
        error_request_report: 'Failed to request report.',
        error_group_name_required: 'Group name is required.',
        // Success messages
        success_settings_saved: 'Settings saved successfully.',
        // Confirm dialogs
        confirm_retire_vendor: 'Are you sure you want to retire vendor "{name}"?',
        confirm_retire_framework: 'Are you sure you want to retire framework "{name}"?',
        confirm_retire_template: 'Are you sure you want to retire form template "{name}"?',
        confirm_dissolve_group: 'Are you sure you want to dissolve "{name}"? This action cannot be undone.',
        confirm_revoke_role: 'Are you sure you want to revoke the "{role}" role from "{name}"?',
        confirm_delete_report: 'Are you sure you want to delete the report "{name}"?',
        // Dissolve
        dissolve_group: 'Dissolve Group',
        dissolve_warning: 'This will permanently dissolve the organization group. All member organizations will be removed.',
        // Pluralization
        count_groups: '{count} group',
        count_groups_plural: '{count} groups',
        count_users: '{count} user',
        count_users_plural: '{count} users with group roles',
        count_events: '{count} event',
        count_events_plural: '{count} events',
        count_reports: '{count} report',
        count_reports_plural: '{count} reports',
        count_vendors: '{count} vendor shared across the group',
        count_vendors_plural: '{count} vendors shared across the group',
        count_frameworks: '{count} framework shared across the group',
        count_frameworks_plural: '{count} frameworks shared across the group',
        count_templates: '{count} template shared across the group',
        count_templates_plural: '{count} templates shared across the group',
        count_shared_vendors: '{count} shared vendors',
        count_local_vendors: '{count} local vendors',
        merged_combined_view: 'Combined view of {shared} shared and {local} local vendors',
        // Dashboard labels
        dashboard: {
            title: 'Group Dashboard',
            loading: 'Loading dashboard data...',
            total_groups: 'Total Groups',
            total_members: 'Total Members',
            active_groups: 'Active Groups',
            overall_compliance_score: 'Overall Compliance Score',
            group_risk_level: 'Group Risk Level',
            sla_compliance: 'SLA Compliance',
            compliance_posture: 'Compliance Posture',
            risk_heatmap: 'Risk Heatmap',
            sla_performance: 'SLA Performance',
            recent_incidents: 'Recent Incidents',
            recent_activity: 'Recent Activity',
            refresh_label: 'Refresh dashboard data',
            // Score labels
            score_good: 'Good',
            score_needs_attention: 'Needs attention',
            score_critical: 'Critical',
            // Trend labels
            trend_up: 'Trending up',
            trend_down: 'Trending down',
            trend_stable: 'Stable',
            // Risk level labels
            risk_low: 'Low',
            risk_medium: 'Medium',
            risk_high: 'High',
            risk_critical: 'Critical',
            risk_unknown: 'Unknown',
            // Posture table headers
            col_organization: 'Organization',
            col_score: 'Score',
            col_trend: 'Trend',
            col_open_issues: 'Open Issues',
            col_critical_issues: 'Critical Issues',
            col_health: 'Health',
            // Risk heatmap headers
            col_vendor_risk: 'Vendor Risk',
            col_data_risk: 'Data Risk',
            col_compliance_risk: 'Compliance Risk',
            // SLA headers
            col_total_cases: 'Total Cases',
            col_on_time: 'On Time',
            col_breached: 'Breached',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Avg Days',
            // Screen reader
            sr_vendor_risk: 'Vendor risk score: {score}',
            sr_data_risk: 'Data risk score: {score}',
            sr_compliance_risk: 'Compliance risk score: {score}',
            sr_sla_breaches: 'Warning: {count} SLA breaches',
            sr_severity: 'Severity: {severity}',
        },
        detail: {
            title: 'Group Detail',
            members: 'Members',
            settings: 'Settings',
            audit: 'Audit',
            sr_loading: 'Loading organization group details',
            sr_loading_hierarchy: 'Loading hierarchy',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'member organization',
            root_org_label: 'root organization',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Group Users',
            add_user: 'Add User',
            no_users: 'No users in this group.',
            role_group_admin: 'Group Admin',
            role_group_reader: 'Group Reader',
            sr_loading: 'Loading group users',
        },
        reports: {
            title: 'Group Reports',
            no_reports: 'No reports found.',
            generate_report: 'Generate new report',
            report_type_compliance: 'Compliance Summary',
            report_type_risk: 'Risk Overview',
            report_type_sla: 'SLA Performance',
            report_type_incident: 'Incident Report',
            report_type_executive: 'Executive Brief',
            report_type_board: 'Board Report',
            status_queued: 'Queued',
            status_generating: 'Generating',
            status_completed: 'Completed',
            status_failed: 'Failed',
            period_start: 'Period Start',
            period_end: 'Period End',
            sr_loading: 'Loading group reports',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Shared Vendors',
            no_vendors: 'No shared vendors found.',
            merged_vendors: 'Merged Vendors',
            publish_form_label: 'Publish shared vendor',
            sr_loading: 'Loading shared vendors',
            sr_loading_merged: 'Loading merged vendors',
            local_vendors: 'Local Vendors',
            source_shared: 'Shared from group',
            source_local: 'Local vendor',
            // Category labels
            category_technology: 'Technology',
            category_consulting: 'Consulting',
            category_outsourcing: 'Outsourcing',
            category_cloud: 'Cloud',
            category_data_processing: 'Data Processing',
            category_other: 'Other',
            // Risk tier labels
            risk_tier_low: 'Low',
            risk_tier_medium: 'Medium',
            risk_tier_high: 'High',
            risk_tier_critical: 'Critical',
            // Compliance status labels
            compliance_compliant: 'Compliant',
            compliance_non_compliant: 'Non-Compliant',
            compliance_pending: 'Pending',
            compliance_under_review: 'Under Review',
            // Status labels
            status_active: 'Active',
            status_retired: 'Retired',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Framework Code',
            title: 'Shared Frameworks',
            no_frameworks: 'No shared frameworks found.',
            publish_form_label: 'Publish shared framework',
            sr_loading: 'Loading shared frameworks',
            status_active: 'Active',
            status_retired: 'Retired',
        },
        shared_forms: {
            title: 'Shared Form Templates',
            no_forms: 'No shared form templates found.',
            publish_form_label: 'Publish shared form template',
            sr_loading: 'Loading shared form templates',
            status_active: 'Active',
            status_retired: 'Retired',
        },
        audit: {
            title: 'Group Audit',
            consolidated_title: 'Consolidated Audit Trail',
            consolidated_subtitle: 'Cross-organization audit events for this group',
            audit_log_title: 'Group Audit Log',
            event_type: 'Event Type',
            actor: 'Actor',
            timestamp: 'Timestamp',
            source_org: 'Source Org',
            org: 'Org',
            summary: 'Summary',
            details: 'Details',
            no_events: 'No audit events found.',
            all_events: 'All Events',
            all_orgs: 'All Orgs',
            organization: 'Organization',
            from: 'From',
            to: 'To',
            search_events: 'Search events...',
            clear: 'Clear',
            events_24h: 'Events (24h)',
            events_7d: 'Events (7d)',
            active_orgs: 'Active Orgs',
            exporting: 'Exporting...',
            export_csv: 'Export CSV',
            export_json: 'Export JSON',
            back_to_group: 'Back to Group',
            back_to_detail: 'Back to Detail',
            sr_loading: 'Loading audit events',
            sr_loading_summary: 'Loading audit summary',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Group Settings',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Settings Inheritance',
            inheritance_subtitle: 'Configure which settings categories inherit from the group vs. use local overrides',
            sla_settings: 'SLA Settings',
            calendar_settings: 'Calendar Settings',
            notification_settings: 'Notification Settings',
            intake_settings: 'Intake Settings',
            cmp_integration: 'CMP Integration',
            overridden: 'Overridden',
            inheriting: 'Inheriting',
            hide_defaults: 'Hide Defaults',
            show_group_defaults: 'Show Group Defaults',
            group_defaults: 'Group Defaults',
            inherit_from_group: 'Inherit from Group',
            use_local_override: 'Use Local Override',
            local_override_json: 'Local Override (JSON)',
            save_override: 'Save Override',
            saving: 'Saving...',
            sr_loading: 'Loading settings inheritance',
            sr_loading_settings: 'Loading group settings',
            sr_enabled: 'Enabled',
            sr_disabled: 'Disabled',
            sr_override_active: 'Local override active',
            sr_inherited: 'Inherited from parent',
            danger_zone: 'Danger Zone',
            sharing_options: 'Sharing Options',
            advanced_options: 'Advanced Options',
            // fix-sprint-20260717-2118-followup F3 — single-string section heading
            // that replaces `${t.org_groups.settings.title} configuration` on
            // OrgGroupCreatePage (mixed-language render in every non-EN locale).
            configuration_heading: 'Group settings configuration',
            max_member_orgs: 'Maximum member organizations',
        },
        // Status badge labels
        status_active: 'Active',
        status_dissolved: 'Dissolved',
        status_pending: 'Pending',
        status_suspended: 'Suspended',
    },
    // ---------- Integrations: Webhooks, Domains, Exports (Sprint 3.6.0) ----------
    integrations: {
        nav: {
            webhooks: 'Webhooks',
            domains: 'Domains',
            export_schedules: 'Export Schedules',
            email_log: 'Email Log',
        },
    },
    // ---------- Platform: Users, Roles, Profile (Sprint 3.0.0) ----------
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Users',
            roles: 'Roles',
            profile: 'My Profile',
        },
        security: {
            mfa: 'MFA Settings',
            sso: 'SSO Configuration',
            api_keys: 'API Keys',
            service_accounts: 'Service Accounts',
            ip_allowlist: 'IP Allowlist',
            delegations: 'Delegations',
            break_glass: 'Break-Glass Access',
            session_settings: 'Session Settings',
            active_sessions: 'Active Sessions',
            access_reviews: 'Access Reviews',
            my_reviews: 'My Reviews',
            role_analytics: 'Role Analytics',
            attestation_schedules: 'Attestation Schedules',
            sod_rules: 'SoD Rules',
            sod_violations: 'SoD Violations',
        },
        module_config: {
            title: 'Module Configuration',
            subtitle: 'Enable or disable feature modules for the whole installation: the setting applies to every organisation on it. Disabled modules are hidden from the navigation.',
            enabled: 'Enabled',
            disabled: 'Disabled',
            save: 'Save Configuration',
            saving: 'Saving...',
            saved: 'Configuration saved successfully',
            nav_label: 'Module Configuration',
            settings: 'Settings',
            webhooks: 'Webhooks',
            domains: 'Domains',
            export_schedules: 'Export Schedules',
            email_log: 'Email Log',
            announce_loaded: 'Module configuration loaded',
        },
        // Sprint 41.4.0 F14 — Platform → Files Configuration & Browser
        // EN translations. The 14 other locales are deferred to Sprint 51.0.0
        // per the kickoff scope amendment; only en.ts and vi.ts contain
        // these keys in this sprint.
        files_nav: {
            section: 'Files',
            configuration: 'Configuration',
            browser: 'File Browser',
            dashboard: 'Files Dashboard',
        },
        // Immutable Audit Trails — shared shell strings for the four sub-pages
        // (Settings, Proof Status, Integrity Center, Usage & Credits). EN + VI
        // are authored here; the other 14 locales fall back to EN via the
        // deepMerge in i18n/index.ts.
        immutable_audit_trails: {
            nav_group_label: 'Audit & Proof',
            nav_settings: 'Settings',
            nav_status: 'Proof Status',
            nav_integrity_center: 'Integrity Center',
            nav_usage: 'Usage & Credits',
            settings_title: 'Immutable Audit Trails — Settings',
            settings_subtitle: 'Per-org settings for the Immutable Audit Trails add-on.',
            settings_banner: 'The Immutable Audit Trails add-on is not enabled for your organization. Enable it here to start anchoring proof for Tier-A and Tier-B audit events.',
            status_title: 'Immutable Audit Trails — Proof Status',
            status_subtitle: 'Per-event proof relationships across modules.',
            status_banner: 'The Immutable Audit Trails add-on is not enabled for your organization. Visit Settings to enable it.',
            integrity_center_title: 'Immutable Audit Trails — Integrity Center',
            integrity_center_subtitle: 'Verify proofs and export Integrity Packs.',
            integrity_center_banner: 'The Immutable Audit Trails add-on is not enabled for your organization. Visit Settings to enable it before running verification or exporting Integrity Packs.',
            usage_title: 'Immutable Audit Trails — Usage & Credits',
            usage_subtitle: 'Credit balance, package purchases, and usage history.',
            usage_banner: 'Enable Immutable Audit Trails in Settings to start consuming proof credits.',
            module_tips_placeholder: 'Tips and guidance for this surface are available in the help center.',
            column_label: 'Proof',
            no_proof_aria: 'No proof recorded',
            proof_status_receipt_label: 'receipt {id}',
            in_progress_hint: 'In progress — updates automatically',
            status_pills: {
                pending: 'Pending',
                submitted: 'Submitted',
                anchored: 'Anchored',
                confirmed: 'Confirmed',
                retrying: 'Retrying',
                paused_cap: 'Paused (cap)',
                failed: 'Failed',
                failed_auth: 'Auth failed',
            },
            status_tooltips: {
                pending: 'Pending — queued for the proof service. Will move to Submitted shortly; no action needed.',
                submitted: 'Submitted — your event was sent to the proof service. For batch mode it is waiting to be sealed and anchored on-chain. Updates automatically; no action needed.',
                anchored: 'Anchored — the batch has been written on-chain and is awaiting final confirmation. Updates automatically; no action needed.',
                confirmed: 'Confirmed — the proof is anchored on-chain and verifiable.',
                retrying: 'Retrying — a transient error occurred. The reconciler is retrying automatically; no action needed.',
                paused_cap: 'Paused — your monthly proof-credit cap has been reached. Redeem more credits or wait until next billing month.',
                failed: 'Failed — anchoring did not succeed. It is not retried automatically; contact support.',
                failed_auth: 'Auth failed — the proof service rejected our credentials. Check the Immutable Audit Trails Settings page.',
            },
            mode_pills: {
                direct: 'Direct',
                batch: 'Batch',
            },
            drawer: {
                title: 'Proof details',
                close: 'Close',
                section_proof: 'Proof',
                section_batch: 'Batch membership',
                status: 'Status',
                proof_reference: 'Proof reference',
                receipt_id: 'Receipt ID',
                taxonomy_class: 'Taxonomy class',
                units_consumed: 'Units consumed',
                created_at: 'Created',
                verification_ref: 'Chain verification reference',
                chain: 'Chain',
                tx_hash: 'Tx hash',
                block_ref: 'Block ref',
                batch_reference: 'Batch reference',
                leaf_index: 'Leaf index',
                local_event_hash: 'Local event hash',
                show_inclusion: 'Show inclusion path',
                hide_inclusion: 'Hide inclusion path',
                privacy_note: 'Payload is hashed; no personal data left ComplianceOne for this event.',
                verify_now: 'Verify now',
                verify_pending: 'Verification in progress…',
                verify_verified: 'Verified — local + chain-side checks passed.',
                verify_issue_prefix: 'Verification issue: ',
                verify_failed: 'Verification call failed.',
                verify_result_prefix: 'Verification verdict: ',
                verify_pending_note: 'Verification pending: this proof is still awaiting on-chain anchoring. Try again after the batch is sealed.',
                awaiting_anchor_note: 'This proof is part of an open batch. Verification becomes available once the batch is sealed and anchored on-chain — typically within the org’s configured batch window. The status badge will update automatically.',
                terminal_error_note: 'This proof failed to anchor and is not retried automatically; manual verification has nothing to check yet.',
            },
            integrity_center: {
                section_run: 'Run verification',
                section_progress: 'Run progress',
                section_issues: 'Issues',
                period_start: 'Period start',
                period_end: 'Period end',
                module_filter: 'Modules (comma-separated, optional)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Run verification',
                export_button: 'Export Integrity Pack',
                export_success: 'Integrity Pack exported.',
                export_failed: 'Export failed.',
                empty_disabled: 'Enable Immutable Audit Trails in Settings to run verification passes.',
                error_period_required: 'Pick a start and end date.',
                error_start_run: 'Failed to start run.',
                processed: 'Processed: {processed} of {total}',
                col_verdict: 'Verdict',
                col_count: 'Count',
                col_proof_link: 'Proof link',
                col_module: 'Module',
                col_event_type: 'Event type',
                col_expected: 'Expected hash',
                col_recomputed: 'Recomputed hash',
                col_raised_at: 'Raised at',
                any_verdict: 'Any verdict',
                no_issues: 'No issues found.',
            },
            settings_page: {
                load_error: 'Failed to load settings.',
                save: 'Save',
                save_success: 'Settings saved.',
                save_error: 'Save failed.',
                discard: 'Discard changes',
                read_only_banner: 'You have read-only access. Contact a Platform Admin to make changes.',
                section_status: 'Status & enablement',
                enabled_label: 'Add-on enabled',
                enabled_help: 'When enabled, every Tier-A and Tier-B audit event is hashed and anchored.',
                section_mode: 'Default proof mode',
                mode_direct: 'Direct (one anchor per event)',
                mode_batch: 'Batch (Merkle-root anchor per group)',
                mode_hybrid: 'Hybrid (per-class registry default)',
                batch_window_label: 'Batch window (minutes, 1–60)',
                section_taxonomy: 'Protected event taxonomy',
                taxonomy_help: 'Per-event-class overrides. Tier A → direct mode, Tier B → batch mode, Off → not proofed.',
                taxonomy_search: 'Search overrides',
                taxonomy_reset: 'Reset to defaults',
                taxonomy_event_class: 'Event class',
                taxonomy_override: 'Override',
                taxonomy_actions: 'Actions',
                taxonomy_clear: 'Clear',
                taxonomy_no_overrides: 'No overrides — using registry defaults.',
                taxonomy_add_class: 'event_class.code',
                taxonomy_add: 'Add override',
                section_caps: 'Caps & limits',
                cap_label: 'Monthly cap (units; blank = unlimited)',
                warn_label: 'Warn threshold (units; must be less than cap)',
                auto_pause_label: 'Auto-pause new proofs once cap is reached',
                section_credentials: 'Credentials',
                credential_id: 'Credential ID',
                credential_rotated_at: 'Last rotated',
                rotate_button: 'Rotate credential',
                rotate_success: 'Credential rotated. Old credential valid for {n} more minutes.',
                rotate_failed: 'Credential rotation failed.',
                rotate_modal_title: 'Rotate AesirX credential?',
                rotate_modal_body: 'Rotation issues a new credential. The old credential remains accepted by AesirX for a 60-minute grace window so in-flight workers can finish their current jobs. After 60 minutes the old credential is revoked.',
                rotate_modal_confirm: 'Rotate now',
                tier_a_off_modal_title: 'Disable proofing for an event class?',
                tier_a_off_modal_body: 'Setting an event class to "Off" stops the proof tap from anchoring those events. Architecture §6 explains the regulatory implication: Tier-A events are regulator-relevant; turning them off may break your audit trail defensibility.',
                tier_a_off_modal_confirm: 'I understand — apply override',
                error_warn_lt_cap: 'Warn threshold must be less than monthly cap.',
                error_window_range: 'Batch window must be between 1 and 60 minutes.',
                error_run_in_progress: 'A verification run is in progress; cannot disable the add-on right now.',
            },
            status_page: {
                filter_status: 'Status',
                filter_mode: 'Proof mode',
                filter_module: 'Module',
                filter_source_collection: 'Source collection',
                filter_from_date: 'From',
                filter_to_date: 'To',
                filter_search: 'Search',
                any_status: 'Any status',
                any_mode: 'Any mode',
                col_module: 'Module',
                col_event_type: 'Event type',
                col_proof_status: 'Status',
                col_proof_mode: 'Mode',
                col_created_at: 'Created',
                col_units: 'Units',
                empty_disabled: 'The Immutable Audit Trails add-on is not enabled for your organization. Visit Settings to enable it.',
                empty_enabled_no_rows: 'No proof links match the current filters.',
                export_csv: 'Export CSV',
                export_xlsx: 'Export XLSX',
                export_success: 'Export complete',
                export_failed: 'Export failed',
            },
            usage_page: {
                section_alerts: 'Alerts',
                alert_dismiss: 'Dismiss',
                alert_dismiss_aria: 'Dismiss alert {id}',
                alert_dismissed: 'Alert dismissed',
                alert_dismiss_failed: 'Failed to dismiss alert',
                section_balance: 'Credit balance',
                balance_label: 'Available credits',
                balance_hint: 'Units remaining (1 unit = 1 protected event proof).',
                section_current_month: 'Current month',
                consumed_label: 'Consumed',
                granted_label: 'Granted',
                refunded_label: 'Refunded',
                net_label: 'Net',
                section_history: 'Usage history',
                history_kind: 'Kind',
                history_units: 'Units',
                history_balance_after: 'Balance after',
                history_notes: 'Notes',
                history_at: 'When',
                cap_warning_banner: 'You have crossed your warn threshold for this billing month. Consider redeeming additional credits.',
                cap_paused_banner: 'Monthly cap reached. Proof submissions are paused until next month or until cap is increased.',
                section_redeem: 'Redeem credit package',
                redeem_purchase_id: 'Invoice Number',
                redeem_package: 'Package',
                redeem_button: 'Redeem',
                redeem_pending: 'Redeeming…',
                redeem_success: 'Credits redeemed',
                redeem_failed: 'Redeem failed',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'units',
                alert_dismissing: 'Dismissing…',
                // fix-sprint-20260515-2129 Findings 16 + 17 — localized alert
                // kind + usage kind labels so end-users see human-readable
                // phrasing instead of snake_case enum identifiers.
                alert_kind: {
                    cap_reached: 'Monthly cap reached',
                    low_balance: 'Low balance',
                    auth_failure: 'Authentication failure',
                    merkle_mismatch: 'Merkle mismatch',
                    batch_terminal_failure: 'Batch terminal failure',
                    direct_terminal_failure: 'Direct submission terminal failure',
                },
                usage_kind: {
                    consume: 'Consume',
                    refund: 'Refund',
                    grant: 'Grant',
                    adjustment: 'Adjustment',
                    reservation: 'Reservation',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Starter',
                package_growth: 'Growth',
                package_enterprise: 'Enterprise',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Buy more credits',
                    purchase_title: 'Buy more credits',
                    purchase_subtitle: 'Choose a package. You will be taken to secure Stripe checkout, and your invoice will arrive by email.',
                    checkout_button: 'Continue to checkout',
                    checkout_pending: 'Opening checkout…',
                    cancel_button: 'Cancel',
                    purchase_error: 'Could not start checkout. Please try again shortly.',
                    redeem_subtitle: 'Already paid? Select the package you purchased and enter the invoice ID from your email to add the credits.',
                    error_not_found: 'No invoice was found with that number. Check the invoice ID and try again.',
                    error_not_paid: 'This invoice is not marked paid yet.',
                    error_org_mismatch: 'This invoice was not issued to your organisation.',
                    error_package_mismatch: 'This invoice does not match the selected package.',
                    error_billing_unavailable: 'Could not reach the billing service. Please try again shortly.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Files Configuration',
            subtitle: 'Configure file upload limits, retention policies, and per-owner-type overrides for the platform attachment surface.',
            section_global: 'Global defaults',
            section_global_help: 'These limits apply to every file uploaded by users in your organization unless a per-owner-type override below changes them.',
            section_overrides: 'Per-owner-type overrides',
            section_overrides_help: 'Override the global limits for a specific owner type. Per-owner-type values always win over the global setting.',
            max_file_size_label: 'Max file size',
            max_file_size_hint: 'Per individual upload (MiB)',
            max_total_attachments_label: 'Max total attachments',
            max_total_attachments_hint: 'Across the entire organization',
            max_total_size_label: 'Max total storage',
            max_total_size_hint: 'Across the entire organization (MiB)',
            allowed_mime_types_label: 'Allowed file types',
            allowed_mime_types_placeholder: 'Select MIME types',
            retention_days_label: 'Retention period (days)',
            retention_days_hint: 'How long files must be kept before they are eligible for deletion. Leave blank for no retention policy.',
            legal_hold_label: 'Legal hold',
            legal_hold_hint: 'When enabled, hard-deleting a file of this owner type requires the legal hold override permission and explicit operator opt-in.',
            save: 'Save changes',
            saving: 'Saving…',
            reset: 'Reset',
            save_success: 'Settings saved',
            /*
              Gate E12.5 pass 10, P3-8. This ended "Reload to see the latest values." —
              an imperative naming a control, delivered ONLY through `toast.error` at
              `pages/admin/platform/FilesConfigPage.tsx:184,235,285`. A toast has no
              control beside it, so the sentence named an action nothing performed, which
              is the defect this sprint exists to close.
      
              It gets no control, for the reason the two `manufacturing` session
              fallbacks get none: the operator is mid-edit, and a reload here replaces
              their unsaved entries with someone else's values.
      
              Gate E12.5 pass 11, P2-2 — the previous wording was wrong twice. It said
              "this SAVE was refused" and "your ENTRIES are still on screen", and the third
              of its three call sites is `handleRemoveOverride`, a DELETE that reads no
              draft and can be pressed with nothing typed: both clauses false there. One
              sentence, three sites, one wrong — which is pass 10's P2-5 reproduced by the
              commit that was fixing it.
      
              Worse, declining to recommend a reload closed the only exit. `state.version`
              moved only on a successful write, so after one conflict every save and remove
              failed identically until the page was reloaded — and the sentence described
              reloading purely as a cost. The fix is mechanical, not verbal:
              `refreshSavedVersion` re-reads the saved settings on a conflict without
              touching the drafts, so a second attempt can succeed. "This change" now covers
              the save and the remove, and the clause the operator acts on is only claimed
              when the re-read actually happened.
            */
            save_version_conflict: 'Another operator updated these settings while you were editing, so this change was refused. Their saved values have been re-read and anything you typed is untouched — apply your change again to write it over theirs.',
            /*
              The same conflict when the re-read ALSO failed, so the version has not moved
              and a second attempt would be refused for the same reason. Separate key rather
              than one sentence covering both, because "have been re-read" is the clause the
              operator acts on and it must not be claimed when it did not happen.
            */
            save_version_conflict_unreadable: 'Another operator updated these settings while you were editing, so this change was refused, and their saved values could not be re-read either. Anything you typed is untouched; reloading the page in your browser will show their values and discard yours.',
            empty_state_title: 'Using platform defaults',
            empty_state_body: 'Your organization is currently running on the platform default file policy. Edit any value below to create an org-level override.',
            read_only_banner: 'You have read-only access to file configuration. Contact a Platform Admin to make changes.',
            owner_type_default_badge: 'Default',
            owner_type_overridden_badge: 'Overridden',
            owner_type_label: 'Owner type',
            owner_type_collapse: 'Collapse',
            owner_type_expand: 'Expand',
            override_save: 'Save override',
            override_remove: 'Remove override',
            mime_labels: {
                'application/pdf': 'PDF document',
                'image/png': 'PNG image',
                'image/jpeg': 'JPEG image',
                'image/jpg': 'JPG image',
                'application/msword': 'Word document (legacy)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word document',
                'application/vnd.ms-excel': 'Excel spreadsheet (legacy)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel spreadsheet',
                'text/csv': 'CSV file',
                'text/plain': 'Plain text',
                'message/rfc822': 'Email message',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'File Browser',
            subtitle: 'Search, filter, and manage every attachment uploaded across the organization.',
            filter_owner_type: 'Owner type',
            filter_uploader: 'Uploaded by',
            filter_from: 'Uploaded after',
            filter_to: 'Uploaded before',
            filter_size_min: 'Min size (MiB)',
            filter_size_max: 'Max size (MiB)',
            filter_mime: 'File type',
            filter_deleted_state: 'Deletion state',
            filter_deleted_any: 'All files',
            filter_deleted_only: 'Only deleted',
            filter_deleted_exclude: 'Exclude deleted',
            filter_orphaned: 'Orphaned only',
            filter_search_placeholder: 'Search filename…',
            filter_apply: 'Apply filters',
            filter_clear: 'Clear filters',
            col_filename: 'Filename',
            col_owner_type: 'Owner type',
            col_owner_entity: 'Owner',
            col_uploader: 'Uploaded by',
            col_uploaded_at: 'Uploaded',
            col_size: 'Size',
            col_mime: 'Type',
            col_state: 'State',
            col_actions: 'Actions',
            state_active: 'Active',
            state_deleted: 'Deleted',
            state_orphaned: 'Orphaned',
            action_view_detail: 'View details',
            action_download: 'Download',
            // Sprint 47.5.3b — Forseti AI redline review link.
            action_ai_edits: 'AI Edits',
            action_soft_delete: 'Soft delete',
            action_restore: 'Restore',
            action_hard_delete: 'Hard delete',
            bulk_soft_delete: 'Soft-delete selected',
            bulk_export_csv: 'Export CSV',
            bulk_select_all: 'Select all',
            reason_label: 'Reason',
            reason_min_10_chars: 'Reason is required (at least 10 characters)',
            reason_min_20_chars: 'Reason is required (at least 20 characters)',
            legal_hold_override_label: 'Override legal hold',
            legal_hold_override_warning: 'This file is under legal hold. Hard-deleting it will be recorded in the audit trail with your reason and override flag.',
            empty_state_title: 'No files match your filters',
            empty_state_body: 'Adjust your filters to see results, or clear them to view all files.',
            pagination_prev: 'Previous',
            pagination_next: 'Next',
            pagination_page: 'Page {page} of {total}',
            pagination_size_label: 'Per page',
            pagination_total: 'Total: {count}',
            toast_soft_deleted: 'File soft-deleted',
            toast_restored: 'File restored',
            toast_hard_deleted: 'File permanently deleted',
            toast_bulk_deleted: '{count} file(s) deleted',
            toast_error: 'Operation failed',
            confirm_soft_delete_title: 'Soft delete file',
            confirm_restore_title: 'Restore file',
            confirm_hard_delete_title: 'Permanently delete file',
            confirm_bulk_soft_delete_title: 'Soft delete selected files',
            confirm_submit: 'Confirm',
            confirm_cancel: 'Cancel',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'File details',
            close: 'Close',
            meta_filename: 'Filename',
            meta_size: 'Size',
            meta_mime: 'Type',
            meta_owner_type: 'Owner type',
            meta_owner_entity: 'Owner entity',
            meta_uploader: 'Uploaded by',
            meta_uploaded_at: 'Uploaded',
            meta_storage_path: 'Storage path',
            meta_deleted_state: 'Deletion state',
            audit_section_title: 'Audit trail',
            audit_load_more: 'Load more events',
            audit_empty: 'No audit events recorded for this file.',
            audit_config_changed: 'Configuration changed',
            audit_soft_deleted: 'Soft-deleted',
            audit_restored: 'Restored',
            audit_hard_deleted: 'Permanently deleted',
            audit_downloaded: 'Downloaded',
            audit_quota_breached: 'Quota breach attempted',
            audit_owner_type_download_denied: 'Retrieval refused',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} by {actor} on {at}',
            action_download: 'Download',
            action_soft_delete: 'Soft delete',
            action_restore: 'Restore',
            action_hard_delete: 'Hard delete',
            effective_settings_title: 'Effective policy',
            effective_max_file_size: 'Max file size',
            effective_retention_days: 'Retention',
            effective_legal_hold: 'Legal hold',
            effective_legal_hold_yes: 'Active',
            effective_legal_hold_no: 'None',
        },
        files_dashboard: {
            title: 'Files Dashboard',
            subtitle: 'Storage usage, growth, and orphaned files across the organization.',
            kpi_total_files: 'Total files',
            kpi_total_size: 'Total storage',
            kpi_near_quota: 'Quota usage',
            kpi_orphaned: 'Orphaned files',
            chart_by_owner_type: 'Files by owner type',
            chart_growth_30d: '30-day growth',
            toggle_count: 'By count',
            toggle_size: 'By size',
            orphaned_panel_title: 'Orphaned files (top 20)',
            orphaned_view_all: 'View all orphaned files',
            orphaned_empty: 'No orphaned files found.',
            growth_empty: 'No upload activity in the last 30 days.',
            retention_n_days: '{days} day(s)',
            retention_none: 'No policy',
        },
    },
    // ---------- Module labels (for sample data, tours, etc.) ----------
    // ---------- Help article category labels (Sprint 44.1.9.5) ----------
    // Raw category values from seed-help-articles.ts: user-guide, admin-guide,
    // getting-started, faq. Rendered in the article header chip next to Module.
    category_labels: {
        'user-guide': 'User Guide',
        'admin-guide': 'Admin Guide',
        'getting-started': 'Getting Started',
        faq: 'FAQ',
    },
    // Common help-article tags that are NOT module codes and therefore don't
    // resolve via module_labels. Kept short — unmapped tags fall back to the
    // raw string (which is usually already readable).
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Keys',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Purposes',
        preferences: 'Preferences',
        onboarding: 'Onboarding',
        'first-steps': 'First Steps',
        login: 'Login',
        navigation: 'Navigation',
        users: 'Users',
        user: 'User',
        roles: 'Roles',
        role: 'Role',
        'access-control': 'Access Control',
        'access-management': 'Access Management',
        permissions: 'Permissions',
        permission: 'Permission',
        consent: 'Consent',
        'consent-management': 'Consent Management',
        privacy: 'Privacy',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Audit',
        auditing: 'Auditing',
        breach: 'Breach',
        incident: 'Incident',
        incidents: 'Incidents',
        notification: 'Notification',
        notifications: 'Notifications',
        reporting: 'Reporting',
        risk: 'Risk',
        vendor: 'Vendor',
        vendors: 'Vendors',
        'vendor-risk': 'Vendor Risk',
        assessment: 'Assessment',
        assessments: 'Assessments',
        dpia: 'DPIA',
        'cross-border': 'Cross-Border',
        transfer: 'Transfer',
        transfers: 'Transfers',
        deletion: 'Deletion',
        retention: 'Retention',
        'data-mapping': 'Data Mapping',
        discovery: 'Discovery',
        classification: 'Classification',
        workflow: 'Workflow',
        workflows: 'Workflows',
        template: 'Template',
        templates: 'Templates',
        catalog: 'Catalog',
        monitoring: 'Monitoring',
        tasks: 'Tasks',
        task: 'Task',
        'task-management': 'Task Management',
        organizations: 'Organizations',
        org: 'Organization',
        governance: 'Governance',
        compliance: 'Compliance',
        forms: 'Forms',
        form: 'Form',
        connectors: 'Connectors',
        connector: 'Connector',
        integration: 'Integration',
        integrations: 'Integrations',
        security: 'Security',
        encryption: 'Encryption',
        backup: 'Backup',
        recovery: 'Recovery',
        maintenance: 'Maintenance',
        settings: 'Settings',
        configuration: 'Configuration',
        sla: 'SLA',
        escalation: 'Escalation',
        remediation: 'Remediation',
        evidence: 'Evidence',
        'case-management': 'Case Management',
        profile: 'Profile',
        delegation: 'Delegation',
        'service-accounts': 'Service Accounts',
        license: 'License',
        'license-management': 'License Management',
        plan: 'Plan',
        plans: 'Plans',
        dashboard: 'Dashboard',
        'web-consent': 'Web Consent',
        'partner-marketplace': 'Partner Marketplace',
        partner: 'Partner',
        partners: 'Partners',
        deliverables: 'Deliverables',
        'service-tiers': 'Service Tiers',
        'support-tickets': 'Support Tickets',
        'audit-trail': 'Audit Trail',
        'first-time-setup': 'First-Time Setup',
        setup: 'Setup',
        quickstart: 'Quick Start',
        tour: 'Tour',
        tours: 'Tours',
        'sample-data': 'Sample Data',
        'data-subject': 'Data Subject',
        'data-subjects': 'Data Subjects',
        pseudonymous: 'Pseudonymous',
        identified: 'Identified',
        'help-center': 'Help Center',
        article: 'Article',
        articles: 'Articles',
        search: 'Search',
        'platform-settings': 'Platform Settings',
        'platform-config': 'Platform Configuration',
        'module-config': 'Module Configuration',
        'module-configuration': 'Module Configuration',
        'rights-requests': 'Rights Requests',
        'access-accountability': 'Access Accountability',
        'data-discovery': 'Data Discovery',
        'incident-ops': 'Incident Operations',
        'deletion-orchestration': 'Deletion Orchestration',
        'compliance-forms': 'Compliance Forms',
        'program-governance': 'Program Governance',
        localization: 'Localization',
        'privacy-scanner': 'Privacy Scanner',
        'consent-properties': 'Consent Properties',
        acceptances: 'Acceptances',
        access: 'Access',
        'access-review': 'Access Review',
        'access-reviews': 'Access Reviews',
        accountability: 'Accountability',
        acknowledgment: 'Acknowledgment',
        'action-plans': 'Action Plans',
        activation: 'Activation',
        activations: 'Activations',
        'active-response': 'Active Response',
        activity: 'Activity',
        adequacy: 'Adequacy',
        'admin-guide': 'Admin Guide',
        aggregation: 'Aggregation',
        agreements: 'Agreements',
        ai: 'AI',
        alerts: 'Alerts',
        analytics: 'Analytics',
        anchoring: 'Anchoring',
        approval: 'Approval',
        approvals: 'Approvals',
        apps: 'Apps',
        assets: 'Assets',
        assignments: 'Assignments',
        attestation: 'Attestation',
        'audit-log': 'Audit Log',
        'audit-pack': 'Audit Pack',
        authentication: 'Authentication',
        authority: 'Authority',
        'auto-play': 'Auto-Play',
        automated: 'Automated',
        automation: 'Automation',
        baseline: 'Baseline',
        batches: 'Batches',
        bidirectional: 'Bidirectional',
        bilingual: 'Bilingual',
        bindings: 'Bindings',
        blockchain: 'Blockchain',
        board: 'Board',
        brand: 'Brand',
        brds: 'BRDs',
        'break-glass': 'Break-Glass',
        builder: 'Builder',
        bulk: 'Bulk',
        'bulk-sync': 'Bulk Sync',
        bundles: 'Bundles',
        calendar: 'Calendar',
        campaigns: 'Campaigns',
        cases: 'Cases',
        categories: 'Categories',
        category: 'Category',
        certification: 'Certification',
        chain: 'Chain',
        change: 'Change',
        'change-awareness': 'Change Awareness',
        'change-log': 'Change Log',
        'change-management': 'Change Management',
        'change-requests': 'Change Requests',
        'cmp-validation': 'CMP Validation',
        collection: 'Collection',
        comparison: 'Comparison',
        completeness: 'Completeness',
        completion: 'Completion',
        'compliance-posture': 'Compliance Posture',
        'compliance-proof': 'Compliance Proof',
        'compliance-reviews': 'Compliance Reviews',
        config: 'Config',
        confirmation: 'Confirmation',
        conflicts: 'Conflicts',
        consolidation: 'Consolidation',
        continuous: 'Continuous',
        contracts: 'Contracts',
        controls: 'Controls',
        cookies: 'Cookies',
        cooperation: 'Cooperation',
        coordination: 'Coordination',
        'corporate-governance': 'Corporate Governance',
        'corrective-actions': 'Corrective Actions',
        coverage: 'Coverage',
        'credential-vault': 'Credential Vault',
        credentials: 'Credentials',
        cycles: 'Cycles',
        dashboards: 'Dashboards',
        'data-assets': 'Data Assets',
        'data-flows': 'Data Flows',
        'data-sensitivity': 'Data Sensitivity',
        'data-steward': 'Data Steward',
        deadlines: 'Deadlines',
        'decree-356': 'Decree 356',
        dedup: 'Dedup',
        definitions: 'Definitions',
        deliverable: 'Deliverable',
        departments: 'Departments',
        'dependency-order': 'Dependency Order',
        deployment: 'Deployment',
        detection: 'Detection',
        diff: 'Diff',
        'digital-properties': 'Digital Properties',
        dimensions: 'Dimensions',
        disable: 'Disable',
        'disaster-recovery': 'Disaster Recovery',
        disclosure: 'Disclosure',
        documentation: 'Documentation',
        documents: 'Documents',
        domains: 'Domains',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Drift',
        'drift-detection': 'Drift Detection',
        'due-diligence': 'Due Diligence',
        email: 'Email',
        emergency: 'Emergency',
        enable: 'Enable',
        'enterprise-map': 'Enterprise Map',
        erasure: 'Erasure',
        evaluation: 'Evaluation',
        'event-bus': 'Event Bus',
        exceptions: 'Exceptions',
        execution: 'Execution',
        executive: 'Executive',
        exercises: 'Exercises',
        export: 'Export',
        exports: 'Exports',
        filing: 'Filing',
        filter: 'Filter',
        filtering: 'Filtering',
        findings: 'Findings',
        flows: 'Flows',
        'follow-up': 'Follow-Up',
        forensic: 'Forensic',
        forensics: 'Forensics',
        'form-filler': 'Form Filler',
        framework: 'Framework',
        frameworks: 'Frameworks',
        'gap-analysis': 'Gap Analysis',
        'gap-report': 'Gap Report',
        gaps: 'Gaps',
        gates: 'Gates',
        'getting-started': 'Getting Started',
        'golden-templates': 'Golden Templates',
        grc: 'GRC',
        'group-management': 'Group Management',
        groups: 'Groups',
        'guided-tours': 'Guided Tours',
        guides: 'Guides',
        handling: 'Handling',
        'hands-on': 'Hands-On',
        health: 'Health',
        help: 'Help',
        hierarchy: 'Hierarchy',
        history: 'History',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Identity Provider',
        implementation: 'Implementation',
        import: 'Import',
        improvement: 'Improvement',
        'incident-response': 'Incident Response',
        inconsistencies: 'Inconsistencies',
        instances: 'Instances',
        intake: 'Intake',
        'intake-key': 'Intake Key',
        interactive: 'Interactive',
        international: 'International',
        inventory: 'Inventory',
        investigation: 'Investigation',
        investigations: 'Investigations',
        'ip-allowlist': 'IP Allowlist',
        issues: 'Issues',
        justification: 'Justification',
        justifications: 'Justifications',
        kanban: 'Kanban',
        keyboard: 'Keyboard',
        keys: 'Keys',
        'knowledge-base': 'Knowledge Base',
        koncernstyring: 'Corporate Governance',
        kpi: 'KPI',
        labeling: 'Labeling',
        language: 'Language',
        'language-packs': 'Language Packs',
        languages: 'Languages',
        lawfulness: 'Lawfulness',
        layout: 'Layout',
        'legal-basis': 'Legal Basis',
        'lessons-learned': 'Lessons Learned',
        levels: 'Levels',
        library: 'Library',
        lifecycle: 'Lifecycle',
        lineage: 'Lineage',
        links: 'Links',
        listings: 'Listings',
        live: 'Live',
        locale: 'Locale',
        log: 'Log',
        logging: 'Logging',
        'managed-service': 'Managed Service',
        mapping: 'Mapping',
        mappings: 'Mappings',
        'market-packs': 'Market Packs',
        marketplace: 'Marketplace',
        maturity: 'Maturity',
        metrics: 'Metrics',
        models: 'Models',
        module: 'Module',
        modules: 'Modules',
        mps: 'MPS',
        'multi-factor': 'Multi-Factor',
        'multi-market': 'Multi-Market',
        'multi-org': 'Multi-Org',
        'multi-property': 'Multi-Property',
        'my-tasks': 'My Tasks',
        obligations: 'Obligations',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orchestration',
        'org-groups': 'Org Groups',
        organization: 'Organization',
        overdue: 'Overdue',
        overview: 'Overview',
        'owner-review': 'Owner Review',
        package: 'Package',
        packages: 'Packages',
        packs: 'Packs',
        pagination: 'Pagination',
        'parent-child': 'Parent-Child',
        patterns: 'Patterns',
        performance: 'Performance',
        periodic: 'Periodic',
        periods: 'Periods',
        personal: 'Personal',
        'personal-data': 'Personal Data',
        personas: 'Personas',
        planning: 'Planning',
        platform: 'Platform',
        playbooks: 'Playbooks',
        poc: 'PoC',
        'point-in-time': 'Point-in-Time',
        policies: 'Policies',
        portfolio: 'Portfolio',
        'post-incident': 'Post-Incident',
        'power-user': 'Power User',
        practice: 'Practice',
        pricing: 'Pricing',
        'privacy-impact': 'Privacy Impact',
        procedures: 'Procedures',
        processes: 'Processes',
        processing: 'Processing',
        'processing-activities': 'Processing Activities',
        procurement: 'Procurement',
        productivity: 'Productivity',
        profiles: 'Profiles',
        programs: 'Programs',
        progress: 'Progress',
        proof: 'Proof',
        propagation: 'Propagation',
        properties: 'Properties',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Public Form',
        quality: 'Quality',
        questionnaires: 'Questionnaires',
        'quick-links': 'Quick Links',
        'quick-start': 'Quick Start',
        'quick-starts': 'Quick Starts',
        raci: 'RACI',
        readiness: 'Readiness',
        'real-time': 'Real-Time',
        reassessments: 'Reassessments',
        reconciliation: 'Reconciliation',
        record: 'Record',
        records: 'Records',
        recurring: 'Recurring',
        register: 'Register',
        registry: 'Registry',
        regulations: 'Regulations',
        regulator: 'Regulator',
        regulatory: 'Regulatory',
        'regulatory-frameworks': 'Regulatory Frameworks',
        remap: 'Remap',
        reminders: 'Reminders',
        reports: 'Reports',
        requests: 'Requests',
        requirements: 'Requirements',
        response: 'Response',
        'response-plans': 'Response Plans',
        'response-team': 'Response Team',
        'response-templates': 'Response Templates',
        'response-time': 'Response Time',
        responses: 'Responses',
        responsibility: 'Responsibility',
        results: 'Results',
        review: 'Review',
        reviews: 'Reviews',
        'right-to-delete': 'Right to Delete',
        rights: 'Rights',
        'risk-tiers': 'Risk Tiers',
        rollouts: 'Rollouts',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Routing',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Rules',
        runs: 'Runs',
        saml: 'SAML',
        scan: 'Scan',
        scanning: 'Scanning',
        scc: 'SCC',
        schedule: 'Schedule',
        scheduled: 'Scheduled',
        schedules: 'Schedules',
        scheduling: 'Scheduling',
        scores: 'Scores',
        scoring: 'Scoring',
        scripts: 'Scripts',
        'security-reviews': 'Security Reviews',
        'sensitive-data': 'Sensitive Data',
        sensitivity: 'Sensitivity',
        'separation-of-duties': 'Separation of Duties',
        service: 'Service',
        sessions: 'Sessions',
        'setup-wizard': 'Setup Wizard',
        severity: 'Severity',
        'shared-governance': 'Shared Governance',
        'shared-settings': 'Shared Settings',
        shortcuts: 'Shortcuts',
        sidebar: 'Sidebar',
        signals: 'Signals',
        'single-sign-on': 'Single Sign-On',
        snapshots: 'Snapshots',
        sod: 'SoD',
        sorting: 'Sorting',
        sources: 'Sources',
        staging: 'Staging',
        standards: 'Standards',
        status: 'Status',
        structure: 'Structure',
        'sub-processor': 'Sub-Processor',
        'sub-processors': 'Sub-Processors',
        submissions: 'Submissions',
        subprocessors: 'Subprocessors',
        subscription: 'Subscription',
        suggestions: 'Suggestions',
        summaries: 'Summaries',
        supplements: 'Supplements',
        support: 'Support',
        sync: 'Sync',
        system: 'System',
        systems: 'Systems',
        tables: 'Tables',
        tagging: 'Tagging',
        'tamper-proof': 'Tamper-Proof',
        taxonomies: 'Taxonomies',
        taxonomy: 'Taxonomy',
        tenants: 'Tenants',
        termination: 'Termination',
        'third-party': 'Third-Party',
        'ticket-sync': 'Ticket Sync',
        tickets: 'Tickets',
        tiers: 'Tiers',
        tokens: 'Tokens',
        totp: 'TOTP',
        traceability: 'Traceability',
        tracking: 'Tracking',
        trail: 'Trail',
        training: 'Training',
        translation: 'Translation',
        translations: 'Translations',
        transparency: 'Transparency',
        trends: 'Trends',
        unified: 'Unified',
        updates: 'Updates',
        validation: 'Validation',
        vault: 'Vault',
        'vendor-requests': 'Vendor Requests',
        verification: 'Verification',
        versioning: 'Versioning',
        versions: 'Versions',
        vietnam: 'Vietnam',
        violations: 'Violations',
        visualization: 'Visualization',
        waivers: 'Waivers',
        'web-records': 'Web Records',
        website: 'Website',
        websites: 'Websites',
        weights: 'Weights',
        'white-label': 'White-Label',
        withdrawal: 'Withdrawal',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Rights Requests',
        data_mapping: 'Data Mapping',
        governance: 'Governance',
        consent: 'Consent Governance',
        consent_properties: 'Consent Properties',
        assessments: 'DPIA & Assessments',
        classification: 'Data Classification',
        monitoring: 'Monitoring Programs',
        program: 'Monitoring Program',
        incidents: 'Incident Response',
        connectors: 'Connectors',
        vendor_risk: 'Vendor Risk',
        deletion_orchestration: 'Deletion Orchestration',
        discovery: 'Data Discovery',
        incident_ops: 'Incident Operations',
        program_governance: 'Program Governance',
        access_accountability: 'Access Accountability',
        localization: 'Localization',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Partner Workspace',
        privacy_scanner: 'Privacy Scanner',
        onboarding: 'Onboarding',
        compliance_forms: 'Compliance Forms',
        audit_trail: 'Audit Trail',
        data_subjects: 'Data Subjects',
        organizations: 'Organizations',
        task_management: 'Task Management',
        // Legacy aliases for sidebar/other references
        deletion_orch: 'Deletion Orchestration',
        forms: 'Compliance Forms',
        partner_marketplace: 'Partner Marketplace',
        dashboard: 'Dashboard',
        platform_settings: 'Platform Settings',
        help_center: 'Help Center',
    },
    // ---------- Module descriptions (Sprint 15.6.0) ----------
    module_descriptions: {
        rights_requests: 'Manage data subject rights requests including access, deletion, and portability. Track request lifecycle, assign handlers, and meet regulatory deadlines.',
        data_mapping: 'Map data flows across your organization. Track processing activities, data categories, purposes, retention policies, and system integrations.',
        governance: 'Oversight and the evidence behind it. Manage the vendor register and its lifecycle, approve the decisions this module gates, watch for compliance drift, and hold the trust and identity, regulatory artifact, payments and eKYC, and platform evidence registers.',
        consent: 'Manage consent collection, storage, and withdrawal. Track consent records, purposes, and integration with consent management platforms.',
        consent_properties: 'Configure consent properties, groups, and golden templates. Define reusable consent configurations for consistent deployment.',
        assessments: 'Conduct Data Protection Impact Assessments (DPIAs) and privacy risk assessments. Evaluate processing activities for compliance risks.',
        classification: 'Classify data assets by sensitivity level. Apply taxonomy labels, track lifecycle records, and identify classification gaps.',
        monitoring: 'Set up monitoring programs to track compliance metrics, key risk indicators, and control effectiveness over time.',
        incidents: 'Report and manage privacy incidents and data breaches. Track investigation, notification, and remediation workflows.',
        connectors: 'Configure data source connectors and integrations. Connect external systems for automated data discovery and monitoring.',
        vendor_risk: 'Assess and monitor third-party vendor risks. Score vendors, track risk mitigation, and manage vendor risk configurations.',
        deletion_orchestration: 'Orchestrate data deletion across systems. Track deletion requests, verify proof chains, and ensure complete data removal.',
        discovery: 'Discover personal data across connected systems. Scan data sources to build a comprehensive data inventory.',
        incident_ops: 'Operational incident management with playbooks, escalation workflows, and SLA tracking for incident resolution.',
        program_governance: 'Track overall privacy program maturity, compliance scores, evidence gaps, and executive reporting across your organization.',
        access_accountability: 'Investigate and audit user access patterns. Track access configurations, monitor suspicious activity, and enforce accountability.',
        localization: 'Install and manage market localization packs. Add regulatory frameworks, language packs, and region-specific compliance content.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Collaborate with external partners. Share compliance data, track partner obligations, and manage partner relationships.',
        privacy_scanner: 'Scan websites for privacy compliance issues. Monitor cookie usage, tracking scripts, and privacy policy presence in real-time.',
        onboarding: 'Guide new organizations through platform setup. Step-by-step onboarding wizard for initial configuration and data import.',
        compliance_forms: 'Create and manage compliance forms from a catalog of templates. Fill out regulatory forms, track submissions, and generate reports.',
        audit_trail: 'View a complete audit trail of all system actions. Track who did what, when, and where across all modules.',
        organizations: 'Manage organizations and multi-tenant settings. Configure organization profiles, hierarchies, and cross-org relationships.',
    },
    // ---------- Privacy Scanner (Sprint 11.3.0, expanded Sprint 29.1.0) ----------
    privacy_scanner: {
        // Sprint 48.1.7 — Forseti anomaly explanation panel
        anomaly_explain: {
            explain_label: 'Explain anomalies',
            explaining: 'Explaining…',
            results_heading: 'Anomaly explanations ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} items',
            remediation_label: 'Remediation steps',
            items_detail_label: 'Affected items',
            accept_label: 'Accept explanation',
            reject_label: 'Reject explanation',
            error_generic: 'Could not explain the anomalies. Try again or check the scan completed successfully.',
        },
        // Sprint 48.1.8 — Per-cookie / per-tracker AI insight
        cookie_insight: {
            insight_label: 'Cookie & tracker AI insight',
            producing: 'Producing insight…',
            results_heading: 'Cookie & tracker insight ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} items',
            classification_label: 'Classification',
            severity_label: 'Severity',
            banner_copy_label: 'Suggested banner copy',
            retention_label: 'Retention advice',
            citations_label: 'Citations',
            kind_cookie: 'Cookie',
            kind_tracker: 'Tracker',
            class_necessary: 'Necessary',
            class_analytics: 'Analytics',
            class_marketing: 'Marketing',
            class_social: 'Social',
            accept_label: 'Accept insight',
            reject_label: 'Reject insight',
            no_items: 'No cookies or trackers were detected in this scan.',
            error_generic: 'Could not produce the cookie insight. Try again or check the scan completed successfully.',
        },
        monitoring: {
            title: 'Privacy Monitoring',
            new_scan: 'New Scan',
            scan_url: 'URL to Scan',
            scan_frequency: 'Scan Frequency',
            last_scan: 'Last Scan',
            score: 'Privacy Score',
            issues: 'Issues Found',
            history: 'Scan History',
            settings: 'Monitoring Settings',
        },
        real_time: {
            title: 'Real-Time Scanner',
            scan_now: 'Scan Now',
            scanning: 'Scanning...',
            results: 'Scan Results',
            no_results: 'No scan results yet.',
            cookies: 'Cookies',
            trackers: 'Trackers',
            consent_banners: 'Consent Banners',
            third_party: 'Third-Party Scripts',
            archive: 'Results Archive',
            settings: 'Scanner Settings',
        },
        // Sprint 29.1.0 — full page-level keys for all 6 privacy scanner pages
        dashboard: {
            page_title: 'Privacy Monitoring Dashboard',
            privacy_monitoring: 'Privacy Monitoring',
            plan_label: '{package} plan',
            expires_label: 'expires {date}',
            settings: 'Settings',
            refresh: 'Refresh',
            refreshing_dashboard: 'Refreshing monitoring dashboard',
            refresh_aria: 'Refresh monitoring dashboard',
            not_configured_title: 'Privacy Monitoring Not Configured',
            not_configured_desc: 'Connect your AesirX Privacy Monitoring service to start tracking website compliance.',
            configure_monitoring: 'Configure Monitoring',
            license_expired_title: 'License Expired',
            license_expired_desc: 'Your AesirX Privacy Monitoring license has expired. Please renew it to avoid service interruption.',
            compliance_score: 'Compliance Score',
            cookies_detected: 'Cookies Detected',
            trackers_label: 'Trackers',
            open_issues: 'Open Issues',
            score_good: 'Good',
            score_needs_improvement: 'Needs improvement',
            score_critical: 'Critical',
            action_required: 'Action required',
            cookies_sr: 'cookies detected',
            trackers_sr: 'trackers found',
            open_issues_sr: 'open issues',
            action_required_sr: 'action required',
            metric_label: 'Metric',
            value_label: 'Value',
            status_label: 'Status',
            aria_metrics: 'Privacy monitoring metrics',
            plan_limit_warning: 'Scanning URL: {current}/{max} — you have reached your plan limit.',
            manage_domains: 'Manage domains or upgrade your plan.',
            monitored_domains: 'Monitored Domains',
            domain_added: 'Domain {domain} added',
            domain_removed: 'Domain {domain} removed',
            failed_add_domain: 'Failed to add domain',
            failed_remove_domain: 'Failed to remove domain',
            failed_load: 'Failed to load monitoring dashboard',
            new_domain_aria: 'New domain URL',
            add_domain: 'Add Domain',
            adding_domain: 'Adding\u2026',
            no_domains: 'No monitored domains configured yet.',
            aria_domains_table: 'Monitored domains',
            col_domain: 'Domain',
            col_compliance_score: 'Compliance Score',
            col_status: 'Status',
            col_cookies: 'Cookies',
            col_trackers: 'Trackers',
            col_issues: 'Issues',
            col_last_scan: 'Last Scan',
            col_action: 'Action',
            compliance_status_sr: 'compliance status',
            showing_chart: 'Showing chart view',
            showing_table: 'Showing data table view',
            loading_sr: 'Loading monitoring dashboard',
            // AC1 — framework tagging, continuous monitoring timestamps, audit trail
            col_framework_tags: 'Framework Tags',
            col_last_monitored: 'Last Monitored',
            framework_tags_sr: 'applicable regulatory frameworks',
            last_monitored_sr: 'continuous monitoring timestamp',
            audit_trail_heading: 'Audit Trail',
            audit_trail_info: 'Every monitoring scan is timestamped and logged. Domain-level compliance changes produce an immutable audit record that can be exported for regulatory review.',
            cybersecurity_law_info_heading: 'Cybersecurity Law Detection',
            cybersecurity_law_info: 'Monitored domains are automatically checked against cybersecurity law requirements (e.g., Vietnam Cybersecurity Law, NIS2 Directive). Framework tags indicate which regulations apply. Issues are categorized by regulatory framework for targeted remediation.',
        },
        scan: {
            page_title: 'Real-Time Scan',
            title: 'Real-Time Privacy Scanner',
            settings: 'Settings',
            results_archive: 'Results Archive',
            launch_scan: 'Launch Scan',
            target_url: 'Target URL',
            protocol_aria: 'Protocol',
            url_required: 'URL is required',
            url_whitespace: 'Please remove whitespace from the URL',
            url_invalid: 'Please enter a valid domain (e.g. example.com)',
            add_subpages: 'Add Subpages',
            add_subpages_aria: 'Add subpages, {count} added',
            start_scan: 'Start Scan',
            launching: 'Launching\u2026',
            scan_launched: 'Scan launched successfully',
            scan_failed: 'Failed to launch scan',
            scan_progress: 'Scan Progress',
            scanning_in_progress: 'Scanning in progress\u2026',
            scan_id: 'Scan ID',
            status_label: 'Status',
            status_unknown: 'unknown',
            progress_label: 'Progress',
            pages_label: 'Pages',
            refresh_status: 'Refresh Status',
            new_scan: 'New Scan',
            scan_completed: 'Scan completed, results available',
            failed_check_status: 'Failed to check scan status',
            scan_results: 'Scan Results',
            main_page: 'Main Page',
            page_n: 'Page {n}',
            result_pages_nav: 'Result pages',
            page_url: 'Page URL',
            risk_score: 'Risk Score',
            risk_score_sr: 'risk score',
            cookies_label: 'Cookies',
            cookies_sr: 'cookies detected',
            scripts_label: 'Scripts',
            scripts_sr: 'scripts detected',
            issues_label: 'Issues',
            issues_sr: 'issues found',
            issues_found: 'Issues Found',
            aria_issues_table: 'Issues found during scan',
            col_severity: 'Severity',
            col_category: 'Category',
            col_title: 'Title',
            col_remediation: 'Remediation',
            severity_sr: 'severity',
            scan_result_metrics: 'Scan result metrics',
            view_full_detail: 'View Full Detail',
            // AC3 — compliance case linkage, consent categorization, rule config, audit trail
            col_consent_category: 'Consent Category',
            consent_category_sr: 'consent categorization',
            compliance_evidence_heading: 'Compliance Evidence',
            compliance_evidence_info: 'Scan findings are linked to compliance cases automatically. Each issue can be traced to a consent category (essential, functional, analytics, marketing) and produces audit-ready evidence for regulatory review.',
            rule_config_heading: 'Rule Configuration',
            rule_config_info: 'Scan rules are configurable per regulatory framework. Cookie checks, script detection, and vendor analysis can be enabled or disabled via Settings. Custom rules can be defined to match your organization\u2019s compliance requirements.',
            case_linkage_heading: 'Compliance Case Linkage',
            case_linkage_info: 'Issues found during real-time scans can be linked to compliance cases for tracking and remediation. Navigate to the Scan Result Detail page to view case associations.',
            subpages_modal_title: 'Add Subpages',
            subpages_modal_desc: 'Add up to 5 subpage URLs to scan alongside the main domain.',
            subpage_n: 'Subpage {n}',
        },
        result_detail: {
            page_title: 'Scan Result Detail',
            title: 'Scan Result',
            back_to_archive: 'Back to Archive',
            failed_load: 'Failed to load scan result',
            loading_sr: 'Loading scan result\u2026',
            scan_id: 'Scan ID',
            target_url: 'Target URL',
            status_label: 'Status',
            status_sr: 'status',
            started: 'Started',
            completed: 'Completed',
            pages_scanned: 'Pages Scanned',
            https_label: 'HTTPS',
            highest_risk_score: 'Highest Risk Score',
            most_cookies: 'Most Cookies',
            most_vendors: 'Most 3rd-party Vendors',
            most_issues: 'Most Issues',
            across_pages: 'across pages',
            highest_values_aria: 'Highest values across all pages',
            scanned_pages_nav: 'Scanned pages',
            main_page: 'Main Page',
            page_n: 'Page {n}',
            page_url: 'Page URL',
            risk_score: 'Risk Score',
            overall_risk_sr: 'overall risk score',
            cookies_label: 'Cookies',
            cookies_sr: 'cookies detected',
            vendors_label: '3rd-party Vendors',
            vendors_sr: 'third-party vendors detected',
            issues_label: 'Issues',
            issues_sr: 'issues found',
            risk_breakdown_aria: 'Risk score breakdown',
            issues_found: 'Issues Found',
            aria_issues_table: 'Issues found during scan',
            col_severity: 'Severity',
            col_category: 'Category',
            col_title: 'Title',
            col_remediation: 'Remediation',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies detected',
            col_name: 'Name',
            col_domain: 'Domain',
            col_path: 'Path',
            col_secure: 'Secure',
            col_httponly: 'HttpOnly',
            col_samesite: 'SameSite',
            vendors_heading: 'Third-party Vendors ({count})',
            aria_vendors_table: 'Third-party vendors detected',
            col_hostname: 'Hostname',
            scripts_heading: 'Scripts / Beacons ({count})',
            aria_scripts_table: 'Scripts and beacons detected',
            col_url: 'URL',
            col_type: 'Type',
            links_heading: 'Links',
            first_party: 'First-party',
            third_party: 'Third-party',
            // AC2 — risk category mapping, actionable detail
            col_risk_category: 'Risk Category',
            col_regulatory_framework: 'Regulatory Framework',
            col_actionable_detail: 'Actionable Detail',
            risk_category_sr: 'regulatory risk category',
            risk_category_mapping_heading: 'Risk Category Mapping',
            risk_category_mapping_info: 'Each scan finding is mapped to a regulatory risk category (data collection, cross-border transfer, consent management, data retention, security controls). This mapping enables targeted remediation prioritized by regulatory impact.',
            compliance_case_heading: 'Compliance Case Association',
            compliance_case_info: 'Scan results are linked to compliance cases for end-to-end tracking. Issues can be escalated directly from scan results into the compliance case workflow.',
        },
        results_archive: {
            page_title: 'Scan Results Archive',
            title: 'Scan Results Archive',
            back_to_scanner: 'Back to Scanner',
            failed_load: 'Failed to load scan results',
            results_loaded: 'Scan results loaded, {count} records',
            total_label: 'Total: {count}',
            aria_table: 'Scan results archive',
            col_scan_date: 'Scan Date',
            col_target_url: 'Target URL',
            col_duration: 'Duration',
            col_issues_found: 'Issues Found',
            col_risk_score: 'Risk Score',
            col_status: 'Status',
            col_actions: 'Actions',
            scan_status_sr: 'scan status',
            view_result: 'View',
            no_results: 'No scan results found.',
            loading_sr: 'Loading scan results',
            pagination_aria: 'Scan results pagination',
            // AC2 — archive retention, export with context
            col_risk_category: 'Risk Category',
            risk_category_sr: 'regulatory risk category',
            archive_retention_heading: 'Archive Retention',
            archive_retention_info: 'Scan results are retained for the duration specified in your organization\u2019s data retention policy. Archived results include full scan context (framework tags, risk categories, remediation guidance) and can be exported as CSV for regulatory evidence.',
            export_context_info: 'Exported CSV files include regulatory context: risk category, applicable frameworks, and actionable remediation details for each finding.',
        },
        scanner_settings: {
            page_title: 'Scanner Settings',
            title: 'Scanner Settings',
            back_to_scanner: 'Back to Scanner',
            failed_load: 'Failed to load scanner configuration',
            endpoint_url: 'Endpoint URL',
            save_success: 'Scanner configuration saved successfully.',
            save_failed: 'Failed to save scanner configuration',
            test_connection: 'Test Connection',
            testing: 'Testing...',
            saving: 'Saving...',
            loading_sr: 'Loading scanner settings',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Privacy Monitoring Settings',
            title: 'Monitoring Settings',
            back_to_dashboard: 'Back to Dashboard',
            failed_load: 'Failed to load monitoring configuration',
            credentials_heading: 'AesirX Credentials',
            connected_as: 'Connected as {username}',
            credentials_desc: 'Enter your AesirX account credentials to connect Privacy Monitoring. Find your Member ID in your AesirX account profile page.',
            client_id: 'Client ID',
            client_secret: 'Client Secret',
            username: 'AesirX Username (email)',
            password: 'AesirX Password',
            placeholder_keep: '(leave blank to keep existing)',
            credential_form_aria: 'AesirX credential form',
            test_connection: 'Test Connection',
            testing: 'Testing\u2026',
            test_aria: 'Test AesirX connection',
            testing_aria: 'Testing connection',
            save_configuration: 'Save Configuration',
            saving: 'Saving\u2026',
            save_aria: 'Save credentials',
            saving_aria: 'Saving credentials',
            credentials_saved: 'AesirX credentials saved successfully.',
            credentials_saving: 'AesirX credentials saved. Reloading configuration\u2026',
            credentials_save_failed: 'Failed to save credentials',
            client_id_required: 'Client ID and username are required.',
            all_fields_required: 'All credential fields are required.',
            email_heading: 'Email Notifications',
            receive_emails: 'Receive scan report emails',
            email_toggle_aria: 'Toggle scan report emails',
            only_on_change: 'Only email when risk status changes',
            only_on_change_aria: 'Toggle email only on risk status change',
            emails_enabled: 'Scan report emails enabled',
            emails_disabled: 'Scan report emails disabled',
            only_change_on: 'Only emailing on risk change',
            only_change_off: 'Emailing on every scan',
            email_failed: 'Failed to update email setting',
            email_settings_aria: 'Email notification settings',
            loading_sr: 'Loading monitoring settings',
        },
        entity_types: {
            user: 'User',
            case: 'Case',
            incident: 'Incident',
            assessment: 'Assessment',
            vendor: 'Vendor',
            consent: 'Consent',
            policy: 'Policy',
            data_map: 'Data Map',
        },
        actions: {
            create: 'Create',
            update: 'Update',
            delete: 'Delete',
            view: 'View',
            export: 'Export',
            status_change: 'Status Change',
        },
    },
    // ---------- Compliance Forms (Sprint 11.3.0) ----------
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Forms Dashboard',
            total_templates: 'Total Templates',
            installed: 'Installed',
            available: 'Available',
            by_market: 'By Market',
            back_dashboard: 'Back to Dashboard',
            browse_templates: 'Browse Templates',
            catalog_coverage: 'Catalog Coverage',
            quick_start: 'Quick Start',
            recent_instances: 'Recent Instances',
            total_instances: 'Total Instances',
            fetch_failed: 'Failed to fetch dashboard data.',
            no_instances: 'No instances found.',
            form_metrics: 'Form metrics overview',
            templates_total: 'templates total',
            instances_total: 'instances total',
            installed_of_available: '{installed} of {available} installed',
            templates_in_category: 'templates in {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Form Templates',
            new: 'New Template',
            edit: 'Edit Template',
            name: 'Template Name',
            form_type: 'Form Type',
            market: 'Market',
            language: 'Language',
            schema: 'Form Schema',
            preview: 'Preview',
            duplicate: 'Duplicate Template',
            grid_view: 'Grid View',
            list_view: 'List View',
            fetch_failed: 'Failed to fetch templates.',
            filter_templates: 'Filter templates',
            version_history: 'Version History',
            no_version_history: 'No version history — this is the initial version.',
            form_id: 'Form ID',
            source_instrument: 'Source Instrument',
            issuer: 'Issuer',
            all_frameworks: 'All Frameworks',
            view_mode: 'View mode',
            classification: 'Classification',
        },
        catalog: {
            title: 'Form Catalog',
            install: 'Install',
            uninstall: 'Uninstall',
            open_form: 'Open form',
            install_all: 'Install All',
            installed_tab: 'Installed',
            catalog_tab: 'Catalog',
            search_placeholder: 'Search templates...',
            no_templates: 'No templates found.',
            template_count: '{count} templates',
            all_markets: 'All Markets',
            sections: 'Sections',
            all_categories: 'All Categories',
            category: 'Category',
            category_install_failed: 'Failed to install category templates.',
            category_install_success: 'Category templates installed.',
            fetch_failed: 'Failed to fetch templates.',
            fetch_installed_failed: 'Failed to fetch installed templates.',
            fields: 'Fields',
            install_failed: 'Failed to install template.',
            install_success: 'Template installed successfully.',
            installing: 'Installing...',
            removing: 'Removing...',
            uninstall_confirm: 'Are you sure you want to uninstall this template?',
            uninstall_failed: 'Failed to uninstall template.',
            uninstall_success: 'Template uninstalled successfully.',
            installation_summary: 'Installation summary',
            filter_catalog: 'Filter catalog',
            all_frameworks: 'All Frameworks',
            templates_installed_count: '{count} template(s) installed successfully',
            all_already_installed: 'All templates in this category are already installed',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Form ID',
            issuer: 'Issuer',
            catalog_tabs: 'Form catalog tabs',
        },
        filler: {
            back_templates: 'Back to Templates',
            no_sections: 'No sections found.',
            draft_created: 'Draft created.',
            draft_saved: 'Draft saved.',
            fetch_template_failed: 'Failed to fetch template.',
            save_draft: 'Save Draft',
            save_draft_failed: 'Failed to save draft.',
            saving: 'Saving...',
            select_option: 'Select an option',
            submit: 'Submit',
            submit_failed: 'Failed to submit.',
            submitting: 'Submitting...',
            title: 'Form Wizard',
            form_id_label: 'Form ID',
            schema_label: 'Schema',
            form_number_label: 'Form',
            framework_label: 'Framework',
            issuer_label: 'Issuer',
            source_label: 'Source',
            market_label: 'Market',
            section_of: 'Section {current} of {total}',
            form_navigation: 'Form navigation',
            form_progress: 'Form progress: Section {current} of {total}',
            removed_item: 'Removed item {index} from {field}',
            added_item: 'Added new item to {field}',
            go_to_previous: 'Go to previous section, section {current} of {total}',
            go_to_next: 'Go to next section, section {current} of {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Filing Status',
            advance: 'Advance filing status...',
            not_started: 'Not Started',
            drafting: 'Drafting',
            internal_review: 'Internal Review',
            // Sprint 62.57.0 (gate E12.5 pass 3) — the organisation's own acts, in the
            // dossier filing's step words (AC-R3).
            approved_for_submission: 'Approved internally',
            submitted_to_authority: 'Filed',
            supplement_requested: 'Supplement Requested',
            supplement_in_progress: 'Supplement In Progress',
            resubmitted: 'Resubmitted',
            accepted: 'Accepted',
            rejected: 'Rejected',
            closed: 'Closed',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Approved By',
            form_answers: 'Form Answers',
            column_header: 'Form',
            no_answers: 'No answers found.',
            view_outputs: 'View Outputs',
            approve: 'Approve',
            approve_failed: 'Failed to approve.',
            approving: 'Approving...',
            detail_title: 'Instance Detail',
            fetch_failed: 'Failed to fetch instances.',
            generate_failed: 'Failed to generate output.',
            generate_output: 'Generate Output',
            generating: 'Generating...',
            submitted_at: 'Submitted At',
            instance_metadata: 'Form instance metadata',
            filing_status_steps: 'Filing status steps',
            current_status: 'current status',
            filing_status_update_failed: 'Failed to update filing status',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Reload the dossier',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                // Sprint 63.40.0 (decision 7) — the dossier's export: its submission slip.
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Back to Instance',
            no_outputs_msg: 'No outputs have been generated yet.',
            no_outputs_title: 'No Outputs',
            output_name: 'Output Name',
            download: 'Download',
            download_failed: 'Failed to download.',
            fetch_failed: 'Failed to fetch outputs.',
            format: 'Format',
            generated_at: 'Generated At',
            generated_by: 'Generated By',
            size: 'Size',
            title: 'Form Outputs',
            print: 'Print',
            print_outputs: 'Print form outputs',
            printing: 'Printing form outputs',
            pdf_preview: 'PDF Preview',
            downloading: 'Downloading {name}',
            preview_in_new_window: 'Preview {name} ({format}) in new window',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        // Cybersecurity form domain headings — Sprint 14.9.0
        cybersec_domains: {
            scope: 'Scope & Applicability',
            risk: 'Risk Assessment & Control Mapping',
            policy: 'Organizational Security Policies',
            localization: 'Data Localization & Local Presence',
            vendor: 'Third-Party & Supply Chain',
            authority: 'Authority Request Handling',
            incident: 'Incident Response & Evidence',
            training: 'Training & Readiness',
        },
        // Market finalization form domain headings — Sprint 15.0.0
        market_finalization_domains: {
            data_law: 'Data Law — Governance & Operational Controls',
            pdpl_2025: 'PDPL 2025 — Privacy Governance & Data Subject Rights',
            decree_356: 'Decree 356/2025 — Vendor & Cross-Border Controls',
            telecom: 'Telecom Law — Confidentiality & Authority Requests',
        },
        nav: {
            filing_case_links: 'Filing Case Links',
            section_routing: 'Section Routing',
            submission_packages: 'Submission Packages',
        },
        filing_case_links: {
            title: 'Filing Case Links',
            link_form: 'Link Form to Filing Case',
            filing_case_type: 'Filing Case Type',
            form_instance: 'Form Instance',
            select_form_instance: 'Select a form instance...',
            select_filing_case: 'Select a filing case...',
            no_links_found: 'No filing case links found.',
            linked: 'Linked',
            filing_case_id: 'Filing Case ID',
            link_created: 'Filing case link created',
            link_deleted: 'Filing case link deleted',
            aria_delete_link: 'Delete link {name}',
            case_type_dpia_dossier: 'DPIA Dossier',
            case_type_cross_border_filing: 'Cross-Border Filing',
            case_type_breach_notification: 'Breach Notification',
            no_instances_guidance: 'No form instances yet. Create one from Form Templates.',
            no_filings_guidance: 'No filing cases yet. Create one from Assessments.',
        },
        section_routing: {
            title: 'Section Routing',
            assign_section: 'Assign Section',
            department: 'Department',
            select_department: 'Select a department...',
            assignee: 'Assignee',
            select_assignee: 'Select an assignee...',
            section: 'Section',
            select_section: 'Select a section...',
            select_form_instance: 'Select a form instance...',
            no_assignments_found: 'No section assignments found.',
            select_instance_prompt: 'Select a form instance to view section assignments.',
            complete_section: 'Complete Section',
            status_pending: 'Pending',
            status_in_progress: 'In Progress',
            status_completed: 'Completed',
            status_reviewed: 'Reviewed',
            form_instance_id: 'Form Instance ID',
            assignment_created: 'Section assignment created',
            assignment_deleted: 'Section assignment deleted',
            status_updated: 'Section status updated',
            aria_delete_assignment: 'Delete assignment {name}',
            deadline: 'Deadline',
            deadline_help: 'Optional. Assignments past this date show as overdue.',
            overdue: 'Overdue',
            days_remaining: 'days remaining',
            approval_requested: 'Submitted for review',
            form_approved: 'Form approved',
            form_rejected: 'Form rejected',
            no_departments_guidance: 'No departments configured. Set up in Organization Settings.',
            no_sections_guidance: 'No sections defined in the template. Ensure the form template has sections.',
            select_instance_first: 'Select a form instance first to load available sections.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Submission Packages',
            assemble_package: 'Assemble Submission Package',
            dossier: 'Dossier',
            select_dossier: 'Select a dossier...',
            package_label: 'Package Label',
            cover_letter: 'Cover Letter',
            checklist: 'Checklist',
            evidence_index: 'Evidence Index',
            completeness: 'Completeness',
            incomplete_warning: 'Cannot assemble: form has incomplete sections.',
            no_packages_found: 'No submission packages found.',
            version: 'Version',
            package_assembled: 'Submission package assembled',
            package_deleted: 'Submission package deleted',
            aria_delete_package: 'Delete package {name}',
            status_draft: 'Draft',
            status_complete: 'Complete',
            status_submitted: 'Submitted',
            status_accepted: 'Accepted',
            status_rejected: 'Rejected',
            package_updated: 'Submission package updated',
            document_generated: 'Document generated',
            documents: 'Documents',
            generate: 'Generate',
            preview: 'Preview',
            download: 'Download',
            download_all: 'Download All',
            no_dossiers_guidance: 'No dossiers yet. Create one from Assessments.',
            package_plan_help: 'Include in package plan (document auto-generation available):',
            cover_letter_help: 'Official cover letter addressed to the receiving authority',
            checklist_help: 'Compliance checklist verifying all submission requirements are met',
            evidence_index_help: 'Index of supporting evidence documents referenced in the submission',
            completeness_skipped: 'Completeness check skipped — no form instance linked to this dossier.',
            completeness_unavailable: 'Completeness check unavailable — proceeding with package assembly.',
            duplicate_package_error: 'A submission package is already open for this dossier. Assemble a new one once it is rejected.',
            doc_cover_letter: 'Cover Letter',
            doc_checklist: 'Checklist',
            doc_evidence_index: 'Evidence Index',
            export_csv: 'Export CSV',
            export_success: 'Filing case links exported successfully',
            no_assignments_guidance: 'No section assignments yet. Assign sections to departments and team members to begin routing.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 26.3.3: Template Picker
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Select a Template',
            hint: 'Choose a regulatory form template or start with a blank form.',
            market_filter: 'Filter by market',
            framework_filter: 'Filter by framework',
            all_markets: 'All Markets',
            all_frameworks: 'All Frameworks',
            search_placeholder: 'Search templates...',
            blank_form: 'Blank / Generic Form',
            blank_form_desc: 'Create without a template — fill in fields manually.',
            no_templates: 'No templates found for this filter.',
            // fix-sprint-20260411-0440-followup F6 — a failed template read is stated, never shown as "none found".
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Select',
            link_error: 'Template linking failed — you can link a template later from the Edit view.',
            internal_badge: 'Internal template',
            legacy_badge: 'Legacy',
            draft_badge: 'Draft',
            upcoming_badge: 'Upcoming',
            hidden_badge: 'Hidden',
            show_hidden_toggle: 'Show hidden / legacy templates',
            show_hidden_toggle_hint: 'Admin view — include templates flagged internal_only, hidden, legacy, or draft.',
        },
        // FU2b (2026-04-21) — Cover-letter preview-and-edit modal
        cover_letter_modal: {
            title: 'Cover letter — preview & edit',
            iframe_title: 'Cover letter preview',
            stage_label: 'Stage',
            stage_initial: 'Initial submission',
            stage_supplement: 'Supplement response',
            stage_resubmission: 'Resubmission',
            subject_label: 'Subject',
            body_label: 'Body paragraph',
            notes_label: 'Additional notes',
            refresh_preview: 'Refresh preview',
            confirm: 'Confirm & save',
            regenerate: 'Regenerate',
            snapshot_saved: 'Cover letter saved',
        },
        // Sprint 25.6.0: Related Forms panel
        related_panel: {
            title: 'Related Regulatory Forms',
            open_form: 'Fill Form',
            no_forms: 'No related forms found.',
            hint_vn: 'VN-specific regulatory forms available — select a form to open the compliance form wizard.',
            internal_badge: 'Internal',
            official_badge: 'Official',
            loading: 'Loading related forms...',
            error: 'Failed to load related forms.',
            form_id: 'Form ID',
            form_name: 'Form Name',
            framework: 'Framework',
        },
        view_form: 'View Form',
        edit_form: 'Edit Form',
        save_draft: 'Save Draft',
        module_settings: 'Module Settings',
        validation_errors: 'Please fix validation errors',
        custom_name_help: 'Optional — defaults to template name if left blank',
        select_placeholder: 'Select...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Previous',
        next_section: 'Next',
        // fix-sprint-20260411-B F7 — keys used by FormSectionRenderer,
        // FormAnswersModal, UnifiedCreationModal (replacing hardcoded
        // English strings).
        loading: 'Loading...',
        array_add: 'Add',
        array_remove: 'Remove',
        array_item_label: '{label} item {index}',
        array_add_item: 'Add item to {label}',
        array_remove_item: 'Remove item {index} from {label}',
        legacy_banner: 'This record was created before unified forms. Some data may differ between the entity and form instance.',
        field_required: '{label} is required',
        // fix-sprint-20260411-0417 Findings 1, 2, 3 — replace hardcoded
        // English in FormSectionRenderer + UnifiedCreationModal.
        section_announcement: 'Section {current} of {total}: {title}',
        sections_nav_label: 'Form sections',
        no_sections: 'No template sections available.',
        section_label_tpl: 'Section {index}: {title}',
        field_shown_announcement: '{field} is now shown.',
        field_required_announcement: '{field} is now required.',
        submit_blocked_hint_one: '1 required field still needs an answer.',
        submit_blocked_hint_many: '{count} required fields still need an answer.',
        module_settings_auto_applied: 'Module-specific settings are applied automatically.',
        // fix-sprint-20260411-0417 Finding 17 — shared fallbacks for the
        // FormAnswersModal / UnifiedCreationModal extractErrorMessage path.
        operation_failed: 'Operation failed',
        failed_to_load_form: 'Failed to load form',
        failed_to_save_form: 'Failed to save form',
        failed_to_submit_form: 'Failed to submit form',
        failed_to_load_template: 'Failed to load template',
        failed_to_save_draft: 'Failed to save draft',
        failed_to_create: 'Failed to create',
        no_content_generated: 'No content generated',
    },
    // ---------- Pack Catalog (Sprint 11.3.0) ----------
    pack_catalog: {
        title: 'Pack Catalog',
        install: 'Install Pack',
        uninstall: 'Uninstall Pack',
        install_batch: 'Install Cluster',
        installed_tab: 'Installed',
        catalog_tab: 'Catalog',
        pack_details: 'Pack Details',
        frameworks: 'Frameworks',
        requirements: 'Requirements',
        artifacts: 'Artifacts',
        language_pack: 'Language Pack',
        dependencies: 'Dependencies',
        no_packs: 'No packs found.',
    },
    // ---------- Platform: Users, Roles, Profile details (Sprint 11.3.0) ----------
    platform_pages: {
        users: {
            title: 'Users',
            new: 'New User',
            invite: 'Invite User',
            edit: 'Edit User',
            name: 'Name',
            email: 'Email',
            role: 'Role',
            status: 'Status',
            identity_type: 'Identity Type',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. The user must change this password the first time they sign in, and can do nothing else until they do.',
            last_login: 'Last Login',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Deactivate',
            reactivate: 'Reactivate',
            unlock: 'Unlock',
            reset_password: 'Reset Password',
            remove: 'Remove',
            remove_confirm: 'Remove "{name}"? This action is permanent and the user will lose all access. The record is retained for audit purposes.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Roles',
            new: 'New Role',
            edit: 'Edit Role',
            name: 'Role Name',
            description: 'Description',
            permissions: 'Permissions',
            users_count: 'Users',
            detail: 'Role Detail',
            all_tiers: 'All Tiers',
            tier: 'Tier',
            type_system: 'System',
            type_custom: 'Custom',
            role_name_placeholder: 'e.g. Regional Compliance Lead',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'My Profile',
            name: 'Name',
            display_name: 'Display Name',
            email: 'Email',
            phone: 'Phone',
            department: 'Department',
            job_title: 'Job Title',
            language: 'Language',
            role: 'Role',
            persona: 'Detected Persona',
            last_login: 'Last Login',
            save: 'Save Changes',
            save_success: 'Profile updated successfully',
            module_tips_section: 'Module Tips',
            tips_toggle: 'Show regulatory tips in modules',
            dismissed_tips: 'Dismissed tips',
            reset_dismissed: 'Reset All Dismissed Tips',
            notification_prefs: 'Notification Preferences',
            notification_prefs_link: 'Manage notification preferences →',
            change_password: 'Change Password',
            current_password: 'Current Password',
            new_password: 'New Password',
            confirm_password: 'Confirm Password',
            accessibility: 'Accessibility',
            high_contrast: 'High Contrast Mode',
            high_contrast_desc: 'Increase contrast for better readability. Meets WCAG 2.1 AAA standards.',
            passwords_no_match: 'Passwords do not match',
            password_changed_logout: 'Password changed. You will be logged out.',
            failed_update: 'Failed to update profile',
            failed_change_password: 'Failed to change password',
            none: 'None',
            never: 'Never',
            password_requirements: 'Min 12 chars, uppercase, lowercase, digit, special character',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA Settings',
            mfa_enable: 'Enable MFA',
            mfa_disable: 'Disable MFA',
            mfa_setup: 'Scan this QR code with your authenticator app',
            sso_title: 'SSO Configuration',
            sso_provider: 'Provider',
            sso_client_id: 'Client ID',
            sso_redirect: 'Redirect URI',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API Keys',
            api_keys_new: 'New API Key',
            api_keys_name: 'Key Name',
            api_keys_last_used: 'Last Used',
            api_keys_expires: 'Expires',
            api_keys_revoke: 'Revoke',
            service_accounts_title: 'Service Accounts',
            ip_allowlist_title: 'IP Allowlist',
            ip_add: 'Add IP Address',
            ip_address: 'IP Address',
            ip_description: 'Description',
            delegations_title: 'Delegations',
            delegations_new: 'New Delegation',
            delegations_from: 'From User',
            delegations_to: 'To User',
            delegations_expires: 'Expires',
            break_glass_title: 'Break-Glass Access',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Request Access',
            break_glass_reason: 'Reason for Access',
            break_glass_duration: 'Duration',
            session_settings_title: 'Session Settings',
            eviction_policy_section: 'Eviction Policy',
            session_timeout: 'Session Timeout (minutes)',
            session_max_concurrent: 'Max Concurrent Sessions',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Active Sessions',
            active_sessions_device: 'Device',
            active_sessions_ip: 'IP Address',
            active_sessions_started: 'Started',
            active_sessions_revoke: 'Revoke Session',
            active_sessions_confirm_terminate: 'Are you sure you want to terminate this session?',
            active_sessions_confirm_terminate_current: 'This is your current session. Terminating it will sign you out immediately. Continue?',
            active_sessions_confirm_terminate_all: 'Are you sure you want to terminate all other sessions? Only your current session will remain active.',
            active_sessions_terminated: 'Session terminated successfully.',
            active_sessions_all_terminated: 'All other sessions have been terminated.',
            active_sessions_terminate_all: 'Terminate All Other Sessions',
            active_sessions_last_activity: 'Last Activity',
            active_sessions_current: 'Current Session',
            active_sessions_revoke_selected: 'Revoke Selected',
            active_sessions_confirm_terminate_selected: 'Are you sure you want to terminate the selected sessions?',
            active_sessions_selected_terminated: 'Selected sessions terminated successfully.',
            ip_allowlist_desc: 'Manage allowed IP addresses for API access.',
            ip_no_entries: 'No IP addresses configured.',
            session_settings_desc: 'Configure session timeout and concurrent session limits.',
            session_settings_save_policy: 'Save Session Policy',
            session_settings_saved: 'Session settings updated successfully.',
            session_settings_idle_timeout: 'Idle Timeout (minutes)',
            session_settings_absolute_timeout: 'Absolute Timeout (minutes)',
            session_settings_help_idle: 'Session will expire after this period of inactivity.',
            session_settings_help_absolute: 'Session will expire after this total duration regardless of activity.',
            session_settings_help_max: 'Maximum number of simultaneous sessions per user.',
            sso_note: 'Changes to SSO configuration may require users to re-authenticate.',
            service_accounts_warning: 'This API key will only be shown once. Copy it now and store it securely.',
            service_accounts_new: 'New Service Account',
            service_accounts_name: 'Account Name',
            service_accounts_role: 'Role',
            service_accounts_created: 'Created',
            service_accounts_desc: 'Manage machine-to-machine service accounts for API integrations.',
            break_glass_warning: 'Break-glass access is audited and logged. Use only for genuine emergencies.',
            break_glass_min_chars: '{count} / {min} minimum characters',
            break_glass_permission_count: '{count} permission(s) selected',
            break_glass_submitted: 'Break-glass request submitted. Awaiting approval.',
            break_glass_approved: 'Break-glass request approved.',
            break_glass_denied: 'Break-glass request denied.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Confirm Activation',
            break_glass_pending: 'Pending Requests',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Time Remaining',
            break_glass_expired: 'Expired',
            break_glass_approved_denied_by: 'Approved/Denied By',
            delegations_desc: 'Manage temporary permission delegations between users.',
            delegations_my: 'My Delegations',
            delegations_to_me: 'Delegated to Me',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'All my permissions, as they stand today',
            delegations_scope_specific: 'Selected permissions',
            delegations_created: 'Delegation created successfully.',
            delegations_revoked: 'Delegation revoked.',
            delegations_confirm_revoke: 'Are you sure you want to revoke this delegation?',
            delegations_confirm_revoke_title: 'Confirm Revoke',
            delegations_permission_count: '{count} permission(s)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Status',
            api_keys_prefix: 'Key Prefix',
            api_keys_permissions_count: 'Permissions',
            api_keys_ip_count: 'IP Restrictions',
            api_keys_created: 'Created',
            api_keys_confirm_revoke_title: 'Confirm Revoke',
            api_keys_confirm_revoke_message: 'Are you sure you want to revoke the API key "{name}"? This action cannot be undone.',
            api_keys_confirm_rotate_title: 'Confirm Rotation',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API key created successfully.',
            api_keys_revoked_success: 'API key "{name}" has been revoked.',
            api_keys_rotated_success: 'API key "{name}" has been rotated. Copy the new key below.',
            api_keys_load_error: 'Failed to load API keys.',
            api_keys_create_error: 'Failed to create API key.',
            api_keys_revoke_error: 'Failed to revoke API key.',
            api_keys_rotate_error: 'Failed to rotate API key.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minutes',
            break_glass_duration_30: '30 minutes',
            break_glass_duration_60: '60 minutes (1h)',
            break_glass_duration_120: '120 minutes (2h)',
            break_glass_duration_240: '240 minutes (4h)',
            sod_rules_title: 'Segregation of Duties Rules',
            sod_violations_report: 'Violations Report',
            sod_create_rule: 'Create Rule',
            sod_col_name: 'Name',
            sod_col_description: 'Description',
            sod_col_conflict_count: 'Conflict Count',
            sod_col_severity: 'Severity',
            sod_col_status: 'Status',
            sod_col_actions: 'Actions',
            sod_no_rules: 'No SoD rules found',
            sod_severity_warning: 'Warning',
            sod_severity_blocking: 'Blocking',
            sod_active: 'Active',
            sod_inactive: 'Inactive',
            sod_edit: 'Edit',
            sod_exceptions: 'Exceptions',
            sod_deactivate: 'Deactivate',
            sod_confirm_deactivate_title: 'Confirm Deactivation',
            sod_confirm_deactivate_message: 'Deactivate rule "{name}"? It will no longer flag violations.',
            sod_deactivate_error: 'Failed to deactivate rule',
            sod_rule_count: '{count} {count, plural, one {rule} other {rules}}',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: the self-service TOTP flow and the backup-codes file ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Access Reviews',
            campaigns: 'Review Campaigns',
            new_campaign: 'New Campaign',
            campaign_name: 'Campaign Name',
            scope: 'Scope',
            reviewers: 'Reviewers',
            due_date: 'Due Date',
            progress: 'Progress',
            my_reviews: 'My Reviews',
            approve: 'Approve',
            revoke: 'Revoke',
            analytics: 'Role Analytics',
            analytics_title: 'Role Analytics',
            permission_usage: 'Permission Usage',
            role_coverage: 'Role Coverage',
            campaign_detail: 'Campaign Details',
            campaign_description: 'Description',
            campaign_status: 'Status',
            campaign_deadline: 'Deadline',
            campaign_scope_type: 'Scope Type',
            campaign_completion: 'Completion',
            campaign_total_items: 'Total Items',
            campaign_pending_items: 'Pending',
            campaign_confirmed_items: 'Confirmed',
            campaign_revoked_items: 'Revoked',
            campaign_escalated_items: 'Escalated',
            campaign_launch: 'Launch Campaign',
            campaign_cancel: 'Cancel Campaign',
            campaign_confirm_launch: 'Are you sure you want to launch this campaign?',
            campaign_confirm_cancel: 'Are you sure you want to cancel this campaign?',
            campaign_launched: 'Campaign launched successfully.',
            campaign_cancelled: 'Campaign cancelled.',
            review_items: 'Review Items',
            review_item_user: 'User',
            review_item_role: 'Role',
            review_item_reviewer: 'Reviewer',
            review_item_decision: 'Decision',
            review_item_justify: 'Justification',
            review_item_confirm: 'Confirm',
            review_item_revoke: 'Revoke',
            review_item_escalate: 'Escalate',
            all_decisions: 'All Decisions',
            bulk_action: 'Bulk Action',
            bulk_confirm: 'Bulk Confirm',
            bulk_revoke: 'Bulk Revoke',
            selected_count: '{count} selected',
            justify_required: 'Justification is required',
            scope_all_users: 'All Users',
            scope_role_owner: 'Role Owner',
            scope_department: 'Department',
            back_to_campaigns: 'Back to Campaigns',
            recommendations: 'Recommendations',
            refreshing: 'Refreshing...',
            decide: 'Decide',
            decision_by: 'Decision By',
            loading_items: 'Loading items...',
            loading_review_items: 'Loading review items...',
            campaign_not_found: 'Campaign not found',
            loading_campaign: 'Loading campaign details...',
            campaign_completed: 'Campaign completed successfully.',
            confirm_cancel_title: 'Confirm Cancel',
            justify_bulk_confirm: 'Justification for bulk confirm:',
            justify_bulk_revoke: 'Justification for bulk revoke:',
            items_selected: '{count} item(s) selected',
            justify_bulk_required: 'Justification is required for bulk actions',
            create_campaign_title: 'Create Access Review Campaign',
            name_required: 'Name is required',
            scope_type_required: 'Scope type is required',
            reviewer_strategy_required: 'Reviewer strategy is required',
            deadline_required: 'Deadline is required',
            reviewer_id_required: 'Reviewer ID is required for specific user strategy',
            invalid_json: 'Invalid JSON',
            scope_filter_label: 'Scope Filter (JSON)',
            reviewer_strategy_label: 'Reviewer Strategy',
            reviewer_id_label: 'Reviewer ID',
            deadline_label: 'Deadline',
            create_campaign_btn: 'Create Campaign',
            scope_type_label: 'Scope Type',
            scope_by_role: 'By Role',
            scope_by_department: 'By Department',
            scope_custom: 'Custom',
            reviewer_manager: 'Manager',
            reviewer_specific_user: 'Specific User',
            enter_json_filter: 'Enter JSON filter',
            hint_label: 'Hint:',
            role_usage: 'Role Usage',
            over_privileged_users: 'Over-Privileged Users',
            role_name_col: 'Role Name',
            assigned_users: 'Assigned Users',
            active_users_30d: 'Active Users (30d)',
            total_perms: 'Total Perms',
            used_perms: 'Used Perms',
            unused_perms: 'Unused Perms',
            no_data_available: 'No data available',
            roles_col: 'Roles',
            total_col: 'Total',
            used_col: 'Used',
            unused_pct: 'Unused %',
            current_count: 'Current Count',
            recommended_count: 'Recommended Count',
            rationale: 'Rationale',
            loading_analytics: 'Loading analytics...',
            loading_role_analytics: 'Loading role analytics data...',
            computed_at: 'Computed at:',
            form_errors: 'Form has {count} error(s). Please correct them.',
            scope_filter_invalid_json: 'Scope filter contains invalid JSON',
            scope_hint_all_users: 'No filter needed -- all users will be included.',
            review_type_label: 'Review Type',
            review_type_required: 'Review type is required',
            reviewer_ids_label: 'Reviewer IDs',
            reviewer_ids_hint: 'Enter user IDs separated by commas',
            reviewer_ids_required: 'At least one reviewer ID is required',
            start_date_label: 'Start Date',
            start_date_required: 'Start date is required',
            end_date_label: 'End Date',
            end_date_required: 'End date is required',
            review_type_user_access: 'User Access',
            review_type_role_permissions: 'Role Permissions',
            review_type_service_accounts: 'Service Accounts',
            review_type_privileged_access: 'Privileged Access',
            review_type_application_access: 'Application Access',
            auto_revoke_label: 'Auto-Revoke on Deny',
            campaigns_title: 'Access Review Campaigns',
            create_campaign_label: '+ Create Campaign',
            no_campaigns_found: 'No campaigns found',
            loading_campaigns: 'Loading campaigns...',
            no_reviews_assigned: 'No reviews assigned to you',
            loading_reviews: 'Loading reviews...',
            review_decision_title: 'Review Decision',
            justification_label: 'Justification',
            announce_loaded_review_items: 'Loaded {count} review items',
            announce_loaded_campaigns: 'Loaded {count} campaigns',
            announce_decision_submitted: 'Decision "{decision}" submitted for review item',
            announce_decision_submitted_for: 'Decision "{decision}" submitted for {name}',
            announce_bulk_decision: 'Bulk {decision} applied to {count} items',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Attestation Schedules',
            new: 'New Schedule',
            edit: 'Edit Schedule',
            create_title: 'Create Attestation Schedule',
            edit_title: 'Edit Attestation Schedule',
            schedule_history: 'Schedule History',
            next_run_date: 'Next Run Date',
            schedule_name: 'Schedule Name',
            frequency: 'Frequency',
            next_run: 'Next Run',
            last_run: 'Last Run',
            history: 'Attestation History',
            description: 'Description',
            scope: 'Scope',
            status: 'Status',
            created_at: 'Created',
            updated_at: 'Updated',
            trigger_now: 'Trigger Now',
            back_to_schedules: 'Back to Schedules',
            confirm_trigger: 'Are you sure you want to trigger this schedule now?',
            confirm_delete: 'Are you sure you want to delete this schedule?',
            triggered: 'Schedule triggered successfully.',
            deleted: 'Schedule deleted.',
            saved: 'Schedule saved successfully.',
            campaigns_generated: 'Campaigns Generated',
            run_date: 'Run Date',
            run_status: 'Run Status',
            create_schedule: 'Create Schedule',
            view_history: 'View History',
            deactivate: 'Deactivate',
            overdue: 'Overdue',
            overdue_sr: 'Schedule is overdue',
            confirm_trigger_title: 'Confirm Action',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now? This will create a new access review campaign.',
            confirm_deactivate_title: 'Confirm Deactivation',
            confirm_deactivate_message: 'Are you sure you want to deactivate this schedule?',
            triggered_success: 'Schedule triggered successfully.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Schedule deactivated.',
            name_label: 'Name *',
            frequency_label: 'Frequency *',
            scope_type_label: 'Scope Type *',
            scope_filter_label: 'Scope Filter (JSON)',
            reviewer_strategy_label: 'Reviewer Strategy *',
            reviewer_id_label: 'Reviewer ID *',
            frequency_quarterly: 'Quarterly',
            frequency_semi_annual: 'Semi-Annual',
            frequency_annual: 'Annual',
            scope_all_users: 'All Users',
            scope_by_role: 'By Role',
            scope_by_department: 'By Department',
            scope_custom: 'Custom',
            reviewer_manager: 'Manager',
            reviewer_role_owner: 'Role Owner',
            reviewer_specific_user: 'Specific User',
            computed_next_run_date: 'Computed Next Run Date',
            current_scheduled_date: 'Current scheduled date',
            recomputed_frequency_changed: '(recomputed -- frequency changed)',
            estimated_days_hint: 'Estimated based on {days} days from today',
            validation_name_required: 'Name is required',
            validation_frequency_required: 'Frequency is required',
            validation_scope_type_required: 'Scope type is required',
            validation_reviewer_strategy_required: 'Reviewer strategy is required',
            validation_reviewer_id_required: 'Reviewer ID is required for specific user strategy',
            validation_invalid_json: 'Invalid JSON',
            validation_scope_filter_invalid_json: 'Scope filter contains invalid JSON',
            form_errors: 'Form has {count} error(s). Please correct them.',
            create_schedule_btn: 'Create Schedule',
            schedule_history_title: 'Schedule History',
            campaign_name_col: 'Campaign Name',
            items_col: 'Items',
            no_campaigns_yet: 'No campaigns generated yet',
            loading_history: 'Loading history...',
            loading_schedule: 'Loading schedule...',
            total_campaigns: 'Total campaigns: {count}',
            results_per_page: 'Results per page',
            page_size_option: '{size} / page',
        },
        sod: {
            title: 'Separation of Duties Rules',
            new: 'New Rule',
            edit: 'Edit Rule',
            create_title: 'Create SoD Rule',
            edit_title: 'Edit SoD Rule',
            rule_name: 'Rule Name',
            conflicting_permissions: 'Conflicting Permissions',
            permission_a: 'Permission A',
            permission_b: 'Permission B',
            violations: 'SoD Violations',
            violation_user: 'User',
            violation_rule: 'Rule Violated',
            violation_date: 'Detected At',
            exceptions: 'SoD Exceptions',
            exception_reason: 'Exception Reason',
            exception_approver: 'Approved By',
            exception_status: 'Exception Status',
            has_exception: 'Has Exception',
            no_exception: 'No Exception',
            grant_exception: 'Grant Exception',
            granting: 'Granting...',
            grant_new_exception: 'Grant New Exception',
            exceptions_for: 'Exceptions for: {name}',
            back_to_rules: 'Back to Rules',
            no_violations: 'No violations found',
            no_exceptions_found: 'No exceptions found for this rule',
            all_exception_statuses: 'All Exception Statuses',
            user_id_label: 'User ID',
            justification_label: 'Justification',
            compensating_controls_label: 'Compensating Controls',
            expires_at_label: 'Expires At',
            granting_exception_for: 'Granting an exception for rule: {name}',
            severity: 'Severity',
            severity_warning: 'Warning',
            severity_blocking: 'Blocking',
            violation_count: '{count} violation(s)',
            exception_count: '{count} exception(s)',
            rule_description: 'Description',
            confirm_delete: 'Are you sure you want to delete this rule?',
            deleted: 'Rule deleted.',
            framework_code: 'Framework',
            name_label: 'Name *',
            severity_label: 'Severity *',
            conflict_pairs_legend: 'Conflict Permission Pairs',
            // fix-sprint-20260717-2118-followup F4/F5 — single-string section
            // heading shared by CreateSodRulePage and EditSodRulePage. Replaces
            // `${conflict_pairs_legend} configuration` which shipped hardcoded
            // English into every non-EN locale.
            section_conflict_pairs_configuration: 'Conflict pairs configuration',
            conflict_pairs_hint: 'Each pair defines two permissions that should not be held by the same user.',
            placeholder_permission_a: '-- Permission A --',
            placeholder_permission_b: '-- Permission B --',
            permission_a_for_pair: 'Permission A for pair {index}',
            permission_b_for_pair: 'Permission B for pair {index}',
            conflict_pair_label: 'Conflict pair {index}',
            remove_conflict_pair: 'Remove conflict pair {index}',
            add_pair: '+ Add Pair',
            placeholder_name: 'e.g. Payment Approval Separation',
            create_rule: 'Create Rule',
            loading_rule: 'Loading SoD rule...',
            failed_load_rule: 'Failed to load rule.',
            failed_create_rule: 'Failed to create SoD rule.',
            failed_update_rule: 'Failed to update SoD rule.',
            validation_name_required: 'Name is required.',
            validation_pair_required: 'At least one conflict permission pair is required.',
            validation_pair_both_required: 'Pair {index}: Both permissions must be selected.',
            validation_pair_must_differ: 'Pair {index}: Permission A and Permission B must be different.',
            added_conflict_pair: 'Added conflict pair {index}',
            removed_conflict_pair: 'Removed conflict pair {index}',
            announce_loaded_violations: 'Loaded {count} violations',
            announce_loaded_exceptions: 'Loaded {count} exceptions',
            announce_loaded_rules: 'Loaded {count} SoD rules',
            announce_loading_exceptions: 'Loading exceptions…',
            regulatory_info: {
                sod_context: 'SoD rules enforce regulatory role separation required by PDPL (controller vs processor roles must not be combined). Violations linked to a framework_code can be included in framework-specific evidence packs for cross-framework audit inspection (UC-VN-09). Severity levels distinguish regulatory violations from operational policy violations per fines-risks.json.',
                violation_export_context: 'SoD violation exports include framework_code, conflicting_roles, severity, created_by, and detected_at fields. This data feeds into cross-framework evidence packs reviewed by the Internal Audit Lead (P-VN-10) during audit inspections.',
                exception_context: 'SoD exceptions produce audit-grade evidence: approver identity, justification, compensating controls, and expiry date. Expired exceptions remain visible for audit trail continuity. These records support gap and exception identification in cross-framework audit packs (UC-VN-09 Step 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at. Key lifecycle events (create/revoke/expire) appear in the unified audit trail with entity_type "api_key" for Cybersecurity evidence packs (UC-VN-05 Step 5). Revoked and expired keys remain visible for audit trail continuity.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session timeout, max concurrent sessions, and eviction policy are configurable. Session events (login, logout, timeout, eviction) produce audit trail entries. Active sessions show IP address, user agent, and login timestamp for incident investigation (UC-VN-05 Step 3).',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions requested, duration, approver identity, approval/denial timestamps, and denial reason. Expired and denied records are retained and searchable for cross-framework evidence packs (UC-VN-09 Step 5). Break-glass events appear in the unified audit trail.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS from shared types. Framework-specific modules (e.g. AI Law modules) require appropriate license tiers. Tier upgrade/downgrade events produce audit trail entries with old_tier, new_tier, changed_by, and timestamp for compliance evidence.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness. Framework dependency indicators help the Enterprise DPO (P-VN-01) understand which modules are required for installed framework packs.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates, authority response deadlines) are marked with a compliance_critical flag. These notifications cannot be fully suppressed to ensure regulatory deadline adherence. The notification queue shows pending, sent, and failed status with compliance context.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Delegation creation and expiry events appear in the unified audit trail. Expired and revoked delegations remain visible for audit trail continuity, supporting cross-framework evidence packs.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist restricts access with change audit trail, MFA configuration changes are audit-trailed, service accounts show last_used_at/permissions/status lifecycle, and SSO provider changes produce audit trail entries. All security settings changes are logged for compliance evidence.',
        },
    },
    // ---------- CMP Integration pages (Sprint 11.3.0) ----------
    cmp: {
        integration: {
            title: 'CMP Integration',
            status: 'Connection Status',
            endpoint: 'CMP Endpoint',
            last_sync: 'Last Sync',
            configure: 'Configure',
        },
        purpose_mapping: {
            title: 'Purpose Mapping',
            cmp_purpose: 'CMP Purpose',
            grc_purpose: 'GRC Purpose',
            mapping_status: 'Mapping Status',
            auto_map: 'Auto Map',
            new_mapping: 'New Mapping',
        },
        webhook: {
            title: 'Webhook Configuration',
            webhook_url: 'Webhook URL',
            secret: 'Webhook Secret',
            events: 'Event Types',
            test_webhook: 'Test Webhook',
        },
        bulk_sync: {
            title: 'Bulk Sync',
            sync_now: 'Sync Now',
            last_run: 'Last Run',
            records_synced: 'Records Synced',
            sync_history: 'Sync History',
            syncing: 'Syncing...',
        },
        sync_settings: {
            sync_failures: 'Sync Failures',
            sync_secret_warning: 'Sync Secret (save this now — it will not be shown again):',
            push_description: 'Configure the GRC Suite to push case status updates to AesirX CMP.',
            technical_description: 'This configuration controls how the GRC Suite pushes case status updates to AesirX CMP.',
            direction_label: 'Direction',
            direction_value: 'GRC Suite → AesirX CMP (outbound)',
            auth_label: 'Authentication',
            auth_value: 'HMAC-SHA256 signature in X-GRC-Signature header',
            retry_label: 'Retry Policy',
            retry_value: '3 retries with exponential backoff (30s, 2min, 10min)',
            inbound_label: 'Inbound Config',
            inbound_value: 'Inbound webhook (CMP → GRC) is configured in',
            cmp_webhook_settings: 'CMP Webhook Settings',
            page_title: 'CMP Sync Settings',
            outbound_sync_configured: 'Outbound sync configured.',
            configure_sync_failed: 'Failed to configure sync',
            configuration_updated: 'Configuration updated.',
            update_configuration_failed: 'Failed to update configuration',
            connection_test_failed: 'Connection test failed',
            test_result_message: 'Test sent to {endpoint}. Status: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'New → Under Review (assigned)',
                under_review_to_identity_verification: 'Under Review → Identity Verification',
                under_review_to_in_progress: 'Under Review → In Progress',
                in_progress_to_completed: 'In Progress → Completed',
                in_progress_to_denied: 'In Progress → Denied',
                any_to_closed: 'Any → Closed',
            },
        },
    },
    // ---------- Settings pages (Sprint 11.3.0) ----------
    settings_pages: {
        webhooks: {
            title: 'Webhooks',
            new: 'New Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Events',
            secret: 'Secret',
            active: 'Active',
            test: 'Test',
            create_failed: 'Failed to create webhook.',
            create_success: 'Webhook created successfully.',
            deactivate_failed: 'Failed to deactivate webhook.',
            deactivate_success: 'Webhook deactivated.',
            fetch_failed: 'Failed to fetch webhooks.',
            send_test_failed: 'Failed to send test.',
            test_failed: 'Webhook test failed.',
            test_success: 'Webhook test succeeded.',
            edit_title: 'Edit Webhook',
            announce_loaded: 'Webhooks loaded',
            update_success: 'Webhook updated successfully.',
            update_failed: 'Failed to update webhook.',
            activate_success: 'Webhook activated.',
            activate_failed: 'Failed to activate webhook.',
        },
        domains: {
            title: 'Domains',
            new: 'New Domain',
            domain_name: 'Domain Name',
            verified: 'Verified',
            verify: 'Verify',
            add_failed: 'Failed to add domain.',
            add_success: 'Domain added successfully.',
            domain_placeholder: 'e.g., grc.example.com',
            fetch_failed: 'Failed to fetch domains.',
            remove_failed: 'Failed to remove domain.',
            remove_success: 'Domain removed successfully.',
            txt_record: 'TXT Record',
            verify_dns_failed: 'DNS verification failed.',
            verify_failed: 'Failed to verify domain.',
            verify_success: 'Domain verified successfully.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Export Schedules',
            new: 'New Schedule',
            schedule_name: 'Name',
            frequency: 'Frequency',
            format: 'Format',
            last_run: 'Last Run',
            next_run: 'Next Run',
            create_failed: 'Failed to create schedule.',
            create_success: 'Schedule created successfully.',
            day_of_month: 'Day of Month',
            day_of_week: 'Day of Week',
            deactivate_failed: 'Failed to deactivate schedule.',
            deactivate_success: 'Schedule deactivated.',
            fetch_failed: 'Failed to fetch schedules.',
            freq_daily: 'Daily',
            freq_monthly: 'Monthly',
            freq_weekly: 'Weekly',
            recipients: 'Recipients',
            time_utc: 'Time (UTC)',
            trigger_failed: 'Failed to trigger export.',
            trigger_success: 'Export triggered successfully.',
            type_audit_trail: 'Audit Trail',
            type_compliance_report: 'Compliance Report',
            type_custom: 'Custom',
            type_incident_report: 'Incident Report',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'Email Log',
            recipient: 'Recipient',
            subject: 'Subject',
            sent_at: 'Sent At',
            status: 'Status',
            resend: 'Resend',
            fetch_failed: 'Failed to fetch email logs.',
            announce_loaded: 'Email log loaded',
        },
    },
    // ---------- Phase 9 Extension: Organization Management ----------
    organizations: {
        title: 'Organizations',
        create: 'Create Organization',
        edit: 'Edit Organization',
        name: 'Name',
        slug: 'Slug',
        timezone: 'Timezone',
        locale: 'Default Locale',
        parent: 'Parent Organization',
        group: 'Group',
        org_group: 'Organization Group',
        no_group: '(None - not in a group)',
        created: 'Created',
        no_parent: '(None - standalone)',
        no_orgs: 'No organizations found',
        load_error: 'Failed to load organizations',
        save_error: 'Failed to save organization',
        created_msg: 'Organization created',
        updated: 'Organization updated',
        deactivate: 'Deactivate',
        deactivated: 'Organization deactivated',
        confirm_deactivate: 'Are you sure you want to deactivate this organization?',
        loaded_results: 'Organizations loaded, {count} results',
        form_loaded: 'Organization form loaded',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'English (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'All Frameworks',
        assigned_at: 'Assigned At',
    },
    // ---------- Phase 9 Extension: Public Intake Configs ----------
    intake_configs: {
        title: 'Public Intake Configs',
        create: 'Create Config',
        edit: 'Edit Intake Config',
        label_col: 'Label',
        key: 'Intake Key',
        key_hint: 'Public URL will be: /rights-request?key=',
        mapped_org: 'Target Organization',
        status: 'Status',
        enabled: 'Enabled',
        disabled: 'Disabled',
        request_types: 'Enabled Request Types',
        language: 'Default Language',
        public_url: 'Public URL',
        no_configs: 'No intake configs found. Create one to enable public rights request forms.',
        load_error: 'Failed to load intake configs',
        save_error: 'Failed to save',
        delete_error: 'Failed to delete',
        created_msg: 'Config created',
        updated: 'Config updated',
        deleted: 'Intake config deleted',
        confirm_delete: 'Delete this intake config?',
        select_types: 'Select at least one request type',
        select_org: 'Select a target organization',
        routing_mode: 'Routing Mode',
        single_org: 'Single Organization',
        org_group_mode: 'Organization Group',
        org_group: 'Organization Group',
        select_group: 'Select an organization group...',
        group_root_hint: 'Auto-set to the group root organization when group is selected.',
        loaded_results: 'Intake configurations loaded, {count} results',
        form_loaded: 'Intake configuration form loaded',
    },
    // ---------- Frequency labels ----------
    frequency: {
        weekly: 'Weekly',
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        semi_annually: 'Semi-Annually',
        annually: 'Annually',
        custom: 'Custom',
    },
    // ---------- Task Management module (Sprint 17.4.0) ----------
    /**
     * Sprint 65.6.0 — DT-01 training and awareness.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity plans and exercises (toolkit row IRP-01). */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /**
     * 2026-09-24 owner ruling (legacy label) — the frame `DraftInstrumentNotice`
     * renders over a value whose declaring framework is `legacy` rather than
     * `draft`. Both are "duties not owed" to `frameworkDutiesAreOwed`, and the
     * draft frame ("not in force", "not an obligation") is wrong for an
     * instrument that has lapsed or been replaced while a transition rule keeps
     * its results. Generic by design (R-46): `legacy` also covers a draft
     * replaced by its enacted text, so nothing here says WHY the instrument
     * ended — only that it did. Rendered as text, never colour alone (WCAG 1.4.1).
     */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (toolkit row ANON-01). */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Calendar',
            all_tasks: 'All Tasks',
            my_tasks: 'My Tasks',
            overdue: 'Overdue',
            board: 'Board',
            workflows: 'Workflows',
        },
        list: {
            title: 'All Tasks',
            my_title: 'My Tasks',
            overdue_title: 'Overdue Tasks',
            new_task: 'Create Task',
            no_tasks: 'No tasks found.',
            empty_state: 'No tasks found. Tasks are generated from module workflows like assessments, incidents, and review campaigns.',
            total: 'Total Tasks',
            search_placeholder: 'Search tasks...',
            bulk_actions: 'Bulk Actions',
            change_status: 'Change Status',
            change_priority: 'Change Priority',
            assign: 'Assign',
            sr_my_tasks_count: '{count} tasks assigned to you',
            sr_days_overdue: '{count} days overdue',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Task Detail',
            description: 'Description',
            workflow_progress: 'Workflow Progress',
            checklist: 'Checklist',
            dependencies: 'Dependencies',
            comments: 'Comments',
            activity_timeline: 'Activity Timeline',
            assignees: 'Assignees',
            add_assignee: 'Add Assignee',
            remove_assignee: 'Remove Assignee',
            due_date: 'Due Date',
            start_date: 'Start Date',
            sla_status: 'SLA Status',
            sla_on_track: 'On track',
            sla_at_risk: 'At risk',
            sla_breached: 'Breached',
            tags: 'Tags',
            source_entity: 'Source Entity',
            view_source: 'View Source',
            add_dependency: 'Add Dependency',
            remove_dependency: 'Remove Dependency',
            write_comment: 'Write a comment...',
            post_comment: 'Post',
            edit_comment: 'Edit',
            delete_comment: 'Delete',
            add_checklist_item: 'Add item',
            no_dependencies: 'No dependencies',
            no_comments: 'No comments yet',
            blocks: 'Blocks',
            blocked_by: 'Blocked by',
            related: 'Related',
            working_days_remain: 'working days remain',
            created_by: 'Created by',
            completed_at: 'Completed at',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'No assignees',
            add_assignee_placeholder: 'Enter user ID...',
            edited: 'edited',
            timeline: 'Activity Timeline',
            no_timeline: 'No activity yet',
            timeline_reason: 'Reason',
            no_workflow: 'No workflow assigned',
            add_dependency_placeholder: 'Enter task ID...',
            sr_status_updated: 'Status updated to {status}',
            sr_priority_updated: 'Priority updated to {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            // Sprint 47.5.8.5 — task → workflow-run conversion + linked chip
            convert_to_workflow: 'Run as Workflow',
            convert_modal_title: 'Convert task to workflow run',
            convert_workflow_label: 'Workflow',
            convert_workflow_placeholder: 'Pick a workflow…',
            convert_attachments_label: 'Attachments',
            convert_attachments_empty: 'No attachments on this task.',
            convert_persona_label: 'Persona override (optional)',
            convert_submit: 'Start workflow run',
            convert_starting: 'Starting workflow run…',
            convert_started: 'Workflow run started — opening run…',
            convert_disabled_in_flight: 'A workflow run from this task is already in progress.',
            linked_workflow_chip: 'From {workflow_title}',
            linked_chat_chip: 'From AI Chat',
            linked_more: '+{count} more',
        },
        calendar: {
            title: 'Task Calendar',
            page_title: 'Calendar',
            create_task: 'Create Task',
            month: 'Month',
            week: 'Week',
            day: 'Day',
            today: 'Today',
            more_tasks: '+{count} more',
            no_tasks_day: 'No tasks this day',
            all_day: 'All day',
            quick_create_tooltip: 'Create task for this day',
            view_changed: 'Calendar view changed to {view}',
            tasks_in_view: '{count} tasks in view',
            task_in_view: '{count} task in view',
            filter_my_tasks: 'My tasks',
            filter_overdue: 'Overdue',
            filter_high_priority: 'High priority',
            filter_all: 'All',
        },
        filters: {
            assignee: 'Assignee',
            me: 'Me',
            role: 'Role',
            status: 'Status',
            priority: 'Priority',
            task_type: 'Task Type',
            source_module: 'Source Module',
            organization: 'Organization',
            sla_breached_only: 'Show only SLA breached',
            clear_filters: 'Clear Filters',
            apply_filters: 'Apply',
            // Sprint 47.5.8.5 — source-kind chip on TaskListPage
            source_label: 'Source',
            source_manual: 'Manual',
            source_module_kind: 'Module-generated',
            source_chat: 'From AI Chat',
            source_workflow_run: 'From AI Workflow Run',
        },
        status: {
            pending: 'Pending',
            in_progress: 'In Progress',
            blocked: 'Blocked',
            completed: 'Completed',
            overdue: 'Overdue',
            escalated: 'Escalated',
            cancelled: 'Cancelled',
        },
        priority: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        task_types: {
            manual_task: 'Manual Task',
            dsr_case: 'DSR Case',
            dsr_review: 'DSR Review',
            dsr_escalation: 'DSR Escalation',
            dpia_review: 'DPIA Review',
            dpia_approval: 'DPIA Approval',
            transfer_review: 'Transfer Review',
            incident_task: 'Incident Task',
            incident_notification: 'Incident Notification',
            playbook_step: 'Playbook Step',
            regulator_deadline: 'Regulator Deadline',
            monitoring_task: 'Monitoring Task',
            kpi_alert: 'KPI Alert',
            vendor_review: 'Vendor Review',
            vendor_questionnaire: 'Vendor Questionnaire',
            governance_review: 'Governance Review',
            drift_alert: 'Drift Alert',
            governance_approval: 'Governance Approval',
            deletion_task: 'Deletion Task',
            deletion_verification: 'Deletion Verification',
            access_review: 'Access Review',
            sod_alert: 'SoD Alert',
            consent_workflow: 'Consent Workflow',
            consent_alert: 'Consent Alert',
            property_review: 'Property Review',
            inconsistency_alert: 'Inconsistency Alert',
            form_review: 'Form Review',
            dossier_deadline: 'Dossier Deadline',
            submission_task: 'Submission Task',
            translation_review: 'Translation Review',
            governance_task: 'Governance Task',
            reporting_deadline: 'Reporting Deadline',
            scanner_alert: 'Scanner Alert',
            classification_review: 'Classification Review',
            lifecycle_task: 'Lifecycle Task',
            discovery_review: 'Discovery Review',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Training Assignment',
        task_type_groups: {
            training: 'Training',
            manual: 'Manual',
            dsr: 'DSR / Rights Requests',
            assessments: 'Assessments',
            incidents: 'Incidents',
            incident_ops: 'Incident Ops',
            monitoring: 'Monitoring',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion: 'Deletion',
            access_review: 'Access Review',
            consent: 'Consent',
            consent_properties: 'Consent Properties',
            forms: 'Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Classification',
            discovery: 'Discovery',
            forseti: 'Forseti',
        },
        create: {
            title: 'Create Task',
            task_title: 'Title',
            task_description: 'Description',
            task_type: 'Task Type',
            task_type_label: 'Task Type',
            task_type_placeholder: 'Select a task type',
            priority: 'Priority',
            assignees: 'Assignees',
            assignee_search_placeholder: 'Search users...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Remove {name}',
            due_date: 'Due Date',
            start_date: 'Start Date',
            checklist: 'Checklist',
            tags: 'Tags',
            add_checklist_item: 'Add checklist item',
            validation: {
                title_required: 'Title is required',
                title_max_length: 'Title must be 500 characters or fewer',
                description_max_length: 'Description must be 5000 characters or fewer',
                due_date_before_start: 'Due date must be after start date',
                max_reminders: 'Maximum 5 reminders per task',
            },
            reminders: {
                title: 'Reminders',
                add: 'Add Reminder',
                at_due_date: 'At due date',
                minutes_before: '{count} minutes before',
                hours_before: '{count} hours before',
                days_before: '{count} days before',
                weeks_before: '{count} weeks before',
                custom: 'Custom',
                channel_platform: 'Platform',
                channel_email: 'Email',
                channel_both: 'Both',
                offset_label: 'Reminder {n} timing',
                channel_label: 'Reminder {n} channel',
                remove_label: 'Remove reminder {n}',
            },
            workflow: {
                label: 'Workflow',
                none: 'None (manual status management)',
                steps_count: '{count} steps',
                preview_label: 'Workflow Steps',
            },
        },
        errors: {
            fetch_failed: 'Failed to load tasks.',
            save_failed: 'Failed to save task.',
            delete_failed: 'Failed to delete task.',
            status_change_failed: 'Failed to change task status.',
            assign_failed: 'Failed to assign task.',
            comment_failed: 'Failed to save comment.',
            checklist_failed: 'Failed to update checklist.',
            dependency_failed: 'Failed to update dependency.',
            workflow_failed: 'Failed to save workflow.',
            workflow_activate_failed: 'Failed to activate workflow.',
            workflow_delete_failed: 'Failed to delete workflow.',
            template_install_failed: 'Failed to install template.',
            search_failed: 'Failed to search tasks.',
            // Sprint 47.5.8.5
            convert_failed: 'Could not start the workflow run.',
            convert_quota_exhausted: 'Token bucket low. Top up before re-running.',
        },
        dependency_types: {
            blocks: 'Blocks',
            blocked_by: 'Blocked by',
            related: 'Related',
        },
        workflow: {
            current_step: 'Current Step',
            transition: 'Transition',
            history: 'Step History',
            no_workflow: 'No workflow assigned',
            detail_title: 'Workflow Details',
            list_title: 'Workflow Definitions',
            create_workflow: 'Create Workflow',
            browse_templates: 'Browse Templates',
            template_catalog_title: 'Workflow Template Catalog',
            name: 'Name',
            description: 'Description',
            task_types: 'Task Types',
            steps: 'Steps',
            version: 'Version',
            created: 'Created',
            actions: 'Actions',
            status_draft: 'Draft',
            status_active: 'Active',
            status_archived: 'Archived',
            activate: 'Activate',
            archive: 'Archive',
            save: 'Save',
            edit: 'Edit',
            delete: 'Delete',
            clone: 'Clone',
            install: 'Install',
            installed: 'Installed',
            system: 'System',
            general_settings: 'General Settings',
            default_assignment: 'Default Assignment',
            default_sla: 'Default SLA',
            workflow_steps: 'Workflow Steps',
            add_step: 'Add Step',
            step_name: 'Step Name',
            step_status: 'Status Value',
            assignment_strategy: 'Assignment Strategy',
            assignment_role: 'Assignment Role',
            sla_hours: 'SLA Hours',
            sla_calendar_type: 'Calendar Type',
            working_days: 'Working Days',
            calendar_days: 'Calendar Days',
            is_terminal: 'Terminal Step',
            required_fields: 'Required Fields',
            checklist_template: 'Checklist Template',
            transitions: 'Transitions',
            add_transition: 'Add Transition',
            from_step: 'From',
            to_step: 'To',
            permission: 'Permission',
            sla_action: 'SLA Action',
            notify: 'Notify',
            escalation_rules: 'Escalation Rules',
            add_rule: 'Add Rule',
            trigger: 'Trigger',
            threshold: 'Threshold',
            action: 'Action',
            target_role: 'Target Role',
            trigger_sla_threshold: 'SLA Threshold',
            trigger_sla_breach: 'SLA Breach',
            trigger_blocked_duration: 'Blocked Duration',
            trigger_manual: 'Manual',
            action_notify: 'Notify',
            action_escalate: 'Escalate Tier',
            action_reassign: 'Reassign',
            sla_start: 'Start',
            sla_pause: 'Pause',
            sla_resume: 'Resume',
            sla_stop: 'Stop',
            strategy_manual: 'Manual',
            strategy_role_based: 'Role-based',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Least Loaded',
            strategy_creator: 'Creator',
            confirm_delete: 'Are you sure you want to delete this workflow?',
            confirm_archive: 'Are you sure you want to archive this workflow?',
            cannot_delete_active: 'Active workflows cannot be deleted. Archive first.',
            validation_errors: 'Workflow validation errors',
            step_count: '{count} steps',
            no_workflows: 'No workflows found.',
            no_templates: 'No templates available.',
            sr_templates_count: '{count} templates available',
            template_installed: 'Template installed successfully.',
            activate_success: 'Workflow activated successfully.',
            archive_success: 'Workflow archived successfully.',
            save_success: 'Workflow saved successfully.',
            delete_success: 'Workflow deleted successfully.',
            applicable_task_types: 'Applicable Task Types',
            steps_column: 'Steps',
            status_value_hint: 'Workflow state key used by transitions. Lowercase letters, numbers and underscores.',
            from_status: 'From Status',
            to_status: 'To Status',
            required_permissions: 'Required Permissions',
            transition_required_fields: 'Required Fields (transition)',
            auto_assign: 'Auto Assign',
            auto_assign_role: 'Auto Assign Role',
            threshold_percent: 'Threshold (%)',
            blocked_hours: 'Blocked Hours',
            notify_roles: 'Notify Roles',
            escalate_to_role: 'Escalate To Role',
            notification_template: 'Notification Template',
            action_escalate_tier: 'Escalate Tier',
            create_modal_title: 'Create Workflow',
            create_modal_description: 'Define steps, transitions and escalation rules. The workflow is created as a draft.',
            create_modal_footer_note: 'Created as a draft. Activate it once the graph validates.',
            validation_name_required: 'Workflow name is required.',
            validation_task_types_required: 'Select at least one applicable task type.',
            validation_min_one_step: 'Add at least one step.',
            validation_min_one_transition: 'Add at least one transition.',
            validation_step_name_required: 'Step name is required.',
            validation_status_value_required: 'Status value is required.',
            validation_status_value_duplicate: 'Status value must be unique across steps.',
            validation_transition_endpoints_required: 'Select both a from-status and a to-status.',
            validation_transition_unknown_status: 'This transition references a status that no step defines.',
            validation_sla_positive: 'SLA hours must be greater than zero. Leave empty for no SLA.',
            validation_threshold_percent_range: 'Threshold must be between 0 and 100.',
            validation_blocked_hours_positive: 'Blocked hours must be greater than zero.',
            warning_no_terminal_step: 'No terminal step. Activation will fail until one step is marked terminal.',
            warning_step_no_outgoing: 'Non-terminal steps without outgoing transitions: {statuses}',
            warning_unreachable_steps: 'Steps not reachable from the first step: {statuses}',
            orphaned_transitions_warning: '{count} transitions reference a deleted step.',
            remove_orphaned_transitions: 'Remove orphaned transitions',
            transitions_renamed_announcement: '{count} transitions updated to the new status value.',
            add_checklist_item: 'Add checklist item',
            checklist_item_label: 'Checklist item',
        },
        columns: {
            priority: 'Priority',
            title: 'Title',
            status: 'Status',
            type: 'Type',
            assignees: 'Assignee(s)',
            due_date: 'Due Date',
            source: 'Source',
            actions: 'Actions',
        },
        groups: {
            overdue: 'Overdue',
            due_today: 'Due Today',
            due_this_week: 'Due This Week',
            due_later: 'Due Later',
            no_due_date: 'No Due Date',
            closed: 'Completed or Cancelled',
        },
        board: {
            title: 'Task Board',
            drag_hint: 'Drag tasks between columns to change status',
            empty_column: 'No tasks',
            keyboard_hint: 'Keyboard: Arrow keys to navigate, Space to pick up a task, Arrow Left/Right to select column, Enter to drop, Escape to cancel.',
            sr_moving: 'Moving {title}. Over {column}. Press Enter to drop, Escape to cancel.',
            sr_tasks_shown: '{count} tasks shown',
            sr_picked_up: 'Picked up {title}',
            sr_returned: '{title} returned to {column}',
            sr_dropped: 'Dropped {title} in {column}, position {position} of {total}',
            sr_move_cancelled: 'Move cancelled',
            sr_over_column: 'Over {column}',
            sr_moved_to: '{title} moved to {column}',
            invalid_transition: 'Cannot move task from {from} to {to}.',
            terminal_status: 'This task is in a terminal status and cannot be moved.',
            invalid_drop_target: 'Invalid drop target',
        },
        team: {
            title: 'Team Workload',
            assignee: 'Assignee',
            active: 'Active',
            overdue: 'Overdue',
            blocked: 'Blocked',
            completed_7d: 'Completed (7d)',
            load: 'Load',
            load_high: 'High',
            load_medium: 'Medium',
            load_normal: 'Normal',
        },
        graph: {
            title: 'Dependency Graph',
            list_view: 'List View',
            graph_view: 'Graph View',
            zoom_in: 'Zoom In',
            zoom_out: 'Zoom Out',
            reset_zoom: 'Reset',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'No dependencies to visualize.',
        },
        add_dependency_modal: {
            title: 'Add Dependency',
            search_placeholder: 'Search tasks by title or ID...',
            no_results: 'No tasks found.',
            this_blocks: 'This task blocks selected',
            this_blocked_by: 'This task is blocked by selected',
            related: 'Related',
            max_dependencies: 'Maximum 10 dependencies per task.',
            circular_error: 'Cannot add: would create a circular dependency.',
        },
        conflict_detection: {
            title: 'Conflict Detection',
            unresolved: 'unresolved',
            no_conflicts: 'No conflicts detected',
            section: 'Section',
            field: 'Field',
            dept_a: 'Department A',
            value_a: 'Value A',
            dept_b: 'Department B',
            value_b: 'Value B',
            severity: 'Severity',
            status: 'Status',
            resolved: 'Resolved',
            open: 'Open',
            resolve: 'Resolve',
            load_failed: 'Failed to load conflicts',
            resolve_success: 'Conflict resolved successfully',
            resolve_failed: 'Failed to resolve conflict',
            severity_critical: 'Critical',
            severity_warning: 'Warning',
            severity_info: 'Info',
        },
        consolidation: {
            title: 'Consolidation Progress',
            add_child: 'Add Child Task',
            child_task_id: 'Child Task ID',
            department_id: 'Department ID',
            link: 'Link',
            overall_completion: 'Overall Completion',
            departments_complete: 'Departments Complete',
            overdue: 'Overdue',
            ready: 'Ready',
            not_ready: 'Not Ready',
            master_status: 'Master Status',
            department_breakdown: 'Department Breakdown',
            department: 'Department',
            status: 'Status',
            completion: 'Completion',
            due_date: 'Due Date',
            overdue_flag: 'Overdue',
            child_tasks: 'Child Tasks',
            task: 'Task',
            load_failed: 'Failed to load consolidation data',
            child_linked: 'Child task linked successfully',
            link_failed: 'Failed to link child task',
            // fix-sprint-20260411-D F8 — confirm + semantic toast keys for unlink
            confirm_unlink: 'Are you sure you want to unlink this child task?',
            child_unlinked: 'Child task unlinked',
            unlink_failed: 'Failed to unlink child task',
            unlink: 'Unlink',
            status_completed: 'Completed',
            status_in_progress: 'In Progress',
            status_pending: 'Pending',
            select_child_task: 'Select child task',
            select_department: 'Select department',
        },
        section_assignments: {
            title: 'Section Assignments',
            assign_section: 'Assign Section',
            section_ref: 'Section Ref',
            section_label: 'Section Label',
            department_id: 'Department ID',
            department: 'Department',
            assignee_id: 'Assignee ID',
            assignee: 'Assignee',
            mandatory: 'Mandatory',
            assign: 'Assign',
            no_assignments: 'No section assignments',
            status: 'Status',
            completion: 'Completion',
            mark_complete: 'Complete',
            load_failed: 'Failed to load section assignments',
            assign_success: 'Section assigned successfully',
            assign_failed: 'Failed to assign section',
            status_updated: 'Status updated',
            status_update_failed: 'Failed to update status',
            section_ref_placeholder: 'e.g. 3.2',
            status_completed: 'Completed',
            status_in_progress: 'In Progress',
            status_pending: 'Pending',
            select_department: 'Select department',
            select_assignee: 'Select assignee',
        },
        overdue_columns: {
            days_overdue: 'Days Overdue',
        },
        regulatory_info: {
            deadline_context: 'When creating DPIA-related tasks, due dates should align with PDPL filing deadlines: department input = 10 working days (from vn-pdpl/deadlines.json). Overdue flagging triggers when this window is exceeded. Cybersecurity authority response deadlines (48h legal review, 72h response preparation) and Telecom deadlines (5 working days response, 2 working days legal approval) also apply.',
            overdue_deadline_context: 'Regulatory deadlines from pack data: PDPL department input = 10 working days, supplement response = 15 working days, resubmission = 10 working days. Cybersecurity authority response and telecom preparation deadlines are also enforced. SLA-breached tasks are visually distinguished.',
            section_assignment_context: 'DPIA multi-department workflow (PDPL-P1): the Enterprise DPO assigns dossier sections to departments (e.g. Retail Banking, HR, Marketing). Each assignment captures section_ref, section_label, department, assignee, mandatory flag, and completion percentage. Department Data Owners see only their assigned sections in My Tasks. Department input deadline: 10 working days (configurable per vn-pdpl/deadlines.json).',
            template_catalog_context: 'System workflow templates cover framework-specific compliance workflows: PDPL DPIA filing, Data Law annual governance attestation, Cybersecurity incident response, Telecom authority request, and AI system registration. Each template includes step_count, sla_summary, and task_types aligned with pack procedure data. System templates (is_system = true) are non-editable to preserve regulatory procedure integrity.',
            workflow_instantiation_context: 'Installed workflow templates can be instantiated into live workflows from this page. When instantiated, tasks are automatically created with due dates calculated from the workflow SLA and the current date. Active workflows enforce step-by-step status transitions via the task board. System templates from the catalog are non-editable to preserve regulatory procedure integrity.',
        },
        overdue: {
            days_overdue: 'Days Overdue',
        },
    },
    // ---------- Sprint 17.3.0: Notifications ----------
    notifications: {
        bell: {
            title: 'Notifications',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Notifications',
            mark_all_read: 'Mark all as read',
            no_notifications: 'No notifications',
            filter: {
                all: 'All',
                unread: 'Unread',
                tasks: 'Tasks',
                approvals: 'Approvals',
                alerts: 'Alerts',
            },
        },
        preferences: {
            title: 'Notification Preferences',
            description: 'Configure how you receive notifications',
            category: {
                label: 'Category',
                task_assigned: 'Task Assigned',
                task_sla_approaching: 'SLA Approaching',
                task_overdue: 'Task Overdue',
                task_completed: 'Task Completed',
                task_escalated: 'Task Escalated',
                workflow_transition: 'Workflow Transition',
                approval_requested: 'Approval Requested',
                comment_added: 'Comment Added',
                weekly_digest: 'Weekly Digest',
                sla_breach_report: 'SLA Breach Report',
                system_alert: 'System Alert',
                rights_request_received: 'New Rights Request',
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Platform',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Quiet Hours',
                start: 'Start',
                end: 'End',
                timezone: 'Timezone',
            },
            digest: {
                title: 'Digest Frequency',
                daily: 'Daily',
                weekly: 'Weekly',
                none: 'None',
            },
            watched: {
                title: 'Watched Entities',
                empty: 'No watched entities',
            },
            save: 'Save Preferences',
            saving: 'Saving...',
            saved_success: 'Preferences saved successfully',
            load_error: 'Could not load saved preferences. Showing defaults \u2014 changes will be saved when you click Save.',
            enabled: 'Enabled',
            disabled: 'Disabled',
            unwatch: 'Unwatch',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Notification Admin Queue',
            description: 'Monitor email delivery status and retry failed messages',
            load_error: 'Failed to load email queue data',
            retry_error: 'Failed to retry email',
            filter_status: 'Filter by status',
            all_statuses: 'All',
            refresh: 'Refresh',
            status_queued: 'Queued',
            status_sent: 'Sent',
            status_failed: 'Failed',
            status_bounced: 'Bounced',
            status_sending: 'Sending',
            col_recipient: 'Recipient',
            col_category: 'Category',
            col_template: 'Template',
            col_status: 'Status',
            col_retries: 'Retries',
            col_created: 'Created',
            col_sent: 'Sent At',
            col_actions: 'Actions',
            no_items: 'No email queue items found',
            retry: 'Retry',
            retrying: 'Retrying...',
            prev: 'Prev',
            next: 'Next',
            access_denied_title: 'Admin Access Required',
            access_denied: 'This page is only available to administrators. Please contact your admin if you need access.',
            announce_queue_loaded: 'Email queue loaded, {count} items',
            announce_processed: 'Processed {count} email(s)',
            process_now: 'Process Now',
            processing_ellipsis: 'Processing…',
        },
    },
    // ---------- UI Assist (Sprint 17.8.0) ----------
    ui_assist: {
        toolbar: {
            title: 'UI Assist',
            activate: 'Activate',
            deactivate: 'Deactivate',
            connected: 'Connected',
            disconnected: 'Disconnected',
            disconnected_hint: 'Disconnected — run `npm run relay` to connect to Claude CLI',
            open_panel: 'Open UI Assist panel',
            history: 'History',
            settings: 'Settings',
        },
        prompt: {
            placeholder: 'Describe the change you want to make…',
            send: 'Send',
            cancel: 'Cancel',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Processing…',
            complete: 'Complete',
            error: 'Error',
            abort: 'Abort',
            abort_confirm: 'Are you sure you want to abort?',
            aborted_hint: 'Session was interrupted — you can continue from here.',
            undo: 'Undo',
            redo: 'Redo',
            retry: 'Retry',
            dismiss: 'Dismiss',
        },
        label: {
            component: 'Component',
            element: 'Element',
            file: 'File',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'No messages yet. Select elements and send a prompt to get started.',
        },
        panel: {
            tab_conversation: 'Chat',
            tab_tasks: 'Tasks',
            tab_sprint: 'Sprint',
            tab_sessions: 'Sessions',
            tab_lobby: 'Lobby',
            follow_up: 'Follow Up',
            followup_placeholder: 'Ask a follow-up question…',
            interrupt_placeholder: 'Type to interrupt and redirect…',
            send_interrupt: 'Send & Interrupt',
            will_interrupt: 'Will abort current response and send this instead',
            expand: 'Expand to full screen',
            collapse: 'Collapse to side panel',
            no_sessions: 'No previous sessions. Select elements and send a prompt to start.',
            session_active: 'Active',
            session_ended: 'Ended',
            session_messages: '{count} messages',
            switch_session: 'Switch to this session',
            load_session: 'Load session history',
            session_restored: 'Session restored from history',
            archive_session: 'Archive session',
            archive_confirm: 'Archive this session? The session file will be deleted.',
            new_session: 'New Session',
            new_session_desc: 'Use Activate to select a UI element and start a new session',
            start_new_session: 'Start New Session',
            no_active_session_chat: 'Start a new session or use Activate to select a UI element on the page to begin.',
            no_active_session_tasks: 'Start a new session or select an existing session to create tasks.',
            no_active_session_sprint: 'Start a new session or select an existing session to plan a sprint.',
            previous_sessions: 'Previous sessions',
        },
        tasks: {
            none_found: 'No tasks found in the response. Ask Claude to suggest tasks.',
            selected: 'selected',
            create_selected: 'Create Tasks',
            execute_tasks: 'Execute Tasks',
            creating: 'Creating…',
            created: 'tasks created',
            create_error: 'Failed to create task',
            plan_sprint: 'Plan Sprint',
            priority_critical_desc: 'Blocker — must fix immediately',
            priority_high_desc: 'Important — address this sprint',
            priority_medium_desc: 'Normal — plan for upcoming sprint',
            priority_low_desc: 'Nice to have — backlog candidate',
            add_custom: 'Add Task',
            add_title_placeholder: 'Task title…',
            add_desc_placeholder: 'Description (optional)',
            add_save: 'Add',
            add_cancel: 'Cancel',
        },
        sprint: {
            title: 'Sprint Planner',
            assigned: 'assigned',
            new_placeholder: 'Sprint name (e.g. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Backlog',
            move_to: 'Move to…',
            empty: 'Create a sprint group and assign tasks to it.',
            create_all: 'Create All Tasks',
            remove: 'Remove',
            drop_here: 'Assign tasks from the backlog below',
            add_task: 'Add task from backlog…',
        },
        feature_disabled: 'UI Assist is not enabled',
        permission_denied: 'You do not have permission to use UI Assist',
    },
    // ---------- Sprint 18.0.0: License & Package Management ----------
    license: {
        title: 'License & Package',
        current_license: 'Current License',
        license_key: 'License Key',
        activate: 'Activate License',
        deactivate: 'Deactivate License',
        validate_now: 'Validate Now',
        status: 'License Status',
        active: 'Active',
        expired: 'Expired',
        suspended: 'Suspended',
        invalid: 'Invalid',
        pending: 'Pending',
        no_license: 'No License',
        expires_at: 'Expires',
        last_validated: 'Last Validated',
        domain: 'Licensed Domain',
        activation_success: 'License activated successfully.',
        activation_failed: 'License activation failed.',
        domain_mismatch: 'License domain does not match the current domain.',
        deactivation_success: 'License deactivated.',
        validation_success: 'License validated successfully.',
        validation_failed: 'License validation failed.',
        enter_key: 'Enter your license key',
        confirm_deactivate: 'Are you sure you want to deactivate this license? Module restrictions will apply based on the default tier.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Package',
        module_column: 'Module',
        current_tier: 'Current Package',
        light: 'Light',
        standard: 'Standard',
        enterprise_unlimited: 'Enterprise Unlimited',
        change_package: 'Change Package',
        upgrade: 'Upgrade',
        downgrade: 'Downgrade',
        target_tier: 'Target Package',
        new_license_key: 'License Key for Target Package',
        confirm_change: 'Confirm Package Change',
        change_success: 'Package changed successfully.',
        change_failed: 'Package change failed.',
        modules_included: 'Modules Included',
        modules_restricted: 'Not Available in Current Package',
        upgrade_to_enable: 'Upgrade to enable this module.',
        module_availability: 'Module Availability',
        comparison: 'Package Comparison',
        power_users: 'Power Users',
        unlimited: 'Unlimited',
        impact_warning: 'Downgrading will disable the following modules:',
        invalid_license: 'The provided license key is not valid for this package.',
        tier_mismatch: 'The license key is for a different package tier.',
    },
    // ---------- Sprint 18.2.0: Board Report UX ----------
    board_report: {
        title: 'Board Compliance Report',
        subtitle: 'Executive compliance summary across all modules',
        date_from: 'Date From',
        date_to: 'Date To',
        language: 'Report Language',
        generate: 'Generate Report',
        generating: 'Generating Report...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Download PDF',
        close: 'Close Report',
        open_board_report: 'Generate Board Report',
        close_board_report: 'Close Board Report',
        report_period: 'Report Period',
        generated_at: 'Generated At',
        generated_by: 'Generated By',
        organization: 'Organization',
        section_rights_requests: 'Rights Requests',
        section_vendors: 'Vendors',
        section_dpias: 'DPIAs',
        section_consent: 'Consent Policies',
        section_data_mapping: 'Data Mapping',
        section_incidents: 'Incidents',
        section_classification: 'Data Classification',
        section_governance: 'Governance',
        open: 'Open',
        closed: 'Closed',
        sla_compliance: 'SLA Compliance',
        total: 'Total',
        expired_dpa: 'Expired DPA',
        high_risk: 'High Risk',
        completed: 'Completed',
        overdue: 'Overdue',
        policies_active: 'Active Policies',
        coverage: 'Coverage',
        systems: 'Systems',
        completeness: 'Completeness',
        avg_resolution_days: 'Avg. Resolution (days)',
        labeled: 'Labeled',
        unlabeled_systems: 'Unlabeled Systems',
        compliance_score: 'Compliance Score',
        drift_events_open: 'Open Drift Events',
        no_data: 'No data available for the selected period.',
        error_date_range: 'Please select both start and end dates.',
        table_category: 'Category',
        table_metric: 'Metric',
        table_value: 'Value',
        showing_chart_view: 'Showing chart view',
        showing_table_view: 'Showing data table view',
        error_generate: 'Failed to generate board report.',
        // Sprint 41.2.0 E5 — playbook execution evidence summary
        playbook_evidence_title: 'Playbook Execution Evidence',
        playbook_evidence_description: 'Per-playbook execution activity and attached evidence files for the reporting period.',
        playbook_name: 'Playbook',
        playbook_execution_count: 'Executions',
        playbook_steps_total: 'Steps',
        playbook_steps_required: 'Evidence Required',
        playbook_steps_attached: 'Evidence Attached',
        playbook_coverage_pct: 'Coverage',
        playbook_evidence_files: 'Evidence Files',
        playbook_no_evidence: 'No playbook executions in this period.',
        playbook_download_file: 'Download file',
    },
    framework_module_links: {
        title: 'Framework Linkage',
        modules: 'Linked Modules',
        add_module: 'Add Module',
        module_code: 'Module Code',
        required: 'Required',
        notes: 'Notes',
        link: 'Link',
        no_modules: 'No modules linked',
        personas: 'Linked Personas',
        add_persona: 'Add Persona',
        persona_id: 'Persona ID',
        role_description: 'Role Description',
        no_personas: 'No personas linked',
    },
    framework_channels: {
        title: 'Submission Channels & Deadlines',
        channels: 'Submission Channels',
        add_channel: 'Add Channel',
        channel_type: 'Type',
        name: 'Name',
        instructions: 'Instructions',
        active: 'Active',
        no_channels: 'No submission channels configured',
        portal: 'Portal',
        in_person: 'In Person',
        postal: 'Postal',
        email: 'Email',
        add: 'Add',
        deadlines: 'Regulatory Deadlines',
        add_deadline: 'Add Deadline',
        label: 'Label',
        days: 'Days',
        day_type: 'Day Type',
        calendar_days: 'Calendar Days',
        working_days: 'Working Days',
        description: 'Description',
        no_deadlines: 'No deadlines configured',
    },
    regulatory_updates: {
        title: 'Regulatory Updates & Framework Dashboard',
        coverage_dashboard: 'Framework Coverage Dashboard',
        no_frameworks: 'No frameworks found',
        framework: 'Framework',
        status: 'Status',
        modules: 'Modules',
        templates: 'Templates',
        forms: 'Forms',
        channels: 'Channels',
        deadlines: 'Deadlines',
        pending: 'Pending Updates',
        updates: 'Regulatory Updates',
        create_update: 'Create Update',
        framework_id: 'Framework ID',
        update_title: 'Title',
        description: 'Description',
        effective_date: 'Effective Date',
        create: 'Create',
        no_updates: 'No regulatory updates',
        impact: 'Impact',
        assess: 'Assess',
    },
    vehicle_monitoring: enVehicleMonitoring,
    record_links: enRecordLinks,
    module_tips: enModuleTips,
    framework_rights: {
        be_informed: 'Right to be informed',
        consent: 'Right to consent',
        refuse_consent: 'Right to refuse consent',
        withdraw_consent: 'Right to withdraw consent',
        access: 'Right of access',
        rectification: 'Right to rectification',
        data_provision: 'Right to data provision',
        deletion: 'Right to deletion',
        restriction_of_processing: 'Right to restriction of processing',
        object_to_processing: 'Right to object to processing',
        complaint: 'Right to lodge a complaint',
        denunciation: 'Right to denunciation',
        lawsuit: 'Right to bring legal action',
        claim_damages: 'Right to claim damages',
        request_protection_measures: 'Right to request protection measures',
    },
    // ---------- Framework Content i18n (Sprint 30.1.0) ----------
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Directive 2002/58/EC (ePrivacy Directive)',
            description: 'Parent directive for national ePrivacy transpositions. Covers cookies, electronic communications privacy, direct marketing. Each member state must transpose into national law.',
        },
        EU_GDPR: {
            display_name: 'General Data Protection Regulation (EU) 2016/679',
            description: 'The General Data Protection Regulation (GDPR) is an EU regulation on data protection and privacy. It applies directly in all EU/EEA member states.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Directive (EU) 2022/2555 (NIS2)',
            description: 'Parent directive for national NIS2 transpositions. Covers cybersecurity risk management, incident reporting, supply chain security for essential and important entities.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnam AI Law (134/2025/QH15) — Effective 01 Mar 2026',
            description: 'Vietnam AI Law (Luật Trí tuệ nhân tạo 134/2025/QH15) governing the development, deployment, and use of artificial intelligence systems. Establishes risk classification, transparency and labeling duties, incident reporting requirements, impact assessment obligations, and a controlled testing (sandbox) mechanism for AI activities in Vietnam.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnam Cybersecurity Law (24/2018 era)',
            description: "Vietnam's Law on Cybersecurity (Law No. 24/2018/QH14) establishing requirements for cybersecurity, data localization, and incident reporting. Active until superseded by Law 116/2025/QH15 effective 2026-07-01.",
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Decree 53 — Cybersecurity Law Implementation',
            description: 'Decree 53/2022/ND-CP implementing the Cybersecurity Law, covering in-scope determination, data localization triggers, local presence requirements, and authority cooperation workflows.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Law on Cybersecurity (116/2025/QH15)',
            description: "Vietnam's consolidated Law on Cybersecurity (Law 116/2025/QH15), replacing Law 24/2018/QH14, effective July 1, 2026. Consolidates scope of Decree 53/2022/NĐ-CP (see VN_CYBERSEC_DECREE_53).",
        },
        VN_DATA_LAW: {
            display_name: 'Vietnam Data Law',
            description: "Vietnam's Data Law (Law 60/2024/QH15) establishing comprehensive data governance requirements including data inventory, classification, sharing governance, and cross-border data transfer governance. Overlaps with VN_PDPL_LAW_2025 (personal data aspects) and VN_CYBERSEC_LAW_2025 (data localization/classification).",
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Decree 165/2025/NĐ-CP (Data Law Implementation)',
            description: 'Decree 165/2025/NĐ-CP implementing the Vietnam Data Law (Law 60/2024/QH15). Provides official forms for cross-border data transfer impact assessment, annual risk assessment reporting, and national database access. Defines 11 official Mẫu số forms.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Draft Decree on Data Exchange Operations 2026',
            description: 'Draft decree on the operation of data exchange platforms (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Regulates platform classification, participant eligibility, data product and service listing controls, sandbox testing, transaction workflow, electronic contracting, certificates of property rights over data, and complaint/dispute handling. Currently in public consultation; not yet promulgated. Cites Law on Data 60/2024/QH15, Law on Personal Data Protection 91/2025/QH15, Law on Cybersecurity 116/2025/QH15, and Law on Electronic Transactions 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Law on Electronic Commerce (122/2025/QH15)',
            description: 'Vietnam Law on Electronic Commerce (Luật Thương mại điện tử 122/2025/QH15) establishing a unified national regime for electronic commerce, covering domestic and cross-border platform operations, platform governance and accountability, consumer protection, seller and affiliate controls, complaint handling, content moderation and takedown enforcement, and administrative procedure readiness for the state e-commerce management system.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Decree 13/2023/NĐ-CP — Personal Data Protection (2023–2025)',
            description: 'Decree 13/2023/ND-CP, the operational instrument for PDPL duties including dossier filing, governance artifacts, cross-border mechanics, and MoPS administrative procedures. Ceased to have effect on 1 January 2026 under Article 42(2) of Decree 356/2025/NĐ-CP; Law 91/2025/QH15 now governs personal data protection.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Personal Data Protection Law (91/2025/QH15)',
            description: "Vietnam's Personal Data Protection Law (Law 91/2025/QH15) establishing comprehensive personal data protection framework, effective January 1, 2026. Covers compliance themes A–J: lawful processing bases, sensitive data consent, data subject rights (access/rectification/erasure/restriction/objection/portability), breach notification to authority and data subjects, DPIA for high-risk processing, cross-border transfer governance, DPO designation, records of processing activities, processor contractual obligations. Authority filing obligations via MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Cross-references: VN_PDPL_DECREE_356_2025 (implementing decree), VN_CYBERSEC_LAW_2025 (cybersecurity overlaps), VN_DATA_LAW (data governance).",
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Decree 356/2025/ND-CP (PDPL Implementation)',
            description: 'Decree 356/2025/ND-CP implementing the Personal Data Protection Law (Law 91/2025/QH15). Covers dossier filing, governance artifacts, cross-border mechanics, and service provider certification. Annex form inventory: Phụ lục I–VIII (impact assessment, cross-border transfer, certification). MoPS administrative procedures via VN_PDPL_ADMIN_PROCEDURES_778_2026 (Decision 778/QĐ-BCA-A05). Certification workflow: application → issuance → change request → reissue → revocation. Cross-references: VN_PDPL_LAW_2025 (parent law), VN_CYBERSEC_LAW_2025 (data localization), VN_DATA_LAW (data governance overlaps).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Administrative Procedures (Decision 778/QĐ-BCA-A05)',
            description: 'Decision 778/QĐ-BCA-A05 (February 2026) — Administrative procedures for personal data protection under A05 (MPS). Defines dossier submission, processing, and result issuance procedures including DLCN-HC forms and Mẫu số statutory forms.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Draft Cybersecurity + PDPL Enforcement Decree 2026',
            description: '116-page draft enforcement and sanctions decree consolidating sanctions for violations of the Personal Data Protection Law (91/2025/QH15) AND the Cybersecurity Law (116/2025/QH15). Canonical code VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026; existing code VN_PDPL_ENFORCEMENT_DRAFT_2026 preserved as legacy alias. Draft enforcement overlay only — fine amounts MUST NOT be presented as final law until promulgation.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnam Telecommunications Law 2023',
            description: 'Vietnam Telecommunications Law 2023 (Luật Viễn thông), a sector overlay for telco/ISP/platform companies with confidentiality, disclosure control, and authority request handling obligations. Cross-references: VN_CYBERSEC_LAW_2025 (information system security), VN_PDPL_LAW_2025 (user data processing), VN_DATA_LAW (data governance).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Decree 163/2024/NĐ-CP (Telecom Law Implementation)',
            description: 'Decree 163/2024/NĐ-CP implementing the Telecommunications Law (Law 24/2023/QH15). Provides official forms for telecom service notification, licensing, registration, and data center development plans. Defines 7 official Mẫu số forms.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Complaints, disputes, and risk management',
            description: 'Platform operators must provide complaint intake, dispute handling workflow with evidence preservation, risk identification and early-warning controls, and periodic review of privacy, cybersecurity, and access-management risks.',
        },
        vn_dp_draft_e: {
            title: 'Transaction workflow and certificate of property rights over data',
            description: 'Transactions require account registration, listing approval, electronic contracting, payment confirmation, delivery record, and issuance of a certificate of property rights over data where applicable. Transaction history and traceability must be preserved in a transaction register.',
        },
        vn_dp_draft_d: {
            title: 'Sandbox and testing environment controls',
            description: 'Test environments must be isolated, access-restricted, security-monitored, and reviewed before any output is extracted. No source data may be extracted from the test environment, and closure/cleanup of test sessions must be recorded.',
        },
        vn_dp_draft_c: {
            title: 'Data product and service listing controls',
            description: 'Listings must be supported by lawful-source and ownership/usage-right evidence, technical validation records, quality disclosures, and platform-side listing review and approval records. Prohibited data must be filtered before listing.',
        },
        vn_dp_draft_b: {
            title: 'Participant eligibility and onboarding',
            description: 'Platform operators must verify legal status, legal capacity, and conditional-business requirements for sellers and buyers, register transaction and payment accounts, and maintain a participant register.',
        },
        vn_dp_draft_a: {
            title: 'Data platform classification and operating model',
            description: 'Platform operators must establish a classification of platform type (national data platform vs other data platforms), define operator eligibility and responsibilities, and document pricing, fees, rules of operation, and technical governance.',
        },
        d165_r5: {
            title: 'National database information access',
            description: 'Requests to extract/use information from the national digital data database use prescribed forms (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Annual risk assessment reporting',
            description: 'Annual risk assessment reports must be filed using prescribed forms (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Cross-border transfer termination notice',
            description: 'Termination of cross-border transfer must be notified using prescribed forms (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Assessment result notification',
            description: 'Authority issues assessment result notices using prescribed form (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Cross-border data transfer impact assessment filing',
            description: 'Organizations must file cross-border data transfer/processing impact assessment dossiers using prescribed forms (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Consent for cookies and similar technologies',
            description: 'Storing or accessing information on a terminal equipment is only allowed with prior informed consent, except for technically necessary storage.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Confidentiality of communications',
            description: 'Member States shall ensure the confidentiality of communications and related traffic data by means of a public communications network and publicly available electronic communications services.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Consent for direct marketing (opt-in)',
            description: 'Use of electronic communications systems for direct marketing requires prior consent of the subscriber.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Soft opt-in for existing customers',
            description: 'Direct marketing to existing customers for similar products/services is permitted without consent if opt-out is provided.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Breach notification for telecom providers',
            description: 'Providers of publicly available electronic communications services shall notify the competent national authority of personal data breaches.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Right to object to direct marketing',
            description: 'Subscribers shall be given the opportunity to object to receiving direct marketing messages at any time and free of charge.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Traffic data retention and erasure',
            description: 'Traffic data relating to subscribers and users must be erased or made anonymous when no longer needed for transmission, except for billing or interconnection payments.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Location data processing conditions',
            description: 'Location data other than traffic data may only be processed when made anonymous or with the consent of the user, to the extent and for the duration necessary.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Lawfulness of processing — consent',
            description: 'Processing shall be lawful only if the data subject has given consent to the processing of their personal data for one or more specific purposes.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Lawfulness of processing — contract',
            description: 'Processing is necessary for the performance of a contract to which the data subject is party.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Lawfulness of processing — legal obligation',
            description: 'Processing is necessary for compliance with a legal obligation to which the controller is subject.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Lawfulness of processing — vital interests',
            description: 'Processing is necessary to protect the vital interests of the data subject or another natural person.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Lawfulness of processing — public interest',
            description: 'Processing is necessary for the performance of a task carried out in the public interest or in the exercise of official authority.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Lawfulness of processing — legitimate interests',
            description: 'Processing is necessary for the purposes of the legitimate interests pursued by the controller or a third party.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Processing of special categories (sensitive data)',
            description: 'Processing of special categories of personal data (racial origin, political opinions, health, etc.) is prohibited unless specific conditions in Art. 9(2) are met.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Conditions for consent',
            description: 'Controller must demonstrate consent was given. Consent must be freely given, specific, informed, and unambiguous. Withdrawal must be as easy as giving consent.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: "Child's consent — digital services",
            description: 'Processing of personal data of a child below the age of 16 (or lower age set by member state, not below 13) requires parental consent for information society services.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Transparent information and communication',
            description: 'Controller shall provide information relating to processing in a concise, transparent, intelligible, and easily accessible form.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Information for direct collection',
            description: 'Where personal data are collected from the data subject, the controller shall provide specified information at the time of collection.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Information for indirect collection',
            description: 'Where personal data have not been obtained from the data subject, the controller shall provide specified information within a reasonable period.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Right of access',
            description: 'The data subject shall have the right to obtain confirmation of processing and access to the personal data and supplementary information.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Right to rectification',
            description: 'The data subject shall have the right to obtain rectification of inaccurate personal data without undue delay.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Right to erasure (right to be forgotten)',
            description: 'The data subject shall have the right to obtain erasure of personal data without undue delay where specified grounds apply.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Right to restriction of processing',
            description: 'The data subject shall have the right to obtain restriction of processing where specified conditions apply.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Notification obligation — rectification/erasure/restriction',
            description: 'Controller shall communicate any rectification, erasure, or restriction to each recipient to whom data was disclosed.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Right to data portability',
            description: 'The data subject shall have the right to receive their personal data in a structured, commonly used, machine-readable format.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Right to object',
            description: 'The data subject shall have the right to object to processing based on public interest or legitimate interests, including profiling.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Automated individual decision-making, including profiling',
            description: 'The data subject shall have the right not to be subject to a decision based solely on automated processing which produces legal or similarly significant effects.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Breach notification to supervisory authority (72h)',
            description: 'In case of a personal data breach, the controller shall notify the supervisory authority within 72 hours of becoming aware, unless unlikely to result in risk.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Breach notification to data subjects (high risk)',
            description: 'When a breach is likely to result in a high risk to rights and freedoms, the controller shall communicate the breach to the data subject without undue delay.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Response to data subject requests (30 days)',
            description: 'Controller shall respond to data subject requests without undue delay and within one month of receipt, extendable by two further months for complex requests.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Data protection impact assessment',
            description: 'Where processing is likely to result in a high risk to rights and freedoms, the controller shall carry out a DPIA prior to the processing.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Prior consultation with supervisory authority',
            description: 'Controller shall consult the supervisory authority where a DPIA indicates that processing would result in high risk without mitigation measures.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA mandatory processing list',
            description: 'DPIA is required for systematic evaluation/profiling, large-scale processing of special categories, and systematic monitoring of public areas.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Records of processing activities (controller)',
            description: 'Each controller shall maintain a record of processing activities under its responsibility, including purposes, categories, recipients, transfers, and retention.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Records of processing activities (processor)',
            description: 'Each processor shall maintain a record of all categories of processing activities carried out on behalf of a controller.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Security of processing',
            description: 'Controller and processor shall implement appropriate technical and organisational measures to ensure security appropriate to the risk.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Data protection by design and by default',
            description: 'Controller shall implement appropriate measures for ensuring that only personal data necessary for each specific purpose is processed (data minimisation).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Data protection officer designation',
            description: 'Controller and processor shall designate a DPO where processing is carried out by a public authority, involves regular and systematic monitoring, or large-scale processing of special categories.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'General principle for transfers',
            description: 'Any transfer of personal data to a third country shall take place only if the conditions of Chapter V are complied with.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Transfers based on adequacy decision',
            description: 'A transfer may take place where the Commission has decided that the third country ensures an adequate level of protection.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Transfers subject to appropriate safeguards',
            description: 'In the absence of an adequacy decision, transfers may take place where appropriate safeguards are provided, including SCCs or BCRs.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Standard contractual clauses',
            description: 'Standard contractual clauses adopted by the Commission or by a supervisory authority provide appropriate safeguards for international transfers.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Binding corporate rules',
            description: 'Binding corporate rules approved by a competent supervisory authority provide appropriate safeguards for intra-group international transfers.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Derogations for specific situations',
            description: 'In the absence of adequacy or safeguards, transfers may take place based on explicit consent, contract necessity, public interest, legal claims, vital interests, or public register.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Management body responsibility and training',
            description: 'Management bodies of essential and important entities must approve cybersecurity risk-management measures and undergo regular training.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Cybersecurity risk-management measures',
            description: 'Essential and important entities shall take appropriate and proportionate technical, operational, and organisational measures to manage cybersecurity risks.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Incident handling policies and procedures',
            description: 'Entities shall implement policies on incident handling including detection, analysis, containment, and response procedures.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Business continuity and crisis management',
            description: 'Entities shall implement business continuity measures including backup management, disaster recovery, and crisis management.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Supply chain security',
            description: 'Entities shall address security-related aspects of relationships with direct suppliers and service providers, including supply chain risk assessments.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Security in network and information systems',
            description: 'Entities shall implement security in network and information system acquisition, development, and maintenance, including vulnerability handling.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Vulnerability handling and disclosure',
            description: 'Entities shall implement policies and procedures for coordinated vulnerability disclosure and handling.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Cybersecurity risk assessment effectiveness',
            description: 'Entities shall implement policies and procedures to assess the effectiveness of cybersecurity risk-management measures.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Cryptography and encryption policies',
            description: 'Entities shall implement policies and procedures regarding the use of cryptography and, where appropriate, encryption.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'HR security, access control, asset management',
            description: 'Entities shall implement human resources security, access control policies, and asset management.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Multi-factor authentication',
            description: 'Entities shall use multi-factor authentication or continuous authentication solutions, secured voice/video/text communications, and secured emergency communication systems.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Early warning to CSIRT (24h)',
            description: 'Entities shall submit an early warning to the competent CSIRT within 24 hours of becoming aware of a significant incident.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Incident notification to CSIRT (72h)',
            description: 'Entities shall submit an incident notification to the competent CSIRT within 72 hours of becoming aware of a significant incident.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Final report to CSIRT (1 month)',
            description: 'Entities shall submit a final report to the competent CSIRT not later than one month after the incident notification.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Applicability determination for AI activities',
            description: 'Determine whether the organization conducts AI activities subject to the Vietnam AI Law and document the applicability decision.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AI system risk classification by risk level',
            description: 'Classify each AI system by risk level (minimal, limited, high, unacceptable) using the guided classification methodology.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI System Register entry maintenance',
            description: 'Maintain a register of AI systems with system name, owner, purpose, deployment context, risk level, and vendor involvement.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI System Dossier with artifact checklist and evidence',
            description: 'Build and maintain a system dossier with required artifact checklist, evidence library, and version history.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Transparency obligations for AI system operators',
            description: 'Implement transparency measures for AI systems including user notification, disclosure of AI involvement, and system documentation.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AI output labeling, watermarking, and metadata signing',
            description: 'Implement labeling and watermarking for AI-generated content including metadata signing where applicable.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Disclosure requirements for AI-generated content',
            description: 'Disclose to users when content is AI-generated and provide mechanisms for users to identify AI involvement.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Explainability and user-facing transparency duties',
            description: 'Provide explainable outputs for AI decisions affecting individuals and maintain user-facing transparency documentation.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Human oversight obligations for high-risk AI systems',
            description: 'Ensure human oversight mechanisms are in place for high-risk AI systems including escalation and override capabilities.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI incident monitoring and detection',
            description: 'Implement monitoring capabilities to detect AI system incidents, malfunctions, and adverse outcomes.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Serious AI incident reporting to authority',
            description: 'Report serious AI incidents to the competent authority within the prescribed timeline (72 hours).',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI incident notification timeline compliance',
            description: 'Comply with notification timelines for AI incidents including initial notification, updates, and closure reports.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI impact assessment for high-risk systems',
            description: 'Conduct impact assessments for high-risk AI systems covering risks, mitigations, human oversight, and monitoring plans.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Controlled testing / sandbox eligibility determination',
            description: 'Determine eligibility for controlled testing (sandbox) participation and document the eligibility assessment.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Sandbox application submission and approval tracking',
            description: 'Submit sandbox applications to the competent authority and track approval status through the submission lifecycle.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Sandbox monitoring checkpoints and completion/exit',
            description: 'Conduct periodic monitoring checkpoints during sandbox participation and prepare completion/exit reports.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Labeling exemption/exception documentation with rationale',
            description: 'Document any labeling exemptions or exceptions with rationale and supporting evidence.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Data localization for Vietnamese users',
            description: 'Data of Vietnamese users on services provided in Vietnam must be stored within the territory of Vietnam.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Cybersecurity incident reporting',
            description: 'Cybersecurity incidents must be reported to competent authorities in a timely manner.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Information system classification',
            description: 'Information systems must be classified by importance level and appropriate security measures applied.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Cybersecurity risk assessment',
            description: 'Regular cybersecurity risk assessments must be conducted for information systems.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'In-scope determination (decision tree)',
            description: 'Organizations must determine whether they are in scope of Decree 53 using the prescribed decision tree.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Data localization assessment and readiness',
            description: 'Organizations must assess data localization requirements and demonstrate readiness.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Storage and retention readiness checklist',
            description: 'A storage and retention readiness checklist must be completed for data localization compliance.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Local presence requirement assessment',
            description: 'Organizations must assess whether local presence requirements apply and plan accordingly.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Authority request receipt and verification',
            description: 'Authority requests must be received, verified, and processed according to prescribed procedures.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Authority request response within deadline',
            description: 'Responses to authority requests must be provided within the prescribed deadline.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Authority request handling log',
            description: 'A log of all authority request handling activities must be maintained.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Applicability and in-scope determination (consolidated)',
            description: 'Organizations must determine whether they are in scope under the consolidated cybersecurity law, covering both the old Law 24/2018 and Decree 53/2022 scope.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Information system classification by importance level',
            description: 'Information systems must be classified by importance level (general, important, critical) with corresponding security obligations.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Cybersecurity governance structure and responsible personnel',
            description: 'Organizations must establish cybersecurity governance structures and designate responsible personnel.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Periodic cybersecurity risk assessment',
            description: 'Regular cybersecurity risk assessments must be conducted for all classified information systems.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Security measures per information system classification',
            description: 'Technical security measures must be applied appropriate to each information system classification level.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Access control and identity management',
            description: 'Access control and identity management measures must be implemented for information systems.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Logging, monitoring, and audit trail',
            description: 'Information systems must maintain logging, monitoring, and audit trail capabilities.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Vulnerability management and patching',
            description: 'Vulnerability management and patching processes must be established and maintained.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Data localization for Vietnamese user data',
            description: 'Data of Vietnamese users on services provided in Vietnam must be stored within the territory of Vietnam.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Local presence requirements for foreign service providers',
            description: 'Foreign service providers operating in Vietnam must maintain local presence as prescribed.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Local storage architecture and cross-border flow documentation',
            description: 'Local storage architecture must be documented and cross-border data flows must be mapped and controlled.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Cybersecurity incident detection and response',
            description: 'Organizations must have capabilities for detecting and responding to cybersecurity incidents.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Mandatory incident reporting to competent authority',
            description: 'Qualifying cybersecurity incidents must be reported to MPS/MIC within prescribed timelines.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Incident notification timeline compliance',
            description: 'Incident notifications must comply with prescribed timeline requirements (initial report within 24h, full report within 72h).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Authority request handling and cooperation',
            description: 'Authority requests must be handled promptly and cooperation provided according to prescribed procedures.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Authority request case register and disclosure log',
            description: 'All authority requests and disclosures must be logged in a case register with full audit trail.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Vendor and third-party security assessment',
            description: 'Third-party vendors with access to information systems must undergo security assessment.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Vendor data access and incident notification controls',
            description: 'Vendor data access must be documented and contractual incident notification obligations established.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Security awareness training and exercises',
            description: 'Regular cybersecurity awareness training and tabletop exercises must be conducted.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Periodic pen test and security audit',
            description: 'Periodic penetration testing and security audits must be performed on classified information systems.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Maintain digital data inventory',
            description: 'Organizations must maintain a comprehensive digital data inventory cataloging all data assets.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Data classification by sensitivity and importance',
            description: 'Data must be classified according to sensitivity and importance levels as defined by the Data Law.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Define handling rules by classification level',
            description: 'Handling rules must be established for each data classification level.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Track data sharing and disclosure with approvals',
            description: 'All data sharing and disclosure activities must be tracked with appropriate approvals.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Cross-border data transfer governance',
            description: 'Cross-border data transfers must comply with governance requirements under the Data Law.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Maintain governance policies and responsibilities',
            description: 'Organizations must maintain data governance policies and assign governance responsibilities.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Annual governance attestation',
            description: 'An annual governance attestation must be completed to confirm ongoing compliance.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Data risk assessment',
            description: 'A data risk assessment must be conducted to identify and mitigate data governance risks.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Platform operator accountability and governance obligations',
            description: 'Establish platform operator accountability structures including governance policies, monitoring responsibilities, and enforcement obligations.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Platform rules publication and transparency of information',
            description: 'Publish platform rules, trading conditions, and operational disclosures in a transparent and accessible manner.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'E-commerce sales website/app notification to MOIT',
            description: 'Submit notification to MOIT for e-commerce sales websites and applications via Online.gov.vn or designated channels.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'E-commerce service platform/marketplace registration with MOIT',
            description: 'Register e-commerce service platforms and marketplaces with MOIT including dossier submission and approval tracking.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Seller register maintenance (domestic and foreign)',
            description: 'Maintain a register of all sellers on the platform including domestic and foreign sellers with onboarding, verification, and lifecycle tracking.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Livestream seller register and compliance controls',
            description: 'Maintain a register of livestream sellers with identity verification, activity tracking, and compliance controls.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Affiliate and marketing partner register',
            description: 'Maintain a register of affiliate marketers and referral partners with contract status, content guidelines, and compliance acknowledgements.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Product listing and prohibited/conditional goods enforcement',
            description: 'Implement product listing controls including prohibited and conditional goods screening, monitoring, and enforcement actions.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Consumer complaints register with intake, actions, and audit trail',
            description: 'Maintain a complaints register with intake records, investigation actions, resolution outcomes, and complete audit trail.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Complaint handling procedure and resolution timelines',
            description: 'Establish complaint handling procedures with defined acknowledgement, investigation, and resolution timelines.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Content moderation, monitoring, and takedown enforcement',
            description: 'Implement content moderation, monitoring, and takedown enforcement procedures for illegal, prohibited, or violating content.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Takedown and enforcement actions register with evidence preservation',
            description: 'Maintain a register of takedown and enforcement actions with decision records, evidence preservation, and seller notifications.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Platform policy and disclosure register (versions, publication dates, acceptance)',
            description: 'Maintain a register of platform policies and disclosures with version history, publication dates, and acceptance evidence.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Consumer information duties - accurate product info and anti-deception',
            description: 'Ensure accurate, clear product and service information and implement measures to prevent deceptive practices on the platform.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Foreign platform operating conditions and registration/notification',
            description: 'Comply with operating conditions for foreign platforms serving Vietnamese users including registration or notification requirements.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Local representation or authorization for foreign platforms',
            description: 'Designate a local representative or authorized entity in Vietnam for foreign platforms as required by law.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Cross-border seller and complaint handling coordination',
            description: 'Coordinate cross-border seller handling and complaint resolution including escalation and evidence management.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'State e-commerce management system interaction and reporting readiness',
            description: 'Prepare for interaction with the state e-commerce management system including data fields mapping and reporting capability.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Post-approval material change notification',
            description: 'Submit notifications for material changes to platform details, policies, or operations after initial approval.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Termination and deactivation notification',
            description: 'Submit termination or deactivation notifications when ceasing e-commerce operations including public notice and consumer protection handling.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Trustmark and credibility rating program eligibility and maintenance',
            description: 'Assess eligibility for trustmark and credibility rating programs and maintain compliance evidence for ongoing participation.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Consent for personal data processing',
            description: 'Personal data processing requires consent of the data subject unless an exemption applies.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Right of access',
            description: 'Data subjects have the right to access their personal data held by the data controller.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Right to rectification',
            description: 'Data subjects have the right to request rectification of inaccurate personal data.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Right to erasure',
            description: 'Data subjects have the right to request erasure of their personal data.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Right to restrict processing',
            description: 'Data subjects have the right to request restriction of processing of their personal data.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Right to object',
            description: 'Data subjects have the right to object to the processing of their personal data.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Right to withdraw consent',
            description: 'Data subjects have the right to withdraw previously given consent at any time.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Breach notification to authority (72h)',
            description: 'Data controller must notify the Ministry of Public Security within 72 hours of discovering a personal data breach.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Breach notification to data subjects',
            description: 'Data controller must notify affected data subjects of a personal data breach without undue delay.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA for high-risk processing',
            description: 'A Data Protection Impact Assessment must be conducted for processing activities that are likely to result in high risk.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Cross-border transfer assessment',
            description: 'An impact assessment must be conducted before transferring personal data outside Vietnam.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Record of processing activities',
            description: 'Data controllers must maintain a Record of Processing Activities (RoPA) documenting all processing activities.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Data processing agreement records',
            description: 'Records of data processing agreements with processors and sub-processors must be maintained.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Cross-border transfer safeguards',
            description: 'Personal data transfers outside Vietnam require appropriate safeguards and documented assessments.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Acknowledgment within 2 working days',
            description: 'Data subject requests must be acknowledged within 2 working days of receipt.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Completion within 15 working days',
            description: 'Data subject requests must be completed within 15 working days, with a possible 5 working day extension.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'MoPS impact assessment dossier filing',
            description: 'Organizations conducting high-risk processing must file an impact assessment dossier with the Ministry of Public Security using the prescribed administrative procedure.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'MoPS cross-border transfer dossier filing',
            description: 'Organizations transferring personal data outside Vietnam must file a cross-border transfer impact assessment dossier with the Ministry of Public Security.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Processing service provider certification',
            description: 'Organizations providing personal data processing as a service must obtain a service processing certificate from the Ministry of Public Security.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Processing impact assessment dossier when triggers met',
            description: 'A processing impact assessment dossier must be filed when prescribed triggers are met.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Cross-border transfer impact dossier',
            description: 'A cross-border transfer impact dossier must be prepared and filed for international data transfers.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Data protection officer designation',
            description: 'A data protection officer must be designated as required by Decree 13.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Governance artifacts (policies, roles, procedures)',
            description: 'Governance artifacts including policies, roles, and procedures must be maintained.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Evidence pack completeness and immutable audit trail',
            description: 'Evidence packs must be complete and an immutable audit trail must be maintained.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Filing case workflow support',
            description: 'Filing case workflows must be supported for impact assessment and cross-border dossiers.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Processor contractual requirements',
            description: 'Contractual requirements for data processors must be established and monitored.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Annual compliance review',
            description: 'An annual compliance review must be conducted to verify ongoing adherence.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Authority filing submission records',
            description: 'Records of all authority filing submissions must be maintained.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Lawful bases for personal data processing',
            description: 'Personal data processing must be based on lawful bases as defined by the PDPL.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Data subject rights (access, rectification, erasure, restrict, object, portability)',
            description: 'Data subjects have comprehensive rights including access, rectification, erasure, restriction, objection, and portability.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Breach notification to authority',
            description: 'Data controllers must notify the competent authority of personal data breaches within the prescribed timeframe.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Breach notification to data subjects',
            description: 'Data controllers must notify affected data subjects of personal data breaches.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Data protection impact assessment',
            description: 'A data protection impact assessment must be conducted for high-risk processing activities.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Cross-border transfer governance',
            description: 'Cross-border transfers of personal data must comply with governance requirements.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Data protection officer designation',
            description: 'Organizations must designate a data protection officer as required.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Records of processing activities',
            description: 'Organizations must maintain records of all processing activities.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Processor contractual obligations',
            description: 'Contractual obligations must be established with data processors.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Impact assessment dossier filing',
            description: 'Impact assessment dossiers must be filed with the competent authority as prescribed.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Cross-border transfer impact dossier',
            description: 'A cross-border transfer impact dossier must be prepared and filed for international data transfers.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO appointment and notification',
            description: 'A data protection officer must be appointed and the authority notified.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Governance policies and procedures',
            description: 'Governance policies and procedures must be established and maintained.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Evidence pack and immutable audit trail',
            description: 'Evidence packs must be complete and an immutable audit trail must be maintained.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Filing case lifecycle',
            description: 'Filing case lifecycles must be managed through prescribed stages.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Processor contractual requirements',
            description: 'Contractual requirements for data processors must be established and monitored.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Service processing provider certification',
            description: 'Organizations providing data processing services must obtain certification.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Authority filing submission records',
            description: 'Records of all authority filing submissions must be maintained.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Annual compliance review',
            description: 'An annual compliance review must be conducted to verify ongoing adherence.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Data security technical measures',
            description: 'Technical measures for data security must be implemented and maintained.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Decree 356 official Mẫu số form requirements',
            description: 'The 13 official Mẫu số administrative procedure forms (Mẫu số 01a–10) prescribed by Decree 356/2025/NĐ-CP must be used for the corresponding filings with the Ministry of Public Security. The Phụ lục I–VIII items are internal data-structure mappings, not official regulatory forms.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA dossier submission to A05',
            description: 'Organizations must submit DPIA dossiers to A05/MPS for processing activities involving sensitive personal data or large-scale processing.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Cross-border transfer dossier submission',
            description: 'Organizations must submit cross-border data transfer dossiers for A05/MPS review and approval.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Processing service certificate application',
            description: 'Data processing service providers must apply for certificates from A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Personal data processing notification',
            description: 'Controllers must notify A05/MPS of personal data processing activities using prescribed forms (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Annual situation reporting',
            description: 'Controllers must submit annual personal data processing situation reports (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'User information confidentiality by default',
            description: 'User information must be treated as confidential by default.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Minimum organizational and technical protection controls',
            description: 'Minimum organizational and technical protection controls must be implemented.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Disclosure only with proper basis',
            description: 'Disclosure of user information is permitted only with a proper legal basis.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Mandatory disclosure logging and auditability',
            description: 'All disclosures must be logged and auditable.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Internal approval workflow for disclosures',
            description: 'An internal approval workflow must be established for all disclosures.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Authority request response handling',
            description: 'Authority requests must be handled and responded to according to prescribed procedures.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'The data subject has the right to be informed about personal-data processing activities.',
        consent: 'The data subject has the right to consent to processing of their personal data.',
        refuse_consent: 'The data subject has the right to not consent to processing of their personal data.',
        withdraw_consent: 'The data subject has the right to request withdrawal of previously given consent.',
        access: 'The data subject has the right to view and access their personal data held by the controller.',
        rectification: 'The data subject has the right to correct or request correction of inaccurate personal data.',
        data_provision: 'The data subject has the right to request provision of their personal data (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: "The data subject has the right to request deletion or destruction of personal data, subject to the law's exceptions.",
        restriction_of_processing: 'The data subject has the right to request restriction of processing of their personal data.',
        object_to_processing: 'The data subject has the right to send a request objecting to the processing of their personal data.',
        complaint: 'The data subject has the right to lodge a complaint (khiếu nại) regarding personal data processing.',
        denunciation: 'The data subject has the right to make a denunciation (tố cáo) regarding violations of personal data protection.',
        lawsuit: 'The data subject has the right to bring legal action (khởi kiện) for violations of their data protection rights.',
        claim_damages: 'The data subject has the right to claim compensation for damage caused by violations of personal data protection.',
        request_protection_measures: 'The data subject has the right to request competent authorities or relevant organizations/individuals to implement measures to protect their personal data.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Risk management and monitoring (draft)',
            steps: {
                '1': 'Identify risk categories',
                '2': 'Maintain early warning controls',
                '3': 'Review collection, storage, processing, sharing, and transaction risks',
                '4': 'Record privacy, cybersecurity, and access-management risks',
                '5': 'Trigger remediation actions',
                '6': 'Preserve review history',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Complaints and disputes (draft)',
            steps: {
                '1': 'Receive complaint / dispute intake',
                '2': 'Check completeness of required information; request supplement within 24 hours if incomplete',
                '3': 'Within 3 working days of receiving a complete request, notify parties of handling approach and expected timeline',
                '4': 'Request responses and supporting documents',
                '5': 'Assess dispute facts and platform responsibility',
                '6': 'Record resolution outcome',
                '7': 'Preserve evidence and close case',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Delivery and certificate of property rights over data (draft)',
            steps: {
                '1': 'Deliver product/service according to contract method',
                '2': 'Record delivery event and time',
                '3': 'Generate certificate of property rights over data',
                '4': 'Register transaction completion',
                '5': 'Enable later lookup / verification where required',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Transaction initiation and contracting (draft)',
            steps: {
                '1': 'Review listing and evaluation result',
                '2': 'Initiate purchase, negotiation, or auction workflow',
                '3': 'Generate electronic contract',
                '4': "Secure both parties' acceptance",
                '5': 'Record payment completion',
                '6': 'Authorize delivery',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Sandbox / test environment access (draft)',
            steps: {
                '1': 'Submit sandbox request',
                '2': 'Define scope, allowed data, time period, and processing permissions',
                '3': 'Provision isolated technical environment',
                '4': 'Monitor security and access during testing',
                '5': 'Review output before extraction',
                '6': 'Approve extraction only where permitted',
                '7': 'Close test session and clean up environment',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Data product / service listing (draft)',
            steps: {
                '1': 'Create listing application',
                '2': 'Capture source and ownership / usage-right evidence',
                '3': 'Record technical characteristics, format, API details, and restrictions',
                '4': 'Record quality disclosures',
                '5': 'Submit listing dossier to platform review',
                '6': 'Platform performs completeness, legality, and technical checks',
                '7': 'Approve or reject listing',
                '8': 'If approved, publish listing with attached evaluation result',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Participant onboarding (draft)',
            steps: {
                '1': 'Determine participant type: seller or buyer',
                '2': 'Verify legal status and legal capacity',
                '3': 'Verify additional conditions if conditional business lines apply',
                '4': 'Register transaction account',
                '5': 'Link payment account',
                '6': 'Approve platform participation',
                '7': 'Store participant record and audit trail',
            },
        },
        AI_A1: {
            name: 'AI System Registration / Notification',
            steps: {
                '1': 'Applicability check and AI system classification',
                '2': 'Build registration/notification dossier',
                '3': 'Internal approval',
                '4': 'Submit via channel',
                '5': 'Amendment loop — if returned, update and resubmit',
                '6': 'Close and store confirmation',
            },
        },
        AI_A2: {
            name: 'AI Risk Classification Workflow',
            steps: {
                '1': 'Create AI System Register entry',
                '2': 'Run classification wizard',
                '3': 'Attach evidence',
                '4': 'Approve classification',
                '5': 'Link outputs to system dossier',
            },
        },
        AI_A3: {
            name: 'Labeling and Disclosure Compliance',
            steps: {
                '1': 'Identify labeling obligations by system and use case',
                '2': 'Implement labeling notices',
                '3': 'Capture evidence — screenshots, UI copy, technical metadata',
                '4': 'Handle exemptions and record justification',
                '5': 'Approve and version',
            },
        },
        AI_A4: {
            name: 'Serious Incident Reporting',
            steps: {
                '1': 'Create AI Incident Record',
                '2': 'Classify severity',
                '3': 'Execute containment actions and evidence capture',
                '4': 'Generate Serious Incident Report template export',
                '5': 'Submit to authority via channel',
                '6': 'Track authority responses and closure',
            },
        },
        AI_A5: {
            name: 'AI Impact Assessment',
            steps: {
                '1': 'Create assessment record',
                '2': 'Populate required sections',
                '3': 'Attach evidence — data flows, model documentation, controls',
                '4': 'Internal approval',
                '5': 'Export report and store in dossier',
            },
        },
        AI_A6: {
            name: 'Controlled Testing / Sandbox Participation',
            steps: {
                '1': 'Complete eligibility and readiness record',
                '2': 'Prepare application and export',
                '3': 'Submit via channel',
                '4': 'Track approval',
                '5': 'Execute monitoring checkpoints and logs',
                '6': 'Complete completion report and closure package',
            },
        },
        CYBERSEC_C1: {
            name: 'Critical Information System Security Assessment',
            steps: {
                '1': 'Classify information systems according to national cybersecurity requirements',
                '2': 'Conduct security assessment and risk evaluation for classified systems',
                '3': 'Implement required security measures and baseline controls',
                '4': 'Document evidence of compliance and schedule periodic reviews',
            },
        },
        CYBERSEC_C2: {
            name: 'Cybersecurity Incident Notification',
            steps: {
                '1': 'Detect, classify, and assess the cybersecurity incident',
                '2': 'Execute immediate response actions and evidence preservation',
                '3': 'Report to authority and participate in national incident-response coordination',
                '4': 'Conduct post-incident review and document lessons learned',
            },
        },
        CYBERSEC_C3: {
            name: 'Data Localization Compliance Verification',
            steps: {
                '1': 'Assess whether data localization triggers apply based on service type and data categories',
                '2': 'Verify local storage architecture and cross-border flow controls are in place',
                '3': 'Assess local presence requirements (branch / representative office) where applicable',
                '4': 'Document compliance evidence and maintain for authority review',
            },
        },
        CYBERSEC_C4: {
            name: 'Authority Cooperation Request Response',
            steps: {
                '1': 'Receive and verify the legitimacy and scope of the authority request',
                '2': 'Gather and prepare requested information or execute required actions',
                '3': 'Submit response to authority within required timeline',
                '4': 'Log the disclosure and close the case',
            },
        },
        CYBERSEC_C5: {
            name: 'Cybersecurity Personnel Qualification Verification',
            steps: {
                '1': 'Assess vendor security posture using cybersecurity assessment framework',
                '2': 'Verify vendor data access scope and register in vendor data access log',
                '3': 'Review vendor incident notification clause and contractual cybersecurity requirements',
                '4': 'Schedule periodic vendor security reviews and monitor compliance',
            },
        },
        DATA_D1: {
            name: 'Data Inventory and Classification Setup',
            steps: {
                '1': 'Catalog all data assets with types and sources in data inventory register',
                '2': 'Classify each asset by sensitivity level',
                '3': 'Define handling rules per classification level',
                '4': 'Capture evidence — screenshots, architecture diagrams, access controls',
                '5': 'Schedule periodic reviews',
            },
        },
        DATA_D2: {
            name: 'Data Sharing Governance',
            steps: {
                '1': 'Evaluate new sharing arrangements via data sharing risk assessment',
                '2': 'Complete internal approval workflow',
                '3': 'Record sharing event in data sharing/disclosure log',
                '4': 'Capture evidence — approval records, contractual documents',
                '5': 'Periodic review of sharing arrangements',
            },
        },
        DATA_D3: {
            name: 'Cross-Border Data Transfer Assessment',
            steps: {
                '1': 'Identify cross-border transfers in data inventory',
                '2': 'Conduct transfer risk assessment',
                '3': 'Apply safeguards and governance measures',
                '4': 'Document in cross-border section of governance attestation',
                '5': 'Export assessment package for audit',
            },
        },
        DATA_D4: {
            name: 'Annual Governance Attestation',
            steps: {
                '1': 'Review governance RACI matrix',
                '2': 'Verify policy status (governance, classification, sharing, retention)',
                '3': 'Complete attestation pack',
                '4': 'Obtain internal approval',
                '5': 'Export and archive attestation',
            },
        },
        DATA_D5: {
            name: 'Data Incident Handling',
            steps: {
                '1': 'Classify the incident using decision record',
                '2': 'Execute incident response per playbook',
                '3': 'Capture evidence — logs, screenshots, communications',
                '4': 'Conduct post-incident review',
                '5': 'If authority notification required: create submission case and submit to MIC',
                '6': 'Close and archive',
            },
        },
        ECOM_P1: {
            name: 'Notification (Sales Website/App)',
            steps: {
                '1': 'Determine classification: sales site/app vs marketplace/service platform',
                '2': 'Create internal dossier record with company identity, website/app info, and required disclosures',
                '3': 'Generate evidence pack — screenshots of disclosures, policy URLs, version history',
                '4': 'Submit via Online.gov.vn — upload documents, record timestamp and confirmation',
                '5': 'Track processing — if returned for amendment, capture reasons and resubmit',
                '6': 'Close — store confirmation, link evidence and audit trail',
            },
        },
        ECOM_P2: {
            name: 'Registration (Service Platforms / Marketplaces)',
            steps: {
                '1': 'Determine platform model and obligations — marketplace vs mixed vs social commerce',
                '2': 'Build registration dossier — application form, service provision scheme, T&Cs, platform rules',
                '3': 'Internal approvals — assign legal/compliance owner, approve for submission',
                '4': 'Submit via Online.gov.vn — upload dossier, record timestamp',
                '5': 'Processing loop — if returned for amendment, update and resubmit',
                '6': 'Approval and operationalization — store confirmation, maintain ongoing compliance registers',
            },
        },
        ECOM_P3: {
            name: 'Update/Change Notification',
            steps: {
                '1': 'Identify change trigger — material change to dossier items',
                '2': 'Generate change package — change summary, updated documents, evidence updates',
                '3': 'Submit update and record submission status',
                '4': 'Track processing and close',
            },
        },
        ECOM_P4: {
            name: 'Termination / Deactivation',
            steps: {
                '1': 'Prepare termination package — cease date, reason, consumer protection handling plan, proof of public notice',
                '2': 'Submit termination — record timestamp and confirmation',
                '3': 'Close — store final status and evidence',
            },
        },
        ECOM_P5: {
            name: 'Trustmark / Credibility Rating Program',
            steps: {
                '1': 'Eligibility and readiness review',
                '2': 'Prepare dossier using official form',
                '3': 'Submit and track',
                '4': 'Maintain ongoing compliance evidence',
            },
        },
        PDPL_P1: {
            name: 'DPIA Submission Filing to MPS',
            steps: {
                '1': 'Prepare processing impact report (Mẫu số 10) and submission notice (02a for an organisation; 02b for an individual)',
                '2': 'Assemble cover documents and supporting evidence',
                '5': 'Retain dossier and evidence of submission for audit trail',
            },
        },
        PDPL_P2: {
            name: 'Cross-Border Transfer Application',
            steps: {
                '1': 'Complete cross-border transfer dossier using Decree 356 forms (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Data Processing Service Certificate Application',
            steps: {
                '1': 'Prepare certification application dossier per Decree 356 requirements',
                '2': 'Submit application to MPS with required supporting documents',
                '3': 'Respond to MPS review comments and supplement requests',
            },
        },
        PDPL_P4: {
            name: 'Data Subject Rights Request Processing',
            steps: {
                '1': 'Receive and log request; verify identity of data subject within 2 working days',
                '2': 'Assess request type and determine applicable deadline (10/15/20 days by type)',
                '3': 'Execute fulfillment actions across relevant systems and departments',
                '4': 'Prepare and send response to data subject with outcome and rationale',
                '5': 'Close case and retain evidence for audit; document any refusal rationale',
            },
        },
        PDPL_P5: {
            name: 'Breach Notification to MPS',
            steps: {
                '1': 'Detect and assess the breach; determine whether the notification obligation applies and resolve the applicable notification deadline from the installed breach deadline records',
                '2': 'Prepare notification package per PDPL requirements',
                '3': 'Submit the notification to MPS within the notification deadline resolved from the installed breach deadline records, counted from discovery',
                '4': 'Respond to MPS follow-up requests; submit supplements and coordination evidence',
            },
        },
        TELECOM_T1: {
            name: 'Authority Request Handling',
            steps: {
                '1': 'Receive and log the authority request via intake record',
                '2': 'Verify authority identity, legal basis, and scope of request',
                '3': 'Conduct internal legal review and obtain management approval',
                '4': 'Prepare response package with assembled evidence',
                '5': 'Draft and approve response letter',
                '6': 'Create submission case and submit response via channel',
                '7': 'Log disclosure and track authority acknowledgment',
                '8': 'Close case and retain evidence for audit trail',
            },
        },
        TELECOM_T2: {
            name: 'Disclosure Management',
            steps: {
                '1': 'Receive disclosure request (user consent, legal obligation, or authority request)',
                '2': 'Complete disclosure approval workflow',
                '3': 'Execute disclosure and record in log register',
                '4': 'Capture evidence — approval records, communication records',
                '5': 'Periodic review of disclosure log',
            },
        },
        TELECOM_T3: {
            name: 'Confidentiality Policy and Access Control Setup',
            steps: {
                '1': 'Draft and approve confidentiality policy',
                '2': 'Draft and approve access control and logging policy',
                '3': 'Document processing activities in user information processing register',
                '4': 'Capture evidence — policy documents, implementation evidence',
                '5': 'Schedule periodic reviews',
            },
        },
        TELECOM_T4: {
            name: 'Telecom Incident Assessment',
            steps: {
                '1': 'Classify and assess the incident',
                '2': 'Determine notification requirements',
                '3': 'If authority notification required: create submission case',
                '4': 'Capture and preserve evidence',
                '5': 'Resolution and closure',
            },
        },
        TELECOM_T5: {
            name: 'Retention and Deletion Management',
            steps: {
                '1': 'Define retention and deletion schedule per data category',
                '2': 'Implement deletion procedures',
                '3': 'Capture evidence — deletion records',
                '4': 'Periodic review of schedules',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registration / Notification / Administrative Readiness',
            'ECOM-FINE-B': 'Complaints and Consumer Protection',
            'ECOM-FINE-C': 'Monitoring and Takedown Enforcement',
            'ECOM-FINE-D': 'Cross-Border Operations',
            'ECOM-FINE-E': 'Platform Rules and Transparency',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Authority Request Handling Failures',
            'TELECOM-FINE-B': 'Confidentiality and Unauthorized Disclosure',
            'TELECOM-FINE-C': 'Access Control and Logging Failures',
            'TELECOM-FINE-D': 'Retention and Deletion Violations',
            'TELECOM-FINE-E': 'Telecom Incident Assessment Failures',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Immediate upon detection',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'As specified in the authority request',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'As specified per complaint handling obligations',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'Before commencing e-commerce operations',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Ongoing operation of the platform',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Platform Operator / Risk',
                2: 'Platform Operator / Security',
                3: 'Platform Operator / Risk',
                4: 'Platform Operator / Security',
                5: 'Platform Operator',
                6: 'Compliance',
            },
            step_outputs: {
                1: ['Risk assessment record'],
                2: ['Early warning log'],
                3: ['Risk review findings'],
                4: ['Risk register entries'],
                5: ['Remediation record'],
                6: ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'A participant submits a complaint or dispute request',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Platform Operator',
                2: 'Platform Operator',
                3: 'Platform Operator',
                4: 'Platform Operator',
                5: 'Platform Operator / Legal',
                6: 'Platform Operator',
                7: 'Compliance',
            },
            step_outputs: {
                1: ['Complaint intake record'],
                2: ['Completeness check', 'Supplement request (if any)'],
                3: ['Handling approach notice'],
                4: ['Evidence pack'],
                5: ['Assessment findings'],
                6: ['Dispute resolution record'],
                7: ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Delivery is completed after contract and payment conditions are met',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Seller / Platform Operator',
                2: 'Platform Operator',
                3: 'Platform Operator',
                4: 'Platform Operator',
                5: 'Platform Operator',
            },
            step_outputs: {
                1: ['Delivery record'],
                2: ['Delivery event log entry'],
                3: ['Property rights certificate'],
                4: ['Transaction register entry'],
                5: ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'A buyer initiates a transaction for data, product, or service',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Buyer',
                2: 'Buyer / Platform Operator',
                3: 'Platform Operator',
                4: 'Buyer / Seller',
                5: 'Platform Operator / Payment',
                6: 'Platform Operator',
            },
            step_outputs: {
                1: ['Listing review decision'],
                2: ['Transaction initiation record'],
                3: ['Electronic contract record'],
                4: ['Contract acceptance record'],
                5: ['Payment confirmation'],
                6: ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'A participant wants to evaluate data products/services in the isolated environment',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Participant',
                2: 'Platform Operator / Participant',
                3: 'Platform Operator / IT',
                4: 'Platform Operator / Security',
                5: 'Platform Operator',
                6: 'Platform Operator',
                7: 'Platform Operator / IT',
            },
            step_outputs: {
                1: ['Sandbox request'],
                2: ['Sandbox scope record'],
                3: ['Provisioned environment'],
                4: ['Security monitoring log'],
                5: ['Output review record'],
                6: ['Extraction approval decision'],
                7: ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'A seller seeks to list data, product, or data service for trading',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Seller',
                2: 'Seller / Legal',
                3: 'Seller / IT',
                4: 'Seller',
                5: 'Seller',
                6: 'Platform Operator',
                7: 'Platform Operator',
                8: 'Platform Operator',
            },
            step_outputs: {
                1: ['Listing application'],
                2: ['Lawful-source verification record'],
                3: ['Technical validation record'],
                4: ['Quality disclosure record'],
                5: ['Submitted listing dossier'],
                6: ['Review findings'],
                7: ['Listing approval record'],
                8: ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'A seller or buyer joins the data platform',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Platform Operator',
                2: 'Platform Operator / Legal',
                3: 'Platform Operator / Legal',
                4: 'Platform Operator',
                5: 'Platform Operator',
                6: 'Platform Operator',
                7: 'Compliance',
            },
            step_outputs: {
                1: ['Participant type decision'],
                2: ['Legal capacity verification record'],
                3: ['Conditional-business check record'],
                4: ['Transaction account registration record'],
                5: ['Payment account link record'],
                6: ['Participation approval record'],
                7: ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Commencement of personal data processing requiring impact assessment',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                1: 'DPO / Privacy Team',
                2: 'Privacy Team',
                3: 'Legal / Compliance',
                4: 'DPO / Privacy Team',
                5: 'Compliance Team',
            },
            step_outputs: {
                1: ['Completed DPIA dossier'],
                2: ['Dossier package', 'Cover letter'],
                3: ['Submission confirmation', 'Tracking reference'],
                4: ['Supplement package', 'Updated dossier'],
                5: ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Intent to transfer personal data outside Vietnam',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                1: 'DPO / Privacy Team',
                2: 'Legal / Compliance',
                3: 'DPO / Privacy Team',
            },
            step_outputs: {
                1: ['Transfer dossier package'],
                2: ['Submission confirmation', 'Tracking reference'],
                3: ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organization providing data processing services requiring certification',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                1: 'Legal / Compliance',
                2: 'Legal / Compliance',
                3: 'DPO / Privacy Team',
                4: 'Compliance Team',
            },
            step_outputs: {
                1: ['Certification application dossier'],
                2: ['Submission confirmation'],
                3: ['Supplement response'],
                4: ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Receipt of a data subject rights request',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                1: 'Privacy Team / Customer Service',
                2: 'DPO / Privacy Team',
                3: 'Department Leads / IT',
                4: 'DPO / Privacy Team',
                5: 'Compliance Team',
            },
            step_outputs: {
                1: ['DSR intake record', 'Identity verification record'],
                2: ['Request assessment', 'Deadline assignment'],
                3: ['Fulfillment evidence', 'Department confirmations'],
                4: ['Response letter'],
                5: ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Discovery of a personal data protection violation or breach',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                1: 'Incident Response Team / DPO',
                2: 'DPO / Privacy Team',
                3: 'Legal / Compliance',
                4: 'DPO / Incident Response Team',
            },
            step_outputs: {
                1: ['Breach assessment record', 'Notification decision record'],
                2: ['Breach notification package'],
                3: ['Submission confirmation', 'Tracking reference'],
                4: ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Decree 13 Art. 24',
        'MoPS Admin Procedures': 'MoPS Admin Procedures',
        'Annual cycle': 'Annual cycle',
        'Decree 13/2023/NĐ-CP Art. 24': 'Decree 13/2023/NĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Decree 356/2025/NĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Decree 356/2025/NĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Draft Decree on Data Platforms 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Law 122/2025/QH15 Ch. IV (Consumer Protection)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Law 122/2025/QH15 — Online.gov.vn operational procedure',
        'Law 134/2025/QH15 Article 12': 'Law 134/2025/QH15 Article 12',
        'Law 134/2025/QH15 Article 21': 'Law 134/2025/QH15 Article 21',
        'Law 134/2025/QH15 Article 27': 'Law 134/2025/QH15 Article 27',
        'Law 134/2025/QH15 Articles 9–10': 'Law 134/2025/QH15 Articles 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Law 24/2023/QH15 — article mapping pending',
        'Law 60/2024/QH15 (internal control target)': 'Law 60/2024/QH15 (internal control target)',
        'Law 91/2025/QH15 Art. 21': 'Law 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Law 91/2025/QH15 Art. 23',
        'Policy-defined': 'Policy-defined',
        Quarterly: 'Quarterly',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 administrative procedure',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)',
        'source verification pending': 'source verification pending',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)",
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Internal Audit Governance',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Outsourcing Controls',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (National Assembly)',
        'Ministry of Public Security / A05 Department': 'Ministry of Public Security / A05 Department',
        'Ministry of Public Security': 'Ministry of Public Security',
        'Ministry of Information and Communications': 'Ministry of Information and Communications',
        'Government of Vietnam': 'Government of Vietnam',
        'European Data Protection Board (EDPB)': 'European Data Protection Board (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / National CSIRTs',
        'State Bank of Vietnam': 'State Bank of Vietnam',
    },
    tour_titles: {
        access_accountability: 'Access Accountability Tour',
        assessments: 'Assessments Tour',
        audit_trail: 'Audit Trail Tour',
        classification: 'Classification Tour',
        compliance_forms: 'Compliance Forms Tour',
        connectors: 'Connectors Tour',
        consent_properties: 'Consent Properties Tour',
        consent: 'Consent Tour',
        dashboard: 'Dashboard Tour',
        data_mapping: 'Data Mapping Tour',
        data_subjects: 'Data Subjects Tour',
        deletion_orchestration: 'Deletion Orchestration Tour',
        discovery: 'Discovery Tour',
        governance: 'Governance Tour',
        help_center: 'Help Center Tour',
        incident_ops: 'Incident Operations Tour',
        incidents: 'Incidents Tour',
        localization: 'Localization Tour',
        monitoring: 'Monitoring Tour',
        onboarding: 'Onboarding Tour',
        organizations: 'Organizations Tour',
        partner_workspace: 'Partner Workspace Tour',
        platform_settings: 'Platform Settings Tour',
        privacy_scanner: 'Privacy Scanner Tour',
        program_governance: 'Program Governance Tour',
        rights_requests: 'Rights Requests Tour',
        task_management: 'Task Management Tour',
        vendor_risk: 'Vendor Risk Tour',
    },
    tour_descriptions: {
        access_accountability: 'As the Internal Audit Lead (P-VN-10), review access evidence, audit permission usage, and investigate anomalous patterns to demonstrate GDPR Article 5(1)(f) integrity and confidentiality.',
        assessments: 'As the Enterprise DPO (P-VN-01), conduct processing impact assessments under Decree 356, identify compliance gaps, and assess cross-border transfer risks under PDPL.',
        audit_trail: 'As the Internal Audit Lead (P-VN-10), query cross-module evidence, assemble audit packs, and export tamper-evident logs for regulatory submissions.',
        classification: 'As the Data Steward (P-VN-05), define data classification taxonomies, assign sensitivity labels, enforce retention policies, and resolve coverage gaps required for PDPL compliance.',
        compliance_forms: 'As the Enterprise DPO (P-VN-01), browse, complete, and submit official Vietnamese regulatory forms including Mẫu số 02a, Mẫu số 02b and other statutory forms for Ministry of Public Security submissions.',
        connectors: 'As the IT Systems Lead (P-VN-05), capture business requirements for integrations, map connectors to compliance data flows, and verify POC readiness before going live.',
        consent_properties: 'As the Enterprise DPO (P-VN-01), configure CMP deployment: digital property registration, golden templates for consistent PDPL consent banners, and binding matrices that link properties to processing activities.',
        consent: 'As the Enterprise DPO (P-VN-01), manage the consent policy lifecycle: define collection purposes, enforce PDPL withdrawal rights, and integrate with your Consent Management Platform.',
        dashboard: 'As the Enterprise DPO (P-VN-01), orient yourself around the compliance posture command center that surfaces PDPL deadlines, breach SLAs, and audit-readiness KPIs.',
        data_mapping: 'As the Data Steward (P-VN-05), build your ROPA inventory, map cross-border data flows, and maintain the processing activity register required by PDPL.',
        data_subjects: 'As the Enterprise DPO (P-VN-01) or Education Compliance Lead (P-VN-13), manage unified data subject profiles that link consent records, rights requests, and timeline activity across the platform.',
        deletion_orchestration: 'As the DPO (P-VN-01), verify end-to-end data deletion across internal systems and third-party vendors, with cryptographic proof chains for GDPR Article 17 right-to-erasure compliance.',
        discovery: 'As the Data Steward (P-VN-05), run automated scans to discover personal data across connected systems, review staged findings, and feed results into the Classification module for PDPL labeling.',
        governance: 'As the Vendor Risk Manager (P-VN-08), maintain the vendor registry, track subprocessor chains, assess cross-border transfer risks, and enforce governance playbooks under PDPL.',
        help_center: 'For all personas -- DPOs, auditors, IT leads, and compliance managers -- find answers, how-to guides, and module-specific documentation to support your daily compliance workflows.',
        incident_ops: 'As the Incident Commander (P-VN-09), activate response playbooks, track SLA compliance against the 72-hour PDPL notification window, manage escalations, and coordinate vendor response.',
        incidents: 'As the Incident Commander (P-VN-09), detect and report data breaches with 72-hour PDPL notification obligations to the PDP Committee, triage by severity, and build audit-grade incident dossiers.',
        localization: 'As the Enterprise DPO (P-VN-01), manage the regulatory framework lifecycle: install Vietnamese market packs, activate PDPL and Decree 13 frameworks, map requirements to platform modules, and manage translations.',
        monitoring: 'As the Enterprise DPO (P-VN-01), establish ongoing compliance monitoring programs, track regulatory changes, measure KPIs, and manage regulatory interaction cases under PDPL.',
        onboarding: 'As the Compliance Trainer (P-VN-02), deliver training via guided tours, load realistic sample data for hands-on exercises, and run the setup wizard for quick-start scenarios.',
        organizations: 'As the DPO (P-VN-01), configure multi-organization compliance structures, establish department hierarchies, and route data subject requests across your corporate group.',
        partner_workspace: 'As a Partner Account Manager, onboard partner organizations, configure service tiers, track compliance deliverables, and manage the partner marketplace.',
        platform_settings: 'As the Platform Administrator, manage all operational configuration -- identity, security, integrations, and module access that underpin your compliance program.',
        privacy_scanner: 'As the Privacy Analyst (P-VN-06), run continuous monitoring scans to detect cookie consent violations, unauthorized trackers, and policy gaps -- generating evidence for ePrivacy and GDPR compliance.',
        program_governance: 'Navigate between Maturity Models, Assessments, Evidence Gaps, Executive Summaries, and more. Each sub-module contributes to a holistic view of your internal program health.',
        rights_requests: 'As the Enterprise DPO (P-VN-01), manage data subject rights requests under PDPL with 15-day statutory deadlines, identity verification, and audit-grade resolution trails.',
        task_management: 'Route compliance tasks across personas, track SLA deadlines, and automate workflows that keep your team accountable.',
        vendor_risk: 'As the Vendor Risk Manager (P-VN-08), score third-party risk, enforce gate reviews, and trigger reassessments -- distinct from Program Governance, which tracks your internal maturity.',
    },
    // ---------- Consolidation (Sprint 28.7.1) ----------
    consolidation: {
        title: 'Consolidation Cases',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Create Consolidation Case',
        case_count: 'case',
        created: 'Consolidation case created',
        create: 'Create Case',
        creating: 'Creating...',
        cancel: 'Cancel',
        no_cases: 'No consolidation cases found.',
        all_statuses: 'All Statuses',
        all_purposes: 'All Purposes',
        filter_status: 'Filter by status',
        filter_purpose: 'Filter by purpose',
        col_title: 'Title',
        col_purpose: 'Purpose',
        col_status: 'Status',
        col_progress: 'Progress',
        col_deadline: 'Deadline',
        col_department: 'Department',
        col_version: 'Version',
        col_evidence: 'Evidence',
        sections_accepted: 'accepted',
        prev: 'Previous',
        next: 'Next',
        page: 'Page',
        prev_page: 'Previous page',
        next_page: 'Next page',
        field_title: 'Title',
        field_purpose: 'Purpose',
        field_framework: 'Framework Code',
        field_deadline: 'Deadline',
        // fix-sprint-20260821-2140 F7 — this said 'Reviewer User ID', and the
        // placeholder below said 'Enter reviewer user ID'. PR #2609 replaced the
        // free-text reviewer input with a people picker showing names and email
        // addresses, and reused the copy written for the control it replaced — so
        // the shipped modal labelled a dropdown of names 'Reviewer User ID *' and
        // told the operator to type an id into it. The same commit fixed exactly
        // this mismatch one field over (`framework_placeholder`, below).
        field_reviewer: 'Reviewer',
        title_placeholder: 'Enter case title',
        // The framework field is a picker over the org's installed frameworks, not
        // a free-text code, so this must not read as an example to type.
        framework_placeholder: 'Select framework (optional)',
        reviewer_placeholder: 'Select reviewer',
        /*
         * fix-sprint-20260821-2140 F6 — labels for the section-input modal's
         * default fields.
         *
         * `DEFAULT_SECTION_FIELD_KEYS` (section-fields.ts) is
         * ['summary','details','risk_assessment','mitigation_measures'], and the
         * modal looked each one up as `field_${key}`. None of the four existed in
         * ANY of the 16 bundles, so the fallback fired every time and every locale
         * saw the raw lower-cased English key names as both the visible label and
         * the accessible name — on the primary form the modal exists to present,
         * in the state every freshly assigned section starts in.
         */
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        // fix-sprint-20260717-2118-followup — dedicated section titles for
        // ConsolidationCaseCreatePage. Field-level `field_title`/`field_deadline`
        // keys were being reused as section headings, and both had a hardcoded
        // English fallback in the JSX.
        section_case_context: 'Case context',
        section_review_plan: 'Review plan',
        purposes: {
            dpia_filing: 'DPIA Filing',
            cross_border_filing: 'Cross-Border Filing',
            annual_governance_attestation: 'Annual Governance Attestation',
            breach_package: 'Breach Package',
            audit_inspection_pack: 'Audit Inspection Pack',
        },
        statuses: {
            draft: 'Draft',
            collecting: 'Collecting',
            consolidating: 'Consolidating',
            review: 'Review',
            corrections: 'Corrections',
            pending_approval: 'Pending Approval',
            approved: 'Approved',
            completed: 'Completed',
            closed: 'Closed',
            rejected: 'Rejected',
        },
        section_statuses: {
            pending: 'Pending',
            in_progress: 'In Progress',
            submitted: 'Submitted',
            accepted: 'Accepted',
            needs_correction: 'Needs Correction',
            corrected: 'Corrected',
        },
        flag_types: {
            missing_section: 'Missing Section',
            conflicting_entry: 'Conflicting Entry',
            stale_entry: 'Stale Entry',
            missing_evidence: 'Missing Evidence',
            incomplete_data: 'Incomplete Data',
            duplicate_entry: 'Duplicate Entry',
        },
        flag_severities: {
            critical: 'Critical',
            warning: 'Warning',
            info: 'Info',
        },
        sections_title: 'Sections',
        no_sections: 'No sections assigned yet.',
        flags_title: 'Flags',
        no_flags: 'No flags detected.',
        open: 'open',
        approval_chain: 'Approval Chain',
        pending: 'Pending',
        audit_trail: 'Audit Trail',
        no_audit: 'No audit events.',
        auto_assign: 'Auto-Assign Sections',
        assign_department: 'Assign Department',
        assign_dept_modal_title: 'Assign Department Section',
        field_section_title: 'Section Title',
        field_section_description: 'Description',
        field_assignee: 'Assignee',
        select_department_placeholder: 'Select department…',
        select_assignee_placeholder: 'Select assignee…',
        section_assigned: 'Section assigned',
        start_collection: 'Start Collection',
        review_case: 'Review Case',
        generate_export: 'Generate Export',
        review_title: 'Consolidation Review',
        advance_approval: 'Advance to Approval',
        back_to_case: 'Back to Case',
        accept: 'Accept',
        return_section: 'Return',
        return_comment: 'Return comment...',
        resolve: 'Resolve',
        dismiss: 'Dismiss',
        resolution_notes: 'Resolution notes...',
        notes: 'Notes',
        section_accepted: 'Section accepted',
        section_returned: 'Section returned for correction',
        advanced_to_approval: 'Case advanced to pending approval',
        section_data: 'Section Data',
        save_progress: 'Save Progress',
        submit_section: 'Submit Section',
        submitting: 'Submitting...',
        resubmit: 'Resubmit',
        progress_saved: 'Progress saved',
        section_submitted: 'Section submitted',
        correction_required: 'Correction Required',
        evidence_title: 'Evidence',
        no_evidence: 'No evidence attached.',
        add_evidence: 'Add Evidence',
        evidence_added: 'Evidence added',
        evidence_files: 'evidence files',
        uploaded_by: 'uploaded by',
        comments_title: 'Comments',
        no_comments: 'No comments yet.',
        comment_placeholder: 'Add a comment...',
        add_comment: 'Add Comment',
        comment_added: 'Comment added',
        view_data: 'View submitted data',
        export_title: 'Dossier Export',
        export_generated: 'Export package generated',
        regenerate: 'Regenerate',
        regenerating: 'Regenerating...',
        generated_at: 'Generated at',
        artifacts_title: 'Export Artifacts',
        artifact_type: 'Artifact',
        file_name: 'File Name',
        file_size: 'Size',
        generated: 'Generated',
        download: 'Download',
        artifacts: {
            master_report: 'Master Report',
            evidence_bundle: 'Evidence Bundle (ZIP)',
            contributor_lineage: 'Contributor Lineage',
            conflict_resolution: 'Conflict Resolution',
            approval_history: 'Approval History',
        },
        official_forms: 'Official Forms',
        forms_description: 'The following official forms can be generated from the consolidated data. Official forms are separate from internal working papers.',
        announce_loaded_cases: 'Loaded {count} consolidation cases',
        announce_case_detail_loaded: 'Case detail loaded',
        announce_case_transitioned: 'Case transitioned to {status}',
        announce_flag_resolved: 'Flag resolved',
        announce_flag_dismissed: 'Flag dismissed',
        case_detail_loaded: 'Case detail loaded',
        sections_auto_assigned: 'Sections auto-assigned from department hierarchy',
        errors: {
            load_failed: 'Failed to load data. Please try again.',
            create_failed: 'Failed to create case. Please try again.',
            required_fields: 'Please fill all required fields.',
            not_found: 'Not found.',
            comment_required: 'Comment is required when returning a section.',
            notes_required: 'Notes are required when resolving or dismissing a flag.',
            assign_missing_fields: 'Department and assignee are required.',
            assign_failed: 'Failed to assign section. Please try again.',
            no_departments_with_heads: 'No active departments with assigned heads were found. Please add department heads or assign sections manually.',
            // fix-sprint-20260827-2129 F18 — these four were hardcoded English string
            // literals in `ConsolidationCaseDetailPage`, rendered into the page-level
            // `role="alert"` region, so a Vietnamese operator whose transition failed
            // without a server message read and heard English.
            transition_failed: 'Failed to change case status. Please try again.',
            auto_assign_failed: 'Failed to auto-assign sections. Please try again.',
            save_failed: 'Failed to save section. Please try again.',
            submit_failed: 'Failed to submit section. Please try again.',
            // fix-sprint-20260827-2129 residue (B-F18 class) — the review, section and
            // export pages' catch blocks fell back to hardcoded English.
            accept_failed: 'Failed to accept section. Please try again.',
            return_failed: 'Failed to return section. Please try again.',
            flag_action_failed: 'Failed to update the flag. Please try again.',
            comment_failed: 'Failed to add comment. Please try again.',
            evidence_failed: 'Failed to add evidence. Please try again.',
            export_failed: 'Failed to generate the export package. Please try again.',
        },
    },
    // ---------- Rights Request Import (Sprint 40.0.0) ----------
    rights_requests: {
        import: {
            title: 'Import Cases',
            nav_title: 'Bulk Import',
            description: 'Upload a CSV file to bulk import rights request cases.',
            select_file: 'Select CSV File',
            file_help: 'CSV with headers matching case fields.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'File too large. Maximum size is 2MB.',
            preview: 'Preview',
            columns_detected: 'Columns detected: {count}',
            column_mapping: 'Column Mapping',
            import_button: 'Import',
            import_complete: 'Import complete',
            import_error: 'Import failed',
            result: 'Import Result',
            total: 'Total',
            imported: 'Imported',
            skipped: 'Skipped',
            failed: 'Failed',
            errors: 'Errors',
            error_row: 'Row',
            error_field: 'Field',
            error_message: 'Message',
            history: 'Import History',
            no_history: 'No import history.',
            history_file: 'Filename',
            history_date: 'Date',
        },
        // Sprint 48.1.1 (finish) — DSR Batch Process page (Forseti AI workflow).
        batch: {
            nav_title: 'Batch Process',
            page_title: 'DSR Batch Process',
            page_subtitle: 'Upload a CSV of pending DSRs. Forseti drafts a per-request acknowledgement response and queues a verification task per row. The DPO reviews + accepts each before send.',
            upload_heading: 'Upload',
            csv_hint: "Required columns: request_id, requester_email, request_type. Optional: deadline (ISO-8601), notes, requester_locale (the language of the drafted response, for example en or vi; when it is empty, the organisation's default language is used).",
            rows_heading: '{count} rows parsed',
            submit_label: 'Run batch',
            submitting: 'Drafting…',
            col_request_id: 'Request id',
            col_email: 'Requester email',
            col_type: 'Type',
            col_status: 'Status',
            col_actions: 'Actions',
            status_queued: 'Queued',
            status_drafted: 'Drafted',
            status_failed: 'Failed',
            status_accepted: 'Accepted',
            status_rejected: 'Rejected',
            status_parsing_error: 'Parse error',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Accept',
            reject: 'Reject',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    // ---------- Assessment Triggers settings (Sprint 40.1.0) ----------
    assessment_triggers: {
        title: 'Assessment Triggers',
        subtitle: 'Configure automated assessment creation and DPO notifications for privacy workflows.',
        auto_create_dpia_label: 'Auto-create DPIA when processing activity marked as DPIA required',
        auto_notify_new_purpose_label: 'Notify the DPO when a new processing purpose is created',
        auto_notify_cross_border_label: 'Auto-notify DPO when cross-border transfer added to processing activity',
        default_dpo_label: 'Default DPO',
        default_dpo_placeholder: 'Select default DPO...',
        notification_channels_label: 'Notification Channels',
        channel_in_app: 'In-App',
        channel_email: 'Email',
        save_success: 'Assessment trigger settings saved.',
        save_error: 'Failed to save assessment trigger settings.',
        fetch_error: 'Failed to load assessment trigger settings.',
        fetch_users_error: 'Failed to load users.',
        announce_loaded: 'Assessment trigger settings loaded',
        dpia_status: 'DPIA Status',
        dpia_linked: 'DPIA Linked',
        dpia_required_no_dpia: 'DPIA Required — Not Created',
        create_dpia: 'Create DPIA',
        create_dpia_success: 'DPIA created and linked to activity.',
        create_dpia_error: 'Failed to create DPIA for activity.',
        view_dpia: 'View DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    // ---------- Data Subject Profiles (Sprint 40.2.0) ----------
    data_subjects: {
        title: 'Data Subject Profiles',
        nav_title: 'Data Subjects',
        nav: {
            profiles: 'Profiles',
        },
        create: 'Create Profile',
        edit: 'Edit Profile',
        delete: 'Delete Profile',
        delete_confirm: 'Are you sure you want to delete this data subject profile?',
        display_name: 'Display Name',
        email: 'Email',
        phone: 'Phone',
        employee_id: 'Employee ID',
        student_id: 'Student ID',
        notes: 'Notes',
        external_refs: 'External References',
        cases_count: 'Cases',
        workflows_count: 'Consent Records',
        last_activity: 'Last Activity',
        timeline: 'Timeline',
        link_case: 'Add DSR Case',
        link_workflow: 'Add Consent Workflow',
        no_profiles: 'No data subject profiles found.',
        search_placeholder: 'Search by name, email, phone, or ID...',
        create_success: 'Data subject profile created.',
        create_error: 'Failed to create data subject profile.',
        update_success: 'Data subject profile updated.',
        update_error: 'Failed to update data subject profile.',
        delete_success: 'Data subject profile deleted.',
        delete_error: 'Failed to delete data subject profile.',
        link_case_success: 'Case linked to profile.',
        link_case_error: 'Failed to link case to profile.',
        link_workflow_success: 'Workflow linked to profile.',
        link_workflow_error: 'Failed to link workflow to profile.',
        load_error: 'Failed to load data subject profiles.',
        detail_load_error: 'Failed to load data subject profile.',
        timeline_load_error: 'Failed to load timeline.',
        tab_cases: 'DSR Cases',
        tab_consent: 'Consent Records',
        tab_timeline: 'Timeline',
        tab_consent_workflows: 'Consent Workflows',
        tab_web_consent: 'CMP Consent',
        web_consent_empty: 'No CMP consent records found for this subject.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Failed to load CMP consent records.',
        col_policy_version: 'Policy Version',
        col_versions: 'Versions',
        col_consent_version: 'Consent',
        col_cookie_declaration_version: 'Cookie Declaration',
        col_purposes: 'Purposes',
        purpose_granted: 'Granted',
        purpose_denied: 'Denied',
        granted_domains: 'Granted',
        denied_domains: 'Denied',
        col_action: 'Action',
        col_timestamp: 'Timestamp',
        col_domain: 'Domain',
        no_cases: 'No linked cases.',
        no_workflows: 'No linked consent workflows.',
        no_timeline: 'No timeline events.',
        case_id: 'Case ID',
        case_status: 'Status',
        case_type: 'Request Type',
        case_created: 'Created',
        workflow_id: 'Workflow ID',
        workflow_type: 'Type',
        workflow_status: 'Status',
        workflow_created: 'Created',
        timeline_date: 'Date',
        timeline_event: 'Event',
        timeline_details: 'Details',
        timeline_time: 'Time',
        timeline_type: 'Type',
        timeline_summary: 'Summary',
        type_case: 'DSR Case',
        type_consent_workflow: 'Consent Workflow',
        profile_info: 'Profile Information',
        announce_loaded: 'Data subject profiles loaded',
        announce_detail_loaded: 'Data subject profile loaded',
        select_case: '— Select a case —',
        select_workflow: '— Select a workflow —',
        no_cases_hint: 'Link cases to see them here.',
        no_workflows_hint: 'Link consent workflows to see them here.',
        // fix-sprint-20260411-0440-followup F2/F3 — the link pickers' failed option reads.
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    // fix-sprint-20260415-0434 F13 — Visual Data Flow Map (FlowMapView) keys.
    flows: {
        map: {
            description_prefix: '{systems} systems · {flows} flows · {crossBorder} cross-border · {highRisk} high/critical risk',
            switch_to_list: 'Switch to list view',
            switch_to_list_aria: 'Switch to table view',
            svg_aria: 'Data flow map: {systems} systems, {flows} flows, {crossBorder} cross-border',
            selected_system: 'Selected system',
            drill_hint: 'Open the list tab to edit the linked flows, DPIAs, and vendor assignments for this system.',
        },
    },
    packages: {
        light: {
            name: 'Light',
            description: 'Essential privacy compliance tools for small teams',
        },
        standard: {
            name: 'Standard',
            description: 'Full-featured compliance platform for growing organizations',
        },
        enterprise_unlimited: {
            name: 'Enterprise Unlimited',
            description: 'Enterprise-grade compliance with multi-org support and dedicated services',
        },
    },
    // Sprint 47.5.1 — Forseti AI Legal Advisor: provider settings + usage dashboard
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Provider Settings',
            subtitle: 'Configure the Claude provider for the Forseti AI Legal Advisor.',
            healthHeading: 'Provider health',
            statusLabel: 'Status',
            statusOk: 'OK',
            statusFailed: 'Unavailable',
            providerLabel: 'Provider',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI relay',
            providerAesirManaged: 'Aesir-managed tokens',
            lastCheckedLabel: 'Last checked',
            reasonLabel: 'Reason',
            refreshButton: 'Refresh',
            apiKeyHeading: 'Claude API key',
            apiKeyHint: 'Setting an API key activates Claude API mode. Leave unset to use the local CLI relay (developer / self-hosted mode).',
            apiKeyLabel: 'API key',
            apiKeyMinLength: 'Minimum 32 characters. Encrypted at rest; never shown again.',
            apiKeyTooShort: 'API key must be at least 32 characters.',
            apiKeySaved: 'API key saved. Refreshing health…',
            apiKeyCleared: 'API key cleared. Forseti now uses the local CLI relay.',
            setKeyButton: 'Save API key',
            clearKeyButton: 'Clear API key (use CLI relay)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Usage',
            recentHeading: 'Recent activity',
            historyHeading: 'Last 12 periods',
            noActivity: 'No activity yet this period.',
            featureLabel: 'Feature',
            whenLabel: 'When',
            periodLabel: 'Period',
            // Sprint 47.5.9.6 — by-feature breakdown + admin per-user view.
            byFeatureHeading: 'This month by feature',
            byFeatureEmpty: 'No feature activity yet this period.',
            featureColumn: 'Feature',
            featureUnitsColumn: 'Units',
            featureRunsColumn: 'Runs',
            byUserHeading: 'This month by user (admin view)',
            byUserEmpty: 'No per-user activity yet this period.',
            userColumn: 'User',
            reconciliationLinkLabel: 'View reconciliation drift report',
            reconciliationLinkHint: 'Aesir-internal admin only.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Chat',
                workflow: 'Workflow run',
                tabular: 'Tabular extraction',
                redline: 'Redline review',
                title: 'Conversation title',
                unknown: 'Other',
            },
        },
        // Sprint 47.5.9.6 — Forseti reconciliation drift report (Aesir-internal only).
        reconciliation: {
            title: 'Forseti Quota Reconciliation',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Back to Forseti Usage',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'This page is restricted to Aesir-internal administrators. If you believe you should have access, contact your AesirX representative.',
        },
        // Sprint 47.5.2 — Forseti Projects (workspace shell).
        projects: {
            title: 'Projects',
            subtitle: 'Workspaces for documents, conversations, workflows, and reviews.',
            modeLabel: 'View',
            tabMine: 'My projects',
            tabShared: 'Shared with me',
            searchLabel: 'Search projects',
            searchPlaceholder: 'Search…',
            newButton: 'New project',
            colName: 'Name',
            colVisibility: 'Visibility',
            colDocs: 'Documents',
            colMembers: 'Members',
            colUpdated: 'Updated',
            visibilityOwn: 'Own only',
            visibilityShared: 'Shared',
            emptyMine: 'No projects yet. Create one to get started.',
            emptyShared: 'No projects have been shared with you.',
            pageHint: 'Showing {shown} of {total}.',
            createTitle: 'New project',
            createSubmit: 'Create project',
            fieldName: 'Name',
            fieldDescription: 'Description (optional)',
            fieldCmNumber: 'Case/matter number (optional)',
            fieldVisibility: 'Visibility',
            errorNameRequired: 'Name is required.',
            errorSelectorRequired: 'Select a user or org group to add.',
            // Detail page
            renameButton: 'Rename',
            renamePrompt: 'Rename project',
            deleteButton: 'Delete',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Delete this folder? Documents inside will remain in the project, just unfiled.',
            removeMemberConfirm: 'Remove this member?',
            foldersHeading: 'Folders',
            foldersEmpty: 'No folders yet.',
            addFolderButton: 'New folder',
            addFolderTitle: 'New folder',
            documentsHeading: 'Documents',
            documentsEmpty: 'No documents attached yet.',
            colFilename: 'Filename',
            colAdded: 'Added',
            unattachButton: 'Unattach',
            membersHeading: 'Members',
            membersEmpty: 'Owner only.',
            addMemberButton: 'Add member',
            addMemberTitle: 'Add member',
            addMemberSubmit: 'Add member',
            colMember: 'Member',
            colRole: 'Role',
            selectorTypeLabel: 'Add by',
            selectorUser: 'User ID',
            selectorGroup: 'Org group ID',
            fieldUserId: 'User',
            fieldGroupId: 'Org group',
            fieldRole: 'Role',
            roleAdmin: 'Admin',
            roleEditor: 'Editor',
            roleViewer: 'Viewer',
            notFound: 'Project not found.',
            // Sprint 47.5.9.3.1 — selector picker copy + ConfirmDialog titles.
            selectUserPlaceholder: 'Select a user…',
            selectGroupPlaceholder: 'Select an org group…',
            noUsersAvailable: 'No users available.',
            noGroupsAvailable: 'No org groups available.',
            loadFailed: 'Failed to load options.',
            deleteProjectTitle: 'Delete project?',
            deleteFolderTitle: 'Delete folder?',
            removeMemberTitle: 'Remove member?',
            removeMemberConfirmGeneric: 'Remove this member from the project?',
            addDocumentButton: 'Add document',
            addDocumentTitle: 'Add document to project',
            fieldDocument: 'Select document',
            selectDocPlaceholder: 'Search for a document...',
            ownerLabel: 'System Owner',
        },
        // Sprint 47.5.4a — Forseti chat shell (read-only). Streaming + tools land in 47.5.4b.
        chat: {
            sidebarLabel: 'Chats',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Your AI Legal & Compliance Advisor — grounded in your tenant data and your installed regulatory packs.',
            personaSelectorLabel: 'Persona',
            personaHeading: 'Choose a persona',
            startChatButton: 'Start a new chat',
            startingChat: 'Starting chat…',
            recentHeading: 'Recent chats',
            recentEmpty: 'No chats yet — start one above.',
            untitledChat: 'New chat',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Conversation',
            detailSubtitle: 'Persona: {persona}',
            composeHeading: 'Send a message',
            composeLabel: 'Message Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persona',
            presetPersona: 'Preset persona',
            changePersona: 'Change persona',
            hidePersonaOptions: 'Hide persona options',
            starterHeading: 'Start with one of these actions',
            renameButton: 'Rename',
            renamePrompt: 'Rename chat',
            deleteButton: 'Delete',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chat not found.',
            emptyChat: 'No messages yet — say hello to start a conversation.',
            roleUser: 'You',
            roleAssistant: 'Forseti',
            roleSystem: 'System',
            thinkingLabel: 'Thinking',
            toolCallPlaceholder: 'Tool call: ',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Document edits proposed (V{version}).',
            disclaimer: 'Forseti supports your judgement — it does not replace licensed counsel.',
            // Sprint 47.5.4b — streaming + defenses keys.
            sendButton: 'Send',
            sending: 'Sending…',
            composePlaceholder: 'Ask Forseti…',
            composingPlaceholder: 'Forseti is responding…',
            streamingPlaceholder: 'Forseti is typing…',
            promptInjectionTitle: 'Forseti dropped some control tokens.',
            promptInjectionBody: 'Stripped patterns: {patterns}. Forseti will only see your message text.',
            rateLimitTitle: "You're sending messages too fast.",
            rateLimitRetryAfter: 'Try again in {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti is uncertain here.',
            counselReviewBody: 'Confirm with licensed counsel before acting on this answer.',
            legalConclusionRefused: 'Forseti refused to issue a legal conclusion without grounding. Try search_regulatory first or escalate to counsel.',
            // Sprint 47.5.4c — ToolCallCard, CreateTaskFromChatModal,
            // AutoRouteHint, ExportChatButton, MessageList affordances.
            toolCallDone: 'Done.',
            toolCallArgsLabel: 'Arguments',
            toolCallResultLabel: 'Result',
            toolResultLogged: 'Tool result captured in audit log.',
            toolResultAria: 'tool result',
            documentEditAria: 'document edit',
            createTaskFromMessage: 'Create task from this',
            createTaskHeading: 'Create task from this message',
            createTaskTitleLabel: 'Title',
            createTaskTitleRequired: 'Title is required.',
            createTaskDescriptionLabel: 'Description',
            createTaskAssigneeLabel: 'Assignee',
            createTaskAssigneePlaceholder: 'Select assignee…',
            createTaskAssigneeNone: '— unassigned —',
            createTaskDueDateLabel: 'Due date',
            createTaskWorkflowLabel: 'Linked workflow (optional)',
            createTaskWorkflowPlaceholder: 'Select a workflow…',
            createTaskWorkflowNone: '— none —',
            // Sprint 47.5.9.3.1 — replace window.prompt rename + window.confirm delete.
            renameModalSubmit: 'Rename',
            renameModalSaving: 'Saving…',
            deleteConfirmTitle: 'Delete chat?',
            createTaskSubmit: 'Create task',
            createTaskSubmitting: 'Creating…',
            cancelButton: 'Cancel',
            autoRouteHintMessage: "This question is more in {persona}'s wheelhouse.",
            autoRouteSwitchButton: 'Switch to {persona}',
            autoRouteSwitching: 'Switching…',
            handoffSuggestionMessage: 'Or open a new chat with {persona} carrying this question forward.',
            handoffSuggestionButton: 'Start new chat with {persona}',
            handoffSuggestionWorking: 'Opening…',
            handoffSuggestionDismiss: 'Not now',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Compliance Specialist',
                'forseti-legal': 'Legal Counsel',
                'forseti-dpo': 'DPO Advisor',
                'forseti-risk': 'Risk Analyst',
                'forseti-auditor': "Auditor's Eye",
            },
            exportButton: 'Export PDF',
            exportRunning: 'Exporting…',
            exportSuccess: 'Exported and saved to the File Browser: {filename}.',
            exportOpenFile: 'Open in File Browser',
            exportDownload: 'Download PDF',
            // Sprint 47.5.7 — citation pill (renders [FrameworkCode Art.X §Y]).
            citation: {
                verifyBadge: '[VERIFY]',
                verifyAria: 'citation could not be verified — review before relying',
                popoverAria: 'Source for {label}',
                openSource: 'Open source page',
            },
        },
        // Sprint 47.8.0c — Forseti badge on the existing /admin/audit-trail
        // page. Detected by event_type prefix client-side; the full
        // grc_ai_audit_events UNION lives on the dedicated lineage page.
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-generated event from the Forseti audit trail.',
        },
        // Sprint 47.8.0b — row-action variant of AdvisorRunPanel surfaced on
        // every list page for the 16 record types. Status-blind on the
        // client; the tool runner enforces mutation refusal server-side.
        rowAction: {
            shortLabel: 'Forseti',
            ask: 'Ask Forseti',
            tooltipOpen: 'Ask Forseti about this {recordType}',
            tooltipLocked: 'This {recordType} is locked — Forseti can answer questions but cannot make changes.',
        },
        // Sprint 47.5.8 — embeddable advisor panel (cross-module entry points).
        // Sprint 47.8.0a — visual redesign: avatar + heading template + R-39
        // disclaimer banner + per-record-type friendly labels.
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            workbenchLabel: 'Forseti Workbench',
            currentRecordLabel: 'Current record',
            // Sprint 63.29.0 (Q5, #540) — the summary is read only when asked for.
            showSummary: 'Show summary',
            summaryLoading: 'Loading summary…',
            summaryLoadFailed: 'The summary could not be loaded.',
            summaryUnavailable: 'No summary is available for this record.',
            summaryOnRequestHint: 'The summary is read only when you ask for it, and each read is recorded in the access log.',
            title: 'Forseti — about this record',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Summary',
            suggestedPersona: 'Suggested persona',
            askForseti: 'Ask Forseti',
            runWorkflow: 'Run a Workflow',
            viewLineage: 'View Audit Lineage',
            createPdplEvidencePack: 'Create PDPL Evidence Pack',
            prepareBreachDraft: 'Prepare notification draft',
            primaryActions: {
                projectSummary: 'Summarize Project Document',
                supplementSummary: 'Summarize Supplement Request',
                authoritySummary: 'Summarize Authority Request',
                rightsRequestResponse: 'Draft DSR Response',
                dpiaQuickReview: 'Run DPIA Quick Review',
                taskControlWalkthrough: 'Walk Through Control Evidence',
                vendorRiskNarrative: 'Generate Vendor Risk Narrative',
                incidentLetter: 'Draft PDPL Breach Letter',
                ropaSufficiency: 'Run RoPA Sufficiency Check',
                vendorDpaReview: 'Run Vendor DPA Review',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti prepares draft content for the linked Mẫu số 08 only. It does not decide whether notification is required and does not file or send the form.',
            breachDraftReady: 'Mẫu số 08 draft ready for review',
            breachDraftReviewBeforeSelecting: 'Review before selecting',
            breachDraftResolved: {
                accepted: 'Accepted: {count} fields applied',
                partially_accepted: 'Partially accepted: {count} fields applied',
                rejected: 'Rejected: no fields applied',
            },
            workflowHeading: 'Relevant workflows',
            workflowLoading: 'Loading relevant workflows…',
            workflowEmpty: 'No suitable workflow found. Create a guided workflow for this record.',
            workflowError: 'Could not load relevant workflows.',
            selectWorkflow: 'Select {title}',
            workflowSelected: 'Workflow selected and ready to run against this record.',
            workflowRunSelected: 'Run selected workflow',
            workflowStarting: 'Starting workflow…',
            workflowRunError: 'Could not start this workflow.',
            workflowCreateGuided: 'Create a guided workflow for this record',
            lineageHeading: 'Audit lineage for this record',
            lineageLoading: 'Loading audit lineage…',
            lineageEmpty: 'No Forseti lineage events are recorded for this record.',
            lineageError: 'Could not load audit lineage.',
            retry: 'Retry',
            chatContextLoading: 'Loading record context…',
            chatGuidance: {
                general: 'Forseti can summarize this record, identify missing information, and suggest review steps.',
                privacy: 'Forseti can review privacy facts, check installed-framework obligations, and help prepare a draft for human review.',
                compliance: 'Forseti can identify compliance gaps, ground requirements in installed sources, and suggest accountable next steps.',
                legal: 'Forseti can summarize the record, locate grounded source material, and help prepare a draft for counsel review.',
                risk: 'Forseti can assess recorded risks, flag missing facts, and suggest proportionate review steps.',
                audit: 'Forseti can inspect evidence gaps, relate the record to installed controls, and suggest audit-ready follow-up.',
            },
            starterPrompts: {
                identifyGaps: 'What information or evidence is missing from this record?',
                reviewFrameworks: 'Which obligations from our installed frameworks should I review for this record?',
                prepareDraft: 'Help me prepare a draft based only on this record and grounded installed sources.',
                nextSteps: 'What review steps should I take next?',
                summarizeRecord: 'Summarize this record and flag points that need human review.',
                assessRisk: 'Assess the recorded risks, identify missing facts, and suggest review steps.',
            },
            disclaimer: 'Forseti supports your judgement — it does not replace licensed counsel.',
            recordType: {
                project: 'Project',
                dpia: 'DPIA',
                dossier_filing: 'Dossier Filing',
                transfer_assessment: 'Cross-Border Transfer',
                vendor: 'Vendor',
                vendor_contract: 'Vendor Contract',
                vendor_acceptance: 'Vendor Risk Acceptance',
                incident: 'Incident',
                incident_op: 'Incident Operation',
                breach_notification: 'Breach Notification',
                authority_request: 'Authority Request',
                supplement_request: 'Supplement Request',
                rights_request: 'Rights Request',
                contract: 'Contract',
                task: 'Task',
                data_mapping_activity: 'RoPA Activity',
                compliance_rule: 'Compliance Rule',
                program: 'Program',
                governance_document: 'Governance Document',
                remediation_plan: 'Remediation Plan',
                cross_border_filing: 'Cross-Border Filing',
                // Sprint 63.22.0 (OD-A5) — the ten 62.13+ registers with a detail view.
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        // Sprint 47.9.6 — DPIA autocomplete cross-module workflow exemplar.
        // Pattern doc: docs/engineering/forseti-cross-module-workflows.md.
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Ask Forseti to draft this',
            drafting: 'Drafting…',
            hint_pick_activity: 'Pick a linked processing activity to enable Forseti drafting.',
            draft_origin_label: 'Forseti draft — review per field',
            tokens_label: '≈ {count} tokens',
            accept_selected: 'Accept selected',
            reject_all: 'Reject draft',
            field_checkbox_label: 'Apply Forseti draft for {field}',
            // Sprint 48.4.x — translatable labels for the 9 DpiaAutocompleteFieldKey
            // enum values so the per-field review UI never renders raw English
            // snake_case identifiers (i18n-raw-key-as-label fix).
            field_labels: {
                description: 'Description',
                purpose: 'Purpose',
                data_subjects: 'Data subjects',
                data_types: 'Data types',
                necessity_justification: 'Necessity & proportionality',
                consultation_notes: 'Consultation notes',
                dpo_opinion: 'DPO opinion',
                system_ids: 'Linked systems',
                vendor_ids: 'Linked vendors',
            },
            error_generic: 'Forseti could not draft this DPIA. Try again or refine the activity record.',
            // Sprint 48.1.0 — framework variant chip group + conflict banner.
            variant_legend: 'Framework frame',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'No installed framework available',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Heads up: {count} cross-framework conflict(s) apply to this variant. Review before accepting.',
        },
        // Sprint 48.1.3 — Cross-border TIA autocomplete (transfer-assessment workflow).
        formDraft: {
            button_label: 'Ask Forseti to draft this form',
            drafting: 'Drafting…',
            draft_origin_label: 'Forseti draft — review each field',
            tokens_label: '≈ {count} tokens',
            accept_selected: 'Accept selected',
            reject_all: 'Reject draft',
            field_checkbox_label: 'Apply Forseti draft for {field}',
            excluded_note: '{count} field(s) can’t be drafted (references, attachments, or read-only).',
            error_generic: 'Forseti could not draft this form. Try again.',
            empty_draft: 'Forseti did not produce any field drafts for this form.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Ask Forseti to draft this TIA',
            drafting: 'Drafting TIA…',
            hint_pick_destination: 'Set the destination country on the transfer record before Forseti can draft.',
            draft_origin_label: 'Forseti TIA draft — review per field',
            tokens_label: '≈ {count} tokens',
            usage_details: 'AI usage details',
            accept_selected: 'Accept selected',
            reject_all: 'Reject draft',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Recommended record updates',
            mechanism_recommendation: 'Transfer mechanism: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Set a review deadline before updating this sensitive transfer.',
            processing_activity_warning: 'Link at least one processing activity / ROPA record before completing the TIA.',
            go_to_field: 'Go to field',
            field_checkbox_label: 'Apply Forseti draft for {field}',
            error_generic: 'Forseti could not draft this TIA. Try again or refine the transfer record.',
            adequacy_label: 'Destination regime',
            adequacy_label_source_route: 'Transfer route',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_status_transitional: 'Transitional / EEA shortcut',
            adequacy_status_unknown: 'Unknown — set destination',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Heads up: {count} cross-framework conflict(s) apply to this transfer regime. Review before accepting.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Missing required facts',
            missing_facts_hint: 'Forseti flagged facts that are not in the record. Fields that depend on them are not pre-selected — supply the facts or accept the field deliberately.',
            risk_label: 'Calibrated risk',
            risk_low: 'Low',
            risk_medium: 'Medium',
            risk_high: 'High',
            jurisdiction_label: 'Jurisdiction triggers',
            jurisdiction_none_installed: 'No installed framework covers this assessment — no legal regime was assessed. Install a regulatory pack that does before relying on the legal conclusions in this draft.',
            jurisdiction_none_assessed: 'Forseti returned no jurisdiction assessment. Nothing was assessed — this is not a finding that no regime applies.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Loading the names of the assessed regimes…',
            trigger_applicable: 'Applies',
            trigger_not_applicable: 'Not triggered',
            trigger_unknown: 'Unknown — fact missing',
            recipient_role_label: 'Recipient role',
            recipient_role_controller: 'Controller',
            recipient_role_processor: 'Processor',
            recipient_role_joint_controller: 'Joint controller',
            recipient_role_unknown: 'Unknown — not stated',
            opt_in_hint: 'Legal conclusion — opt in only if you accept it with the facts on hand.',
        },
        // Sprint 47.9.7 — MCP connector admin (read-only Phase 1).
        mcp: {
            pageTitle: 'MCP Connector Keys',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Issue new key',
            issueModalTitle: 'Issue MCP API key',
            labelField: 'Label',
            labelHint: 'A short description (e.g. "AI capability lab — regulatory research").',
            permittedToolsField: 'Permitted tools',
            expiresAtField: 'Expires (optional)',
            expiresAtHint: 'Leave blank for never-expires.',
            submit: 'Issue key',
            cancel: 'Cancel',
            keyDisplayedOnce: 'Copy this key now — you will NOT be able to see it again. Store it in your secret manager (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: 'I copied it — close',
            colLabel: 'Label',
            colPrefix: 'Prefix',
            colStatus: 'Status',
            colTools: 'Tools',
            colCreatedAt: 'Created',
            colLastUsed: 'Last used',
            colCalls: 'Calls',
            colActions: 'Actions',
            statusActive: 'Active',
            statusRevoked: 'Revoked',
            statusExpired: 'Expired',
            revokeAction: 'Revoke',
            rotateAction: 'Rotate',
            confirmRevokeTitle: 'Revoke this MCP key?',
            confirmRevokeBody: 'External Claude clients using this key will lose access immediately. This cannot be undone.',
            confirmRevoke: 'Revoke key',
            rotatedTitle: 'Key rotated',
            rotatedBody: 'The new plaintext key is below. The old key continues to work for 5 minutes so any in-flight calls finish.',
            emptyState: 'No MCP keys issued yet.',
            errorIssue: 'Could not issue the MCP key.',
            errorList: 'Could not load MCP keys.',
            errorRevoke: 'Could not revoke the key.',
            errorRotate: 'Could not rotate the key.',
            neverUsed: '—',
            neverExpires: 'Never',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again if it is back on within 30 days of each app’s last token refresh; after that, people connect the app again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again if it is back on within 30 days of each app’s last token refresh. After that, people connect the app again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        // Sprint 48.0.0c — Forseti memory browse / curate.
        memory: {
            pageTitle: 'Forseti memory',
            pageSubtitle: 'Browse and curate the org-specific context Forseti has accumulated across conversations and decisions. Curated entries lock against nightly retention and stay available to every persona.',
            filterCategory: 'Category',
            filterAllCategories: 'All categories',
            totalCount: '{count} entries',
            colAnchor: 'Anchor',
            colCategory: 'Category',
            colSource: 'Source',
            colContent: 'Content',
            colCreatedAt: 'Created',
            colActions: 'Actions',
            editButton: 'Edit',
            deleteButton: 'Delete',
            promoteButton: 'Promote to curated',
            emptyState: 'No memories yet — Forseti will accumulate context as you chat.',
            categories: {
                framework_interpretation: 'Framework interpretation',
                process_preference: 'Process preference',
                escalation_threshold: 'Escalation threshold',
                decision_context: 'Decision context',
                glossary_term: 'Glossary term',
                auto_inferred: 'Auto-inferred',
            },
            sources: {
                admin_curated: 'Curated',
                user_explicit: 'Explicit',
                auto: 'Inferred',
            },
            deleteConfirmTitle: 'Delete this memory?',
            deleteConfirmBody: 'Deleting will remove this anchor from every future Forseti chat boot. Anchor: {anchor}',
            deleteConfirmLabel: 'Delete',
            deleteCancelLabel: 'Cancel',
            editModalTitle: 'Edit memory',
            editAnchorLabel: 'Anchor',
            editCategoryLabel: 'Category',
            editContentLabel: 'Content',
            editContentCount: '{n}/{max} characters',
            editEmptyError: 'Content cannot be empty.',
            editTooLongError: 'Content must be ≤1000 characters.',
            editAutoPromoteHint: 'Saving this auto-inferred memory will mark it as user-confirmed and remove it from the retention sweep.',
            editCancelButton: 'Cancel',
            editSaveButton: 'Save',
            editSaving: 'Saving…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        // Sprint 47.5.8 — audit lineage page (cross-module Forseti graph).
        auditLineage: {
            title: 'Audit lineage',
            subtitle: 'Trace every AI-generated artefact attached to a record. Chats, tool calls, tasks, evidence drafts, and document versions.',
            filterForm: 'Filters',
            recordType: 'Record type',
            recordId: 'Record ID',
            dateStart: 'From',
            dateEnd: 'To',
            search: 'Search',
            loading: 'Loading…',
            exportPdf: 'Export PDF',
            exportCsv: 'Export CSV',
            nodesHeading: 'Lineage nodes',
            colType: 'Type',
            colLabel: 'Label',
            colOccurredAt: 'Occurred at',
            colActor: 'Actor',
            colId: 'ID',
            noResults: 'No advisor activity for this record.',
            draftBadge: 'AI-generated draft',
            prev: 'Previous',
            next: 'Next',
            // Sprint 47.5.8.3 — optional graph view.
            viewToggleTable: 'View as table',
            viewToggleGraph: 'View as graph',
            graphAriaLabel: 'Lineage graph (visual). Use the table view for assistive technology.',
            graphLegendDraft: 'Draft AI evidence',
            graphEmpty: 'No nodes to graph.',
            // Sprint 47.5.9.5 — localised aria labels for the lineage page
            // pagination nav and the export status template (replaces the
            // hardcoded English `aria-label="pagination"` and the English
            // `Error: …` / `${format.toUpperCase()}: ${filename}` toasts).
            paginationAriaLabel: 'Pagination',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Export failed: {message}',
            truncatedNotice: 'This lineage is not complete. The platform reads at most a fixed number of rows from each source, newest first, and the limit was reached for: {sources}. The oldest rows beyond a limit, if there are any, are not shown. Narrow the date range to bring them in.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Project',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Vendor',
                incident: 'Incident',
                incident_op: 'Incident operation',
                rights_request: 'Rights request',
                contract: 'Contract',
                task: 'Task',
                data_mapping_activity: 'Data mapping activity',
                compliance_rule: 'Compliance rule',
                program: 'Program',
            },
            nodeTypes: {
                chat: 'Chat',
                tool_call: 'Tool call',
                workflow_run: 'Workflow run',
                task: 'Task',
                evidence: 'Evidence',
                document_version: 'Document version',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        // Sprint 47.5.x — Integrity Center verdict labels (consumed by IAT page).
        integrityCenter: {
            verdicts: {
                ok: 'OK',
                drifted: 'Drifted',
                hash_mismatch: 'Hash mismatch',
                merkle_mismatch: 'Merkle mismatch',
                source_missing: 'Source missing',
                not_found: 'Not found',
                chain_unreachable: 'Chain unreachable',
                service_unreachable: 'Service unreachable',
                pending: 'Pending anchoring',
            },
        },
        // Sprint 47.5.8 — evidence draft review chrome (move-to-review/approve/reject).
        evidenceDraft: {
            draftBadge: 'AI-generated DRAFT',
            pendingReviewBadge: 'Pending review',
            approvedBadge: 'Approved',
            rejectedBadge: 'Rejected',
            moveToReviewButton: 'Mark ready for review',
            approveButton: 'Approve',
            rejectButton: 'Reject',
            rejectReasonLabel: 'Rejection reason',
            humanSignoffBanner: 'Forseti supports your judgement — it does not replace licensed counsel. AI-generated evidence requires human approval before it counts as formal evidence.',
            // Sprint 47.5.8.6 — DraftEvidencePage chrome.
            pageTitle: 'AI evidence drafts',
            pageSubtitle: 'Review, approve, or reject AI-generated evidence. Drafts must be approved by a human before they count as formal evidence.',
            filterAll: 'All actionable',
            filterDrafts: 'Drafts only',
            filterPending: 'Pending review',
            filterApproved: 'Recently approved',
            filterRejected: 'Recently rejected',
            colId: 'Evidence ID',
            colStatus: 'Status',
            colSource: 'Source',
            colCreatedAt: 'Created',
            colActions: 'Actions',
            empty: 'No AI evidence drafts in this status.',
            confirmApprove: 'Approve this AI-generated evidence as formal?',
            confirmReject: 'Reject this evidence draft. This cannot be undone.',
            reasonRequired: 'A rejection reason is required.',
            rejectModalTitle: 'Reject draft evidence',
            // fix-sprint-20260503-2128 F39 — symmetric approve modal title.
            approveModalTitle: 'Approve draft evidence',
            operationFailed: 'Operation failed. Please try again.',
            moveToReviewSuccess: 'Marked ready for review.',
            approveSuccess: 'Approved.',
            rejectSuccess: 'Rejected.',
            // Sprint 47.5.9.3.3 (T-3) — source-label templates. The frontend
            // builds the source label at render time from the resolved title
            // (when projected by the backend) or the raw ID (legacy fallback).
            sourceLabelWorkflowRunTitle: 'Workflow: {title}',
            sourceLabelWorkflowRunId: 'Workflow run {id}',
            sourceLabelChatTitle: 'Chat: {title}',
            sourceLabelChatId: 'Forseti chat {id}',
            sourceLabelEmpty: '—',
            // Sprint 47.5.9.5 — localised aria-label for the filters group.
            filtersAriaLabel: 'Filter status',
        },
        // Sprint 47.5.5 — Forseti AI Workflows.
        workflows: {
            pageTitle: 'AI Workflows',
            pageSubtitle: 'Saved AI prompt templates that produce tasks and draft evidence.',
            libraryOverviewLabel: 'Workflow library overview',
            libraryEyebrow: 'Workflow library',
            libraryTitle: 'Turn compliance records into review-ready work',
            libraryDescription: 'Choose a governed workflow, select its source record, and review every result before it changes operational data.',
            availableLabel: 'Available',
            myWorkflowsLabel: 'Yours',
            customBadge: 'Custom',
            openWorkflow: 'Open workflow',
            governedWorkflowLabel: 'Governed built-in workflow',
            readyToRunHeading: 'Ready for an operator-reviewed run',
            workflowProfileHeading: 'Workflow profile',
            outputsHeading: 'Expected outputs',
            humanReviewLabel: 'Human review required',
            newWorkflow: 'New workflow',
            newWorkflowHeading: 'New AI workflow',
            tabAll: 'All',
            tabBuiltin: 'Built-in pack',
            tabMine: 'My workflows',
            tabDetail: 'Details',
            tabLinkedTasks: 'Linked tasks',
            builtinBadge: 'Built-in',
            builtinSubtitle: 'Built-in workflow.',
            userSubtitle: 'Custom workflow.',
            emptyHint: 'No workflows yet.',
            // Sprint 47.7.4 AC-7 — surface a hung load with a 10s timeout fallback.
            loadTimeoutError: 'Could not load workflows after 10 seconds. Retry, or check the browser Network tab for a hung request.',
            // fix-sprint-20260504-2137 F18 — workflow list error messages.
            loadError: 'Failed to load workflows.',
            hideError: 'Hide failed.',
            unhideError: 'Unhide failed.',
            retry: 'Retry',
            producesTasks: 'Produces tasks',
            producesEvidence: 'Produces draft evidence',
            hideButton: 'Hide from my list',
            unhideButton: 'Unhide',
            runButton: 'Run',
            editButton: 'Edit',
            shareButton: 'Share',
            backToList: 'Back to list',
            syncBuiltinButton: 'Sync built-in pack',
            syncBuiltinSuccess: 'Built-in pack synced ({loaded} workflows).',
            syncBuiltinError: 'Could not sync the built-in pack.',
            syncBuiltinRunning: 'Syncing built-in pack…',
            requireTitleAndPrompt: 'Title and prompt are required.',
            fieldTitle: 'Title',
            fieldDescription: 'Description',
            fieldType: 'Type',
            fieldPersona: 'Persona affinity',
            fieldPractice: 'Practice',
            fieldPrompt: 'Prompt (Markdown)',
            fieldProducesTasks: 'Produces tasks',
            fieldProducesEvidence: 'Produces draft evidence',
            fieldProjectId: 'Project (optional)',
            fieldAttachmentIds: 'Attachments',
            fieldParentRecord: 'Parent record (optional)',
            fieldPersonaOverride: 'Persona override (optional)',
            typeAssistant: 'Assistant',
            typeTabular: 'Tabular',
            practice_display: {
                privacy: 'Privacy',
                legal: 'Legal',
                compliance: 'Compliance',
                risk: 'Risk',
                audit: 'Audit',
            },
            personaNone: '— none —',
            parentNone: '— none —',
            parentIdPlaceholder: 'Parent record ID',
            // Sprint 47.5.9.3.1 — selector picker copy.
            selectProjectPlaceholder: 'Select a project…',
            selectProjectFirst: 'Select a project to choose attachments.',
            selectAttachmentsPlaceholder: 'Select documents…',
            noAttachmentsInProject: 'This project has no attached documents.',
            selectParentRecordPlaceholder: 'Select a record…',
            selectParentRecordFirst: 'Select a record type first.',
            noRecordsForType: 'No records of this type are available.',
            shareUserSelectorLabel: 'Recipients (users)',
            shareGroupSelectorLabel: 'Recipients (org groups)',
            selectUsersPlaceholder: 'Select users…',
            selectOrgGroupsPlaceholder: 'Select org groups…',
            loadingOptions: 'Loading…',
            loadFailed: 'Failed to load options.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Vendor',
                incident: 'Incident',
                rights_request: 'Rights Request',
                contract: 'Contract',
                data_mapping_activity: 'RoPA Activity',
                compliance_rule: 'Compliance Rule',
                program: 'Program',
            },
            cancelButton: 'Cancel',
            closeButton: 'Close',
            createButton: 'Create',
            creating: 'Creating…',
            saveButton: 'Save',
            saving: 'Saving…',
            shareWorkflowHeading: 'Share workflow',
            shareIntro: 'Sharing: {title}',
            shareUserIds: 'User IDs (space or comma separated)',
            shareGroupIds: 'Org Group IDs (space or comma separated)',
            shareAllowEdit: 'Allow recipients to edit',
            shareRequireRecipient: 'Provide at least one user id or group id.',
            shareSuccess: 'Shared.',
            sharing: 'Sharing…',
            runWorkflowHeading: 'Run: {title}',
            runStarting: 'Starting…',
            runStatusLabel: 'Status: {status}',
            runProducedCounts: 'Tasks: {tasks} · Draft evidence: {evidence} · Attachments: {attachments}',
            runPanelHeading: 'Workflow run',
            runPanelHeadingWithTitle: 'Workflow run: {title}',
            runViewTasks: 'View generated tasks',
            runViewEvidence: 'View draft evidence',
            runResultHeading: 'Advisor result',
            runNoResult: 'The run finished but did not return a reviewable result. No tasks, evidence, or attachments were created. Try again with the required record or documents selected.',
            runCitationWarning: 'Some citations could not be verified automatically. Review them before relying on this result.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Could not load the workflow run.',
            runStreamFailed: 'Workflow progress connection failed.',
            // Sprint 47.5.9.5 — translated workflow-run status labels + locale-aware
            // SSE progress lines (replaces the English-only `Status: …`,
            // `Task created: …`, `Draft evidence created: …` strings rendered live
            // in the WorkflowRunPanel). Per finding 3.16 in the 47.5.9.3 review,
            // raw ObjectIds are stripped from the visible label — the user sees
            // the human action, not the database key.
            runStatusValues: {
                pending: 'Pending',
                running: 'Running',
                completed: 'Completed',
                succeeded: 'Succeeded',
                failed: 'Failed',
                cancelled: 'Cancelled',
            },
            runProgressStatus: 'Status: {status}',
            runProgressTaskCreated: 'Task created.',
            runProgressEvidenceDrafted: 'Draft evidence created.',
            runProgressAttachmentGenerated: 'Attachment generated.',
            guidedWorkflowTitle: 'Guided {recordType} workflow',
            guidedWorkflowDescription: 'Created for {recordType} records. Review and customize this workflow before saving.',
            guidedWorkflowPrompt: 'Review the supplied {recordType} record, identify missing facts, and propose grounded next steps for human review.',
            promptHeading: 'Prompt',
            howItWorksHeading: 'What happens when you run this workflow',
            builtinGuidance: 'Forseti reviews the records and documents you select, returns an operator-reviewable result, and creates draft tasks or evidence only when the result supports them. Nothing is approved or submitted automatically.',
            editWorkflowHeading: 'Edit workflow',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Workflow not found.',
            // Sprint 47.7.2 — scheduled workflow runs.
            schedules: {
                tabTitle: 'Schedules',
                sectionHeading: 'Scheduled runs',
                empty: 'No schedules yet. Create one to run this workflow automatically.',
                addButton: 'Add schedule',
                addHeading: 'Schedule workflow',
                editHeading: 'Edit schedule',
                fieldFrequency: 'Frequency',
                fieldDayOfWeek: 'Day of week',
                fieldDayOfMonth: 'Day of month',
                fieldTime: 'Time (UTC)',
                fieldTimezone: 'Timezone',
                fieldProject: 'Project (optional)',
                fieldAttachments: 'Default attachments',
                fieldPersonaOverride: 'Persona override (optional)',
                frequencyDaily: 'Daily',
                frequencyWeekly: 'Weekly',
                frequencyMonthly: 'Monthly',
                weekdaySun: 'Sunday',
                weekdayMon: 'Monday',
                weekdayTue: 'Tuesday',
                weekdayWed: 'Wednesday',
                weekdayThu: 'Thursday',
                weekdayFri: 'Friday',
                weekdaySat: 'Saturday',
                statusActive: 'Active',
                statusPaused: 'Paused',
                nextRunLabel: 'Next run',
                lastRunLabel: 'Last run',
                pauseButton: 'Pause',
                resumeButton: 'Resume',
                deleteButton: 'Delete',
                triggerButton: 'Run now',
                savingButton: 'Saving…',
                saveButton: 'Save',
                triggered: 'Run started — check the run history.',
            },
            // Sprint 47.7.2 — conditional branching in workflow prompts.
            branching: {
                helpHeading: 'Conditional prompt syntax',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        // Sprint 47.7.3 — cross-framework conflict detection.
        crossFrameworkConflicts: {
            pageTitle: 'Cross-framework conflicts',
            pageSubtitle: 'Curated catalog of where your installed regulatory packs interact in conflicting ways. Forseti surfaces; humans decide.',
            noInstalledPacks: 'No regulatory packs installed for this organization — install at least two packs to surface cross-framework conflicts.',
            noConflicts: 'No conflicts match the current filters.',
            allAcknowledged: 'Every visible conflict has been acknowledged. Nice work.',
            columnSeverity: 'Severity',
            columnTitle: 'Title',
            columnFrameworks: 'Frameworks',
            columnTopic: 'Topic',
            columnStatus: 'Status',
            filterFrameworkPlaceholder: 'All frameworks',
            filterTopicPlaceholder: 'All topics',
            filterSeverityPlaceholder: 'All severities',
            filterStatusPlaceholder: 'All statuses',
            filterStatusAcknowledged: 'Acknowledged',
            filterStatusUnacknowledged: 'Unacknowledged',
            severityCritical: 'Critical',
            severityHigh: 'High',
            severityMedium: 'Medium',
            severityLow: 'Low',
            statusAcknowledged: 'Acknowledged',
            statusInReview: 'In review',
            statusResolved: 'Resolved',
            statusWontFix: "Won't fix",
            statusUnacknowledged: 'Unacknowledged',
            drawerSummaryHeading: 'Summary',
            drawerSideAHeading: 'Side A',
            drawerSideBHeading: 'Side B',
            drawerRequirementsLabel: 'Requirements cited',
            drawerProvisionsLabel: 'Provisions cited',
            drawerSuggestedResolutionHeading: 'Suggested resolution',
            drawerLegalCounselBadge: 'Legal counsel review required',
            drawerAckHeading: 'Acknowledgement',
            drawerAckStatusLabel: 'Status',
            drawerAckAssigneeLabel: 'Assigned to',
            drawerAckDueDateLabel: 'Due date',
            drawerAckNotesLabel: 'Notes',
            drawerAckButtonAcknowledge: 'Acknowledge',
            drawerAckButtonUpdate: 'Update',
            drawerAckButtonUnacknowledge: 'Un-acknowledge',
            // fix-sprint-20260912-2123 F23 — strings that were hardcoded English in
            // the drawer's TSX. `drawerCloseLabel` is the accessible name of the
            // drawer's ONLY dismiss control, so a Vietnamese session had no
            // localized way out of a `role="dialog"`.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Search title or summary…',
            // Sprint 47.9.1 — explicit search submit affordance.
            searchButton: 'Search',
            searchButtonAriaLabel: 'Run conflict search',
            // Sprint 47.9.1 — XLSX export sibling of the existing CSV path.
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Export failed: {message}',
            exportAriaLabel: 'Export conflicts',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        // Sprint 47.5.6 — Forseti Tabular Reviews (multi-document extraction grid).
        tabularReviews: {
            pageTitle: 'Tabular Reviews',
            pageSubtitle: 'Compare N documents across M attributes with grounded citations.',
            newReview: 'New review',
            newReviewHeading: 'New tabular review',
            emptyHint: 'No tabular reviews yet.',
            docsLabel: '{n} docs',
            colsLabel: '{n} cols',
            detailSubtitle: '{docs} docs · {cols} columns',
            addColumn: 'Add column',
            addColumnHeading: 'Add column',
            columnInfo: 'View prompt & format',
            columnInfoAria: 'Show prompt and format for column {name}',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Return format',
            columnInfoPresets: 'Allowed values',
            columnInfoCitations: 'Citations',
            columnInfoCitationsRequired: 'Required',
            removeColumn: 'Remove column',
            removeColumnAria: 'Remove column {name}',
            removeColumnConfirm: 'Remove the column "{name}"? Its extracted cells will be deleted.',
            deleteReview: 'Delete',
            deleteReviewAria: 'Delete review {title}',
            deleteReviewConfirm: 'Delete the review "{title}"? This permanently removes its columns, cells, and discussions.',
            runAll: 'Run all',
            running: 'Running…',
            cancel: 'Cancel',
            exportXlsx: 'Export Excel',
            exportCsv: 'Export CSV',
            exporting: 'Exporting…',
            backToList: 'Back to list',
            notFound: 'Tabular review not found.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Attach documents to start the review.',
            noColumns: 'Add columns to define what to extract.',
            tableAriaLabel: 'Extraction grid',
            // fix-sprint-20260504-2137 F15 — TRTable thread aria-labels.
            threadsCountAria: '{n} discussion post(s)',
            startDiscussionAria: 'Start discussion',
            docColumn: 'Document',
            statusPending: 'Pending',
            statusRunning: 'Running…',
            statusFailed: 'Failed: {msg}',
            statusCancelled: 'Cancelled',
            citationsCount: '{n} citation(s)',
            citationsView: 'View source passages',
            citationPage: 'Page {n}',
            titleRequired: 'Title is required.',
            fieldTitle: 'Title',
            fieldProjectId: 'Project (optional)',
            fieldAttachmentIds: 'Attachments',
            fieldColumnName: 'Column name',
            fieldColumnNameHelp: 'A short, descriptive label for this column in the extraction grid.',
            columnNamePlaceholder: 'e.g. Contract Start Date',
            fieldExtractionPrompt: 'Extraction prompt',
            fieldExtractionPromptHelp: 'Describe the information you want to extract. Be specific to get better results.',
            columnPromptPlaceholder: 'What should Forseti look for in the documents?',
            fieldReturnFormat: 'Return format',
            fieldReturnFormatHelp: 'Controls how the AI formats its response (e.g. as a date or a number).',
            fieldPresets: 'Allowed values (one per line)',
            fieldPresetsPlaceholder: 'yes\nno\npartial',
            fieldCitationsRequired: 'Citation required',
            formatShortText: 'Short text',
            formatLongText: 'Long text',
            formatEnum: 'Enum',
            formatNumber: 'Number',
            formatDate: 'Date',
            formatList: 'List',
            columnRequireFields: 'Name and prompt are required.',
            addColumnError: 'Add column failed.',
            cancelButton: 'Cancel',
            createButton: 'Create',
            creating: 'Creating…',
            addButton: 'Add',
            adding: 'Adding…',
            runConfirmHeading: 'Run extraction',
            estimateBody: 'Estimated ~{units} billable units ({confidence} confidence).',
            starting: 'Starting…',
            confirmRun: 'Confirm run',
            // Sprint 47.7.2 — column-level charts panel.
            charts: {
                title: 'Column charts',
                tabsAria: 'Chart columns',
                // fix-sprint-20260504-2137 F12/F13/F14 — chart aria/empty/excluded i18n.
                enumAria: 'Enum value distribution',
                numberAria: 'Number distribution histogram',
                dateAria: 'Date timeline',
                enumEmpty: 'No completed cells.',
                numberEmpty: 'No numeric values.',
                numberExcluded: '{n} cell(s) excluded (empty / non-numeric / not completed).',
                dateEmpty: 'No date values.',
                dateExcluded: '{n} cell(s) excluded.',
                unsupportedFormat: 'Unsupported column format.',
            },
            // Sprint 47.7.2 — per-cell discussion threads.
            threads: {
                drawerTitle: 'Discussion',
                loading: 'Loading…',
                empty: 'No posts yet. Start the conversation.',
                composerLabel: 'New comment',
                composerPlaceholder: 'Add a comment…',
                post: 'Post',
                posting: 'Posting…',
                edit: 'Edit',
                editLabel: 'Edit post',
                delete: 'Delete',
                save: 'Save',
                cancel: 'Cancel',
                close: 'Close',
                editedBadge: 'edited',
                // fix-sprint-20260504-2137 F19 — thread drawer error i18n.
                loadError: 'Failed to load thread',
                postError: 'Failed to post',
                editError: 'Failed to edit',
                deleteError: 'Failed to delete',
            },
            // Sprint 47.7.2 — cross-review comparison page.
            compare: {
                pageTitle: 'Compare reviews',
                leftReviewLabel: 'Left review',
                rightReviewLabel: 'Right review',
                pickReview: 'Pick a review to compare',
                noSharedDocuments: 'These reviews share no documents — comparison requires at least one common attachment.',
                sharedDocumentsHeading: 'Shared documents',
                leftOnlyHeading: 'Columns only in the left review',
                rightOnlyHeading: 'Columns only in the right review',
                differs: 'Differs',
                identical: 'Identical',
                emptyValue: '—',
                compareButton: 'Compare with…',
                // fix-sprint-20260504-2137 F2/F3/F4/F5 — compare page hardcoded strings.
                documentHeading: 'Document',
                errorMissingIds: 'Both left and right review ids are required.',
                errorComparisonFailed: 'Comparison failed.',
                errorLoadFailed: 'Failed to load reviews.',
                pickerLoading: 'Loading…',
                pickerNoOtherReviews: 'No other reviews available to compare with.',
                pickerCancel: 'Cancel',
            },
        },
        // Sprint 47.5.3a — Forseti Document Review (versions + AI edits).
        documentReview: {
            pageTitle: 'Document review',
            pageSubtitle: 'Accept or reject AI-suggested edits to this document.',
            sidePanelLabel: 'AI redlines',
            sidePanelHeading: 'AI redlines',
            noPendingEdits: 'No pending edits.',
            pendingCount: '{count} pending',
            acceptAllButton: 'Accept all',
            rejectAllButton: 'Reject all',
            acceptAllTitle: 'Accept all pending edits?',
            acceptAllConfirm: 'This will accept {count} pending edits and create one or more new versions. Continue?',
            editsHeading: 'Edits',
            editsEmpty: 'No edits to review.',
            versionsHeading: 'Version history',
            versionsEmpty: 'No versions yet.',
            editLabel: 'Edit',
            editLabelWithId: 'Edit #{id}',
            acceptButton: 'Accept',
            rejectButton: 'Reject',
            accepting: 'Accepting…',
            rejecting: 'Rejecting…',
            diffDeletedAria: 'Deleted',
            diffInsertedAria: 'Inserted',
            statusPending: 'Pending',
            statusAccepted: 'Accepted',
            statusRejected: 'Rejected',
            versionLabel: 'Version',
            versionAriaLabel: 'Version {n}',
            versionAriaLabelCurrent: 'Version {n} ({current})',
            versionCurrentTag: 'current',
            versionSourceLabel: 'Source',
            versionCreatedLabel: 'Created',
            versionModelLabel: 'Model',
            sourceUpload: 'Upload',
            sourceUserUpload: 'User upload',
            sourceAssistantEdit: 'Assistant edit',
            sourceUserAccept: 'User accepted edit',
            sourceUserReject: 'User rejected edit',
            sourceGenerated: 'Generated',
            // Extraction status + document text pane (47.9.x follow-on).
            extractionExtracted: 'Text extracted',
            extractionFailed: 'Text extraction failed',
            extractionUnsupported: 'Text could not be extracted',
            extractionPages: '{n} pages',
            extractionWords: '{n} words',
            extractionReasonScanned: 'This looks like a scanned or image-only document. Forseti cannot read it yet — upload a text-based file or paste the text instead.',
            extractionReasonEncrypted: 'The file is password-protected. Remove the protection and upload it again.',
            extractionReasonMalformed: 'The file could not be parsed and may be corrupted.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'The file is too large to process.',
            extractionReasonTimeout: 'Processing took too long. Try a smaller or simpler file.',
            extractionReasonUnknown: 'No further detail is available.',
            // Sprint B — Forseti actions on the document review detail page.
            // Both open a chat scoped to this document; the redline prompt is
            // a starter chip (ChatInput never auto-sends), so the LLM call
            // stays human-triggered.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Document text',
            textPaneTitle: 'Document text',
            textPaneVersion: 'Version {n}',
            textPaneEmpty: 'No text is available for this version. Scanned PDFs and some document types have no extractable text.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        // Sprint 47.8.0a — Document Review index page (replaces blank screen at
        // `/admin/forseti/document-review`).
        documentReviewIndex: {
            sidebarLabel: 'Document Review',
            pageTitle: 'Document Review',
            pageSubtitle: 'Documents in your organisation that have AI-suggested edits pending review or Forseti-sourced versions.',
            colDocument: 'Document',
            colLinkedRecord: 'Linked record',
            colPendingEdits: 'Pending edits',
            colLastActivity: 'Last activity',
            colOpen: 'Open',
            open: 'Open',
            empty: 'No documents under Forseti review yet. Documents enter this queue when Forseti suggests an edit during a chat, when an AI workflow produces a redline, or when you send a document for review using the button above.',
            loading: 'Loading…',
            loadError: 'Failed to load documents under review.',
            retryButton: 'Retry',
            noLinkedRecord: '—',
            pendingZero: 'None',
            // Sprint 47.7.4 AC-4 — Send-document-for-review CTA + modal labels.
            sendForReviewButton: 'Send a document for review',
            sendForReviewTitle: 'Send a document for review',
            sendForReviewSubtitle: 'Upload a .txt, .md, .pdf, or .docx document (up to 50 MB). It is added to the review queue and Forseti will produce a redline pass against it.',
            sendForReviewTabUpload: 'Upload new',
            sendForReviewPickerLabel: 'Search attachments',
            sendForReviewPickerPlaceholder: 'Type to search…',
            sendForReviewPickerEmpty: 'No matching attachments. Try a different search.',
            sendForReviewSubmit: 'Send for review',
            sendForReviewCancel: 'Cancel',
            sendForReviewSubmitting: 'Sending…',
            sendForReviewError: 'Could not send the document for review. Please try again.',
            sendForReviewSuccess: 'Document sent for review.',
            // fix-sprint-20260504-2137 F16 — file validation messages.
            sendForReviewAcceptedExt: 'Accepted: {ext}.',
            sendForReviewMaxSize: 'Max size {mb} MB.',
        },
        // fix-sprint-20260504-2137 F17 — Forseti chats (export errors).
        chats: {
            exportError: 'Export failed.',
        },
    },
    // Sprint 58.4.0 (Phase 25 / Sprint E1a) — PDPL Evidence Pack provider-config wizard.
    // Sprint 58.4.1 + 58.4.2 extend this namespace with pack-builder, draft-editor,
    // send and timeline sub-namespaces; keep keys grouped under `providers` so the
    // later sprints can extend without breaking the existing surface.
    pdplEvidencePacks: {
        title: 'PDPL Evidence Packs',
        subtitle: 'Customer-controlled MoPS A05 evidence submission',
        nav: {
            emailProviders: 'Email Providers',
        },
        providers: {
            pageTitle: 'Email Providers',
            pageSubtitle: "Configure your organisation's outbound mail provider for PDPL evidence-pack submissions to MoPS A05.",
            architectureRuleHeading: 'Architecture rule (locked)',
            architectureRuleBody: 'AesirX is not a sender of evidence-pack email. Every submission uses a provider you own and control. AesirX never sends on your behalf.',
            empty: 'No providers configured yet.',
            createCta: 'Create provider',
            statusPendingConsent: 'Awaiting consent',
            statusPendingConnectionCheck: 'Awaiting connection check',
            statusActive: 'Active',
            statusConnectionCheckFailed: 'Connection check failed',
            statusDisabled: 'Disabled',
            acceptConsentCta: 'Accept consent',
            runConnectionCheckCta: 'Run connection check',
            disableCta: 'Disable',
            disableConfirm: 'Disabling this provider stops direct sends until a new provider is configured and consent is re-acknowledged. Continue?',
            columnDisplayName: 'Name',
            columnFromAddress: 'From address',
            columnStatus: 'Status',
            columnLastCheck: 'Last check',
            columnActions: 'Actions',
            createModalTitle: 'New SMTP provider',
            createDisplayNameLabel: 'Display name',
            createFromAddressLabel: 'From address',
            createReplyToLabel: 'Reply-to address (optional)',
            createSmtpHostLabel: 'SMTP host',
            createSmtpPortLabel: 'SMTP port',
            createSmtpUsernameLabel: 'SMTP username',
            createSmtpPasswordLabel: 'SMTP password',
            createSmtpEncryptionLabel: 'Encryption',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implicit)',
            createSmtpEncryptionNone: 'None',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Coming soon',
            createOauthDisabledHint: 'OAuth providers are not yet enabled. Please use SMTP for now.',
            createSubmitCta: 'Create provider',
            createCancelCta: 'Cancel',
            createErrorInvalidFromAddress: 'From address must be a valid email mailbox.',
            createErrorDuplicateFromAddress: 'Another provider is already configured for this from-address.',
            createErrorGeneric: 'Could not create the provider. Please review the form and try again.',
            consentModalTitle: 'Acknowledge architecture rule',
            consentLocaleToggleEn: 'English',
            consentLocaleToggleVi: 'Tiếng Việt',
            consentAcknowledgeCta: 'I acknowledge — accept consent',
            consentCancelCta: 'Cancel',
            consentErrorMismatch: 'The consent text was modified and no longer matches the canonical disclaimer. Re-open this modal and submit again.',
            testModalTitle: 'Connection check',
            testRunningCta: 'Testing connection…',
            testRunCta: 'Run connection check',
            testRetryCta: 'Retry connection check',
            testSuccessHeading: 'Connection successful',
            testSuccessBody: 'Authenticated against {host}:{port} in {latencyMs} ms. The provider is ready for direct sends.',
            testFailureHeading: 'Connection failed',
            testFailureBody: 'The probe could not complete. Review the reason below and retry once corrected.',
            testFailureReasonLabel: 'Failure reason',
            testCloseCta: 'Close',
            detailDrawerTitle: 'Provider details',
            detailDrawerHistoryHeading: 'Connection check history',
            detailDrawerConsentHeading: 'Consent acknowledgement',
            detailDrawerNoHistory: 'No connection checks recorded yet.',
            detailDrawerNoConsent: 'Consent not yet acknowledged.',
            outcomeSuccess: 'Success',
            outcomeFailure: 'Failure',
        },
        // Sprint 58.4.1 (Phase 25 / Sprint E1b) — Pack Builder + Email Draft + Sent-Proof UI.
        builder: {
            pageTitle: 'Assemble evidence pack',
            pageSubtitle: 'Upload the 6 part files, pick the pack type, and assemble the deterministic 8-part ZIP for submission.',
            packTypeLabel: 'Pack type',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Part 01 — Cover letter (PDF)',
            part02AuthorityForm: 'Part 02 — Authority form (PDF)',
            part03Supporting: 'Part 03 — Supporting evidence (≥1)',
            part04Safeguards: 'Part 04 — Safeguards',
            part04SafeguardsRequiredOtia: '(required for OTIA)',
            part05PriorCorrespondence: 'Part 05 — Prior correspondence (optional)',
            part06Signature: 'Part 06 — Signature + stamp evidence (≥1)',
            uploadPickFile: 'Pick file',
            uploadPickFiles: 'Pick files',
            uploadRemoveFile: 'Remove',
            requiredMissing: 'Required part missing',
            assembleCta: 'Assemble pack',
            assembling: 'Assembling…',
            assembleSuccessHeading: 'Pack assembled',
            assembleSuccessSummary: '{fileCount} files · {bytes} bytes · SHA-256 {shaPrefix}…',
            composeNextCta: 'Compose email draft',
            warningsHeading: 'Validation warnings',
            assembleError: 'Could not assemble the pack. Review the inputs and try again.',
            forsetiAssistHeading: 'Forseti AI assistance (optional)',
            forsetiAssistHint: 'Run the PDPL Evidence Pack Assembly Advisor to surface missing parts, draft cover-letter prose, and flag safeguard gaps. AesirX is not the sender — the AI only advises; you approve every artefact.',
            forsetiAssistCta: 'Open Forseti workflow library',
            forsetiAiRunIdLabel: 'Linked Forseti AI run ID (paste to record AI assistance)',
        },
        draft: {
            pageTitle: 'Email draft',
            pageSubtitle: 'Compose and edit the email draft. Direct-send arrives in a follow-up release.',
            composeCta: 'Compose draft',
            composing: 'Composing…',
            noDraftYet: 'No draft yet. Compose one to get started.',
            localeToggleLabel: 'Locale',
            localeEn: 'English',
            localeVi: 'Tiếng Việt',
            subjectLabel: 'Subject',
            bodyLabel: 'Body',
            saveCta: 'Save changes',
            saving: 'Saving…',
            saveError: 'Could not save the draft. Please try again.',
            manifestSummaryLabel: 'Manifest summary',
            coverLetterLabel: 'Cover letter PDF',
            coverLetterDownloadCta: 'Download PDF',
            editHistoryHeading: 'Edit history',
            editHistoryEmpty: 'No edits yet.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 unofficial-channel notice',
            uploadSentProofCta: 'Upload manual sent-proof',
            versionLabel: 'Version',
            statusDraft: 'Draft',
            statusEdited: 'Edited',
            statusFinalized: 'Finalized',
        },
        sentProof: {
            modalTitle: 'Upload manual sent-proof',
            architectureLockNote: 'AesirX is not the sender. This proof captures the manual transmission you completed via your own mail client.',
            fileLabel: 'Proof file (.eml, .png, .jpg)',
            envelopeFromLabel: 'From',
            envelopeToLabel: 'To',
            envelopeSubjectLabel: 'Subject (at send)',
            envelopeSentAtLabel: 'Sent at',
            envelopeAttachmentShaLabel: 'Attachment SHA-256 (at send)',
            uploadCta: 'Upload proof',
            uploading: 'Uploading…',
            uploadSuccess: 'Sent-proof uploaded.',
            uploadErrorInvalidMime: 'File type not allowed. Use .eml, .png, .jpg, or .jpeg.',
            uploadErrorGeneric: 'Could not upload the proof. Please try again.',
            existingProofsHeading: 'Recorded sent-proofs',
            existingProofsEmpty: 'No sent-proofs recorded yet.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Evidence pack',
            notFoundMessage: 'Pack not found — it may have been removed or the link is incorrect.',
            manifestHeading: 'Manifest',
            dossierLabel: 'Dossier',
            packShaLabel: 'Pack SHA-256',
            fileCountLabel: 'Files',
            bytesLabel: 'Size',
            composeDraftCta: 'Compose draft',
            sendComingSoonCta: 'Direct send: coming soon',
            sendComingSoonHint: 'The direct-send flow is in active development. Use Upload manual sent-proof on the draft page until it ships.',
            sendCta: 'Send via configured provider',
            sendDisabledNoProviderHint: 'Configure a customer-controlled email provider first. AesirX never sends on your behalf.',
            timelineHeading: 'Send history',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Send evidence pack',
            providerPickerLabel: 'Configured email provider',
            providerPickerHint: 'AesirX never sends on your behalf. Pick one of your configured providers below.',
            providerPickerEmpty: 'No active providers are configured. Configure one on the Email Providers page before sending.',
            providerPickerEmptyCta: 'Open Email Providers',
            draftPickerLabel: 'Email draft',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'No drafts have been composed for this pack yet. Compose one on the draft page first.',
            recipientToLabel: 'To',
            recipientToHint: 'One or more email addresses, separated by commas.',
            recipientCcLabel: 'Cc (comma-separated)',
            recipientCcHint: 'Optional. Comma-separated list of email addresses.',
            recipientBccLabel: 'Bcc (comma-separated)',
            recipientBccHint: 'Optional. Comma-separated list of email addresses.',
            consentReaffirmHeading: 'Re-affirm provider consent',
            submitCta: 'Send now',
            sending: 'Sending…',
            cancelCta: 'Cancel',
            sendSuccess: 'Send recorded. The pack has been transmitted via your configured provider.',
            sendErrorGeneric: 'Could not send the pack. Please review the error below and try again.',
            sendErrorAlreadyFinalized: 'This draft has already been used for a successful send. Compose a new draft before sending again.',
            sendErrorInvalidRecipients: 'One or more recipient addresses are not valid email mailboxes.',
            // Timeline
            timelineEmpty: 'No sends recorded yet. Use Send via configured provider above to transmit through your customer-controlled SMTP / Microsoft 365 / Google Workspace provider.',
            timelineProviderLabel: 'Sent via your configured provider',
            timelineSentAtLabel: 'Sent',
            timelineRecipientLabel: 'Recipient',
            timelineOutcomeSent: 'Sent',
            timelineOutcomeFailed: 'Failed',
            timelineViewDetailsCta: 'View details',
            // Authority-response chips
            authorityChipNone: 'Awaiting response',
            authorityChipAcknowledgement: 'Acknowledgement',
            authorityChipAcceptance: 'Acceptance',
            authorityChipRejection: 'Rejection',
            authorityChipSupplementRequest: 'Supplement request',
            authorityChipNoResponse: 'No response',
            // Hard-copy chips
            hardCopyChipNone: 'No hard-copy follow-up',
            hardCopyChipPending: 'Posted — delivery pending',
            hardCopyChipDelivered: 'Delivered',
            // Drawer
            drawerTitle: 'Send record',
            drawerEnvelopeHeading: 'Envelope',
            drawerEnvelopeFromLabel: 'From',
            drawerEnvelopeToLabel: 'To',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Bcc',
            drawerEnvelopeSubjectLabel: 'Subject',
            drawerEnvelopeMessageIdLabel: 'Message-ID',
            drawerOutcomeHeading: 'Outcome',
            drawerSnapshotHeading: 'Chain-of-custody snapshot',
            drawerSnapshotPackShaLabel: 'Pack SHA-256 at send',
            drawerSnapshotDraftVersionLabel: 'Draft version at send',
            drawerSnapshotAttachmentLabel: 'Transmitted ZIP',
            drawerSnapshotAttachmentDownloadCta: 'Download ZIP',
            drawerAuthorityResponseHeading: 'Authority response',
            drawerHardCopyHeading: 'Hard-copy follow-up',
            drawerSentByLabel: 'Sent by',
            drawerSendIdLabel: 'Send record ID',
            drawerSmtpResponseLabel: 'SMTP response',
            drawerFailureReasonLabel: 'Failure reason',
            drawerLatencyLabel: 'Latency',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Accepted',
            drawerRejectedLabel: 'Rejected',
            drawerCloseCta: 'Close',
            // Authority-response panel
            authorityRecordCta: 'Record response',
            authorityReclassifyCta: 'Reclassify',
            authorityClassificationLabel: 'Classification',
            authorityReceivedAtLabel: 'Received at',
            authorityFreeTextLabel: 'Notes (optional)',
            authorityEvidenceLabel: 'Evidence file (.eml / .pdf / .png / .jpg — optional)',
            authorityReasonLabel: 'Reason for reclassification',
            authorityHistoryHeading: 'Reclassification history',
            authorityHistoryEmpty: 'No reclassifications recorded.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Save response',
            authoritySubmitting: 'Saving…',
            authorityErrorInvalidMime: 'Evidence file type not allowed. Use .eml, .pdf, .png, or .jpg.',
            authorityErrorGeneric: 'Could not save the response. Please try again.',
            authorityRecordedAtLabel: 'Recorded at',
            authorityRecordedByLabel: 'Recorded by',
            authorityEvidenceFilenameLabel: 'Evidence',
            authorityEvidenceDownloadCta: 'Download evidence',
            // Hard-copy panel
            hardCopyRecordCta: 'Log hard-copy follow-up',
            hardCopyConfirmCta: 'Confirm delivery',
            hardCopyEditNotesCta: 'Edit delivery notes',
            hardCopyPostedAtLabel: 'Posted at',
            hardCopyCourierLabel: 'Courier',
            hardCopyTrackingLabel: 'Tracking number',
            hardCopyRecipientLabel: 'Recipient on label',
            hardCopyReceiptLabel: 'Receipt evidence (.pdf / .png / .jpg — optional)',
            hardCopyReceiptDownloadCta: 'Download receipt',
            hardCopyDeliveredAtLabel: 'Delivered at',
            hardCopyDeliveryNotesLabel: 'Delivery notes (optional)',
            hardCopySubmitCta: 'Save follow-up',
            hardCopySubmitting: 'Saving…',
            hardCopyErrorInvalidMime: 'Receipt file type not allowed. Use .pdf, .png, or .jpg.',
            hardCopyErrorGeneric: 'Could not save the follow-up. Please try again.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: "The regulated-entity role your organization holds among people's credit funds, microfinance institutions, the cooperative bank and deposit insurance, if any. Requirements addressed to a role apply only when you select it.",
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            // Fix sprint 20261102 F2 — an opt-in overlay the installation's package does not include (OR-26).
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — named types in `en-extras.ts` (TS7056).
    org_identity: enOrgIdentity,
    form_prefill: enFormPrefill,
    // Owner rulings 2026-10-26 — idea #717 (the customs tax-administration export
    // headings) and OQ-86-6 (Cross-Border Filings points to the tax dossier register).
    customs_tax_exports: enCustomsTaxExports,
    cross_border_filing_fence: enCrossBorderFilingFence,
    // Sprint 63.43.0 (AC4, AC8) — Module 30 Sector Overlays: the Overview page.
    sector_overlays_module: enSectorOverlaysModule,
    // Sprint 62.47.0 Slice A — product-identification registers.
    product_identification: enProductIdentification,
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Completed dossier reports and notices',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=en.js.map